import React from "react";

const {useState,useMemo,useReducer,useRef,useEffect,useCallback} = React;
import { DOCS } from "./docs.js";

/* ============================================================================
   1. BACKEND ADAPTER  —  the only file section that talks to a server.
   ----------------------------------------------------------------------------
   Everything below `api` is pure UI. To go live, set USE_MOCK = false, point
   API_BASE at your server, and implement the endpoints listed in each method's
   comment. Full contract + suggested schema is in BACKEND-CONTRACT.md.
   ========================================================================== */
/* ----------------------------------------------------------------------------
   TENANCY

   Path-based, not subdomain-based: /o/:orgSlug/p/:projectSlug/...

   No wildcard DNS, no wildcard TLS, one cookie origin, and email links are
   trivial to generate. The deciding argument is the users you don't control —
   an inspector or a lender works for several developers at once, and making
   them hold four subdomains and four sessions is how they stop logging in.

   The URL only *routes*. It never authorizes. Resolve the org from the path,
   then check membership server-side, and put Postgres row-level security under
   it as a backstop so a missing WHERE clause can't leak another org's job.
   -------------------------------------------------------------------------- */
/* ----------------------------------------------------------------------------
   PRODUCT

   ProceedPoint. A notice to proceed is the authorization without which work
   cannot begin. This applies the same instrument to every line item and every
   payment: nothing proceeds until the parties who have to stand behind it have
   each signed. Not a milestone, not a checkpoint — a gate. That is the whole
   product.

   Screened Aug 2026: .com registered clear of conflicts, and clear of the two
   construction software companies already using the former placeholder name.
   See NAMING.md.
   -------------------------------------------------------------------------- */
const PRODUCT = {
  name: "ProceedPoint",
  tagline: "Nothing moves until it's signed off.",
  company: "JParkerConsulting LLC",
  year: 2026,
};
const COPYRIGHT = "© " + PRODUCT.year + " " + PRODUCT.company;

// The project and org in play. Set once the session and project are known —
// every method below reads them, so nothing hardcodes a tenant.
let ORG_SLUG = null;
let PROJECT_ID = null;
export const setTenant = (orgSlug, projectId) => { ORG_SLUG = orgSlug; PROJECT_ID = projectId; };

const API_BASE = "/api";

/**
 * While a lens is active the session still belongs to the owner, so a write
 * would act as the owner while the screen says somebody else. The buttons are
 * hidden too, but this is the guard that actually holds — one place, every
 * path, rather than trusting that no component forgot.
 */
let READ_ONLY = null;
export const setReadOnly = (why) => { READ_ONLY = why; };

async function http(method, path, body, isForm) {
  if (READ_ONLY && method !== "GET") {
    throw new Error(READ_ONLY);
  }
  const res = await fetch(path.startsWith("/auth") ? path : API_BASE + path, {
    method,
    credentials: "include",
    headers: isForm ? {} : { "Content-Type": "application/json" },
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });
  if (!res.ok) {
    let msg = res.status + " " + method + " " + path;
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch (e) {}
    throw new Error(msg);
  }
  return res.status === 204 ? null : res.json();
}

/**
 * The camera and the file picker hand us data URLs; the server wants the
 * original bytes as multipart. Converting here — rather than posting base64 —
 * is what keeps the stored file byte-identical to what came off the device,
 * which is the whole point of hashing it.
 */
async function toFile(dataUrl, filename, mime) {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], filename || "upload", { type: mime || blob.type || "application/octet-stream" });
}
async function uploadForm(photo, extra = {}) {
  const fd = new FormData();
  fd.append("file", await toFile(photo.url, photo.filename, photo.mime));
  if (photo.thumb) fd.append("thumb", await toFile(photo.thumb, "thumb.jpg", "image/jpeg"));
  for (const [k, v] of Object.entries(extra)) if (v !== undefined && v !== null && v !== "") fd.append(k, String(v));
  return fd;
}

const api = {
  // ---- session -------------------------------------------------------------
  me:        () => http("GET", "/auth/me"),
  login:     (payload) => http("POST", "/auth/login", payload),
  register:  (payload) => http("POST", "/auth/register", payload),
  logout:    () => http("POST", "/auth/logout"),
  forgot:    (payload) => http("POST", "/auth/forgot", payload),
  resetPassword: (payload) => http("POST", "/auth/reset", payload),
  changePassword: (payload) => http("POST", "/auth/change-password", payload),

  // ---- the project bundle --------------------------------------------------
  getProject: (id, lens) => http("GET", `/projects/${id}` +
    (lens && lens.role ? `?view=${lens.role}` + (lens.party ? `&party=${lens.party}` : ``) : ``)),
  updateProject: (id, patch) => http("PATCH", `/projects/${id}`, patch),
  initProject: (payload) => http("PATCH", `/projects/${PROJECT_ID}/setup`, payload.project || payload),
  resetProject: (payload) => http("POST", `/orgs/${ORG_SLUG}/projects`, payload.project || payload),
  createProject: (payload) => http("POST", `/orgs/${ORG_SLUG}/projects`, payload.project || payload),
  updateOrg: (payload) => http("PATCH", `/orgs/${ORG_SLUG}`, payload.patch),
  updateSettings: (payload) => http("PATCH", `/projects/${PROJECT_ID}/settings/${payload.section}`, payload.patch),
  alerts: () => http("GET", `/projects/${PROJECT_ID}/alerts`),
  verifyChain: () => http("GET", `/projects/${PROJECT_ID}/audit/verify`),

  // ---- the ledger ----------------------------------------------------------
  updateLineItem: (id, patch) => http("PATCH", `/line-items/${id}`, patch),
  createLineItem: (payload) => http("POST", `/projects/${PROJECT_ID}/line-items`, payload.item || payload),
  importLineItems: (projectId, payload) => http("POST", `/projects/${projectId || PROJECT_ID}/line-items:bulk`, payload),
  shiftDates: (payload) => http("POST", `/projects/${PROJECT_ID}/line-items:shift`, payload),
  transition: (payload) => http("POST", `/line-items/${payload.id}/transitions`, payload),
  addNote: (id, note) => http("POST", `/line-items/${id}/notes`, note),
  addPhoto: async (id, photo) =>
    http("POST", `/line-items/${id}/photos`, await uploadForm(photo, {
      kind: photo.kind, capturedAt: photo.capturedAt, lat: photo.lat, lng: photo.lng }), true),
  removePhoto: (itemId, photoId) => http("DELETE", `/photos/${photoId}`),

  // ---- assignment ----------------------------------------------------------
  assign: (payload) => http("POST", `/line-items/${payload.id}/assignment`, payload),
  respondToAssignment: (payload) => http("POST", `/line-items/${payload.id}/assignment:respond`, payload),
  reassign: (payload) => http("POST", `/line-items/${payload.id}/reassign`, payload),
  bulkAssign: (payload) => http("POST", `/projects/${PROJECT_ID}/line-items:assign`, payload),

  // ---- punch list ----------------------------------------------------------
  addPunch: (payload) => http("POST", `/line-items/${payload.id}/punch`, payload),
  punchTransition: (payload) => http("POST", `/punch/${payload.punchId}/transitions`, payload),
  addPunchComment: (payload) => http("POST", `/punch/${payload.punchId}/comments`, payload),
  addPunchPhoto: async (payload) =>
    http("POST", `/punch/${payload.punchId}/photos`, await uploadForm(payload.photo, { kind: payload.photo.kind || payload.kind }), true),

  // ---- draws and the payment file -----------------------------------------
  createDraw: (projectId, payload) => http("POST", `/projects/${projectId || PROJECT_ID}/draws`, payload),
  previewDraw: (payload) => http("POST", `/projects/${PROJECT_ID}/draws:preview`, payload),
  transitionDraw: (payload) => http("POST", `/draws/${payload.id}/transitions`, payload),
  addDrawDocument: async (payload) =>
    http("POST", `/draws/${payload.id}/documents`, await uploadForm(payload.doc, {
      type: payload.doc.type, amount: payload.doc.amount, reference: payload.doc.reference,
      paidOn: payload.doc.paidOn, note: payload.doc.note }), true),
  removeDrawDocument: (payload) => http("DELETE", `/draws/${payload.id}/documents/${payload.docId}`),
  drawGaps: () => http("GET", `/projects/${PROJECT_ID}/draws/gaps`),

  // ---- parties, invitations, onboarding ------------------------------------
  addParty: (payload) => http("POST", `/projects/${PROJECT_ID}/parties`, payload.party || payload),
  updateParty: (payload) => http("PATCH", `/parties/${payload.id}`, payload.patch),
  setPartyStatus: (payload) => http("POST", `/parties/${payload.id}/status`, payload),
  inviteMember: (payload) => http("POST", `/projects/${PROJECT_ID}/members`, payload),
  undeliverable: () => http("GET", `/projects/${PROJECT_ID}/email/undeliverable`),
  mailConfig: () => http("GET", `/projects/${PROJECT_ID}/email/config`),
  saveMyContact: (partyId, body) => http("PATCH", `/parties/${partyId}/contact`, body),
  saveEmailSettings: (patch) => http("PATCH", `/projects/${PROJECT_ID}/settings/email`, patch),
  releaseAddress: (address) => http("POST", `/projects/${PROJECT_ID}/email/release`, { address }),
  holdAddress: (address, note) => http("POST", `/projects/${PROJECT_ID}/email/hold`, { address, note }),
  heldDetail: (addresses) => http("GET", `/projects/${PROJECT_ID}/email/held?` +
    addresses.map((a) => "address=" + encodeURIComponent(a)).join("&")),
  resendInvite: (partyId) => http("POST", `/parties/${partyId}/invite`, {}),
  setGrants: (userId, grants) =>
    http("PATCH", `/projects/${PROJECT_ID}/members/${userId}/grants`, { grants }),
  uploadPaperwork: (payload) => {
    const fd = new FormData();
    fd.append("file", payload.file);
    fd.append("kind", payload.kind);
    if (payload.partyId) fd.append("partyId", payload.partyId);
    if (payload.title) fd.append("title", payload.title);
    return http("POST", `/projects/${PROJECT_ID}/party-documents`, fd, true);
  },
  reviewPaperwork: (id, body) => http("POST", `/party-documents/${id}/review`, body),
  withdrawPaperwork: (id) => http("DELETE", `/party-documents/${id}`),
  setMemberRole: (userId, role) =>
    http("PATCH", `/projects/${PROJECT_ID}/members/${userId}`, { role }),
  invite: (payload) => http("POST", `/parties/${payload.partyId}/invite`, payload),
  acceptInvite: (payload) => http("POST", `/invites/${payload.token}/accept`, payload),
  claimInvite: (id) => http("POST", `/invites/${id}/claim`, {}),
  acknowledgeTerms: (payload) => http("POST", `/parties/${payload.partyId}/terms`, payload),
  recordAdobe: (payload) => http("POST", `/parties/${payload.partyId}/agreement`, payload),

  // ---- site record ---------------------------------------------------------
  addDailyLog: (payload) => http("POST", `/projects/${PROJECT_ID}/daily-logs`, payload.entry || payload),
  addDailyPhoto: async (payload) =>
    http("POST", `/daily-logs/${payload.logId}/photos`, await uploadForm(payload.photo), true),

  // ---- procurement ---------------------------------------------------------
  saveSupplier: (payload) => payload.supplier.id
    ? http("PATCH", `/suppliers/${payload.supplier.id}`, payload.supplier)
    : http("POST", `/projects/${PROJECT_ID}/suppliers`, payload.supplier),
  saveProcurement: (payload) => payload.item.id
    ? http("PATCH", `/procurement/${payload.item.id}`, payload.item)
    : http("POST", `/projects/${PROJECT_ID}/procurement`, payload.item),

  // ---- messaging -----------------------------------------------------------
  // The compose form builds the thread nested; the route reads it flat, and had
  // been reading past it — every message composed in the UI arrived with no
  // subject, no recipients and no body, and no email went out. Unwrap here,
  // where the rest of this object already unwraps (see addParty).
  createThread: (payload) => http("POST", `/projects/${PROJECT_ID}/threads`, payload.thread || payload),
  postMessage: (payload) => http("POST", `/threads/${payload.threadId}/messages`, payload),
  markThreadRead: (payload) => http("POST", `/threads/${payload.threadId}/read`, payload),

  // Two counts. Cheap enough to ask repeatedly; see the pulse route.
  pulse: () => http("GET", `/projects/${PROJECT_ID}/pulse`),

  // ---- capital side --------------------------------------------------------
  saveNeed: (payload) => payload.need.id
    ? http("PATCH", `/funding-needs/${payload.need.id}`, payload.need)
    : http("POST", `/projects/${PROJECT_ID}/funding-needs`, payload.need),
  closeNeed: (payload) => http("PATCH", `/funding-needs/${payload.id}`, { status: payload.status || "closed" }),
  savePledge: (payload) => payload.pledge.id
    ? http("PATCH", `/pledges/${payload.pledge.id}`, payload.pledge)
    : http("POST", `/projects/${PROJECT_ID}/pledges`, payload.pledge),
  saveMilestone: (payload) => payload.milestone.id
    ? http("PATCH", `/milestones/${payload.milestone.id}`, payload.milestone)
    : http("POST", `/projects/${PROJECT_ID}/milestones`, payload.milestone),
  saveUpdate: (payload) => payload.update.id
    ? http("PATCH", `/updates/${payload.update.id}`, payload.update)
    : http("POST", `/projects/${PROJECT_ID}/updates`, payload.update),

  // ---- document inbox ------------------------------------------------------
  fileInboxItem: (payload) => http("POST", `/intake/${payload.id}/file`, payload),
  dismissInboxItem: (payload) => http("POST", `/intake/${payload.id}/dismiss`, payload),

  // ---- change orders -------------------------------------------------------
  // The authorisation, not the summary. A G703 revisions column records that a
  // contract changed; only this records who agreed to it.
  changeOrders: () => http("GET", `/projects/${PROJECT_ID}/change-orders`),
  proposeChangeOrder: (payload) => http("POST", `/projects/${PROJECT_ID}/change-orders`, payload),
  editChangeOrder: (payload) => http("PATCH", `/change-orders/${payload.id}`, payload),
  decideChangeOrder: (payload) => http("POST", `/change-orders/${payload.id}/decision`, payload),

  // ---- budget reconciliation ----------------------------------------------
  reconciliation: () => http("GET", `/projects/${PROJECT_ID}/reconciliation`),
  importBudget: (payload) => http("POST", `/projects/${PROJECT_ID}/budget-lines`, payload),
  mapBudgetLine: (payload) => http("POST", `/budget-lines/${payload.id}/map`, payload),
  setBaseline: (payload) => http("POST", `/projects/${PROJECT_ID}/baseline`, payload),

  // ---- the investor page -------------------------------------------------
  investorPage: () => http("GET", `/projects/${PROJECT_ID}/investor-page`),
  saveInvestorPage: (payload) => http("PUT", `/projects/${PROJECT_ID}/investor-page`, payload),
  publishInvestorPage: (payload) =>
    http("POST", `/projects/${PROJECT_ID}/investor-page/publish`, payload),
  uploadInvestorDocument: (payload) => {
    const fd = new FormData();
    fd.append("file", payload.file);
    for (const k of ["title", "description", "kind", "visible"]) {
      if (payload[k] !== undefined) fd.append(k, String(payload[k]));
    }
    return http("POST", `/projects/${PROJECT_ID}/investor-documents`, fd, true);
  },
  updateInvestorDocument: (payload) => http("PATCH", `/investor-documents/${payload.id}`, payload),
  deleteInvestorDocument: (id) => http("DELETE", `/investor-documents/${id}`),
  downloadInvestorDocument: (id) => http("GET", `/investor-documents/${id}/download`),
  expressInterest: (payload) => http("POST", `/projects/${PROJECT_ID}/interest`, payload),

  registerPush: (payload) => http("POST", `/push/subscriptions`, payload),
};

/* ============================================================================
   2. TAMPER-EVIDENT AUDIT CHAIN
   Each audit row hashes (prevHash + canonical payload). Change any historical
   row and every hash after it stops matching — visible on the Audit screen.
   Demo uses FNV-1a for a synchronous, dependency-free hash. IN PRODUCTION the
   server should chain with SHA-256 and sign the head hash daily.
   ========================================================================== */
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ("0000000" + h.toString(16)).slice(-8);
}
function chainHash(prev, ev) {
  return fnv1a(prev + "|" + ev.at + "|" + ev.actor + "|" + ev.role + "|" + ev.action + "|" + (ev.target||"") + "|" + (ev.detail||""));
}

/* ============================================================================
   3. DOMAIN CONSTANTS
   ========================================================================== */
// A "role" is a capability set. A "party" is an actual company or person that
// holds one. Multiple parties can hold the contractor role at the same time —
// that is how subs work, and how you replace a GC without losing the record.
const ROLES = {
  owner:      { label: "Owner / Developer",     signs: true,  scoped: false },
  contractor: { label: "Contractor",            signs: true,  scoped: true  },
  inspector:  { label: "Inspector / Architect", signs: true,  scoped: false },
  lender:     { label: "Lender / Bank",         signs: true,  scoped: false },
  // Scoped: an investor sees the capital picture and site progress, never the
  // contractor accountability machinery. Under the Fund II LOI they also carry a
  // confidentiality duty, so the narrower the surface the better.
  investor:   { label: "Investor",              signs: false, scoped: true, capitalOnly: true },
  // Somebody helping the owner run the project. What they may do is ticked on
  // person by person rather than fixed here, because "partner" is a
  // relationship, not a job.
  partner:    { label: "Partner / staff",       signs: true,  scoped: false },
};

/**
 * The capabilities an owner can hand to a partner, in the words they'd use.
 *
 * The server holds the authoritative list and refuses anything outside it; this
 * is the same list said in English, grouped the way somebody actually thinks
 * about delegating. `money: true` marks the ones that decide what gets paid.
 */
/**
 * Named bundles, for handing the same job to several people at once.
 *
 * A starting point, not a cage: applying one ticks its boxes and you adjust
 * from there. They are defined in terms of the same capabilities the server
 * knows about, so a preset can never grant something a person could not have
 * been granted one box at a time.
 */
const GRANT_PRESETS = [
  { id: "site", name: "Runs the site",
    what: "Daily logs, photos, punch items, suppliers — everything except money and settings.",
    caps: ["uploadPhoto", "postDailyLog", "manageSuppliers", "raisePunch", "verifyPunch"] },
  { id: "capital", name: "Runs the raise",
    what: "The investor briefing, pledges and updates. Nothing about the construction record.",
    caps: ["viewCapital", "manageCapital", "publishUpdate"] },
  { id: "signoff", name: "Signs off work",
    what: "Verifies and approves work, and creates draw requests. Decides what gets paid.",
    caps: ["verifyWork", "approveWork", "createDraw", "attachDrawDocument"] },
  { id: "readonly", name: "Looks, does not touch",
    what: "Nothing beyond what every partner already has: the project and the audit trail.",
    caps: [] },
];

const DELEGABLE_CAPS = [
  ["The project", [
    ["viewCapital",        "See the capital side", "Funding needs, pledges and the investor briefing."],
    ["manageCapital",      "Run the capital side", "Edit the investor briefing, record pledges, publish updates.", true],
    ["changeSettings",     "Change project settings", "Notifications, email and the project's own details."],
    ["manageParties",      "Manage contractors", "Add and update companies, insurance and paperwork.", true],
  ]],
  ["The work", [
    ["editScope",          "Edit the schedule of values", "Change line items, values and dates.", true],
    ["assignScope",        "Offer scope to contractors", "Send work to a company for acceptance."],
    ["uploadPhoto",        "Upload photos", "Add evidence against a line item."],
    ["postDailyLog",       "Post daily logs", "Record what happened on site."],
    ["manageSuppliers",    "Manage suppliers and materials", "Procurement, lead times and orders."],
  ]],
  ["Sign-off and money", [
    ["verifyWork",         "Verify work in the field", "Stand behind that the work is done."],
    ["approveWork",        "Approve work for payment", "The owner's sign-off. This is the gate money passes through.", true],
    ["createDraw",         "Create draw requests", "Ask the bank for money.", true],
    ["attachDrawDocument", "Attach draw paperwork", "Add check copies and lien waivers to a draw."],
    ["removeDrawDocument", "Remove draw paperwork", "Take a document off a draw."],
  ]],
  ["Punch list", [
    ["raisePunch",         "Raise punch items", "Flag work that needs revisiting."],
    ["verifyPunch",        "Verify punch items", "Confirm a punch item is fixed."],
    ["waivePunch",         "Waive punch items", "Release a hold on payment without the fix.", true],
  ]],
  ["Investors", [
    ["publishUpdate",      "Publish investor updates", "Send progress notes to investors."],
  ]],
];
// scoped: true means this party only sees and acts on line items assigned to it.
// Everyone who signs in gets an invitation. Contractors get the clauses;
// everyone else gets a short "here's what you'll be doing" acceptance.
const INVITE_COPY = {
  lender:    { heading:"You've been asked to release funds on this project",
    what:"You'll see draw requests when they're submitted, release or return them, and upload the check or wire confirmation so the owner doesn't have to ask for it.",
    note:"You won't see contractor disputes, penalties or punch items." },
  inspector: { heading:"You've been asked to verify work on this project",
    what:"You'll walk completed work, verify it in the field, and flag anything that needs revisiting. Your signature sits between the contractor's and the owner's.",
    note:"You can raise and close punch items. You can't approve payment." },
  investor:  { heading:"You've been given access to the project's capital status",
    what:"You'll see what the project still needs, how far along construction is, progress updates from the site, and where your own commitment stands.",
    note:"Contractor matters, disputes and payment records are not shown." },
  owner:     { heading:"You've been added as an owner on this project",
    what:"Full access: the schedule of values, approvals, contractors, draws, funding and settings.",
    note:"" },
};

const TRADES = ["General","Concrete","Masonry","Structural","Carpentry","Roofing","Glazing",
  "Flooring","Painting","Tile","Mechanical","Plumbing","Electrical","Fire protection","Sitework","Other"];

// Stage order is the whole point of the product: money is the LAST step.
const STAGES = ["not_started","in_progress","submitted","verified","approved","released","paid"];
const STAGE_META = {
  not_started:{label:"Not started",  tag:"n"},
  in_progress:{label:"In progress",  tag:"p"},
  submitted:  {label:"Submitted",    tag:"p"},
  verified:   {label:"Field verified",tag:"p"},
  approved:   {label:"Owner approved",tag:"g"},
  released:   {label:"Released",     tag:"g"},
  paid:       {label:"Paid",         tag:"g"},
  returned:   {label:"Returned",     tag:"b"},
  closed:     {label:"Closed out",   tag:"n"},
};
// Assignment is separate from stage: a line item can exist before anyone has
// agreed to build it, and the terms someone accepted are frozen at acceptance.
const ASSIGN_META = {
  unassigned:{label:"Unassigned", tag:"n"},
  offered:   {label:"Offered",    tag:"p"},
  accepted:  {label:"Accepted",   tag:"g"},
  declined:  {label:"Declined",   tag:"b"},
};

// action -> { role allowed, from stages, to stage, requires, verb on stamp }
const ACTIONS = {
  start:    {role:"contractor", from:["not_started","returned"], to:"in_progress", label:"Start work",         requires:"before", verb:"Work started"},
  submit:   {role:"contractor", from:["in_progress"],            to:"submitted",   label:"Submit for approval", requires:"after",  verb:"Submitted"},
  verify:   {role:"inspector",  from:["submitted"],              to:"verified",    label:"Verify on site",      verb:"Field verified"},
  approve:  {role:"owner",      from:["submitted","verified"],   to:"approved",    label:"Approve work",        verb:"Approved"},
  release:  {role:"lender",     from:["approved"],               to:"released",    label:"Release funds",       verb:"Released"},
  markPaid: {role:"lender",     from:["released"],               to:"paid",        label:"Mark paid",           verb:"Paid"},
};
ACTIONS.reject = {role:["owner","inspector","lender"], from:["submitted","verified","approved","in_progress"], to:"returned", label:"Return with punch list", verb:"Returned", destructive:true};

const SIGNOFF_ORDER = ["contractor","inspector","owner","lender"];

/* ============================================================================
   CAPITAL SIDE — funding needs, investor participation, milestones.
   ----------------------------------------------------------------------------
   Deliberately kept separate from the construction ledger. The bank's loan pays
   contractors against the schedule of values. Investor capital pays carrying
   costs, equipment, FF&E, inventory and payroll — it is released on operations,
   not on construction. The two are linked by MILESTONES (what has to be built
   AND funded before a thing can open), never by pretending one dollar bought
   one board.
   ========================================================================== */
const PATHS = {
  loan: { label:"Short-term loan", equity:false,
    summary:"15% per annum, principal and interest due in full by February 19, 2027.",
    detail:"Unsecured, no collateral. Prepayable without penalty. A missed installment accrues as additional principal rather than triggering default. A 20% rate applies to any amount still unpaid after the due date. Mississippi law; jury trial waived.",
    docs:["Loan Agreement","Promissory Note"] },
  fundI: { label:"Fund I — add to your position", equity:true,
    summary:"Additional contribution up to $2,500. Ownership and profit adjusted proportionally.",
    detail:"For existing Fund I investors. The fund closes at Historic Tax Credit closing or once a $112,500 cap is reached, whichever comes first.",
    docs:["Optional Investment Contribution Agreement"] },
  fundII: { label:"Fund II — new investors", equity:true,
    summary:"$5,000 per share. $2,500 minimum for a half share.",
    detail:"Binding on signature, with a formal agreement to follow within 30 days and closing on or before October 31, 2026. Funds held in escrow until the subscription agreement is executed. Contributors owe a duty of confidentiality on project information.",
    docs:["Letter of Intent to Contribute"] },
};

// A pledge is not money until it clears. Keeping these four states apart is the
// whole point — a progress bar that counts clicks as capital is how you tell
// investor nine that the round is nearly closed when it isn't.
const PLEDGE_STATUS = {
  interested:{ label:"Interested",     tag:"n", counts:"interested" },
  sent:      { label:"Agreement sent", tag:"p", counts:"interested" },
  signed:    { label:"Signed",         tag:"p", counts:"committed"  },
  received:  { label:"Funds received", tag:"g", counts:"received"   },
  declined:  { label:"Passed",         tag:"b", counts:null         },
};
const NEED_CATEGORIES = {
  carrying:  "Carrying cost",
  equipment: "Equipment",
  ffe:       "Furniture & fixtures",
  inventory: "Inventory",
  payroll:   "Payroll",
  construction: "Construction gap",
};

// What gets attached to a draw. `from` is who normally supplies it — the app
// doesn't enforce that, since somebody always ends up scanning it for somebody
// else, but it drives the missing-documents checklist.
const DOC_TYPES = {
  check:       { label:"Copy of the check",        from:"lender",     payment:true,  required:true },
  wire:        { label:"Wire / ACH confirmation",  from:"lender",     payment:true,  required:false },
  remittance:  { label:"Remittance advice",        from:"lender",     payment:false, required:false },
  waiverCond:  { label:"Conditional lien waiver",  from:"contractor", payment:false, required:true },
  waiverUncond:{ label:"Unconditional lien waiver",from:"contractor", payment:false, required:false },
  invoice:     { label:"Contractor invoice",       from:"contractor", payment:false, required:false },
  inspection:  { label:"Inspection report",        from:"inspector",  payment:false, required:false },
  other:       { label:"Other document",           from:"any",        payment:false, required:false },
};
/* Procurement. A supplier directory on its own is a worse address book. What
   makes it worth keeping is the lead time: an order date worked backward from
   when the material has to be on site, so the alert fires while there is still
   time to do something about it. */
const PROCURE_STATUS = {
  needed:    { label:"Not ordered",  tag:"n" },
  quoted:    { label:"Quoted",       tag:"p" },
  ordered:   { label:"Ordered",      tag:"p" },
  delivered: { label:"On site",      tag:"g" },
  cancelled: { label:"Cancelled",    tag:"n" },
};
const PROCURE_OPEN = ["needed","quoted","ordered"];

/* Threads. Deliberately not a chat app — contractors will not move their
   conversation into a portal. Messages go out by email and replies come back in
   on the same thread, so the record builds itself without anyone changing how
   they work. */
const THREAD_KINDS = {
  direct:   { label:"Message" },
  lineItem: { label:"About a line item" },
  draw:     { label:"About a draw" },
};

/* Email intake. The bank emails the check to an address; the server parses it
   and proposes a filing. It proposes — it does not file. This is an evidence
   system, so a misfiled check corrupts the variance math and a spoofed
   attachment in the payment file is worse than no automation at all. */
const INTAKE_DOMAIN = "draws.calicostation.app";
const intakeAddress = (drawId) =>
  "draw-" + String(drawId).toLowerCase().replace(/\W/g,"") + "-" + fnv1a(drawId).slice(0,6) + "@" + INTAKE_DOMAIN;

const MATCH_CONFIDENCE = {
  high:   { label:"Confident",  tag:"g", blurb:"Addressed to this draw's own intake address by a verified sender." },
  medium: { label:"Likely",     tag:"p", blurb:"Matched on content — check it before filing." },
  low:    { label:"Unsure",     tag:"b", blurb:"Not enough to go on. Pick the draw yourself." },
};
const SENDER_STATUS = {
  verified:   { label:"Verified sender", tag:"g" },
  unknown:    { label:"Unknown sender",  tag:"p" },
  unverified: { label:"Failed DMARC",    tag:"b" },
};

const DRAW_STATUS = {
  submitted:{ label:"With the lender", tag:"p" },
  funded:   { label:"Funded",          tag:"g" },
  rejected: { label:"Returned",        tag:"b" },
};

// A punch item is one specific deficiency on one line item. Severity is a money
// decision, not a mood: it decides whether approval is blocked, whether cash is
// held back, or whether it is simply tracked to opening day.
const PUNCH_SEVERITY = {
  blocking: { label:"Blocks approval", tag:"b", blurb:"Nothing on this line gets approved until it's fixed." },
  holdback: { label:"Hold money back", tag:"p", blurb:"Approve the rest, withhold an amount until it's fixed." },
  minor:    { label:"Track only",      tag:"n", blurb:"No effect on payment. Carried to the opening punch walk." },
};
const PUNCH_STATUS = {
  open:     { label:"Open",          tag:"b" },
  fixed:    { label:"Fix submitted", tag:"p" },
  disputed: { label:"Disputed",      tag:"p" },
  verified: { label:"Verified fixed",tag:"g" },
  waived:   { label:"Waived",        tag:"n" },
};
const PUNCH_OPEN = ["open","fixed","disputed"];

// Plain-language clauses a contractor initials during onboarding. These are a
// summary written so someone actually reads them — NOT a substitute for the
// executed agreement, which goes through Adobe Sign. Have counsel review the
// wording against the real contract before this is used on a live job.
const TERMS_VERSION = "1.1";
const TERMS = [
  { id:"schedule", title:"You are agreeing to dates, not estimates",
    body:"Every line item you accept carries a finish date. Accepting it means you have looked at that date and can hit it. If you can't, decline the item or say so before you accept — not after." },
  { id:"penalties", title:"Missing a date costs money",
    body:"Each day a line item runs past its accepted finish date accrues a delay penalty at the rate shown on that item. Penalties are deducted from amounts otherwise due to you. They stop on the date the work is submitted, and they are capped per line item. Penalties only run against dates you accepted." },
  { id:"payment", title:"Nothing is paid until everyone has signed",
    body:"No line item is released for payment until every required signature is on file. Work performed without an accepted assignment is at your own risk and may not be paid." },
  { id:"photos", title:"Photographs are part of the job",
    body:"You must post at least one photograph before starting a line item and at least one on completion. The system will not let you start or submit without them. Shoot from the same position each time." },
  { id:"retainage", title:"A percentage is held back until the end",
    body:"Retainage is withheld from each payment and released at final completion, per the executed agreement." },
  { id:"punch", title:"Deficiencies can block or reduce payment",
    body:"Items flagged on a site walk are either blocking (nothing on that line is approved until fixed), holdback (a stated amount is withheld until fixed), or tracked. You may dispute any item in writing. You cannot close your own." },
  { id:"insurance", title:"Insurance and licensing stay current",
    body:"You must keep a valid certificate of insurance, a current license, and a W-9 on file. If coverage lapses you cannot be assigned new work until it is restored." },
  { id:"bonding", title:"If the job requires a bond, it stays in place",
    body:"Where this project requires bonding, you must provide a payment and performance bond covering your scope before work is assigned to you, and keep it in force until final completion. A bond that lapses or is cancelled stops new assignments the same way lapsed insurance does. If this project does not require a bond, this clause does not apply to you." },
  { id:"daily", title:"Post a daily update on days you work",
    body:"A short note and photographs, on every day your crew is on site. This is how progress gets recorded between milestones." },
  { id:"changes", title:"Changes are not binding until you re-accept them",
    body:"If the owner changes the value, finish date, or penalty rate on a line item after you accepted it, the change is flagged and must come back to you. You are not held to terms you did not accept." },
  { id:"waivers", title:"Lien waivers accompany each payment",
    body:"You will provide a conditional waiver with each payment application and an unconditional waiver on receipt of funds, for yourself and your lower-tier subs and suppliers." },
];

// Compliance a party must have on file before the owner can assign it work.
const COMPLIANCE = [
  { key:"coiExpires", label:"Certificate of insurance", dated:true },
  { key:"w9",         label:"W-9 on file",              dated:false },
  { key:"licenseNo",  label:"State license",            dated:false },
];

/* ============================================================================
   4. HELPERS
   ========================================================================== */
let TODAY = new Date();
// Set from project.today, which the server computes in the project's timezone.
export const setToday = (isoDay) => { if (isoDay) TODAY = new Date(isoDay + "T12:00:00"); };
const usd = (n, cents) => (n < 0 ? "-" : "") + "$" + Math.abs(n||0).toLocaleString("en-US",{minimumFractionDigits:cents?2:0,maximumFractionDigits:cents?2:0});
const pct = (n) => (Math.round((n||0)*10)/10) + "%";
const iso = (d) => new Date(d).toISOString().slice(0,10);
const fmtDate = (d) => d ? new Date(d+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"2-digit"}) : "—";
/* The list wants a glance, not a full stamp: a time if it landed today, a date
   if it did not. Long enough to place it, short enough not to wrap. */
/* How often an open screen asks whether anything changed, and how long after
   somebody stops touching it before it stops asking. The second number is the
   one that decides whether the database gets to sleep: it suspends after five
   idle minutes, so a screen abandoned at 5pm goes quiet at 5:15 and the
   database is asleep by 5:20. */
/*
 * Who runs the job, as an investor should hear it named.
 *
 * "The owner" is wrong on any screen an investor reads: they hold a share of
 * the thing themselves, so the word quietly tells them they are not one. On
 * their screens that party is the developer, and named outright — Kenneth, not
 * a role — wherever the sentence can carry a name.
 */
const developerParty = (db) => (db.parties || []).find((x) => x.owner === true)
  || (db.parties || []).find((x) => x.role === "owner") || null;
const developerName = (db) => {
  const d = developerParty(db);
  return (d && (d.contact || d.name)) || "the developer";
};

/* A payment application's number, which is not its id. The two used to be the
   same string — a per-project counter sitting in a globally unique column — so
   two projects both reaching application 16 collided, and the second one died
   on a duplicate key the moment somebody tried to bill. The server sends
   `label`; the fallback is for anything assembled client side. */
const drawLabel = (d) => (d && (d.label || "DRAW-" + String(d.no ?? "").padStart(3, "0"))) || "";

const PULSE_MS = 12000;
const IDLE_AFTER_MS = 15 * 60 * 1000;

const fmtBrief = (t, today) => {
  const d = new Date(t);
  const sameDay = today && d.toISOString().slice(0,10) === String(today).slice(0,10);
  return sameDay
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const fmtStamp = (t) => new Date(t).toLocaleString("en-US",{month:"short",day:"numeric",year:"2-digit",hour:"numeric",minute:"2-digit"});
const daysBetween = (a,b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const uid = (p) => p + "_" + Math.random().toString(36).slice(2,9);
const clamp = (n,a,b) => Math.max(a, Math.min(b, n));

function daysLate(it) {
  if (!it.plannedFinish) return 0;
  // Once an item is finished or closed out, lateness stops at that date.
  const done = ["approved","released","paid","closed"].includes(it.stage);
  const end = done && it.actualFinish ? it.actualFinish : iso(TODAY);
  return Math.max(0, daysBetween(it.plannedFinish, end));
}
function ldAccrued(it, project) {
  // Penalties only run against a party that actually accepted the date. Scope
  // sitting unassigned or merely offered is the owner's problem, not a sub's.
  if (!it.assignment || it.assignment.status !== "accepted") return 0;
  const rate = it.ldPerDay != null ? it.ldPerDay : project.ldPerDay;
  const cap = it.scheduledValue * (project.ldCapPct/100);
  return Math.min(daysLate(it) * rate, cap);
}
function signoffs(it) {
  const map = {};
  (it.approvals||[]).forEach(a => { map[a.role] = a; });
  return map;
}
// An exception is money that moved WITHOUT owner sign-off — but only money
// that moved after the baseline. What came before is an opening balance: it is
// recorded, and nobody can now change it, so raising it as an alarm every day
// just teaches the owner to ignore the alarms.
const liveDisbursed = (it) => (it.bankDisbursed || 0) - (it.openingDisbursed || 0);
function isBlocked(it, project) {
  return liveDisbursed(it) > 0 && !signoffs(it).owner;
}

/* --- procurement helpers -------------------------------------------------- */
const suppliersOf = (db) => db.suppliers || [];
const procureOf = (db) => db.procurement || [];
const supplierName = (db, id) => { const x = suppliersOf(db).find(y => y.id === id); return x ? x.name : "No supplier yet"; };
// The date an order has to be placed to land on site in time.
function orderByDate(db, pi) {
  if (!pi.neededOnSite) return null;
  const weeks = pi.leadTimeWeeks != null ? pi.leadTimeWeeks
    : (suppliersOf(db).find(s => s.id === pi.supplierId) || {}).leadTimeWeeks || 0;
  return iso(new Date(new Date(pi.neededOnSite).getTime() - weeks * 7 * 86400000));
}
function procureRisk(db, pi) {
  if (!PROCURE_OPEN.includes(pi.status)) return null;
  const by = orderByDate(db, pi);
  if (!by) return null;
  const slack = daysBetween(iso(TODAY), by);   // negative = the order date has passed
  if (pi.status === "ordered") return null;
  if (slack < 0) return { level:"late", days: -slack };
  if (slack <= 10) return { level:"soon", days: slack };
  return null;
}
const procureAtRisk = (db) => procureOf(db).filter(x => procureRisk(db, x));

/* --- messaging helpers ---------------------------------------------------- */
const threadsOf = (db) => db.threads || [];
const threadsFor = (db, partyId, role) => threadsOf(db).filter(t =>
  ROLES[role].scoped || role === "contractor" ? (t.participants || []).includes(partyId) : true);
const lastMessage = (t) => (t.messages || [])[(t.messages || []).length - 1];
const unreadIn = (t, partyId) => (t.messages || []).filter(m =>
  m.partyId !== partyId && !(m.readBy || []).includes(partyId)).length;

/* --- investor updates ----------------------------------------------------- */
const updatesOf = (db) => (db.investorUpdates || []).slice().sort((a,b) => a.at < b.at ? 1 : -1);
const publishedUpdates = (db) => updatesOf(db).filter(u => u.published);

/* --- email intake helpers ------------------------------------------------ */
const inboxOf = (db) => db.inbox || [];
const inboxPending = (db) => inboxOf(db).filter(x => x.status === "pending");
const inboxNeedsEyes = (db) => inboxPending(db).filter(x =>
  x.confidence !== "high" || x.sender !== "verified");

/* --- capital helpers ----------------------------------------------------- */
const needsOf = (db) => db.fundingNeeds || [];
const pledgesOf = (db) => db.pledges || [];
const pledgesFor = (db, needId) => pledgesOf(db).filter(x => x.needId === needId);

// Three numbers, never conflated: expressed interest, signed commitments, and
// money that has actually cleared.
function needProgress(db, need) {
  const ps = pledgesFor(db, need.id);
  const sum = (k) => ps.filter(x => PLEDGE_STATUS[x.status].counts === k)
                       .reduce((n,x) => n + (x.amount||0), 0);
  const received = sum("received");
  const committed = sum("committed") + received;
  const interested = sum("interested") + committed;
  return { received, committed, interested, remaining: Math.max(0, need.amount - received),
           pctReceived: need.amount ? received / need.amount * 100 : 0,
           pctCommitted: need.amount ? committed / need.amount * 100 : 0,
           count: ps.filter(x => x.status !== "declined").length };
}
function capitalTotals(db) {
  const t = { need:0, received:0, committed:0, interested:0, needs:0, open:0 };
  needsOf(db).filter(n => n.status !== "closed").forEach(n => {
    const p = needProgress(db, n);
    t.need += n.amount; t.received += p.received; t.committed += p.committed;
    t.interested += p.interested; t.needs++;
    if (p.received < n.amount) t.open++;
  });
  t.remaining = Math.max(0, t.need - t.received);
  t.pct = t.need ? t.received / t.need * 100 : 0;
  return t;
}
// A milestone is the honest join between the two sides: it needs work built and
// money raised, and it opens when both are true.
function milestoneProgress(db, m) {
  const its = db.lineItems.filter(i => (m.lineItemIds||[]).includes(i.id));
  const value = its.reduce((s,i) => s + i.scheduledValue, 0);
  const inPlace = its.reduce((s,i) => s + i.scheduledValue * i.percent/100, 0);
  const ns = needsOf(db).filter(n => (m.needIds||[]).includes(n.id));
  const needAmt = ns.reduce((s,n) => s + n.amount, 0);
  const funded = ns.reduce((s,n) => s + needProgress(db, n).received, 0);
  return {
    build: value ? inPlace/value*100 : (its.length ? 0 : null),
    fund: needAmt ? funded/needAmt*100 : (ns.length ? 0 : null),
    blockers: its.filter(i => !["approved","released","paid","closed"].includes(i.stage)).length,
    fundingGap: Math.max(0, needAmt - funded), items: its, needs: ns,
  };
}

/* --- draw helpers -------------------------------------------------------- */
const drawItems = (db, d) => db.lineItems.filter(i => d.lineItemIds.includes(i.id));
function drawTotals(db, d) {
  const p = db.project, its = drawItems(db, d);
  const gross = its.reduce((s,i) => s + i.scheduledValue * i.percent/100, 0);
  const retainage = gross * p.retainagePct/100;
  const prior = its.reduce((s,i) => s + (d.priorPaid != null ? 0 : i.bankDisbursed), 0);
  const penalties = its.reduce((s,i) => s + ldAccrued(i, p), 0);
  const holdbacks = its.reduce((s,i) => s + punchHoldback(i), 0);
  return d.snapshot || { gross, retainage, prior, penalties, holdbacks,
    net: gross - retainage - prior - penalties - holdbacks };
}
const drawDocs = (d) => d.documents || [];
const paidOnDraw = (d) => drawDocs(d).filter(x => DOC_TYPES[x.type].payment)
  .reduce((n,x) => n + (x.amount || 0), 0);
// What is still missing before this draw is properly papered.
function missingDocs(db, d) {
  const have = new Set(drawDocs(d).map(x => x.type));
  const gaps = [];
  if (!have.has("check") && !have.has("wire")) gaps.push("Proof of payment");
  const parties = [...new Set(drawItems(db, d).map(i => i.assignment && i.assignment.partyId).filter(Boolean))];
  const waived = new Set(drawDocs(d).filter(x => x.type === "waiverCond" || x.type === "waiverUncond").map(x => x.partyId));
  parties.filter(pid => !waived.has(pid)).forEach(pid => gaps.push("Lien waiver — " + partyName(db, pid)));
  return gaps;
}
const drawsNeedingDocs = (db) => (db.draws || []).filter(d => d.status === "funded" && missingDocs(db, d).length > 0);

/* --- onboarding helpers -------------------------------------------------- */
const onboardingOf = (p) => p.onboarding || { status:"none", clauses:[], adobe:null };
const isOnboarded = (p) => ["acknowledged","signed"].includes(onboardingOf(p).status);
const inviteFor = (db, partyId) => (db.invites || []).find(i => i.partyId === partyId);

/* --- daily log helpers --------------------------------------------------- */
const logsFor = (db, partyId) => (db.dailyLogs || []).filter(l => !partyId || l.partyId === partyId);
const lastLogDate = (db, partyId) => {
  const l = logsFor(db, partyId).map(x => x.date).sort();
  return l.length ? l[l.length-1] : null;
};

/* --- punch list helpers -------------------------------------------------- */
const punchOf = (it) => it.punch || [];
const openPunch = (it) => punchOf(it).filter(x => PUNCH_OPEN.includes(x.status));
const blockingPunch = (it) => openPunch(it).filter(x => x.severity === "blocking");
// Cash withheld against unfixed work, on top of retainage.
const punchHoldback = (it) => openPunch(it)
  .filter(x => x.severity === "holdback")
  .reduce((n, x) => n + (x.holdbackAmount || 0), 0);
const punchOverdue = (x) => PUNCH_OPEN.includes(x.status) && x.dueDate && daysBetween(x.dueDate, iso(TODAY)) > 0;

/* --- party helpers ------------------------------------------------------- */
const partyById = (db, id) => (db.parties || []).find(p => p.id === id);
const partyName = (db, id) => { const p = partyById(db, id); return p ? p.name : "Unassigned"; };

// A party is clear to be assigned work only with current compliance on file.
function complianceGaps(party) {
  const gaps = [];
  if (!party.w9) gaps.push("W-9");
  if (!party.licenseNo) gaps.push("License");
  if (!party.coiExpires) gaps.push("Insurance");
  else if (daysBetween(iso(TODAY), party.coiExpires) < 0) gaps.push("Insurance expired");
  else if (daysBetween(iso(TODAY), party.coiExpires) < 30) gaps.push("Insurance expiring");
  return gaps;
}
const canBeAssigned = (party) => party.status === "active" &&
  !complianceGaps(party).some(g => g !== "Insurance expiring");

const assignState = (it) => it.assignment ? it.assignment.status : "unassigned";
const acceptedTerms = (it) => it.assignment && it.assignment.status === "accepted" ? it.assignment.terms : null;

// Terms drift: owner changed value, date or penalty after the party accepted.
function termsChanged(it) {
  const t = acceptedTerms(it);
  if (!t) return null;
  const diffs = [];
  if (t.scheduledValue !== it.scheduledValue) diffs.push({ what:"Value", was:usd(t.scheduledValue), now:usd(it.scheduledValue) });
  if (t.plannedFinish !== it.plannedFinish) diffs.push({ what:"Finish date", was:fmtDate(t.plannedFinish), now:fmtDate(it.plannedFinish) });
  if ((t.ldPerDay||0) !== (it.ldPerDay||0)) diffs.push({ what:"Penalty/day", was:usd(t.ldPerDay||0), now:usd(it.ldPerDay||0) });
  return diffs.length ? diffs : null;
}

// What each party is exposed to across the whole job.
function partyLedger(db, partyId) {
  const p = db.project;
  const mine = db.lineItems.filter(i => i.assignment && i.assignment.partyId === partyId);
  const l = { items: mine.length, assigned: 0, inPlace: 0, disbursed: 0, penalties: 0, backcharge: 0,
              open: 0, late: 0, punchOpen: 0, punchHoldback: 0 };
  mine.forEach(it => {
    l.assigned += it.scheduledValue;
    l.inPlace += it.scheduledValue * it.percent / 100;
    l.disbursed += it.bankDisbursed;
    l.penalties += ldAccrued(it, p);
    l.backcharge += it.backcharge || 0;
    l.punchOpen += openPunch(it).length;
    l.punchHoldback += punchHoldback(it);
    if (!["paid","released","closed"].includes(it.stage)) l.open++;
    if (daysLate(it) > 0) l.late++;
  });
  l.overFunded = Math.max(0, l.disbursed - l.inPlace);
  return l;
}

/* ============================================================================
   6. CSV IMPORT / EXPORT
   Column order matches an AIA G703 continuation sheet so the bank and the GC
   are reading a document shape they already recognise.
   ========================================================================== */
const CSV_COLUMNS = [
  ["no","Item No"],["division","Division"],["name","Description of Work"],["scope","Scope Detail"],
  ["scheduledValue","Scheduled Value"],["percent","% Complete"],["stage","Status"],
  ["plannedStart","Planned Start"],["plannedFinish","Planned Finish"],
  ["actualStart","Actual Start"],["actualFinish","Actual Finish"],
  ["materials","Materials / Allowances"],["ldPerDay","Penalty Per Day"],["bankDisbursed","Disbursed To Date"],
  ["assignedTo","Assigned To"],["assignStatus","Assignment Status"],
];
function toCSV(rows, cols) {
  const esc = (v) => { v = v == null ? "" : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g,'""') + '"' : v; };
  return [cols.map(c => esc(c[1])).join(",")]
    .concat(rows.map(r => cols.map(c => esc(r[c[0]])).join(",")))
    .join("\r\n");
}
function parseCSV(text) {
  const rows = []; let row = [], f = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i+1] === '"') { f += '"'; i++; } else q = false; } else f += c; }
    else if (c === '"') q = true;
    else if (c === ",") { row.push(f); f = ""; }
    else if (c === "\n") { row.push(f); rows.push(row); row = []; f = ""; }
    else if (c !== "\r") f += c;
  }
  if (f || row.length) { row.push(f); rows.push(row); }
  return rows.filter(r => r.some(c => c.trim() !== ""));
}
function download(filename, text, mime) {
  const b = new Blob([text], { type: (mime||"text/csv") + ";charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(b); a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
// Guesses which of your spreadsheet's columns is which. Handles the obvious
// synonyms so most files map themselves and Kenneth only fixes the odd one.
const FIELD_HINTS = {
  no:            ["item no","item","no","number","line","line no","ref","code","cost code","csi"],
  division:      ["division","div","category","section","trade","group","phase"],
  name:          ["description of work","description","work","task","scope of work","item description","activity","name"],
  scope:         ["scope detail","detail","notes","scope","comments","remarks"],
  scheduledValue:["scheduled value","value","amount","budget","cost","contract","total","price","estimate","bid"],
  percent:       ["% complete","percent complete","percent","complete","progress","% done","pct"],
  stage:         ["status","stage","state"],
  plannedStart:  ["planned start","start","start date","begin","scheduled start"],
  plannedFinish: ["planned finish","finish","finish date","end","end date","due","due date","completion","deadline","scheduled finish"],
  actualStart:   ["actual start"],
  actualFinish:  ["actual finish","completed","date completed"],
  materials:     ["materials / allowances","materials","allowances","material"],
  ldPerDay:      ["penalty per day","penalty","liquidated damages","ld","damages per day"],
  bankDisbursed: ["disbursed to date","disbursed","paid","paid to date","payments","amount paid"],
  assignedTo:    ["assigned to","contractor","sub","subcontractor","vendor","responsible","assigned"],
};
const REQUIRED_FIELDS = ["no","name","scheduledValue"];
const FIELD_LABEL = Object.fromEntries(CSV_COLUMNS.map(([k,l]) => [k,l]));

function guessMapping(headers) {
  const norm = (x) => String(x||"").trim().toLowerCase().replace(/[_\-]+/g," ").replace(/\s+/g," ");
  const h = headers.map(norm);
  const map = {};
  const used = new Set();
  Object.entries(FIELD_HINTS).forEach(([field, hints]) => {
    let idx = h.findIndex((x,i) => !used.has(i) && hints.includes(x));
    if (idx < 0) idx = h.findIndex((x,i) => !used.has(i) && x && hints.some(hh => x.includes(hh) || hh.includes(x)));
    if (idx >= 0) { map[field] = idx; used.add(idx); }
  });
  return map;
}
// Read rows using an explicit column map rather than fixed headers.
function rowsToItems(rows, map, parties) {
  const num = (v) => { const t = String(v||"").replace(/[$,%\s]/g,""); return t === "" ? 0 : Number(t) || 0; };
  const date = (v) => {
    const t = String(v||"").trim(); if (!t) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const d = new Date(t); return isNaN(d) ? null : iso(d);
  };
  return rows.map((r, i) => {
    const g = (k) => map[k] != null ? String(r[map[k]] ?? "").trim() : "";
    const no = g("no") || "ITEM-" + String(i+1).padStart(3,"0");
    const stage = STAGES.includes(g("stage")) ? g("stage") : "not_started";
    const value = num(g("scheduledValue"));
    return {
      id: "li_" + no.replace(/\W/g,"") + "_" + i, no,
      division: g("division") || "Uncategorised", name: g("name") || no, scope: g("scope"),
      scheduledValue: value, percent: clamp(num(g("percent")),0,100), stage,
      plannedStart: date(g("plannedStart")), plannedFinish: date(g("plannedFinish")),
      actualStart: date(g("actualStart")), actualFinish: date(g("actualFinish")),
      materials: g("materials"), ldPerDay: g("ldPerDay") ? num(g("ldPerDay")) : null,
      bankDisbursed: num(g("bankDisbursed")), backcharge: 0,
      photos: [], approvals: [], notes: [], history: [], punch: [],
      assignment: assignmentFromName(g("assignedTo"), parties, value, date(g("plannedFinish")), g("ldPerDay") ? num(g("ldPerDay")) : null),
    };
  }).filter(x => x.name && x.name !== "undefined");
}

function csvToItems(text, parties) {
  const rows = parseCSV(text);
  if (!rows.length) throw new Error("That file has no rows.");
  const head = rows[0].map(h => h.trim().toLowerCase());
  const idx = {};
  CSV_COLUMNS.forEach(([key, label]) => { const i = head.indexOf(label.toLowerCase()); if (i >= 0) idx[key] = i; });
  if (idx.no == null || idx.name == null || idx.scheduledValue == null)
    throw new Error("Missing required columns: Item No, Description of Work, Scheduled Value. Download the template to see the exact headers.");
  return rows.slice(1).map(r => {
    const g = (k) => idx[k] != null ? (r[idx[k]] || "").trim() : "";
    const num = (k) => { const v = g(k).replace(/[$,%\s]/g,""); return v === "" ? 0 : Number(v) || 0; };
    const no = g("no");
    const stage = STAGES.includes(g("stage")) ? g("stage") : "not_started";
    return {
      id: "li_" + no.replace(/\W/g,""), no, division: g("division") || "Uncategorised",
      name: g("name"), scope: g("scope"), scheduledValue: num("scheduledValue"),
      percent: clamp(num("percent"),0,100), stage,
      plannedStart: g("plannedStart") || null, plannedFinish: g("plannedFinish") || null,
      actualStart: g("actualStart") || null, actualFinish: g("actualFinish") || null,
      materials: g("materials"), ldPerDay: g("ldPerDay") ? num("ldPerDay") : null,
      bankDisbursed: num("bankDisbursed"),
      photos: [], approvals: [], notes: [], history: [], backcharge: 0,
      assignment: assignmentFromName(g("assignedTo"), parties, num("scheduledValue"), g("plannedFinish"), g("ldPerDay") ? num("ldPerDay") : null),
    };
  });
}
// "Assigned To" holds a party name. Unknown names import as unassigned rather
// than silently creating a party nobody vetted.
function assignmentFromName(name, parties, value, finish, ld) {
  if (!name || !parties) return null;
  const p = parties.find(x => x.name.toLowerCase() === name.trim().toLowerCase());
  if (!p) return null;
  return { partyId: p.id, status:"offered", offeredAt: new Date().toISOString(), respondedAt: null,
           terms: { scheduledValue: value, plannedFinish: finish || null, ldPerDay: ld } };
}

// Flatten assignment onto the row for export.
const forExport = (db) => db.lineItems.map(it => ({
  ...it,
  assignedTo: it.assignment ? partyName(db, it.assignment.partyId) : "",
  assignStatus: assignState(it),
}));

/* ============================================================================
   7. SHARED UI
   ========================================================================== */
function Tag({ stage }) {
  const m = STAGE_META[stage] || STAGE_META.not_started;
  return <span className={"tag " + m.tag}>{m.label}</span>;
}
function Bar({ value, late }) {
  return <span className="bar" title={pct(value)}><i style={{width: clamp(value,0,100)+"%"}} className={late?"late":""}/></span>;
}
function Stamp({ role, approval, fresh, org, pending }) {
  const meta = ROLES[role];
  const tilt = approval ? ((parseInt(approval.hash.slice(0,2), 16) % 7) - 3) * 0.85 : 0;
  if (!approval) return (
    <div className="stamp await">
      <div className="role">{meta.label}</div>
      <div className="verb">Awaiting</div>
      <div className="who">{pending || "No signature on file"}</div>
    </div>
  );
  return (
    <div className={"stamp " + (approval.rejected ? "rej " : "") + (fresh ? "inked" : "")} style={{"--tilt": tilt + "deg"}}>
      <div className="role">{meta.label}</div>
      <div className="verb">{approval.rejected ? "Returned" : "Signed off"}</div>
      <div className="who">{org || approval.who}</div>
      <div className="when">{org ? approval.who + " · " : ""}{fmtStamp(approval.at)}</div>
      <div className="hash">#{approval.hash}</div>
    </div>
  );
}
function SectionHead({ children, right }) {
  return <div className="sech"><span className="eyebrow">{children}</span><span className="line"/>{right}</div>;
}
function Stat({ k, v, sub, tone }) {
  return <div className={"stat " + (tone||"")}><div className="k">{k}</div><div className="v">{v}</div>{sub && <div className="sub">{sub}</div>}</div>;
}
function Empty({ title, body, action }) {
  return (
    <div className="card pad" style={{textAlign:"center", padding:"40px 20px"}}>
      <div style={{fontWeight:700, fontSize:15, letterSpacing:"-.02em"}}>{title}</div>
      <div className="muted" style={{fontSize:12.5, marginTop:5, maxWidth:420, marginInline:"auto"}}>{body}</div>
      {action && <div style={{marginTop:14}}>{action}</div>}
    </div>
  );
}

/* ============================================================================
   ACCOUNT — sign in, pick a project, settings.
   ========================================================================== */

/* ---------------------------------------------------------- Landing page */
/* The public front door. In production this is a separate static deployment on
   the apex domain with the app on app.* — different audience, different release
   cadence, and a copy tweak should never redeploy the service holding the audit
   chain. It lives here so the whole flow can be walked end to end. */
function Landing({ onSignIn }) {
  const stages = [
    ["01","Contractor", "Photographs the work before starting and on completion, then submits it."],
    ["02","Inspector",  "Walks it and verifies in the field."],
    ["03","Owner",      "Approves on site, or returns it with a punch list."],
    ["04","Lender",     "Releases funds — and not one step earlier."],
  ];
  return (
    <div className="lp">
      <header className="lp-nav">
        <div>
          <div className="brandmark" style={{fontSize:17,color:"var(--ink)"}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="eyebrow" style={{marginTop:3}}>Construction draw control</div>
        </div>
        <button className="btn" onClick={onSignIn}>Sign in</button>
      </header>

      <section className="lp-hero">
        <div className="eyebrow">Notice to proceed · noun</div>
        <h1 className="lp-h1">The authorization to begin.<br/>Work does not proceed<br/>until it is signed off.</h1>
        <p className="lp-lede">
          On a well-run job, a notice to proceed is the authorization without which work cannot begin.
          {PRODUCT.name} applies the same instrument to money: no line item reaches a payment application until the
          people who have to stand behind it have each put their name on it.
        </p>
        <div className="lp-cta">
          <button className="btn go" onClick={onSignIn}>Sign in</button>
          <a className="btn ghost" href="#how">See how it works</a>
        </div>
      </section>

      <section className="lp-band">
        <div className="lp-bandin">
          <p>
            Construction disputes are rarely arguments about what happened. They are arguments about what can still be
            shown, eighteen months later, by people who have moved on to other jobs.
          </p>
        </div>
      </section>

      <section className="lp-sec" id="how">
        <div className="eyebrow">The gate</div>
        <h2 className="lp-h2">Four signatures, in order</h2>
        <div className="lp-stages">
          {stages.map(([n,who,what]) => (
            <div className="lp-stage" key={n}>
              <div className="lp-stagen mono">{n}</div>
              <div style={{fontWeight:700,fontSize:13.5,marginTop:6}}>{who}</div>
              <p style={{fontSize:12.5,margin:"5px 0 0",lineHeight:1.5}}>{what}</p>
            </div>
          ))}
        </div>
        <p className="lp-note">
          Change who has to sign under project settings. Every change is written to the record, so nobody can quietly
          lower the bar and claim it was always that way.
        </p>
      </section>

      <section className="lp-sec lp-alt">
        <div className="eyebrow">What it protects</div>
        <h2 className="lp-h2">Four places control slips on any job</h2>
        <p className="lp-note" style={{margin:"-10px 0 22px"}}>
          None of these are anyone's fault. They are how construction financing is structured, and they cost owners
          money on well-run projects as readily as on troubled ones.
        </p>
        <div className="lp-grid">
          {[["Money moves ahead of the work",
             "On most construction loans the lender disburses against its own schedule. Every disbursement here is reconciled against work actually in place, so an owner can see what has been funded ahead of the build while there is still time to act on it."],
            ["Dates are assumed rather than agreed",
             "Contractors accept dates instead of receiving them, and accepting freezes the value, the finish date and the penalty rate. A daily log keeps the record between milestones, so a schedule conversation starts from evidence rather than recollection."],
            ["Scope changes hands",
             "Trades come and go on long projects. Split a line item cleanly: completed work closes out under the name of whoever built it, the balance moves on with fresh dates, and nobody inherits responsibility for work they did not do."],
            ["The record lives in text messages",
             "Photographs, signatures, punch items, messages and payment documents on one hash-chained record. Alter a historic row and the chain visibly breaks — which protects everyone who signed it, not just the owner."],
          ].map(([h,b]) => (
            <div className="lp-card" key={h}>
              <div style={{fontWeight:700,fontSize:13.5,letterSpacing:"-.01em"}}>{h}</div>
              <p style={{fontSize:12.5,margin:"7px 0 0",lineHeight:1.55}}>{b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-sec">
        <div className="eyebrow">Everyone on the job</div>
        <h2 className="lp-h2">One record, four ways in</h2>
        <div className="lp-grid lp-grid4">
          {[["Owner","The whole project. Approvals, contractors, draws, funding, settings."],
            ["Contractor","Only the scope they accepted. Photographs, progress, punch, daily updates."],
            ["Lender","Draw requests, and somewhere to put the check when it's cut."],
            ["Investor","Capital status and progress. No contractor disputes, ever."],
          ].map(([h,b]) => (
            <div className="lp-card" key={h}>
              <div style={{fontWeight:700,fontSize:13}}>{h}</div>
              <p className="muted" style={{fontSize:12,margin:"5px 0 0",lineHeight:1.5}}>{b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-close">
        <h2 className="lp-h2" style={{marginTop:0}}>{PRODUCT.tagline}</h2>
        <button className="btn go" style={{marginTop:14}} onClick={onSignIn}>Sign in</button>
      </section>

      <footer className="lp-foot">
        <div>
          <div className="brandmark" style={{fontSize:13,color:"var(--ink)"}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="muted" style={{fontSize:11.5,marginTop:3}}>Construction draw control and project accountability.</div>
        </div>
        <div className="mono" style={{fontSize:11}}>{COPYRIGHT}</div>
      </footer>
    </div>
  );
}

/**
 * Real sign-in: email and password, or Google.
 *
 * The Google button only appears when the server says the credentials are
 * configured — a button that always fails is worse than no button. Errors are
 * deliberately identical for "no such address" and "wrong password"; on a
 * project holding payment records, an endpoint that confirms who has an account
 * is a free directory for whoever asks.
 */
function SignIn({ onSignedIn, onBack, say, googleEnabled, initialMode, notice }) {
  const [mode, setMode] = useState(initialMode || "signin");   // signin | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  // Autofill can populate these without React seeing an event; the field is the
  // truth. Without this, a password manager silently disables the button and
  // nobody can sign in at all.
  const emailRef = useRef(null), pwRef = useRef(null);

  const submit = async () => {
    setErr(null);
    const addr = ((emailRef.current && emailRef.current.value) || email).trim();
    const pw = (pwRef.current && pwRef.current.value) || password;
    if (!addr.includes("@")) { setErr("That doesn't look like an email address."); return; }
    if (mode !== "forgot" && !pw) { setErr("Enter your password."); return; }
    if (mode === "register" && pw.length < 10) {
      setErr("Choose a password of at least 10 characters."); return;
    }
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.forgot({ email: addr });
        setSent(true);
      } else {
        if (mode === "register") await api.register({ email: addr, password: pw, name });
        else await api.login({ email: addr, password: pw });
        const session = await api.me();
        onSignedIn(session);
        say(mode === "register" ? "Account created." : "Signed in.");
      }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };


  return (
    <div className="authwrap">
      <div className="authcard">
        <div className="authbrand">
          <div className="brandmark" style={{color:"var(--ink)",fontSize:17}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="eyebrow" style={{marginTop:4}}>{PRODUCT.tagline}</div>
        </div>

        {onBack && <button className="btn ghost sm" style={{marginTop:16}} onClick={onBack}>← Back</button>}
        {notice && <p style={{fontSize:12,color:"var(--oxide)",marginTop:12}}>{notice}</p>}

        {sent ? (
          <React.Fragment>
            <h1 style={{fontSize:19,letterSpacing:"-.02em",margin:"14px 0 4px"}}>Check your email</h1>
            <p className="muted" style={{fontSize:12.5}}>
              If <strong>{email}</strong> has an account, a reset link is on its way. It works once and
              expires in an hour.
            </p>
            <button className="btn" style={{marginTop:14,width:"100%"}}
                    onClick={()=>{ setSent(false); setMode("signin"); }}>Back to sign in</button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <h1 style={{fontSize:19,letterSpacing:"-.02em",margin:"14px 0 4px"}}>
              {mode === "register" ? "Create an account" : mode === "forgot" ? "Reset your password" : "Sign in"}
            </h1>
            <p className="muted" style={{fontSize:12.5,margin:"0 0 18px"}}>
              {mode === "register" ? "You'll be the owner of any project you create."
               : mode === "forgot" ? "We'll email you a link to set a new one."
               : "Use your email and password, or continue with Google."}
            </p>

            {googleEnabled && mode !== "forgot" && (
              <React.Fragment>
                <a className="btn"
                   href={"/auth/google" + (window.location.pathname.startsWith("/invite/")
                     ? "?next=" + encodeURIComponent(window.location.pathname) : "")}
                   style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,width:"100%",
                           background:"#fff",color:"var(--ink)",border:"1px solid var(--rule)",textDecoration:"none"}}>
                  <svg width="15" height="15" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-3.9H24v7.1h12c-.2 1.9-1.5 4.7-4.4 6.6l6.7 5.2C42.2 35.5 45 30.3 45 24z"/>
                    <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.9-5.4c-1.9 1.3-4.4 2.2-7.6 2.2-5.8 0-10.7-3.8-12.5-9.1l-7.1 5.5C8.1 41 15.4 46 24 46z"/>
                    <path fill="#FBBC05" d="M11.5 28.4c-.5-1.4-.8-2.9-.8-4.4s.3-3 .8-4.4l-7.1-5.5C2.9 17 2 20.4 2 24s.9 7 2.4 9.9l7.1-5.5z"/>
                    <path fill="#EA4335" d="M24 10.7c3.3 0 5.5 1.4 6.8 2.6l6-5.9C33.1 4 29 2 24 2 15.4 2 8.1 7 4.4 14.1l7.1 5.5C13.3 14.5 18.2 10.7 24 10.7z"/>
                  </svg>
                  Continue with Google
                </a>
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"14px 0"}}>
                  <div className="hr" style={{flex:1}}/><span className="eyebrow">or</span><div className="hr" style={{flex:1}}/>
                </div>
              </React.Fragment>
            )}

            {mode === "register" && (
              <label style={{display:"block",marginBottom:10}}><div className="eyebrow">Your name</div>
                <input className="field" value={name} onChange={e=>setName(e.target.value)} placeholder="Kenneth Moye"/></label>
            )}
            <label style={{display:"block"}}><div className="eyebrow">Email</div>
              <input className="field" type="email" ref={emailRef} value={email} autoFocus
                     autoComplete="username"
                     onChange={e=>setEmail(e.target.value)} onInput={e=>setEmail(e.target.value)}
                     onKeyDown={e=>{ if (e.key === "Enter") submit(); }}
                     placeholder="you@company.com"/></label>
            {mode !== "forgot" && (
              <label style={{display:"block",marginTop:10}}><div className="eyebrow">Password</div>
                <input className="field" type="password" ref={pwRef} value={password}
                       autoComplete={mode === "register" ? "new-password" : "current-password"}
                       onChange={e=>setPassword(e.target.value)} onInput={e=>setPassword(e.target.value)}
                       onKeyDown={e=>{ if (e.key === "Enter") submit(); }}
                       placeholder={mode === "register" ? "at least 10 characters" : ""}/></label>
            )}

            {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}

            <button className="btn go" style={{marginTop:14,width:"100%"}} disabled={busy} onClick={submit}>
              {busy ? "…" : mode === "register" ? "Create account" : mode === "forgot" ? "Email me a link" : "Sign in"}
            </button>

            <div style={{display:"flex",justifyContent:"space-between",marginTop:12,fontSize:12}}>
              {mode === "signin" ? (
                <React.Fragment>
                  <button className="linkbtn" onClick={()=>{setMode("register");setErr(null);}}>Create an account</button>
                  <button className="linkbtn" onClick={()=>{setMode("forgot");setErr(null);}}>Forgot password</button>
                </React.Fragment>
              ) : (
                <button className="linkbtn" onClick={()=>{setMode("signin");setErr(null);}}>← Back to sign in</button>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

/**
 * Setting a new password from an emailed link.
 *
 * The link is single-use and expires in an hour. Completing it signs out every
 * other session for that account — if the link reached the wrong person, the
 * damage stops at this page.
 */
function ResetPassword({ token, say, onDone }) {
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  // A password manager can set an input's value without React ever seeing an
  // event, so component state reads empty while the field plainly is not. Read
  // the field itself at submit time and treat that as the truth.
  const nextRef = useRef(null), againRef = useRef(null);
  const read = () => ({
    a: (nextRef.current && nextRef.current.value) || next,
    b: (againRef.current && againRef.current.value) || again,
  });
  const tooShort = next.length < 10;
  const mismatch = next !== again;

  // The button is never disabled. A disabled control that does not say why is
  // indistinguishable from a broken one — which is exactly how this read.
  const save = async () => {
    setErr(null);
    const { a, b } = read();
    if (a.length < 10) { setErr("The password needs to be at least 10 characters."); return; }
    if (a !== b) { setErr("The two passwords don't match. Retype the second one."); return; }
    setBusy(true);
    try {
      await api.resetPassword({ token, password: a });
      setDone(true);
      say("Password set. Sign in with it.");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="authwrap">
      <div className="authcard">
        <div className="authbrand">
          <div className="brandmark" style={{color:"var(--ink)",fontSize:17}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="eyebrow" style={{marginTop:4}}>{PRODUCT.tagline}</div>
        </div>

        {done ? (
          <React.Fragment>
            <h1 style={{fontSize:19,letterSpacing:"-.02em",margin:"18px 0 4px"}}>Password set</h1>
            <p className="muted" style={{fontSize:12.5,margin:"0 0 18px"}}>
              Every other session on this account has been signed out. Sign in with the new password.
            </p>
            <button className="btn go" style={{width:"100%"}} onClick={onDone}>Sign in</button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <h1 style={{fontSize:19,letterSpacing:"-.02em",margin:"18px 0 4px"}}>Set a new password</h1>
            <p className="muted" style={{fontSize:12.5,margin:"0 0 18px"}}>
              This link works once and expires an hour after it was sent.
            </p>
            <label style={{display:"block"}}><div className="eyebrow">New password</div>
              <input className="field" type="password" autoComplete="new-password" autoFocus
                     ref={nextRef} value={next}
                     onChange={e=>setNext(e.target.value)} onInput={e=>setNext(e.target.value)}
                     onKeyDown={e=>{ if (e.key === "Enter") save(); }}
                     placeholder="at least 10 characters"/></label>
            <label style={{display:"block",marginTop:10}}>
              <div className="eyebrow">Type it again to confirm</div>
              <input className="field" type="password" autoComplete="new-password"
                     ref={againRef} value={again}
                     onChange={e=>setAgain(e.target.value)} onInput={e=>setAgain(e.target.value)}
                     onKeyDown={e=>{ if (e.key === "Enter") save(); }}/></label>
            <div style={{marginTop:9,fontSize:11.5,display:"flex",gap:14,flexWrap:"wrap"}}>
              <span style={{color: next && !tooShort ? "var(--verdigris)" : "var(--slate)"}}>
                {next && !tooShort ? "✓" : "○"} at least 10 characters</span>
              <span style={{color: again && !mismatch ? "var(--verdigris)" : "var(--slate)"}}>
                {again && !mismatch ? "✓" : "○"} both match</span>
            </div>
            {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
            <button className="btn go" style={{marginTop:14,width:"100%"}} disabled={busy} onClick={save}>
              {busy ? "…" : "Set password"}</button>
            <button className="linkbtn" style={{marginTop:12}} onClick={onDone}>← Back to sign in</button>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

/** Somebody signed in who holds no project yet: make them one. */
/**
 * What an invited person sees when they arrive without their link.
 *
 * The invitation says who invited them, to what, and as what — before they
 * accept, not after. The alternative they were being shown was a form asking
 * for their company and site address, which answers a question nobody had.
 */
function InvitationWaiting({ session, onAccepted, onSignOut, say }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const invites = session.pendingInvites || [];

  const accept = async (inv) => {
    setBusy(inv.id); setErr(null);
    try {
      const r = await api.claimInvite(inv.id);
      say(r.note || `You're in as ${ROLES[r.role] ? ROLES[r.role].label : r.role}.`);
      onAccepted(r.projectId);
    } catch (e) { setErr(e.message); setBusy(null); }
  };

  return (
    <div className="authwrap">
      <div className="authcard" style={{maxWidth:520}}>
        <div className="authbrand">
          <div className="brandmark" style={{color:"var(--ink)",fontSize:17}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="eyebrow" style={{marginTop:4}}>{session.user.email}</div>
        </div>
        <h1 style={{fontSize:19,letterSpacing:"-.02em",margin:"18px 0 4px"}}>
          {invites.length > 1 ? "You've been invited to these projects" : "You've been invited"}
        </h1>
        <p className="muted" style={{fontSize:12.5,margin:"0 0 18px"}}>
          Accepting adds you to the project. You can leave at any time by asking the developer.
        </p>
        {invites.map(inv => {
          const copy = INVITE_COPY[inv.role] || {};
          return (
            <div key={inv.id} className="card pad" style={{marginBottom:12,borderLeft:"3px solid var(--brass)"}}>
              <div className="eyebrow">{inv.orgName}</div>
              <div style={{fontWeight:700,fontSize:15,margin:"4px 0 2px",letterSpacing:"-.01em"}}>
                {inv.projectName}
              </div>
              <span className="tag n">{ROLES[inv.role] ? ROLES[inv.role].label : inv.role}</span>
              {copy.what && (
                <p style={{fontSize:12.5,margin:"10px 0 0",lineHeight:1.55}}>{copy.what}</p>
              )}
              {copy.note && (
                <p className="muted" style={{fontSize:11.5,margin:"6px 0 0",lineHeight:1.5}}>{copy.note}</p>
              )}
              {inv.claimable ? (
                <button className="btn go" style={{marginTop:12,width:"100%"}}
                        disabled={busy === inv.id} onClick={()=>accept(inv)}>
                  {busy === inv.id ? "…" : "Accept and continue"}
                </button>
              ) : (
                <p className="muted" style={{fontSize:12,margin:"12px 0 0",lineHeight:1.5}}>
                  Open the link in the invitation we emailed to <b>{session.user.email}</b> to accept.
                  It confirms the address belongs to you.
                </p>
              )}
            </div>
          );
        })}
        {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
        <button className="linkbtn" style={{marginTop:6}} onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}

function FirstRun({ session, onCreated, onSignOut, say }) {
  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const org = session.orgs && session.orgs.length
        ? session.orgs[0]
        : await api.register && await http("POST", "/orgs", { name: orgName });
      const slug = org.slug;
      setTenant(slug, null);
      const made = await http("POST", `/orgs/${slug}/projects`, { name, address, timezone: "America/Chicago" });
      onCreated(slug, made.projectId);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div className="authwrap">
      <div className="authcard" style={{maxWidth:520}}>
        <div className="authbrand">
          <div className="brandmark" style={{color:"var(--ink)",fontSize:17}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="eyebrow" style={{marginTop:4}}>{PRODUCT.tagline}</div>
        </div>
        <h1 style={{fontSize:19,letterSpacing:"-.02em",margin:"18px 0 4px"}}>Start your first project</h1>
        <p className="muted" style={{fontSize:12.5,margin:"0 0 18px"}}>
          You'll be the owner. Everything else — the schedule of values, the contractors, the bank — is
          imported or invited once you're in.
        </p>
        {/*
          * These placeholders held a real customer's company, project and site
          * address. Written as examples during the prototype, when there was
          * one customer and nobody else could reach this screen — and then read
          * by an invited stranger as somebody's details already filled in.
          * Examples on a shared screen have to be obviously invented.
          */}
        {(!session.orgs || !session.orgs.length) && (
          <label style={{display:"block",marginBottom:10}}><div className="eyebrow">Your company</div>
            <input className="field" value={orgName} onChange={e=>setOrgName(e.target.value)}
                   placeholder="e.g. Riverbend Development LLC"/></label>
        )}
        <label style={{display:"block",marginBottom:10}}><div className="eyebrow">Project name</div>
          <input className="field" value={name} onChange={e=>setName(e.target.value)}
                 placeholder="e.g. Market Street Renovation"/></label>
        <label style={{display:"block"}}><div className="eyebrow">Site address</div>
          <input className="field" value={address} onChange={e=>setAddress(e.target.value)}
                 placeholder="e.g. 100 Main Street, Springfield"/></label>
        {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
        <button className="btn go" style={{marginTop:16,width:"100%"}}
                disabled={busy || !name.trim() || (!session.orgs?.length && !orgName.trim())} onClick={create}>
          {busy ? "…" : "Create project"}
        </button>
        <button className="linkbtn" style={{marginTop:12}} onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}

/** Chooses among the projects this session actually has a membership on. */
function PickProject({ session, onPick, onSignOut }) {
  return (
    <div className="authwrap" style={{alignItems:"flex-start",paddingTop:"10vh"}}>
      <div className="authcard" style={{maxWidth:560}}>
        <div className="authbrand">
          <div className="brandmark" style={{color:"var(--ink)",fontSize:17}}>{PRODUCT.name.toUpperCase()}</div>
          <div className="eyebrow" style={{marginTop:4}}>{session.user.email}</div>
        </div>
        {session.orgs.map(org => (
          <div key={org.slug} style={{marginTop:18}}>
            <div className="eyebrow" style={{marginBottom:7}}>{org.name}</div>
            {org.projects.map(pr => (
              <button key={pr.id} className="authuser" onClick={()=>onPick(pr.id)}>
                <span style={{fontWeight:600,fontSize:12.5}}>{pr.name}</span>
                <span className="muted" style={{fontSize:11}}>
                  {ROLES[pr.role] ? ROLES[pr.role].label : pr.role}
                  {pr.initialized ? "" : " · not set up yet"}
                </span>
              </button>
            ))}
          </div>
        ))}
        <button className="linkbtn" style={{marginTop:16}} onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}

function Projects({ db, user, onPick, onSignOut, say, refresh }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name:"", address:"" });
  const [busy, setBusy] = useState(false);
  const isOwner = user.role === "owner";
  const t = capitalTotals(db);
  const value = db.lineItems.reduce((s,i)=>s+i.scheduledValue,0);
  const inPlace = db.lineItems.reduce((s,i)=>s+i.scheduledValue*i.percent/100,0);

  return (
    <div className="authwrap" style={{alignItems:"flex-start",paddingTop:"6vh"}}>
      <div className="authcard" style={{maxWidth:640}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start"}}>
          <div>
            <div className="eyebrow">{db.org.name}</div>
            <h1 style={{fontSize:20,letterSpacing:"-.025em",margin:"5px 0 0"}}>Your projects</h1>
            <p className="muted" style={{fontSize:12.5,margin:"3px 0 0"}}>
              Signed in as {user.name} · {ROLES[user.role].label}</p>
          </div>
          <button className="btn ghost sm" onClick={onSignOut}>Sign out</button>
        </div>

        <div style={{marginTop:20}}>
          {db.projects.map(pr => {
            const live = pr.id === PROJECT_ID;
            return (
              <button key={pr.id} className="projcard" onClick={()=>onPick(pr)}
                      disabled={!live && !isOwner}>
                <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"baseline"}}>
                  <span style={{minWidth:0}}>
                    <span style={{fontWeight:700,fontSize:14,letterSpacing:"-.015em"}}>{pr.name}</span>
                    <span className="itemmeta" style={{display:"block"}}>{pr.address}</span>
                  </span>
                  <span className={"tag " + (pr.initialized ? "g" : "n")}>
                    {pr.initialized ? "Live" : "Not set up"}</span>
                </div>
                {live && (
                  <React.Fragment>
                    <div className="fbar" style={{marginTop:11}}>
                      <i className="f-recv" style={{width: clamp(value?inPlace/value*100:0,0,100)+"%"}}/>
                    </div>
                    <div className="itemmeta" style={{marginTop:5}}>
                      {pct(value?inPlace/value*100:0)} built · {usd(inPlace)} of {usd(value)}
                      {isOwner && t.remaining ? " · " + usd(t.remaining) + " capital still to raise" : ""}
                    </div>
                  </React.Fragment>
                )}
                {!live && isOwner && <div className="itemmeta" style={{marginTop:8}}>
                  Open it to run through setup.</div>}
              </button>
            );
          })}
        </div>

        {isOwner && (
          <React.Fragment>
            {!adding
              ? <button className="btn ghost" style={{marginTop:12}} onClick={()=>setAdding(true)}>Start a new project</button>
              : (
                <div className="card pad" style={{marginTop:12,borderLeft:"3px solid var(--brass)"}}>
                  <SectionHead>New project</SectionHead>
                  <label style={{display:"block"}}><div className="eyebrow">Name</div>
                    <input className="field" value={f.name} onChange={e=>setF(x=>({...x,name:e.target.value}))}
                           placeholder="e.g. Market Street Renovation — Phase 2"/></label>
                  <label style={{display:"block",marginTop:10}}><div className="eyebrow">Site address</div>
                    <input className="field" value={f.address} onChange={e=>setF(x=>({...x,address:e.target.value}))}/></label>
                  <div className="toolbar">
                    <button className="btn go" disabled={!f.name.trim()||busy} onClick={async()=>{
                      setBusy(true);
                      await api.createProject({ ...f, actor:user.name });
                      await refresh(); setBusy(false); setAdding(false); setF({name:"",address:""});
                      say(f.name + " created — open it to run setup");
                    }}>Create</button>
                    <button className="btn ghost" onClick={()=>setAdding(false)}>Cancel</button>
                  </div>
                </div>
              )}
          </React.Fragment>
        )}
      </div>
      <p className="authfoot">
        An inspector or a lender working for several developers sees all of their projects here.<br/>
        <span className="mono" style={{fontSize:10.5,opacity:.8}}>{COPYRIGHT}</span>
      </p>
    </div>
  );
}

/* ----------------------------------------------------------- Invitation */
/* Contractors get the clauses. Everyone else gets this. */
function InviteAccept({ db, me, meId, role, say, refresh, setView }) {
  const [busy, setBusy] = useState(false);
  const copy = INVITE_COPY[role] || INVITE_COPY.investor;
  const p = db.project;
  return (
    <React.Fragment>
      <h1 className="pagetitle">{copy.heading}</h1>
      <p className="pagesub">{p.name} · {p.address}</p>
      <div className="card pad" style={{marginTop:16, maxWidth:640}}>
        <SectionHead>What you'll be doing</SectionHead>
        <p style={{fontSize:13,lineHeight:1.6,marginTop:0}}>{copy.what}</p>
        {copy.note && <p className="muted" style={{fontSize:12.5,lineHeight:1.55}}>{copy.note}</p>}
        <div className="kv" style={{marginTop:14}}>
          <div><div className="k">You are</div><div className="v" style={{fontSize:13}}>{me.contact}</div></div>
          <div><div className="k">For</div><div className="v" style={{fontSize:13}}>{me.name}</div></div>
          <div><div className="k">Role</div><div className="v" style={{fontSize:13}}>{ROLES[role].label}</div></div>
          <div><div className="k">Invited by</div><div className="v" style={{fontSize:13}}>
            {(db.parties.find(x=>x.role==="owner")||{}).contact}</div></div>
        </div>
        <div className="toolbar">
          <button className="btn go" disabled={busy} onClick={async()=>{
            setBusy(true);
            await api.acceptInvite({ partyId: meId, actor: me.contact });
            await refresh(); setBusy(false); setView("dashboard"); say("You're in");
          }}>Accept and continue</button>
        </div>
        <p className="muted" style={{fontSize:11.5,marginBottom:0}}>
          Everything you do here is recorded with your name and the time — that's the point of the system, and it cuts
          both ways.
        </p>
      </div>
    </React.Fragment>
  );
}

/* ------------------------------------------------------------- Settings */
function Settings({ db, role, me, meId, say, refresh, setView }) {
  const [tab, setTab] = useState("access");
  if (role !== "owner") return <Empty title="Owner only" body="Project settings and access are managed by the owner."/>;
  return (
    <React.Fragment>
      <h1 className="pagetitle">Settings</h1>
      <p className="pagesub">Who can get in, how the project reaches people, and the account it belongs to.</p>
      <div className="steps noprint" style={{marginTop:16}}>
        {[["access","Access"],["investors","Invite investors"],["notifications","Notifications"],
          ["email","Email & inbox"],["org","Organisation"]].map(([k,l],i) => (
          <button key={k} className={"stepbtn " + (tab===k?"on":"")} onClick={()=>setTab(k)}>
            <span className="stepno">{i+1}</span><span>{l}</span>
          </button>
        ))}
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>setView("setup")}>Project setup</button>
      </div>
      {tab === "access" && <AccessTab {...{db, me, meId, say, refresh}}/>}
      {tab === "investors" && <InviteInvestors {...{db, say, refresh, setView}}/>}
      {tab === "notifications" && <NotifyTab {...{db, me, say, refresh}}/>}
      {tab === "email" && <EmailTab {...{db, me, say, refresh, setView}}/>}
      {tab === "org" && <React.Fragment>
        <OrgTab {...{db, me, say, refresh}}/>
        <PasswordCard {...{say}}/>
      </React.Fragment>}
    </React.Fragment>
  );
}

/**
 * What one partner may do.
 *
 * Ticked on capability by capability, because these four or five people do
 * different jobs: one runs the raise, one is on site every day, one signs
 * nothing. A single "partner" role would have had to be the union of all of
 * that, and the union is very nearly the owner.
 *
 * Nothing is saved until Save is pressed, and the ones that decide what gets
 * paid say so on the row rather than in a note underneath.
 */
function PartnerGrants({ user, onClose, say, refresh }) {
  const [picked, setPicked] = useState(new Set(user.grants || []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const before = (user.grants || []).slice().sort().join(",");
  const dirty = [...picked].sort().join(",") !== before;

  const toggle = (k) => setPicked((s) => {
    const next = new Set(s);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.setGrants(user.id, [...picked]);
      await refresh();
      say(`Saved. ${user.name || user.email} can now do ${picked.size} thing${picked.size === 1 ? "" : "s"}.`);
      onClose();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const money = [...picked].filter((k) =>
    DELEGABLE_CAPS.some(([, caps]) => caps.some(([c, , , m]) => c === k && m)));

  return (
    <div className="card pad" style={{marginTop:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead right={<span className="muted mono" style={{fontSize:11}}>{user.email}</span>}>
        What {user.name || "they"} can do
      </SectionHead>
      <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:640}}>
        They can already see the project, the audit trail and messages — that comes with being a
        partner. Everything below is yours to hand over. Anything you don't tick stays with you.
      </p>

      {DELEGABLE_CAPS.map(([group, caps]) => (
        <div key={group} style={{marginTop:16}}>
          <div className="eyebrow">{group}</div>
          {caps.map(([k, label, note, isMoney]) => (
            <label key={k} className="grantrow">
              <input type="checkbox" checked={picked.has(k)} onChange={()=>toggle(k)}/>
              <span>
                <span className="grantlabel">
                  {label}
                  {isMoney && <span className="tag b" style={{marginLeft:7}}>decides money</span>}
                </span>
                <span className="grantnote">{note}</span>
              </span>
            </label>
          ))}
        </div>
      ))}

      {money.length > 0 && (
        <div className="banner" style={{marginTop:16}}>
          <h4>{money.length} of these decide what gets paid</h4>
          <p>Their signature goes on the record, not yours — every approval names the person who
             made it. You can take any of this back at any time, and that change is on the audit
             trail too.</p>
        </div>
      )}

      <div className="toolbar" style={{marginTop:14}}>
        <button className="btn go" disabled={busy || !dirty} onClick={save}>
          {busy ? "Saving…" : dirty ? "Save what they can do" : "Saved"}</button>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <div style={{flex:1}}/>
        <span className="muted" style={{fontSize:12}}>
          {picked.size} of {DELEGABLE_CAPS.reduce((n, [, c]) => n + c.length, 0)} ticked</span>
      </div>
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
    </div>
  );
}

/**
 * Bringing investors onto the platform, in the owner's own order.
 *
 * Forty-odd of them arrive from a spreadsheet all at once, and inviting them
 * all on the first day is rarely what anybody wants: the core few come on
 * first, get familiar, and the rest follow. So this is a list you choose from
 * rather than a button that does everybody.
 *
 * Every invitation is its own message to one person. There is no group send
 * here or anywhere — an investor must never be able to read the roster off the
 * header of an email.
 */
function InviteInvestors({ db, say, refresh, setView }) {
  const [held, setHeld] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("notyet");
  const [working, setWorking] = useState(null);
  const [report, setReport] = useState(null);

  const load = () => api.undeliverable().then(setHeld).catch(() => setHeld([]));
  useEffect(() => { load(); }, []);

  const investors = (db.parties || []).filter((p) => p.role === "investor" && p.email);
  const byAddress = {};
  for (const h of held || []) byAddress[String(h.address).toLowerCase()] = h;
  const userFor = (partyId) => (db.users || []).find((u) => u.partyId === partyId);
  const inviteFor2 = (partyId) => (db.invites || []).find(
    (i) => i.partyId === partyId && i.status === "sent");

  const stateOf = (p) => {
    const u = userFor(p.id);
    if (u) return { k: "in", label: "On the platform", tag: "g" };
    if (inviteFor2(p.id)) return { k: "invited", label: "Invited, not accepted", tag: "p" };
    return { k: "notyet", label: "Not invited", tag: "n" };
  };
  const isHeld = (p) => !!byAddress[String(p.email).toLowerCase()];

  const rows = investors
    .filter((p) => only === "all" || stateOf(p).k === only)
    .filter((p) => !q.trim() || (p.name + " " + p.email).toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
  const counts = {
    all: investors.length,
    notyet: investors.filter((p) => stateOf(p).k === "notyet").length,
    invited: investors.filter((p) => stateOf(p).k === "invited").length,
    in: investors.filter((p) => stateOf(p).k === "in").length,
  };

  const chosen = investors.filter((p) => picked.has(p.id));
  const chosenHeld = chosen.filter(isHeld);
  const visible = rows.map((p) => p.id);
  const allVisible = visible.length > 0 && visible.every((id) => picked.has(id));

  const send = async () => {
    if (!window.confirm(
      `Send ${chosen.length} invitation${chosen.length === 1 ? "" : "s"}?\n\n` +
      `Each one is a separate email to one person — nobody sees anybody else's address.\n\n` +
      (chosenHeld.length
        ? `${chosenHeld.length} of them ${chosenHeld.length === 1 ? "is" : "are"} on hold, so ` +
          `${chosenHeld.length === 1 ? "that invitation" : "those invitations"} will be recorded ` +
          `and not sent. Allow mail to them first if you want them to arrive.`
        : "They can sign in as soon as they accept."))) return;

    setWorking({ done: 0, total: chosen.length }); setReport(null);
    const sent = [], heldBack = [], failed = [];
    let done = 0;
    for (const p of chosen) {
      try {
        const r = await api.resendInvite(p.id);
        (r.held ? heldBack : sent).push(p.name);
      } catch (e) { failed.push({ name: p.name, why: e.message }); }
      setWorking({ done: ++done, total: chosen.length });
    }
    await refresh(); await load();
    setWorking(null); setPicked(new Set());
    setReport({ sent, heldBack, failed });
    if (!failed.length && !heldBack.length) say(`${sent.length} invitation(s) sent.`);
  };

  return (
    <React.Fragment>
      <p className="muted" style={{fontSize:12.5,maxWidth:660,marginTop:0}}>
        Everyone on the capital side of this project. Choose who to bring on and when — your core
        people first if you like, the rest whenever you are ready. Each invitation is a separate
        email to one person; there is no group send, so no investor ever sees another's address.
      </p>

      <div className="toolbar noprint" style={{marginTop:14}}>
        {[["notyet","Not invited"],["invited","Invited"],["in","On the platform"],["all","Everyone"]]
          .map(([k,l]) => (
          <button key={k} className={"btn " + (only===k ? "" : "ghost")} onClick={()=>setOnly(k)}>
            {l} <span className="muted">{counts[k]}</span>
          </button>
        ))}
        <div style={{flex:1}}/>
        <input className="field" style={{maxWidth:220}} placeholder="Find a name or address"
               value={q} onChange={(e)=>setQ(e.target.value)}/>
      </div>

      {working && (
        <div className="banner brass" style={{marginTop:14}}>
          <h4>Sending — {working.done} of {working.total}</h4>
          <p>One at a time, each to one person. Leave this open until it finishes.</p>
        </div>
      )}

      {report && (
        <div className="banner" style={{marginTop:14}}>
          <h4>
            {report.sent.length} sent
            {report.heldBack.length ? `, ${report.heldBack.length} held back` : ""}
            {report.failed.length ? `, ${report.failed.length} failed` : ""}
          </h4>
          {report.heldBack.length > 0 && (
            <p>Held and not sent: {report.heldBack.slice(0, 6).join(", ")}
              {report.heldBack.length > 6 ? ` and ${report.heldBack.length - 6} more` : ""}.
              {" "}Their invitations are on the record and will not arrive until you allow mail to
              them.{" "}
              <button className="btn ghost sm" onClick={()=>setView("investors")}>Open Investors</button>
            </p>
          )}
          {report.failed.length > 0 && (
            <ul style={{margin:"6px 0 0 16px",fontSize:12.5,lineHeight:1.6}}>
              {report.failed.slice(0, 6).map((f, i) => <li key={i}><b>{f.name}</b> — {f.why}</li>)}
            </ul>
          )}
          <p><button className="btn ghost sm" onClick={()=>setReport(null)}>Dismiss</button></p>
        </div>
      )}

      {chosen.length > 0 && !working && (
        <div className="card pad bulkbar" style={{marginTop:14}}>
          <div className="editline">
            <span className="tag b">{chosen.length} selected</span>
            <button className="linkbtn" style={{fontSize:12}} onClick={()=>setPicked(new Set())}>Clear</button>
            <div style={{flex:1}}/>
            <Action action="inviteMember" className="btn go" onClick={send}>
              Send {chosen.length} invitation{chosen.length === 1 ? "" : "s"}
            </Action>
          </div>
          {chosenHeld.length > 0 && (
            <p className="muted" style={{fontSize:11.5,marginTop:10,marginBottom:0}}>
              {chosenHeld.length} of those selected {chosenHeld.length === 1 ? "is" : "are"} on hold.
              {" "}{chosenHeld.length === 1 ? "That invitation" : "Those invitations"} will be
              recorded and refused rather than sent — allow mail to them on the Investors screen
              first if you want them to arrive.
            </p>
          )}
        </div>
      )}

      {held === null
        ? <p className="eyebrow" style={{padding:"12px 0"}}>Loading…</p>
        : rows.length === 0
        ? <Empty title="Nobody here" body={q ? "No investor matches that." : "Nobody in this group."}/>
        : (
          <div className="tablewrap" style={{marginTop:12}}>
            <table className="sov">
              <thead><tr>
                <th style={{width:28}}>
                  <input type="checkbox" checked={allVisible} aria-label="Select everyone shown"
                         onChange={()=>setPicked((s2)=>{ const n=new Set(s2);
                           allVisible ? visible.forEach(id=>n.delete(id)) : visible.forEach(id=>n.add(id));
                           return n; })}/>
                </th>
                <th>Investor</th><th>Email</th><th>Mail</th><th>On the platform</th>
              </tr></thead>
              <tbody>
                {rows.map((p) => {
                  const st = stateOf(p);
                  return (
                    <tr key={p.id} className={picked.has(p.id) ? "picked" : ""}>
                      <td>
                        <input type="checkbox" checked={picked.has(p.id)} aria-label={"Select " + p.name}
                               onChange={()=>setPicked((s2)=>{ const n=new Set(s2);
                                 n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })}/>
                      </td>
                      <td>
                        <div className="itemname">{p.name}</div>
                        {p.trade === "Entity" && <div className="itemmeta">Company</div>}
                      </td>
                      <td className="mono" style={{fontSize:11.5}}>{p.email}</td>
                      <td>{isHeld(p)
                        ? <span className="tag n" title="Nothing is sent to a held address">Held</span>
                        : <span className="tag g">Can be reached</span>}</td>
                      <td><span className={"tag " + st.tag}>{st.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </React.Fragment>
  );
}

function AccessTab({ db, me, meId, say, refresh }) {
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  // Only an owner changes who has access — the server enforces it too, and
  // refuses regardless of what this shows.
  const canManage = db.me.role === "owner";
  const [f, setF] = useState({ name:"", email:"", role:"contractor", trade:"General" });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const valid = f.name.trim().length > 1 && f.email.includes("@");
  const rows = db.users.map(u => ({ u, party: partyById(db, u.partyId) || {},
    invite: inviteFor(db, u.partyId) }));

  return (
    <React.Fragment>
      <div className="toolbar noprint">
        <button className="btn" onClick={()=>setInviting(i=>!i)}>
          {inviting ? "Cancel" : "Invite someone"}</button>
      </div>

      {inviting && (
        <div className="card pad" style={{marginBottom:14, borderLeft:"3px solid var(--brass)"}}>
          <SectionHead>Invite someone to this project</SectionHead>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
            <label><div className="eyebrow">Company or person</div>
              <input className="field" value={f.name} onChange={e=>set("name",e.target.value)}
                     placeholder="Forrest County Plumbing"/></label>
            <label><div className="eyebrow">Email</div>
              <input className="field" type="email" value={f.email} onChange={e=>set("email",e.target.value)}/></label>
            <label><div className="eyebrow">Role on this project</div>
              <select className="field" value={f.role} onChange={e=>set("role",e.target.value)}>
                {Object.entries(ROLES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></label>
            {f.role === "contractor" && (
              <label><div className="eyebrow">Trade</div>
                <select className="field" value={f.trade} onChange={e=>set("trade",e.target.value)}>
                  {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
                </select></label>
            )}
          </div>
          <div className="note" style={{marginTop:12}}>
            <div className="meta">What they'll get</div>
            <div style={{fontSize:12.5}}>
              {f.role === "contractor"
                ? "A link to onboarding: the scope offered to them, and the clauses to initial including the delay penalty and, where the job requires one, the bond. They can't take work until that's done and their paperwork is on file."
                : (INVITE_COPY[f.role] || {}).what}
            </div>
          </div>
          <div className="toolbar">
            <Action action="inviteMember" className="btn go" disabled={!valid||busy} onClick={async()=>{
              setBusy(true);
              await api.inviteMember({ ...f, actor:me.contact });
              await refresh(); setBusy(false); setInviting(false); setF({name:"",email:"",role:"contractor",trade:"General"});
              say("Invitation sent to " + f.email);
            }}>Send invitation</Action>
            <button className="btn ghost" onClick={()=>setInviting(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="tablewrap">
        <table className="sov">
          <thead><tr>
            <th className="plain">Person</th><th className="plain">Company</th>
            <th className="plain">Role</th><th className="plain">Access</th><th className="plain">Sees</th>
          </tr></thead>
          <tbody>
            {rows.map(({u, party, invite}) => {
              const ob = onboardingOf(party);
              const scoped = ROLES[u.role].capitalOnly ? "Capital status only"
                : ROLES[u.role].scoped ? "Only the scope assigned to them" : "The whole project";
              return (
                <tr key={u.id}>
                  <td><div className="itemname">{u.name}</div><div className="itemmeta">{u.email}</div></td>
                  <td style={{fontSize:12}}>{party.name || "—"}
                    {party.trade && party.trade !== "General" ? <div className="itemmeta">{party.trade}</div> : null}</td>
                  <td>
                    {canManage && u.id !== meId && u.role !== "owner" ? (
                      <select className="field" style={{maxWidth:170,fontSize:12}}
                              value={u.role}
                              onChange={async (e) => {
                                const next = e.target.value;
                                const label = ROLES[next] ? ROLES[next].label : next;
                                if (!window.confirm(
                                  `Make ${u.name || u.email} a ${label}?\n\n` +
                                  (next === "partner"
                                    ? "They will be able to see the project and the audit trail. " +
                                      "Everything beyond that you tick on for them afterwards."
                                    : "This replaces what they can do today.") +
                                  "\n\nAnything previously granted to them is cleared."
                                )) { e.target.value = u.role; return; }
                                try {
                                  await api.setMemberRole(u.id, next);
                                  await refresh();
                                  say(`${u.name || u.email} is now a ${label}.` +
                                      (next === "partner" ? " Choose what they can do." : ""));
                                  if (next === "partner") setEditing(u.id);
                                } catch (err) { say(err.message); }
                              }}>
                        {Object.entries(ROLES).filter(([k]) => k !== "owner")
                          .map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        <option value="owner">Owner / Developer</option>
                      </select>
                    ) : (
                      <span className="tag n">{ROLES[u.role] ? ROLES[u.role].label : u.role}
                        {u.role === "owner" ? " · protected" : ""}</span>
                    )}
                  </td>
                  <td>
                    {u.role === "contractor"
                      ? <span className={"tag " + (ob.status==="signed"?"g":ob.status==="acknowledged"?"p":"b")}>
                          {ob.status==="signed"?"Agreement signed":ob.status==="acknowledged"?"Terms initialled":"Not onboarded"}</span>
                      : <span className={"tag " + (isOnboarded(party) ? "g" : invite ? "p" : "n")}>
                          {isOnboarded(party) ? "Active" : invite ? "Invited" : "Not invited"}</span>}
                  </td>
                  <td style={{fontSize:12}} className="muted">
                    {u.role === "partner"
                      ? ((u.grants || []).length
                          ? (u.grants || []).length + " thing" + ((u.grants||[]).length === 1 ? "" : "s") + " they can do"
                          : "Can see the project, nothing more")
                      : scoped}
                    {u.role === "partner" && (
                      <div><button className="linkbtn" style={{fontSize:11.5,marginTop:4}}
                                   onClick={()=>setEditing(editing === u.id ? null : u.id)}>
                        {editing === u.id ? "Close" : "Choose what they can do"}</button></div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <PartnerGrants key={editing} user={db.users.find(u => u.id === editing)}
                       onClose={()=>setEditing(null)} say={say} refresh={refresh}/>
      )}

      <p className="muted" style={{fontSize:11.5,marginTop:11,maxWidth:660}}>
        Removing someone deactivates them under Contractors &amp; subs — it never deletes them, because their signatures
        are part of the record.
      </p>
    </React.Fragment>
  );
}

function NotifyTab({ db, me, say, refresh }) {
  const [sms, setSms] = useState({ ...db.settings.sms });
  const [digest, setDigest] = useState({ ...db.settings.digest });
  const [busy, setBusy] = useState(false);
  const contactable = db.users.length;

  return (
    <React.Fragment>
      <div className="card pad" style={{maxWidth:720}}>
        <SectionHead>Who gets told what</SectionHead>
        <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:620}}>
          Email reaches everyone and needs nothing installed. The bell only reaches people who live in the app — which
          is you, and maybe your superintendent. Design for email first.
        </p>
        <div className="tablewrap" style={{marginTop:12}}>
          <table className="sov" style={{minWidth:520}}>
            <thead><tr><th className="plain">When this happens</th><th className="plain">Who hears</th>
              <th className="plain">How</th></tr></thead>
            <tbody>
              {[["Work submitted for approval","Inspector, owner","Email + bell"],
                ["Owner approves a line item","Lender, contractor","Email"],
                ["Draw submitted","Lender","Email"],
                ["Check or waiver emailed in","Owner","Email + bell"],
                ["Quarantined email held back","Owner","Email + SMS if on"],
                ["Punch item raised or overdue","The party that owes the fix","Email + bell"],
                ["Order past its order-by date","Owner","Daily digest"],
                ["Disbursement without owner sign-off","Owner","Email + SMS if on"],
                ["Update published","Investors","Email"],
              ].map(([a,b,c]) => (
                <tr key={a}><td style={{fontSize:12.5}}>{a}</td>
                  <td style={{fontSize:12}} className="muted">{b}</td>
                  <td><span className="tag n">{c}</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card pad" style={{marginTop:16, maxWidth:720}}>
        <SectionHead right={<span className={"tag " + (sms.enabled ? "g" : "n")}>{sms.enabled ? "On" : "Off"}</span>}>
          Text messages
        </SectionHead>
        <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:620}}>
          Worth it for the handful of things that can't wait until someone opens their inbox. Until there's a mobile
          app, this is how an urgent alert actually reaches a phone.
        </p>
        <label style={{display:"flex",gap:8,alignItems:"center",marginTop:10}}>
          <input type="checkbox" checked={sms.enabled} onChange={e=>setSms(x=>({...x,enabled:e.target.checked}))}/>
          <span style={{fontSize:12.5,fontWeight:500}}>Send text messages</span></label>
        {sms.enabled && (
          <React.Fragment>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginTop:12}}>
              <label><div className="eyebrow">Provider</div>
                <select className="field" value={sms.provider} onChange={e=>setSms(x=>({...x,provider:e.target.value}))}>
                  <option value="twilio">Twilio</option>
                  <option value="messagebird">MessageBird</option>
                  <option value="sns">Amazon SNS</option>
                </select></label>
              <label><div className="eyebrow">Sending number</div>
                <input className="field" value={sms.fromNumber} onChange={e=>setSms(x=>({...x,fromNumber:e.target.value}))}
                       placeholder="+1 601 555 0100"/></label>
            </div>
            <label style={{display:"flex",gap:8,alignItems:"flex-start",marginTop:12}}>
              <input type="checkbox" checked={sms.urgentOnly} style={{marginTop:3}}
                     onChange={e=>setSms(x=>({...x,urgentOnly:e.target.checked}))}/>
              <span><span style={{fontSize:12.5,fontWeight:500,display:"block"}}>Urgent only</span>
                <span className="muted" style={{fontSize:11.5}}>
                  Quarantined mail, a disbursement without your sign-off, a blocking punch item on a line about to be
                  paid. Text everything and people mute the number, which costs you the one channel that cuts through.</span></span>
            </label>
            <p className="muted" style={{fontSize:11.5,marginTop:12,marginBottom:0}}>
              Numbers are collected per person and need explicit consent plus a working STOP reply — that's a legal
              requirement in the US, not a nicety. {contactable} people on this project.
            </p>
          </React.Fragment>
        )}
      </div>

      <div className="card pad" style={{marginTop:16, maxWidth:720}}>
        <SectionHead>Digests</SectionHead>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
          <label style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="checkbox" checked={digest.daily} onChange={e=>setDigest(x=>({...x,daily:e.target.checked}))}/>
            <span style={{fontSize:12.5,fontWeight:500}}>Daily summary to the owner</span></label>
          <label><div className="eyebrow">Send at</div>
            <input className="field" type="time" value={digest.dailyAt}
                   onChange={e=>setDigest(x=>({...x,dailyAt:e.target.value}))}/></label>
          <label style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="checkbox" checked={digest.weekly} onChange={e=>setDigest(x=>({...x,weekly:e.target.checked}))}/>
            <span style={{fontSize:12.5,fontWeight:500}}>Weekly to everyone</span></label>
        </div>
        <div className="toolbar">
          <button className="btn go" disabled={busy} onClick={async()=>{
            setBusy(true);
            await api.updateSettings({ section:"sms", patch:sms, actor:me.contact });
            await api.updateSettings({ section:"digest", patch:digest, actor:me.contact });
            await refresh(); setBusy(false); say("Notification settings saved");
          }}>Save</button>
        </div>
      </div>
    </React.Fragment>
  );
}

/**
 * How mail is set up, and what has happened to it.
 *
 * This screen used to offer a provider dropdown, a From address and three green
 * SPF/DKIM/DMARC passes — none of which was wired to anything. The passes were
 * literal text in the markup, under a sentence saying all three had to pass
 * before sending anything asking for money. Asserting a security property
 * nobody checked is worse than saying nothing at all.
 *
 * What replaced it reports facts and offers exactly two things it can actually
 * do: set the inbound address, and let mail through to somebody who is held.
 */
function EmailTab({ db, me, say, refresh, setView }) {
  const [cfg, setCfg] = useState(null);
  const [held, setHeld] = useState([]);
  const [inbound, setInbound] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => Promise.all([api.mailConfig(), api.undeliverable()])
    .then(([c, h]) => { setCfg(c); setHeld(h); setInbound(c.inboundDomain || "");
                        setReplyTo(c.repliesGoTo || ""); })
    .catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  if (err) return <Empty title="Could not read the mail settings" body={err}/>;
  if (!cfg) return <p className="eyebrow" style={{padding:"12px 0"}}>Loading…</p>;

  const manual = held.filter((h) => h.reason === "manual");
  const bouncing = held.filter((h) => h.reason !== "manual");

  return (
    <React.Fragment>
      <p className="muted" style={{fontSize:12.5,maxWidth:660,marginTop:0}}>
        What this project sends, what it can receive, and who it currently cannot reach.
      </p>

      <div className="card pad" style={{marginTop:14,maxWidth:720}}>
        <SectionHead right={<span className={"tag " + (cfg.sending ? "g" : "n")}>
          {cfg.sending ? "Working" : "Not sending"}</span>}>What this project sends</SectionHead>
        <p style={{fontSize:12.5,marginTop:0,lineHeight:1.6,maxWidth:620}}>
          Notifications go out from <b className="mono" style={{fontSize:11.5}}>{cfg.sendsAs}</b> —
          a draw request waiting on the bank, work approved for payment, an investor saying they
          are in. Each one goes to a single person; nobody is ever put on a message alongside
          anybody else.
          {!cfg.sending && " Nothing is going out from here at the moment."}
        </p>
      </div>

      <div className="card pad" style={{marginTop:16,maxWidth:720}}>
        <SectionHead right={<span className={"tag " + (cfg.canBeRepliedTo ? "g" : "b")}>
          {cfg.canBeRepliedTo ? "Replies reach a person" : "Replies go nowhere"}</span>}>
          What happens when somebody replies
        </SectionHead>
        <p style={{fontSize:12.5,marginTop:0,lineHeight:1.6,maxWidth:620}}>
          {cfg.repliesGoTo
            ? <React.Fragment>Anybody who replies to a notification reaches{" "}
                <b className="mono" style={{fontSize:11.5}}>{cfg.repliesGoTo}</b>.</React.Fragment>
            : <React.Fragment><b>Nobody is reading replies.</b> Notifications go out with no reply
                address, so a contractor who hits reply is writing to a mailbox nobody opens.
                Setting one needs nothing but an address.</React.Fragment>}
        </p>
        <label style={{display:"block",marginTop:10}}>
          <div className="eyebrow">Replies go to</div>
          <input className="field mono" value={replyTo} placeholder="e.g. k.moye@kmlddc.com"
                 onChange={(e)=>setReplyTo(e.target.value)} style={{maxWidth:340}}/>
          <div className="itemmeta">
            A real person's mailbox. Nothing is filed automatically from it — it is simply somewhere
            a reply lands instead of vanishing.
          </div>
        </label>

        <SectionHead right={<span className={"tag " + (cfg.repliesLandOnThreads ? "g" : "n")}>
          {cfg.repliesLandOnThreads ? "Available" : "Not available yet"}</span>}>
          Letting the bank email documents in
        </SectionHead>
        <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:620,lineHeight:1.6}}>
          {cfg.repliesLandOnThreads
            ? <React.Fragment>Each draw has its own address. Copy it in when you send the draw, and
                whatever the bank replies — including three weeks later — lands on that draw's review
                queue. Nothing is filed automatically, and a message from a lookalike domain is
                quarantined rather than believed.</React.Fragment>
            : <React.Fragment>The bank will not log into a portal, but it will reliably hit reply and
                attach a PDF. Giving it an address that files what it sends against the right draw
                needs mail routing set up on the domain — that is done where the app is hosted, not
                from here, and it is not switched on yet.
                <b> Until it is, attach the bank's check copy to the draw yourself.</b>
              </React.Fragment>}
        </p>
        <div className="toolbar" style={{marginTop:12}}>
          <button className="btn" disabled={busy || replyTo === (cfg.repliesGoTo || "")}
                  onClick={async () => {
                    setBusy(true);
                    try { await api.saveEmailSettings({ replyTo: replyTo.trim() });
                          await load(); await refresh(); say("Saved. Replies will reach that address."); }
                    catch (e) { setErr(e.message); }
                    setBusy(false);
                  }}>{busy ? "…" : "Save"}</button>
        </div>
      </div>

      <div className="card pad" style={{marginTop:16,maxWidth:720}}>
        <SectionHead right={<span className={"tag " + (bouncing.length ? "p" : "g")}>
          {held.length} not being written to</span>}>Who cannot be reached</SectionHead>
        {held.length === 0
          ? <p className="muted" style={{fontSize:12.5,marginTop:0}}>
              Everybody on this project can be written to.</p>
          : (
            <React.Fragment>
              {bouncing.length > 0 && (
                <p style={{fontSize:12.5,marginTop:0,lineHeight:1.6}}>
                  <b>{bouncing.length} not delivering.</b> Mail to these bounced or was marked as
                  spam, so nothing is being sent to them until the address is fixed.
                </p>
              )}
              {manual.length > 0 && (
                <p style={{fontSize:12.5,marginTop:0,lineHeight:1.6}}>
                  <b>{manual.length} held on purpose.</b> Somebody decided not to write to these
                  yet — an imported investor roster, usually. Attempts are recorded and refused.
                </p>
              )}
              <div className="tablewrap" style={{marginTop:10}}>
                <table className="sov">
                  <tbody>
                    {held.slice(0, 8).map((h) => (
                      <tr key={h.address}>
                        <td>{h.who || <span className="muted">—</span>}</td>
                        <td className="mono" style={{fontSize:11}}>{h.address}</td>
                        <td><span className={"tag " + (h.reason === "manual" ? "n" : "p")}>
                          {h.reason === "manual" ? "held" : "not delivering"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {held.length > 8 && (
                <p className="muted" style={{fontSize:11.5,marginTop:8}}>
                  and {held.length - 8} more —{" "}
                  <button className="linkbtn" onClick={()=>setView("investors")}>
                    the Investors screen lists them all, and lets mail through</button>
                </p>
              )}
            </React.Fragment>
          )}
      </div>

      <p className="muted" style={{fontSize:11.5,marginTop:14,maxWidth:660}}>
        Every message this project sends is recorded — who it went to, what caused it, and whether it
        arrived. Nothing is sent to more than one person at a time.
      </p>
    </React.Fragment>
  );
}

function OrgTab({ db, me, say, refresh }) {
  const [f, setF] = useState({ name:db.org.name, address:db.org.address, billingEmail:db.org.billingEmail });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  return (
    <div className="card pad" style={{maxWidth:720}}>
      <SectionHead right={<span className="tag n">{db.org.plan}</span>}>Organisation</SectionHead>
      <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:620}}>
        Projects belong to the organisation, not to you personally. That's what lets you hand a project to someone else
        or run several at once without anything crossing over.
      </p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginTop:12}}>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Legal name</div>
          <input className="field" value={f.name} onChange={e=>set("name",e.target.value)}/></label>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Address</div>
          <input className="field" value={f.address} onChange={e=>set("address",e.target.value)}/></label>
        <label><div className="eyebrow">Billing contact</div>
          <input className="field" type="email" value={f.billingEmail} onChange={e=>set("billingEmail",e.target.value)}/></label>
        <label><div className="eyebrow">Web address</div>
          <input className="field mono" value={"/o/" + db.org.slug} disabled/>
          <div className="muted" style={{fontSize:11,marginTop:3}}>Path-based — no subdomain to set up.</div></label>
      </div>
      <div className="toolbar">
        <button className="btn go" disabled={busy} onClick={async()=>{
          setBusy(true); await api.updateOrg({ patch:f, actor:me.contact });
          await refresh(); setBusy(false); say("Organisation updated");
        }}>Save</button>
      </div>
    </div>
  );

}

/** Change your own password. Signing in is not the same as proving who you are. */
function PasswordCard({ say }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    if (next.length < 10) { setErr("The new password needs to be at least 10 characters."); return; }
    if (next !== again) { setErr("The two new passwords don't match."); return; }
    setBusy(true);
    try {
      await api.changePassword({ current, next });
      setCurrent(""); setNext(""); setAgain("");
      say("Password changed. Any other session you had is now signed out.");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="card pad" style={{maxWidth:720,marginTop:14}}>
      <SectionHead>Your password</SectionHead>
      <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:620}}>
        Changing it signs out every other session on your account and leaves this one alone. If you signed in
        with Google and never set a password, leave the first box empty.
      </p>
      <div style={{display:"grid",gap:10,maxWidth:340,marginTop:10}}>
        <label><div className="eyebrow">Current password</div>
          <input className="field" type="password" autoComplete="current-password"
                 value={current} onChange={e=>setCurrent(e.target.value)}/></label>
        <label><div className="eyebrow">New password</div>
          <input className="field" type="password" autoComplete="new-password"
                 value={next} onChange={e=>setNext(e.target.value)} placeholder="at least 10 characters"/></label>
        <label><div className="eyebrow">New password again</div>
          <input className="field" type="password" autoComplete="new-password"
                 value={again} onChange={e=>setAgain(e.target.value)}/></label>
      </div>
      {next && next.length < 10 && <p className="muted" style={{fontSize:12,marginTop:8}}>Ten characters or more.</p>}
      {again && next !== again && <p style={{fontSize:12,color:"var(--oxide)",marginTop:8}}>Those don't match.</p>}
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:8}}>{err}</p>}
      <button className="btn go" style={{marginTop:12}} disabled={busy} onClick={save}>
        {busy ? "…" : "Change password"}</button>
    </div>
  );
}

/** "Jameson Test Investor" -> "JI". Falls back to the address when there is no name. */
function initialsOf(name, email) {
  // A parenthesised aside is a note about the account, not part of anybody's
  // name: "Jameson Parker (investor)" was taking its last initial from the
  // bracket and rendering "J(".
  const cleaned = String(name || "").replace(/\([^)]*\)/g, " ");
  // Only parts that begin with a letter can supply an initial. Punctuation and
  // titles like "K." are common in company records and read as noise here.
  const parts = cleaned.trim().split(/\s+/).filter((w) => /^\p{L}/u.test(w));
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return String(email || "?").replace(/[^\p{L}]/gu, "").slice(0, 2).toUpperCase() || "?";
}

/**
 * The account menu.
 *
 * A person's full name spelled out in the top bar cost more room than a phone
 * has: on a narrow screen the name wrapped onto three lines and pushed itself
 * off the edge. Two initials say the same thing in a fixed width, and the menu
 * is where the things you do to your own account belong — including changing
 * your password, which until now lived inside owner-only settings and was
 * therefore unreachable by everybody else.
 */
function UserMenu({ db, me, role, open, setOpen, setView, signOut }) {
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) { document.addEventListener("mousedown", h); document.addEventListener("keydown", esc); }
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", esc); };
  }, [open, setOpen]);

  /*
   * The person, not the company.
   *
   * `me` here is the PARTY, so me.name is "Calico Development, LLC" and the
   * avatar read CL rather than KM. Whoever is signed in is a person, and the
   * user record is where their name lives. The party's contact is the fallback
   * because it is also a person's name; the company name never is.
   */
  const name = db.me.name || (me && me.contact) || db.me.email;
  return (
    <div className="usermenu noprint" ref={ref}>
      <button className="avatar" onClick={()=>setOpen(o=>!o)} aria-expanded={open}
              aria-label={"Account menu for " + name} title={name}>
        {initialsOf(name, db.me.email)}
      </button>
      {open && (
        <div className="userpanel">
          <div className="userhead">
            <div style={{fontWeight:700,fontSize:13.5,letterSpacing:"-.01em"}}>{name}</div>
            <div className="muted mono" style={{fontSize:11,marginTop:2,wordBreak:"break-all"}}>{db.me.email}</div>
            <div style={{marginTop:8}}>
              <span className="tag n">{ROLES[role] ? ROLES[role].label : role}</span>
            </div>
          </div>
          <button className="usermenuitem" onClick={()=>{ setOpen(false); setView("account"); }}>
            Account &amp; password</button>
          {role === "owner" && (
            <button className="usermenuitem" onClick={()=>{ setOpen(false); setView("settings"); }}>
              Project settings</button>
          )}
          <button className="usermenuitem danger" onClick={()=>{ setOpen(false); signOut(); }}>
            Sign out</button>
        </div>
      )}
    </div>
  );
}

/** Your own account, for every role — not just the owner. */
function Account({ db, me, role, say, refresh }) {
  /*
   * The person, not the company.
   *
   * `me` here is the PARTY, so me.name is "Calico Development, LLC" and the
   * avatar read CL rather than KM. Whoever is signed in is a person, and the
   * user record is where their name lives. The party's contact is the fallback
   * because it is also a person's name; the company name never is.
   */
  const name = db.me.name || (me && me.contact) || db.me.email;
  // Their own company on this project, if they have one — an owner may not.
  const myParty = (db.parties || []).find((x) => x.id === db.me.partyId);
  return (
    <React.Fragment>
      <h1 className="pagetitle">Your account</h1>
      <p className="pagesub">Who you are on this project, and how you get in.</p>
      <div className="card pad" style={{marginTop:16,maxWidth:520}}>
        <SectionHead>You</SectionHead>
        <div style={{display:"flex",gap:14,alignItems:"center",marginTop:10}}>
          <span className="avatar lg">{initialsOf(name, db.me.email)}</span>
          <div>
            <div style={{fontWeight:700,fontSize:15,letterSpacing:"-.01em"}}>{name}</div>
            <div className="muted mono" style={{fontSize:11.5,marginTop:2}}>{db.me.email}</div>
            <div style={{marginTop:7}}>
              <span className="tag n">{ROLES[role] ? ROLES[role].label : role}</span>
              <span className="muted" style={{fontSize:11.5,marginLeft:8}}>
                on {(db.project || {}).name}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Where somebody comes looking for it. The prompt on the briefing asks
          once; this is the place it can be changed afterwards, or added by
          anybody who waved the prompt away. */}
      {myParty && <MyContact party={myParty} role={role} say={say} refresh={refresh}/>}

      <PasswordCard {...{say}}/>
    </React.Fragment>
  );
}

/** Your own contact details — the parts nobody else can supply. */
function MyContact({ party, role, say, refresh }) {
  const [phone, setPhone] = useState(party.phone || "");
  const [contact, setContact] = useState(party.contact || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const dirty = phone !== (party.phone || "") || contact !== (party.contact || "");

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.saveMyContact(party.id, { phone, contact });
      await refresh();
      say("Saved.");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="card pad" style={{marginTop:16,maxWidth:520}}>
      <SectionHead>How to reach you</SectionHead>
      <p className="muted" style={{fontSize:12.5,marginTop:0,lineHeight:1.55}}>
        Used only to reach you about this project, and never shared with anybody else on it.
      </p>
      <label style={{display:"block",marginTop:12}}>
        <div className="eyebrow">Phone</div>
        <input className="field" type="tel" autoComplete="tel" style={{maxWidth:220}}
               placeholder="(601) 555-0123" value={phone}
               onChange={(e)=>setPhone(e.target.value)}/>
      </label>
      {/* A contractor's record is a company, so the question is who to ask for
          there. An investor's record is often a holding company with a single
          person behind it, and this is the field that puts that person's name
          on the project. Asking a company question gets a company answer. */}
      <label style={{display:"block",marginTop:12}}>
        <div className="eyebrow">{role === "investor" ? "Your name" : "Who to ask for"}</div>
        <input className="field" style={{maxWidth:260}} value={contact}
               autoComplete={role === "investor" ? "name" : "off"}
               placeholder={role === "investor" ? "how you would like to be addressed"
                                               : "the person to speak to"}
               onChange={(e)=>setContact(e.target.value)}/>
        {role === "investor" && !String(party.contact || "").trim() && (
          <p className="muted" style={{fontSize:11.5,margin:"5px 0 0"}}>
            Your record is under {party.name}. Adding your name here is what puts a person
            rather than an entity against your commitment.
          </p>
        )}
      </label>
      <p className="muted" style={{fontSize:11.5,marginTop:12,marginBottom:0,lineHeight:1.5}}>
        Your email address is not editable here — it is the address you were invited at and the one
        your record is kept under. Ask the developer if it needs to change.
      </p>
      <div className="toolbar" style={{marginTop:12}}>
        <button className="btn go" disabled={busy || !dirty} onClick={save}>
          {busy ? "…" : dirty ? "Save" : "Saved"}</button>
      </div>
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
    </div>
  );
}

/**
 * What is actually stopping this project, in the order it has to be unstuck.
 *
 * Every gate here is enforced by the server; this only names the first one a
 * person is standing behind. It matters because the blockage is usually two or
 * three screens upstream of where you notice it: "no draws yet" is really "no
 * approved work", which is really "no contractor has accepted any scope" —
 * and an empty screen that says only "no draws yet" sends somebody hunting.
 */
/**
 * An empty screen that says what is actually in the way, and offers to go there.
 *
 * The alternative — "No draws yet" and nothing else — is true and useless: the
 * reason there are no draws is three screens away, and a new owner has no way
 * to know that.
 */
function Blocked({ db, setView, fallbackTitle, fallbackBody }) {
  const b = whatIsBlocking(db);
  if (!b) return <Empty title={fallbackTitle} body={fallbackBody}/>;
  return (
    <div className="card pad" style={{marginTop:12, borderLeft:"3px solid var(--brass)", maxWidth:640}}>
      <div className="eyebrow">First, though</div>
      <h3 style={{fontSize:15,margin:"6px 0 6px",letterSpacing:"-.01em"}}>{b.title}</h3>
      <p style={{fontSize:12.5,lineHeight:1.6,margin:0}}>{b.body}</p>
      <button className="btn go" style={{marginTop:12}} onClick={()=>setView(b.go)}>{b.cta}</button>
    </div>
  );
}

function whatIsBlocking(db) {
  const items = db.lineItems || [];
  const parties = (db.parties || []).filter(p => p.role === "contractor" && p.status === "active");
  const placeholders = (db.parties || []).filter(p => /@example\.com$/i.test(p.email || ""));
  const accepted = items.filter(i => i.assignment && i.assignment.status === "accepted");
  const approved = items.filter(i => ["approved", "released", "paid"].includes(i.stage));
  const p = db.project || {};

  if (!parties.length) return {
    key: "parties", go: "parties", cta: "Add a contractor",
    title: "No contractor on file yet",
    body: "Nothing can be offered, accepted or built until there is a company to hold the work.",
  };
  if (!accepted.length) return {
    key: "assignment", go: "ledger", cta: "Open the schedule of values",
    title: "No scope has been accepted yet",
    body: `Work cannot start, be submitted, verified or approved until a contractor accepts it. ` +
          `Offer a line to ${parties[0].name} on the schedule of values; they accept the value, ` +
          `the finish date and the penalty, and only then can that line move.`,
  };
  if (!approved.length) return {
    key: "approve", go: "ledger", cta: "Open the schedule of values",
    title: "No work is approved yet",
    body: "A payment application is built out of approved work. Work moves contractor → inspector " +
          "→ owner, and only what you have approved can go on a draw.",
  };
  if (placeholders.length) return {
    key: "addresses", go: "parties", cta: "Fix the addresses",
    title: `${placeholders.length} placeholder address${placeholders.length === 1 ? "" : "es"} on file`,
    body: "Nothing can reach an @example.com address. Put the real ones in before anything is sent.",
  };
  return null;
}

/** The two money rules that are zero because nobody has said otherwise. */
function unsetMoneyRules(db) {
  const p = db.project || {};
  const missing = [];
  if (!p.retainagePct) missing.push("retainage");
  if (!p.ldPerDay) missing.push("the delay penalty");
  return missing;
}

/* ============================================================================
   SAYING WHAT A BUTTON DOES

   Nobody using this has used it before. A label like "Release" or "Raise a
   change order" is obvious to whoever built it and to nobody else, and the cost
   of guessing wrong here is money moving or forty-six strangers getting an
   email.

   Two shapes, and one rule about which:

     <Explain>   a tappable "?" that opens a line of text underneath. Tappable,
                 not hover — the owner of this project reads it on a phone, and
                 a hover tooltip does not exist there.
     helper      a line of text that is simply always visible, for the actions
                 worth slowing somebody down on.

   The words themselves live in EXPLAIN below, in one place, so the same button
   is explained the same way wherever it appears and the explanations can be
   read as a set.
   ========================================================================== */

const EXPLAIN = {
  // Money
  raiseChangeOrder: "Write down a change to the contract — extra work, a new price, a moved date. " +
    "Nothing changes until the owner approves it.",
  approveChangeOrder: "Accept this change. The contract sum moves by this amount and the schedule " +
    "of values is rewritten to match.",
  rejectChangeOrder: "Turn this change down. Nothing moves, and the reason is recorded.",
  createDraw: "Build a payment application from approved work and send it to the bank.",
  fundDraw: "Record that the bank has paid, and how much. A short payment is reconciled against " +
    "what was asked for.",

  // The work — keyed to the state machine's own action names, so the button
  // and its explanation cannot drift apart.
  start:    "Mark this work as started. Needs a photo of the condition before you begin.",
  submit:   "Hand this to the inspector as finished. Needs a photo of the completed work.",
  verify:   "Confirm you have seen this work and it is done. Your name goes on it.",
  approve:  "The owner's sign-off. This is the gate the money passes through.",
  release:  "Release this for payment. Every required signature must already be on it.",
  markPaid: "Record that this line has actually been paid.",
  reject:   "Send this back to the contractor with a reason. It returns to their queue.",

  // People
  inviteMember: "Send someone a link to join this project. They choose their own password.",
  makePartner: "Give someone the run of the project alongside you. What they can do is ticked on " +
    "afterwards, one thing at a time.",
  setGrants: "Choose exactly what this person may do. Anything you do not tick stays with you.",

  // Mail
  allowMail: "Stop refusing to write to this address. Nothing is sent by allowing it.",
  holdMail: "Stop writing to this address. Attempts are recorded and refused until you allow it again.",

  // Capital
  publishPage: "Make the briefing readable by investors. Until you do, they see a holding message.",
  unpublishPage: "Take the briefing back. Investors see a holding message again.",
  publishUpdate: "Send this update to investors.",
};

/** A tappable "?" that opens a line of explanation beneath. */
function Explain({ children, what }) {
  const [open, setOpen] = useState(false);
  const text = what || EXPLAIN[children] || "";
  if (!text) return null;
  return (
    <span className="explainwrap">
      <button type="button" className={"explainq" + (open ? " on" : "")}
              aria-expanded={open} aria-label="What does this do?"
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>?</button>
      {open && <span className="explaintext" role="note">{text}</span>}
    </span>
  );
}

/** A button with its consequence written underneath, always visible. */
function Action({ action, what, children, className = "btn", ...rest }) {
  const text = what || EXPLAIN[action] || "";
  return (
    <span className="actionwrap">
      <button className={className} {...rest}>{children}</button>
      {text && <span className="actionhelp">{text}</span>}
    </span>
  );
}

function Bell({ alerts, bellOpen, setBellOpen, seen, setSeen, setView, setSelId }) {
  const unseen = alerts.filter(a => !seen.includes(a.id));
  const urgent = unseen.some(a => a.level === "urgent");
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setBellOpen(false); };
    if (bellOpen) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [bellOpen, setBellOpen]);

  return (
    <div className="bellwrap noprint" ref={ref}>
      <button className={"bell " + (urgent ? "urgent" : "")} onClick={()=>setBellOpen(o=>!o)}
              aria-label={unseen.length + " alerts"} aria-expanded={bellOpen}>
        {/* An actual bell. The circle that stood here read as a status dot, not
            as notifications — the one icon nobody should have to decode. */}
        <svg className="bellglyph" width="17" height="17" viewBox="0 0 24 24" aria-hidden="true"
             fill="none" stroke="currentColor" strokeWidth="1.9"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8.5a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5"/>
          <path d="M10.5 19a2 2 0 0 0 3 0"/>
        </svg>
        {unseen.length > 0 && <span className="belldot">{unseen.length > 9 ? "9+" : unseen.length}</span>}
      </button>
      {bellOpen && (
        <div className="bellpanel">
          <div className="bellhead">
            <span className="eyebrow">Needs you</span>
            {alerts.length > 0 && (
              <button className="btn ghost sm" onClick={()=>setSeen(alerts.map(a=>a.id))}>Mark all seen</button>
            )}
          </div>
          {alerts.length === 0
            ? <div className="bellempty">Nothing waiting on you. That's the goal.</div>
            : alerts.slice(0,14).map(a => (
              <button key={a.id} className={"bellitem " + a.level + (seen.includes(a.id) ? " seen" : "")}
                onClick={()=>{
                  setSeen(x => x.includes(a.id) ? x : x.concat(a.id));
                  if (a.selId) setSelId(a.selId);
                  setView(a.view); setBellOpen(false);
                }}>
                <span className="belltitle">{a.title}</span>
                {a.body && <span className="bellbody">{String(a.body).slice(0,90)}</span>}
              </button>
            ))}
          {alerts.length > 14 && <div className="bellempty">+{alerts.length - 14} more</div>}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   8. APP SHELL
   ========================================================================== */
/**
 * The left-hand navigation, grouped by the question somebody is asking.
 *
 * Twenty-three entries accumulated in the order they were built, which is not
 * an order anyone reads. The groups are deliberately about the work rather than
 * about the software: "what is happening on site", "what is owed", "who is
 * involved" — the way the person using it would say it.
 *
 * Order within a group is by how often it is opened, not alphabetically.
 */
const NAV_GROUPS = [
  { key: "work",    label: "The work" },
  { key: "money",   label: "Money" },
  { key: "people",  label: "People" },
  { key: "capital", label: "Capital" },
  { key: "record",  label: "The record" },
  { key: "admin",   label: "Setup" },
];

/**
 * What is still unfinished about setting this project up.
 *
 * Setup sits last, which is right for a project that is running and wrong for
 * one that is not yet. Rather than pick a permanent position, it rises to the
 * top while there is something outstanding and settles back down once there
 * is not — so it is prominent exactly as long as it deserves to be.
 *
 * Deliberately about the project's own state rather than a "have you visited
 * this screen" flag: the point is what is unfinished, not what has been read.
 */
function setupOutstanding(db, role) {
  if (role !== "owner") return [];
  const p = db.project || {};
  const out = [];
  if (!p.initialized) out.push("the project has not been set up");
  // Not "is it zero" — zero is a real contract. It is whether anybody has
  // confirmed the terms at all. Saving the setup screen once clears this,
  // whatever the numbers are, and the server refuses a draw until it does.
  if (!p.moneyTermsSetAt) out.push("the money terms are not confirmed");
  // Only things that live on the setup screen. Placeholder addresses belong to
  // the contractors screen and are already raised on the overview — counting
  // them here made the demo, which is full of example.com on purpose, shout
  // about setup that was long since done.
  return out;
}

const NAV = [
  // The work — what is happening on site
  { key:"dashboard",    label:"Overview",              group:"work" },
  { key:"ledger",       label:"Schedule of values",    group:"work" },
  { key:"timeline",     label:"Timeline",              group:"work" },
  { key:"daily",        label:"Daily log",             group:"work" },
  { key:"punch",        label:"Punch list",            group:"work" },
  { key:"supply",       label:"Suppliers & materials", group:"work" },

  // Money — what is owed, what moved, and what does not add up
  { key:"draws",        label:"Draw requests",         group:"money" },
  { key:"changeorders", label:"Change orders",         group:"money" },
  { key:"reconcile",    label:"Budget vs SOV",         group:"money" },
  { key:"exceptions",   label:"Exceptions",            group:"money" },

  // People — who is involved and what has been said
  { key:"parties",      label:"Contractors & subs",    group:"people" },
  { key:"messages",     label:"Messages",              group:"people" },
  { key:"inbox",        label:"Document inbox",        group:"people" },
  { key:"onboarding",   label:"Your agreement",        group:"people" },

  // Capital — the raise, kept deliberately apart from the construction money
  { key:"funding",      label:"Funding",               group:"capital" },
  { key:"investors",    label:"Investors",             group:"capital" },
  { key:"investorpage", label:"Investor page",         group:"capital" },
  { key:"updates",      label:"Investor updates",      group:"capital" },
  { key:"investorview", label:"Investor view",         group:"capital" },

  // The record — what happened, and getting it in or out
  { key:"audit",        label:"Audit trail",           group:"record" },
  { key:"data",         label:"Import / export",       group:"record" },

  // Setup
  { key:"setup",        label:"Project setup",         group:"admin" },
  { key:"settings",     label:"Settings",              group:"admin" },
  { key:"docs",         label:"Documentation",         group:"admin" },
];

function App() {
  const [db, setDb] = useState(null);
  const [session, setSession] = useState(undefined);   // undefined = still asking
  const [projectId, setProjectId] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Looking at the job through an investor's eyes. Not a change of role — the
  // server is asked for the investor payload and returns exactly what an
  // investor would receive, so what you read is what they get.
  const [lens, setLens] = useState(null);   // { role, party } or null
  const [userOpen, setUserOpen] = useState(false);
  // Which groups this person has folded away. Everything starts open — a
  // navigation that hides things on first sight is worse than a long one.
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("pp.nav.collapsed") || "[]"); }
    catch { return []; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("pp.nav.collapsed", JSON.stringify(collapsed)); } catch {}
  }, [collapsed]);
  // A notification email links to the thread it is about, not to the front
  // door. Consumed once: after the app has opened it, the path stays but the
  // state does not re-force the view, so Back behaves.
  const [deepThread, setDeepThread] = useState(() => {
    const m = window.location.pathname.match(/^\/t\/([\w-]+)$/);
    return m ? m[1] : null;
  });
  const [view, setView] = useState(deepThread ? "messages" : "dashboard");

  /*
   * A new screen starts at the top of itself.
   *
   * Nothing reset the scroll position, so signing in left you looking at
   * wherever the page happened to be, which on a phone was partway down because
   * the keyboard had scrolled the sign-in field into view. It read as the app
   * opening halfway through.
   *
   * Keyed on signing in and on picking a project as well as on the view, since
   * those are transitions where the view name does not change but the screen
   * underneath it completely does. Instant rather than smooth: this is a
   * correction, not a movement anybody asked for, and animating it draws the
   * eye to something that should have been right already.
   *
   * Declared here, above every conditional return in this component, because a
   * hook after one is a hook React sees on some renders and not others.
   */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [view, projectId, !!(session && session.user)]);
  const [selId, setSelId] = useState(null);
  const [toast, setToast] = useState(null);
  const [freshStamp, setFreshStamp] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [seen, setSeen] = useState([]);

  // Ask the server who we are before anything else. Every screen below this
  // point is a consequence of the answer.
  useEffect(() => {
    api.me().then(setSession).catch(() => setSession({ user: null }));
  }, []);

  // An invitation link lands here: accept it, then carry on into the project.
  useEffect(() => {
    const m = window.location.pathname.match(/^\/invite\/(.+)$/);
    if (!m || !session || !session.user) return;
    api.acceptInvite({ token: m[1] })
      .then((r) => { window.history.replaceState({}, "", "/"); return api.me().then(setSession).then(() => setProjectId(r.projectId)); })
      .catch((e) => say(e.message));
  }, [session]);

  const projectsOf = (sess) => (sess && sess.orgs ? sess.orgs.flatMap(o => o.projects.map(pr => ({ ...pr, orgSlug: o.slug }))) : []);

  // One project is the common case; don't make Kenneth pick from a list of one.
  useEffect(() => {
    if (!session || !session.user || projectId) return;
    const all = projectsOf(session);
    if (all.length === 1) setProjectId(all[0].id);
  }, [session, projectId]);

  useEffect(() => {
    if (!projectId || !session || !session.user) return;
    const pr = projectsOf(session).find(x => x.id === projectId);
    if (!pr) return;
    setTenant(pr.orgSlug, projectId);
    setDb(null); setLoadError(null);
    api.getProject(projectId, lens || undefined)
      .then((d) => {
        setToday(d.project && d.project.today);
        setReadOnly(d.lensReadOnly
          ? `You're looking at this as ${d.previewingAs}. It's read-only — switch back to your own view to make changes.`
          : null);
        setDb(d);
      })
      .catch((e) => setLoadError(e.message));
  }, [projectId, session, lens]);
  const say = useCallback((m) => { setToast(m); setTimeout(() => setToast(t => t === m ? null : t), 3200); }, []);

  // Identity comes from the server, not from a picker. There is no switcher in
  // a real deployment: you are whoever the session says you are.
  const meId = db && db.me ? db.me.partyId : null;

  const me = db ? (partyById(db, meId) || db.parties[0] || { contact: db.me.name, name: db.me.name, role: db.me.role }) : null;
  const role = db && db.me ? db.me.role : (me ? me.role : "owner");
  // A scoped party (a sub) only sees the scope it was actually given.
  const items = db ? ((ROLES[role].capitalOnly || db.previewingAs === "investor") ? db.lineItems
    : ROLES[role].scoped ? db.lineItems.filter(i => i.assignment && i.assignment.partyId === meId)
    : db.lineItems) : [];
  const allItems = db ? db.lineItems : [];
  const project = db ? db.project : null;

  const refresh = () => api.getProject(PROJECT_ID, lens || undefined)
    .then((d) => { setToday(d.project && d.project.today); setDb(d); });

  /*
   * A screen left open on a desk should still be true.
   *
   * This is a poll, not a push, and it is a poll of two integers rather than of
   * the project — the bundle is the whole job, and refetching it every few
   * seconds to discover that nothing happened would be indefensible. The
   * bundle is refetched only when one of the counts moves.
   *
   * Nothing runs while the tab is hidden: a laptop closed on a job site should
   * not be talking to the server all night. Coming back to the tab checks
   * immediately, so the first thing you see is current rather than however
   * stale it was when you left.
   */
  const pulseRef = useRef(null);
  const lastSeen = useRef(Date.now());
  useEffect(() => {
    if (!PROJECT_ID || !db) return;
    let stopped = false;
    const beat = async () => {
      // Three reasons not to ask. The tab is gone; nobody is there; we already
      // stopped. The middle one is the one that matters for the bill: the
      // database sleeps after five idle minutes, and a screen left up on a desk
      // overnight would hold it awake until morning for nobody's benefit.
      if (stopped || document.hidden) return;
      if (Date.now() - lastSeen.current > IDLE_AFTER_MS) return;
      try {
        const p = await api.pulse();
        const key = String(p.audit) + "/" + String(p.messages);
        if (pulseRef.current === null) { pulseRef.current = key; return; }
        if (pulseRef.current !== key) { pulseRef.current = key; await refresh(); }
      } catch (e) { /* a dropped beat is not worth a message; the next one tries again */ }
    };
    // Take the baseline now, against the bundle we are already holding, rather
    // than on the first beat twelve seconds from now — anything that landed in
    // that window would have been read as "how it has always been" and never
    // shown.
    beat();
    const id = window.setInterval(beat, PULSE_MS);
    const onShow = () => { lastSeen.current = Date.now(); if (!document.hidden) beat(); };
    // Coming back to it — a keystroke, the mouse, the tab — checks straight
    // away, so what you walk back to is current rather than fifteen minutes
    // stale and quietly catching up.
    const mark = () => {
      const wasAway = Date.now() - lastSeen.current > IDLE_AFTER_MS;
      lastSeen.current = Date.now();
      if (wasAway) beat();
    };
    const acts = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"];
    acts.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("focus", onShow);
    return () => { stopped = true; window.clearInterval(id);
      acts.forEach((e) => window.removeEventListener(e, mark));
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("focus", onShow); };
  }, [PROJECT_ID, db === null, lens]);

  const signOut = async () => {
    await api.logout().catch(() => {});
    setSession({ user: null }); setDb(null); setProjectId(null); setShowSignIn(false); setView("dashboard");
  };

  const totals = useMemo(() => {
    if (!db) return null;
    const t = { sum:0, disbursed:0, unapproved:0, approvedValue:0, releasedValue:0, awaiting:0,
                awaitingValue:0, retainage:0, ld:0, earned:0, lateItems:0, complete:0 };
    items.forEach(it => {
      const earned = it.scheduledValue * it.percent / 100;
      t.sum += it.scheduledValue;
      t.earned += earned;
      t.disbursed += it.bankDisbursed;
      if (!signoffs(it).owner && liveDisbursed(it) > 0) t.unapproved += liveDisbursed(it);
      if (["approved","released","paid"].includes(it.stage)) t.approvedValue += it.scheduledValue;
      if (["released","paid"].includes(it.stage)) t.releasedValue += it.scheduledValue;
      if (["submitted","verified"].includes(it.stage)) { t.awaiting++; t.awaitingValue += earned; }
      if (it.stage === "paid") t.complete++;
      t.retainage += (["approved","released","paid"].includes(it.stage) ? it.scheduledValue : earned) * project.retainagePct / 100;
      t.ld += ldAccrued(it, project);
      if (daysLate(it) > 0) t.lateItems++;
    });
    t.pctComplete = t.sum ? (t.earned / t.sum) * 100 : 0;
    t.overdue = daysBetween(project.contractCompletion, iso(TODAY));
    return t;
  }, [db, items, project]);

  const exceptions = useMemo(() => allItems.filter(it => liveDisbursed(it) > 0 && !signoffs(it).owner), [allItems]);
  const drawQueue = db ? (db.draws || []).filter(d =>
    (role === "lender" && d.status === "submitted") ||
    (role !== "investor" && d.status === "funded" && missingDocs(db, d).length > 0)) : [];
  const myQueue = useMemo(() => items.filter(it =>
    availableActions(it, role, meId).some(a => a !== "reject") || assignState(it) === "offered"
  ), [items, role, meId]);

  const doAction = async (it, action, extra) => {
    await api.transition({ id: it.id, action, role, actor: me.contact, partyId: meId, ...extra });
    setFreshStamp(role);
    setTimeout(() => setFreshStamp(null), 800);
    await refresh();
    say(ACTIONS[action].verb + " — " + it.no + " " + it.name);
  };

  // A link out of an email lands here before anything else is decided.
  const resetToken = new URLSearchParams(window.location.search).get("token");
  const onResetPage = window.location.pathname.startsWith("/reset") && !!resetToken;
  if (onResetPage) return (
    <React.Fragment>
      <ResetPassword token={resetToken} say={say}
        onDone={()=>{ window.history.replaceState({}, "", "/"); setShowSignIn(true); setSession({ user: null }); }}/>
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );

  if (session === undefined) return <div style={{padding:40}} className="eyebrow">Loading…</div>;

  if (!session.user) return (
    <React.Fragment>
      {showSignIn || window.location.pathname.startsWith("/invite/")
        || new URLSearchParams(window.location.search).has("auth_error")
        ? <SignIn say={say} googleEnabled={session.googleEnabled}
                  notice={new URLSearchParams(window.location.search).get("auth_error")}
                  onBack={()=>setShowSignIn(false)}
                  onSignedIn={(sess)=>{ setSession(sess); setShowSignIn(false); }}/>
        : <Landing onSignIn={()=>setShowSignIn(true)}/>}
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );

  // Somebody who was invited is not somebody starting a project. They look
  // identical once an identity provider has dropped the invitation link on the
  // way through, so the difference has to be asked for explicitly.
  if (!projectsOf(session).length && (session.pendingInvites || []).length) return (
    <React.Fragment>
      <InvitationWaiting session={session} say={say} onSignOut={signOut}
        onAccepted={(id)=>{ api.me().then(setSession).then(()=>setProjectId(id)); }}/>
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );

  if (!projectsOf(session).length) return (
    <React.Fragment>
      <FirstRun session={session} say={say} onSignOut={signOut}
                onCreated={(slug, id)=>{ api.me().then(setSession).then(()=>setProjectId(id)); }}/>
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );

  if (loadError) return (
    <div className="authwrap"><div className="authcard">
      <h1 style={{fontSize:17}}>That project could not be opened</h1>
      <p className="muted" style={{fontSize:12.5}}>{loadError}</p>
      <button className="btn" style={{marginTop:12}} onClick={()=>{ setProjectId(null); setLoadError(null); }}>Back</button>
    </div></div>
  );

  if (!projectId) return (
    <React.Fragment>
      <PickProject session={session} onPick={setProjectId} onSignOut={signOut}/>
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );

  if (!db) return <div style={{padding:40}} className="eyebrow">Loading…</div>;

  // A project that was never set up drops the owner straight into setup.
  //
  // This used to offer everyone a button into a named project belonging to the
  // first customer — prototype scaffolding that read, to anyone else, as a
  // stranger's job on their own screen. An owner belongs in setup; anybody else
  // belongs told plainly that there is nothing here yet.
  const chosen = db.projects.find(x => x.id === projectId) || db.projects[0];
  if (chosen && !chosen.initialized) return (
    <React.Fragment>
      <div className="authwrap" style={{alignItems:"flex-start",paddingTop:"8vh"}}>
        <div className="authcard" style={{maxWidth:560}}>
          <div className="eyebrow">{db.org.name}</div>
          <h1 style={{fontSize:19,letterSpacing:"-.025em",margin:"5px 0 6px"}}>{chosen.name}</h1>
          {role === "owner" ? (
            <React.Fragment>
              <p className="muted" style={{fontSize:12.5,marginTop:0}}>
                This project hasn't been set up yet. Setup walks through the contract, the schedule of
                values, the people and the money rules.
              </p>
              <button className="btn go" style={{marginTop:12,width:"100%"}}
                      onClick={()=>setView("setup")}>Set this project up</button>
            </React.Fragment>
          ) : (
            <p className="muted" style={{fontSize:12.5,marginTop:0}}>
              This project hasn't been set up yet. The owner will let you know when there's something
              to look at.
            </p>
          )}
          {projectsOf(session).length > 1 && (
            <button className="btn ghost" style={{marginTop:8,width:"100%"}} onClick={()=>setProjectId(null)}>
              Back to projects</button>
          )}
          <button className="linkbtn" style={{marginTop:12}} onClick={signOut}>Sign out</button>
        </div>
      </div>
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );

  // Two hard redirects: an owner on an unstarted project goes to setup, and a
  // contractor who has not accepted the terms cannot see anything else.
  const needsSetup = !db.project.initialized && role === "owner";
  const needsOnboarding = role === "contractor" && !isOnboarded(me);
  const needsInvite = role !== "contractor" && role !== "owner" && !isOnboarded(me) && inviteFor(db, meId);
  const asInvestor = ROLES[role].capitalOnly || db.previewingAs === "investor";
  // Your own account is reachable from wherever you are pinned. An investor is
  // held to one screen and an owner to setup, which is right for the project's
  // screens — but neither is a reason to be unable to change your own password.
  // The two consent gates still hold: an invitation and the contractor terms
  // are things you pass through, not things you browse around.
  // Your own account, and the manual, are reachable from wherever you are
  // pinned. An investor is held to one screen and an owner to setup, which is
  // right for the project's screens. Neither is a reason to be unable to read
  // how the thing works, or to change your own password. The two consent gates
  // still hold: an invitation and the contractor terms are passed through, not
  // browsed around.
  const ownAccount = (view === "account" || view === "docs") && !needsInvite && !needsOnboarding;
  const forced = ownAccount ? null
    : needsInvite ? "invite"
    : asInvestor ? "investorview"
    : needsOnboarding ? "onboarding"
    : needsSetup && view !== "data" && view !== "parties" && view !== "ledger" ? "setup" : null;
  const activeView = forced || view;


  const canViewAsInvestor = role === "owner";
  const alerts = alertsFor(db, role, meId);
  const sel = items.find(i => i.id === selId);
  const punchAlert = items.some(it => openPunch(it).some(px => punchOverdue(px) || px.severity === "blocking"));
  const navCounts = {
    ledger: items.length,
    exceptions: exceptions.length,
    punch: allItems.reduce((n,it)=>n+openPunch(it).length,0),
    daily: (db.dailyLogs||[]).length,
    drawGaps: drawsNeedingDocs(db).length,
    funding: capitalTotals(db).open,
    inbox: inboxPending(db).length + inboxOf(db).filter(x=>x.status==="quarantined").length,
    supply: procureAtRisk(db).length,
    messages: threadsFor(db, meId, role).reduce((n,t)=>n+unreadIn(t,meId),0),
    parties: db.parties.filter(x => x.status === "active" && x.role === "contractor").length,
    investors: db.parties.filter(x => x.role === "investor").length,
    draws: db.draws.length,
  };

  /**
   * What this person may navigate to — decided ONCE, for every navigation.
   *
   * The sidebar filtered carefully and the bottom bar on a phone did not: it
   * carried a hardcoded five, so an investor on a phone was offered Overview,
   * Timeline, Schedule of values, Daily log and Punch. The rule was right in
   * one place and absent in the other, which is what happens whenever the same
   * decision is written twice.
   */
  // A partner's screens follow what they were given, not their role — that is
  // the whole point of them. `may` is only about what to offer; the server
  // refuses regardless.
  const grants = (db.me && db.me.grants) || [];
  const may = (cap) => role === "owner" || grants.includes(cap);

  const navItems = (needsOnboarding || needsInvite || asInvestor) ? [] : NAV.filter(n =>
    (n.key !== "setup" || role === "owner") &&
    (n.key !== "settings" || may("changeSettings")) &&
    (n.key !== "funding" || may("manageCapital") || may("viewCapital")) &&
    (n.key !== "supply" || ["owner","contractor","inspector"].includes(role) || may("manageSuppliers")) &&
    (n.key !== "updates" || may("publishUpdate") || may("manageCapital")) &&
    (n.key !== "messages" || role !== "investor") &&
    (n.key !== "inbox" || ["owner","lender"].includes(role)) &&
    (n.key !== "investorview" || may("viewCapital")) &&
    (n.key !== "investors" || may("viewCapital") || may("manageCapital")) &&
    (n.key !== "investorpage" || may("manageCapital")) &&
    (n.key !== "onboarding" || role === "contractor"));

  // The phone bar shows the day-to-day screens, but only ones they actually
  // have. An investor has none of them, so it does not appear at all.
  const mobNav = navItems.filter(n => ["dashboard","timeline","ledger","daily","punch"].includes(n.key));

  // Setup comes first while it is unfinished, and goes back to last when it is
  // not. Nothing else moves.
  const setupTodo = setupOutstanding(db, role);
  const orderedGroups = setupTodo.length
    ? [NAV_GROUPS.find(g => g.key === "admin"), ...NAV_GROUPS.filter(g => g.key !== "admin")]
    : NAV_GROUPS;

  return (
    <React.Fragment>
      <header className="topbar">
        <button className="brandbtn noprint" onClick={()=>{ setProjectId(null); setSelId(null); }}
                title="Switch project">
          <span className="brandmark">{PRODUCT.name.toUpperCase()}</span>
          <span className="brandsub">{(db.projects.find(x=>x.id===projectId)||{}).name || ""}</span>
        </button>
        <div style={{flex:1}}/>
        <Bell {...{alerts, bellOpen, setBellOpen, seen, setSeen, setView, setSelId}}/>
        {false && drawQueue.length > 0 && (
          <button className="btn warn sm noprint" onClick={() => setView("draws")} title="Draw requests needing you">
            {drawQueue.length} draw{drawQueue.length===1?"":"s"} needing you
          </button>
        )}

        <UserMenu {...{db, me, role, open: userOpen, setOpen: setUserOpen, setView, signOut}}/>
      </header>

      {/*
        * Role and the view-as control get their own line.
        *
        * They were sharing the top bar with the brand, the project name, the
        * bell and a full personal name, which on a phone ran out of room and
        * wrapped off the right-hand edge. A second, slim row costs one line and
        * always fits.
        */}
      <div className="contextbar noprint">
        <span className={"rolechip" + (lens ? " lensed" : "")}>
          {lens ? `Viewing as ${lens.role}` : (ROLES[role] ? ROLES[role].label : role)}
        </span>
        {lens && (
          <button className="btn ghost sm" onClick={()=>{ setLens(null); setView("dashboard"); }}>
            Back to your own view</button>
        )}
        <div style={{flex:1}}/>
        {canViewAsInvestor && (
          <div className="rolepill" style={{borderColor: lens ? "var(--brass)" : undefined}}>
            <span className="eyebrow" style={{color:"#8FA09B"}}>View as</span>
            <select value={lens ? (lens.role + (lens.party ? "|" + lens.party : "")) : ""}
                    aria-label="Look at the project as another role sees it"
                    onChange={e=>{
                      const v = e.target.value;
                      setLens(v ? { role: v.split("|")[0], party: v.split("|")[1] || null } : null);
                      setSelId(null); setView("dashboard");
                    }}>
              <option value="">Yourself · owner</option>
              <optgroup label="What they can see">
                {db.parties.filter(p=>p.role==="contractor" && p.status==="active").map(p =>
                  <option key={p.id} value={"contractor|"+p.id}>Contractor · {p.name}</option>)}
                <option value="inspector">Inspector</option>
                <option value="lender">Lender</option>
                <option value="investor">Investor</option>
              </optgroup>
            </select>
          </div>
        )}
      </div>

      <div className="shell">
        <nav className="sidenav noprint">
          <div className="eyebrow" style={{padding:"4px 10px 8px", color:"#7C8C88"}}>Project</div>
          {orderedGroups.map(g => {
            const items = navItems.filter(n => n.group === g.key);
            if (!items.length) return null;   // a heading over nothing is noise
            const shut = collapsed.includes(g.key);
            // A group holding the screen you are on never hides it.
            const holdsCurrent = items.some(n => n.key === activeView);
            const alerting = items.reduce((n2, n) => n2 + (navCounts[n.key] || 0), 0);
            return (
              <div className="navgroup" key={g.key}>
                <button className={"navhead" + (shut && !holdsCurrent ? " shut" : "")
                          + (g.key === "admin" && setupTodo.length ? " needs" : "")}
                        onClick={() => setCollapsed(c => c.includes(g.key)
                          ? c.filter(x => x !== g.key) : [...c, g.key])}
                        aria-expanded={!shut || holdsCurrent}
                        title={g.key === "admin" && setupTodo.length
                          ? "Still to do: " + setupTodo.join("; ") : ""}>
                  <span>{g.label}</span>
                  {g.key === "admin" && setupTodo.length > 0 && (
                    <span className="navcount alert">{setupTodo.length}</span>
                  )}
                  {shut && !holdsCurrent && alerting > 0 && <span className="navcount">{alerting}</span>}
                  <span className="navchev">{shut && !holdsCurrent ? "›" : "⌄"}</span>
                </button>
                {(!shut || holdsCurrent) && items.map(n => (
                  <button key={n.key} className={"navitem " + (activeView===n.key?"on":"")}
                          onClick={() => { setView(n.key); setSelId(null); }}>
                    <span>{n.label}</span>
                    {navCounts[n.key] != null && navCounts[n.key] > 0 &&
                      <span className={"navcount " + (n.key==="exceptions" || (n.key==="punch" && punchAlert)
                        || (n.key==="draws" && navCounts.drawGaps > 0)
                        || (n.key==="inbox" && inboxOf(db).some(x=>x.status==="quarantined")) ?"alert":"")}>{navCounts[n.key]}</span>}
                  </button>
                ))}
              </div>
            );
          })}
          <div style={{marginTop:20, padding:"0 10px"}}>
            <div className="eyebrow" style={{color:"#7C8C88", marginBottom:6}}>Site</div>
            <div style={{fontSize:11.5, color:"#8FA09B", lineHeight:1.5}}>{project.address}</div>
            <div style={{fontSize:11.5, color:"#8FA09B", marginTop:8}}>
              Contract completion<br/>
              <span className="mono" style={{color: totals.overdue>0 ? "#E08C7C" : "#8FA09B"}}>
                {fmtDate(project.contractCompletion)}{totals.overdue>0 ? ` · ${totals.overdue}d past` : ""}
              </span>
            </div>
            <div className="mono" style={{fontSize:10, color:"#5E6E6A", marginTop:22, lineHeight:1.5}}>
              {PRODUCT.name}<br/>{COPYRIGHT}
            </div>
          </div>
        </nav>

        <main className="main">
          {db.lensReadOnly && (
            <div className="banner brass noprint" style={{marginBottom:14}}>
              <h4>Viewing as {db.previewingAs}
                {db.previewingParty && db.parties.find(p=>p.id===db.previewingParty)
                  ? " · " + db.parties.find(p=>p.id===db.previewingParty).name : ""}</h4>
              <p>This is what they can see — assembled by the same code that serves them, not by
                 hiding things here. Read-only: nothing you click will change the project.{" "}
                <button className="btn ghost sm" style={{marginLeft:6}}
                        onClick={()=>{ setLens(null); setView("dashboard"); }}>Back to your own view</button></p>
            </div>
          )}
          {activeView==="dashboard"  && <Dashboard {...{db,totals,exceptions,role,me,meId,items,setView,setSelId,myQueue}}/>}
          {activeView==="ledger"     && <Ledger {...{db,role,me,meId,items,setSelId,selId,totals,say,refresh}}/>}
          {activeView==="exceptions" && <Exceptions {...{db,exceptions,project,setSelId,totals}}/>}
          {activeView==="timeline"   && <Timeline {...{db,role,meId,items,setSelId}}/>}
          {activeView==="daily"      && <DailyLog {...{db,role,me,meId,items,say,refresh,setSelId}}/>}
          {activeView==="setup"      && <Setup {...{db,role,me,say,refresh,setView,setSelId}}/>}
          {activeView==="invite"     && <InviteAccept {...{db,me,meId,role,say,refresh,setView}}/>}
          {activeView==="settings"   && <Settings {...{db,role,me,meId,say,refresh,setView}}/>}
          {activeView==="docs"       && <Docs/>}
          {activeView==="onboarding" && <Onboarding {...{db,me,meId,role,say,refresh,setView}}/>}
          {activeView==="messages"     && <Messages {...{db,role,me,meId,say,refresh,setSelId,
                                                        deepThread,setDeepThread}}/>}
          {activeView==="supply"       && <Supply {...{db,role,me,meId,say,refresh,setSelId}}/>}
          {activeView==="updates"      && <Updates {...{db,role,me,say,refresh}}/>}
          {activeView==="investorpage" && <InvestorPageEditor {...{db,say,refresh}}/>}
          {activeView==="inbox"        && <Inbox {...{db,role,me,meId,say,refresh,setView}}/>}
          {activeView==="funding"      && <Funding {...{db,role,me,meId,say,refresh,setView,setSelId}}/>}
          {activeView==="investorview" && <InvestorView {...{db,role,me,meId,say,refresh,setView}}/>}
          {activeView==="punch"      && <PunchWalk {...{db,role,me,meId,items,setSelId}}/>}
          {activeView==="parties"    && <Parties {...{db,role,me,meId,say,refresh,setView,setSelId}}/>}
          {activeView==="draws"      && <Draws {...{db,role,me,meId,say,refresh,setSelId,setView}}/>}
          {activeView==="changeorders" && <ChangeOrders {...{db,role,me,meId,items,say,refresh,setView,setSelId}}/>}
          {activeView==="reconcile"    && <Reconcile {...{db,role,say}}/>}
          {activeView==="investors"  && <Investors {...{db, role, say, refresh, setView}}/>}
          {activeView==="account"    && <Account {...{db, me, role, say, refresh}}/>}
          {activeView==="audit"      && <Audit {...{db}}/>}
          {activeView==="data"       && <DataIO {...{db,role,me,say,refresh}}/>}
        </main>
      </div>

      {mobNav.length > 0 && (
        <nav className="mobnav noprint">
          {mobNav.map(n => (
            <button key={n.key} className={activeView===n.key?"on":""}
                    onClick={()=>{ setView(n.key); setSelId(null); }}>{n.label.split(" ")[0]}</button>
          ))}
        </nav>
      )}

      {sel && <Detail item={sel} db={db} project={project} role={role} me={me} meId={meId} onClose={()=>setSelId(null)}
                      doAction={doAction} refresh={refresh} say={say} freshStamp={freshStamp}/>}
      {toast && <div className="toast" role="status">{toast}</div>}
    </React.Fragment>
  );
}

/* Everything that needs this party's attention, computed from current state.
   Derived rather than stored, so it cannot drift out of sync with the thing it
   is pointing at — a notification that outlives its cause is worse than none. */
function alertsFor(db, role, meId) {
  const p = db.project, out = [];
  const push = (o) => out.push(o);
  const items = ROLES[role].scoped && role === "contractor"
    ? db.lineItems.filter(i => i.assignment && i.assignment.partyId === meId)
    : db.lineItems;

  // waiting on your signature
  items.forEach(it => {
    if (availableActions(it, role, meId).some(a => a !== "reject"))
      push({ id:"act_"+it.id, level:"do", view:"ledger", selId:it.id,
             title: (role==="owner"?"Approve":role==="inspector"?"Verify":role==="lender"?"Release":"Work on") + " " + it.no,
             body: it.name, at: it.actualFinish || it.plannedFinish });
    if (assignState(it) === "offered" && it.assignment.partyId === meId)
      push({ id:"off_"+it.id, level:"do", view:"ledger", selId:it.id,
             title:"Scope offered — " + it.no, body: usd(it.scheduledValue) + " · finish " + fmtDate(it.plannedFinish),
             at: it.assignment.offeredAt });
  });

  // punch
  items.forEach(it => openPunch(it).forEach(px => {
    const overdue = punchOverdue(px);
    const mineToFix = role === "contractor" && it.assignment && it.assignment.partyId === meId;
    const mineToWalk = ["owner","inspector"].includes(role) && px.status === "fixed";
    if (overdue || mineToWalk || (mineToFix && px.severity === "blocking"))
      push({ id:"px_"+px.id, level: overdue ? "urgent" : "do", view:"punch", selId:it.id,
             title: mineToWalk ? "Fix submitted — " + px.no : overdue ? px.no + " is " + daysBetween(px.dueDate, iso(TODAY)) + " days overdue" : "Blocking — " + px.no,
             body: px.title, at: px.resolvedAt || px.raisedAt });
  }));

  // draws
  (db.draws||[]).forEach(d => {
    if (role === "lender" && d.status === "submitted")
      push({ id:"dr_"+d.id, level:"do", view:"draws", title:drawLabel(d) + " is waiting on you",
             body: usd(drawTotals(db,d).net) + " requested", at:d.createdAt });
    if (role !== "investor" && d.status === "funded" && missingDocs(db,d).length)
      push({ id:"drg_"+d.id, level:"warn", view:"draws", title:drawLabel(d) + " — paperwork missing",
             body: missingDocs(db,d).join(", "), at:d.fundedAt });
  });

  // emailed documents
  if (["owner","lender"].includes(role)) {
    inboxOf(db).filter(x => x.status === "quarantined").forEach(x =>
      push({ id:"qz_"+x.id, level:"urgent", view:"inbox", title:"Quarantined email held back",
             body: x.subject + " — from " + x.from, at:x.receivedAt }));
    const pend = inboxPending(db).length;
    if (pend) push({ id:"inbox", level:"do", view:"inbox", title:pend + " emailed document" + (pend===1?"":"s") + " to review",
                     body:"Confirm which draw each belongs to", at:(inboxPending(db)[0]||{}).receivedAt });
  }

  // messages
  threadsFor(db, meId, role).forEach(t => {
    const n = unreadIn(t, meId);
    if (n) push({ id:"th_"+t.id, level:"do", view:"messages", title:"New message — " + t.subject,
                  body:(lastMessage(t)||{}).body, at:(lastMessage(t)||{}).at });
  });

  // owner-only: schedule, procurement, capital
  if (role === "owner") {
    procureAtRisk(db).forEach(x => {
      const r = procureRisk(db, x);
      push({ id:"pr_"+x.id, level: r.level === "late" ? "urgent" : "warn", view:"supply",
             title: r.level === "late" ? "Order date passed — " + x.description : "Order soon — " + x.description,
             body: supplierName(db, x.supplierId) + " · needed " + fmtDate(x.neededOnSite), at: orderByDate(db, x) });
    });
    const unapproved = db.lineItems.filter(i => liveDisbursed(i) > 0 && !signoffs(i).owner);
    if (unapproved.length)
      push({ id:"exc", level:"urgent", view:"exceptions", title:usd(unapproved.reduce((n,i)=>n+i.bankDisbursed,0)) + " disbursed without your sign-off",
             body: unapproved.length + " line items", at: iso(TODAY) });
    db.parties.filter(x => x.role === "contractor" && x.status === "active")
      .filter(x => complianceGaps(x).length).forEach(x =>
        push({ id:"cmp_"+x.id, level:"warn", view:"parties", title:x.name + " — paperwork",
               body: complianceGaps(x).join(", "), at: x.addedAt }));
    const t = capitalTotals(db);
    needsOf(db).filter(n => n.urgent && n.status !== "closed" && needProgress(db,n).received < n.amount)
      .forEach(n => push({ id:"fn_"+n.id, level:"warn", view:"funding",
        title:"Time-sensitive — " + n.name,
        body: usd(needProgress(db,n).remaining) + " still needed" + (n.deadline ? " by " + fmtDate(n.deadline) : ""),
        at: n.deadline }));
  }

  const rank = { urgent:0, do:1, warn:2 };
  return out.sort((a,b) => (rank[a.level] - rank[b.level]) || ((a.at||"") < (b.at||"") ? 1 : -1));
}

function availableActions(it, role, meId) {
  return Object.keys(ACTIONS).filter(k => {
    const a = ACTIONS[k];
    const roles = Array.isArray(a.role) ? a.role : [a.role];
    if (!roles.includes(role) || !a.from.includes(it.stage)) return false;
    // Contractor actions belong to the party that accepted the scope. Nobody
    // else can start, submit, or claim work on someone else's line item.
    if (role === "contractor") {
      if (!it.assignment || it.assignment.status !== "accepted") return false;
      if (meId && it.assignment.partyId !== meId) return false;
    }
    return true;
  });
}

/* ---------------------------------------------------------------- Overview */
function Dashboard({ db, totals, exceptions, role, me, meId, items, setView, setSelId, myQueue }) {
  const p = db.project;
  const scoped = ROLES[role].scoped;
  const isOwner = role === "owner";
  const blocking = isOwner ? whatIsBlocking(db) : null;
  const moneyRulesUnset = isOwner ? unsetMoneyRules(db) : [];
  const late = items.filter(i => daysLate(i) > 0).sort((a,b)=>daysLate(b)-daysLate(a)).slice(0,5);
  const mine = scoped ? partyLedger(db, meId) : null;
  // Offered TO ME. The owner sees every offer on the schedule of values; only
  // the party it was made to gets asked to accept it.
  const offers = items.filter(i => assignState(i) === "offered"
    && i.assignment && i.assignment.partyId === meId);
  return (
    <React.Fragment>
      <h1 className="pagetitle">{scoped ? me.name : p.name}</h1>
      <p className="pagesub">{scoped
        ? p.name + " · " + items.length + " line items assigned to you" + (mine.assigned ? " · " + usd(mine.assigned) : "")
        : p.address + " · Contract sum " + usd(p.contractSum) + " · " + db.lineItems.length + " line items"}</p>

      {offers.length > 0 && (
        <div className="banner brass" style={{marginTop:16}}>
          <h4>{offers.length} scope{offers.length===1?"":"s"} offered to you</h4>
          <p>Review the value, finish date and penalty rate before accepting. What you accept is what you're held to —
             the terms freeze at acceptance and any later change has to come back to you.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}} onClick={()=>setSelId(offers[0].id)}>Review {offers[0].no}</button>
          </p>
        </div>
      )}

      {/*
        * What is in the way, said on the first screen.
        *
        * This project arrives 62% built with fifteen payment applications
        * already behind it, and none of that history moved through here. The
        * owner's first question is "does this match what I know", and his
        * second is "why can't I do anything" — the second deserves an answer
        * before he goes looking for one.
        */}
      {isOwner && blocking && (
        <div className="banner brass" style={{marginTop:16}}>
          <h4>{blocking.title}</h4>
          <p>{blocking.body}{" "}
            <button className="btn ghost sm" style={{marginLeft:6}}
                    onClick={()=>setView(blocking.go)}>{blocking.cta}</button></p>
        </div>
      )}
      {isOwner && moneyRulesUnset.length > 0 && (
        <div className="banner" style={{marginTop:16}}>
          <h4>{moneyRulesUnset.join(" and ")} {moneyRulesUnset.length === 1 ? "is" : "are"} still zero</h4>
          <p>
            Zero here means nobody has said what it should be, not that it is nil. Both come off
            every payment application, so a draw built now would ask the bank for more than is
            owed.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}}
                    onClick={()=>setView("setup")}>Set them now</button>
          </p>
        </div>
      )}

      {exceptions.length > 0 && !scoped && (
        <div className="banner" style={{marginTop:16}}>
          <h4>{usd(totals.unapproved)} disbursed without owner sign-off</h4>
          <p>
            {exceptions.length} line item{exceptions.length===1?"":"s"} were funded with no owner approval on file.
            That is the control failure this system exists to close.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}} onClick={()=>setView("exceptions")}>Review exceptions</button>
          </p>
        </div>
      )}
      {totals.overdue > 0 && (
        <div className="banner brass">
          <h4>{totals.overdue} days past contract completion</h4>
          <p>Completion was due {fmtDate(p.contractCompletion)}. {totals.lateItems} line items are running late,
             with {usd(totals.ld)} in delay penalties accrued at {usd(p.ldPerDay)}/day (capped at {p.ldCapPct}% per item).</p>
        </div>
      )}

      {scoped ? (
        <div className="strip" style={{marginTop:4}}>
          <Stat k="Your scope" v={usd(mine.assigned)} sub={`${mine.items} line items`}/>
          <Stat k="Work in place" v={usd(mine.inPlace)} sub={pct(mine.assigned?mine.inPlace/mine.assigned*100:0) + " of your scope"} tone="good"/>
          <Stat k="Paid to you" v={usd(mine.disbursed)}/>
          <Stat k="Still open" v={mine.open} sub={`${mine.late} behind schedule`} tone={mine.late?"flag":""}/>
          <Stat k="Penalties accrued" v={usd(mine.penalties)} tone={mine.penalties?"flag":""}/>
          <Stat k="Punch items open" v={mine.punchOpen} sub={mine.punchHoldback?usd(mine.punchHoldback)+" withheld":"—"} tone={mine.punchOpen?"flag":""}/>
        </div>
      ) : (
      <div className="strip" style={{marginTop:4}}>
        <Stat k="Contract sum" v={usd(p.contractSum)} sub={`${db.lineItems.length} line items`}/>
        <Stat k="Work in place" v={usd(totals.earned)} sub={pct(totals.pctComplete) + " of contract"} tone="good"/>
        <Stat k="Disbursed to date" v={usd(totals.disbursed)} sub={pct(totals.disbursed/p.contractSum*100) + " of contract"}/>
        <Stat k="Paid w/o approval" v={usd(totals.unapproved)} sub={exceptions.length + " line items"} tone={totals.unapproved?"flag":""}/>
        <Stat k="Retainage held" v={p.retainagePct ? usd(totals.retainage) : "not set"}
              tone={p.retainagePct ? undefined : "warn"}
              sub={p.retainagePct ? p.retainagePct + "% of value in place"
                                  : "no rate from the contract yet"}/>
        <Stat k="Delay penalties" v={usd(totals.ld)} sub={`${totals.lateItems} items late`} tone={totals.ld?"flag":""}/>
      </div>
      )}

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))", gap:16, marginTop:20}}>
        <div className="card pad">
          <SectionHead>Waiting on you · {ROLES[role].label}</SectionHead>
          {myQueue.length === 0
            ? <div className="muted" style={{fontSize:12.5}}>Nothing needs your signature right now.</div>
            : myQueue.slice(0,7).map(it => (
              <button key={it.id} onClick={()=>setSelId(it.id)} style={{display:"block",width:"100%",textAlign:"left",
                background:"transparent",border:0,borderBottom:"1px solid var(--rule-soft)",padding:"8px 0"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"baseline"}}>
                  <span className="itemname">{it.name}</span>
                  <span className="num" style={{fontSize:12.5}}>{usd(it.scheduledValue*it.percent/100)}</span>
                </div>
                <div className="itemmeta">{it.no} · <Tag stage={it.stage}/> {daysLate(it)>0 && <span style={{color:"var(--oxide)"}}> · {daysLate(it)}d late</span>}</div>
              </button>
            ))}
          {myQueue.length > 7 && <button className="btn ghost sm" style={{marginTop:10}} onClick={()=>setView("ledger")}>See all {myQueue.length}</button>}
        </div>

        <div className="card pad">
          <SectionHead>Furthest behind schedule</SectionHead>
          {late.length === 0
            ? <div className="muted" style={{fontSize:12.5}}>Every line item is inside its planned finish date.</div>
            : late.map(it => (
              <div key={it.id} style={{padding:"8px 0", borderBottom:"1px solid var(--rule-soft)"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"baseline"}}>
                  <button onClick={()=>setSelId(it.id)} style={{background:"transparent",border:0,padding:0,textAlign:"left"}} className="itemname">{it.name}</button>
                  <span className="num" style={{fontSize:12.5,color:"var(--oxide)",whiteSpace:"nowrap"}}>{daysLate(it)}d · {usd(ldAccrued(it, p))}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:5}}>
                  <Bar value={it.percent} late/>
                  <span className="itemmeta">due {fmtDate(it.plannedFinish)}</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="card pad" style={{marginTop:16}}>
        <SectionHead>How money moves here</SectionHead>
        <div style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"center"}}>
          {SIGNOFF_ORDER.filter(r => p.requiredSignoffs.includes(r)).map((r,i,arr) => (
            <React.Fragment key={r}>
              <div style={{border:"1px solid var(--rule)",borderRadius:3,padding:"7px 11px",background:"#fff",minWidth:130}}>
                <div className="eyebrow">Step {i+1}</div>
                <div style={{fontWeight:600,fontSize:12.5,marginTop:2}}>{ROLES[r].label}</div>
                <div className="muted" style={{fontSize:11}}>
                  {r==="contractor"?"Photos + submit":r==="inspector"?"Field verify":r==="owner"?"Approve on site":"Release funds"}
                </div>
              </div>
              {i < arr.length-1 && <span className="mono muted">→</span>}
            </React.Fragment>
          ))}
        </div>
        <p className="muted" style={{fontSize:12,marginTop:12,marginBottom:0,maxWidth:640}}>
          No line item can reach “Released” without every stamp above. The contractor signature belongs to whichever party
          accepted that specific line item, so a sub signs for their own work and nobody signs for anyone else's.
        </p>
      </div>

      {(() => {
        const flagged = items.flatMap(it => openPunch(it).map(px => ({px, it})))
          .filter(x => x.px.severity === "blocking" || punchOverdue(x.px) || x.px.status === "fixed")
          .sort((a,b) => (punchOverdue(b.px) - punchOverdue(a.px)) || (a.px.severity === "blocking" ? -1 : 1))
          .slice(0, 5);
        if (!flagged.length) return null;
        return (
          <div className="card pad" style={{marginTop:16}}>
            <SectionHead right={<button className="btn ghost sm noprint" onClick={()=>setView("punch")}>Full list</button>}>
              Punch items needing a decision
            </SectionHead>
            {flagged.map(({px, it}) => (
              <button key={px.id} onClick={()=>setSelId(it.id)} style={{display:"block",width:"100%",textAlign:"left",
                background:"transparent",border:0,borderBottom:"1px solid var(--rule-soft)",padding:"8px 0"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"baseline",flexWrap:"wrap"}}>
                  <span className="itemname">{px.title}</span>
                  <span style={{display:"flex",gap:5}}>
                    {px.status === "fixed" && <span className="tag p">Needs your walk</span>}
                    {punchOverdue(px) && <span className="tag b">{daysBetween(px.dueDate, iso(TODAY))}d over</span>}
                    {px.severity === "blocking" && <span className="tag b">Blocking</span>}
                  </span>
                </div>
                <div className="itemmeta">{px.no} · {it.name}{px.location ? " · " + px.location : ""}</div>
              </button>
            ))}
          </div>
        );
      })()}

      {role === "owner" && capitalTotals(db).remaining > 0 && (() => {
        const t = capitalTotals(db);
        const urgent = needsOf(db).filter(n => n.urgent && n.status !== "closed" && needProgress(db,n).received < n.amount);
        return (
          <div className="card pad" style={{marginTop:16}}>
            <SectionHead right={<button className="btn ghost sm noprint" onClick={()=>setView("funding")}>Funding</button>}>
              Capital still to raise
            </SectionHead>
            <div style={{display:"flex",gap:18,flexWrap:"wrap",alignItems:"baseline"}}>
              <span><span className="num" style={{fontSize:20,fontWeight:600}}>{usd(t.remaining)}</span>
                <span className="itemmeta" style={{display:"block"}}>still needed of {usd(t.need)}</span></span>
              <span><span className="num" style={{fontSize:20,fontWeight:600,color:"var(--verdigris)"}}>{usd(t.received)}</span>
                <span className="itemmeta" style={{display:"block"}}>received</span></span>
              <span><span className="num" style={{fontSize:20,fontWeight:600,color:"var(--brass)"}}>{usd(t.committed - t.received)}</span>
                <span className="itemmeta" style={{display:"block"}}>signed, not yet funded</span></span>
            </div>
            <div className="fbar" style={{marginTop:11}}>
              <i className="f-recv" style={{width: clamp(t.pct,0,100)+"%"}}/>
              <i className="f-comm" style={{width: clamp(t.need ? (t.committed-t.received)/t.need*100 : 0,0,100)+"%"}}/>
            </div>
            {urgent.length > 0 && (
              <p style={{fontSize:12.5,marginTop:10,marginBottom:0,color:"var(--oxide)"}}>
                {urgent.map(n=>n.name).join(", ")} {urgent.length===1?"is":"are"} time-sensitive
                {urgent[0].deadline ? " — needed by " + fmtDate(urgent[0].deadline) : ""}.
              </p>
            )}
          </div>
        );
      })()}

      {!scoped && drawsNeedingDocs(db).length > 0 && (
        <div className="banner brass" style={{marginTop:16}}>
          <h4>{drawsNeedingDocs(db).length} funded draw{drawsNeedingDocs(db).length===1?"":"s"} without a complete file</h4>
          <p>Proof of payment or lien waivers are missing on {drawsNeedingDocs(db).map(drawLabel).join(", ")}. The lender
             can upload the check straight onto the draw rather than sending it over on request.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}} onClick={()=>setView("draws")}>Open draws</button></p>
        </div>
      )}

      {!scoped && (
        <div className="card pad" style={{marginTop:16}}>
          <SectionHead right={<button className="btn ghost sm noprint" onClick={()=>setView("parties")}>Manage</button>}>Who is on the job</SectionHead>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:10}}>
            {db.parties.filter(x => x.role === "contractor" && x.status === "active").map(x => {
              const l = partyLedger(db, x.id), gaps = complianceGaps(x);
              return (
                <div key={x.id} style={{border:"1px solid var(--rule)",borderRadius:3,padding:"9px 11px",background:"#fff"}}>
                  <div style={{fontWeight:600,fontSize:12.5}}>{x.name}</div>
                  <div className="itemmeta">{x.trade} · {l.items} items · {usd(l.assigned)}</div>
                  <div style={{marginTop:5,display:"flex",gap:4,flexWrap:"wrap"}}>
                    {gaps.length === 0 ? <span className="tag g">Clear to work</span>
                      : gaps.map(g => <span key={g} className="tag b">{g}</span>)}
                    {l.overFunded > 0 && <span className="tag b">{usd(l.overFunded)} over-funded</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

/* ------------------------------------------------------- Schedule of values */
function Ledger({ db, role, meId, items, setSelId, selId, totals, say, refresh, me }) {
  const [picked, setPicked] = useState([]);
  const [bulk, setBulk] = useState(false);
  const [adding, setAdding] = useState(false);
  const undated = items.filter(i => !i.plannedStart || !i.plannedFinish).length;
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [party, setParty] = useState("all");
  const [sort, setSort] = useState({ key:"no", dir:1 });
  const p = db.project;
  const contractors = db.parties.filter(x => x.role === "contractor");

  const rows = useMemo(() => {
    let r = items.filter(it => {
      const held = it.assignment ? partyName(db, it.assignment.partyId) : "";
      if (q && !(it.name + " " + it.no + " " + it.division + " " + it.scope + " " + held).toLowerCase().includes(q.toLowerCase())) return false;
      if (party !== "all" && (party === "none" ? !!it.assignment : !(it.assignment && it.assignment.partyId === party))) return false;
      if (filter === "mine") return availableActions(it, role, meId).some(a => a !== "reject") || assignState(it) === "offered";
      if (filter === "unassigned") return !it.assignment || it.assignment.status === "declined";
      if (filter === "offered") return assignState(it) === "offered";
      if (filter === "late") return daysLate(it) > 0;
      if (filter === "open") return !["paid","released"].includes(it.stage);
      if (filter === "exception") return liveDisbursed(it) > 0 && !signoffs(it).owner;
      return true;
    });
    const val = (it) => {
      if (sort.key === "late") return daysLate(it);
      if (sort.key === "earned") return it.scheduledValue * it.percent / 100;
      return it[sort.key];
    };
    return r.sort((a,b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
  }, [db, items, q, filter, party, sort, role, meId]);

  const grouped = useMemo(() => {
    const g = [];
    rows.forEach(it => {
      const last = g[g.length-1];
      if (!last || last.division !== it.division) g.push({ division: it.division, items: [it] });
      else last.items.push(it);
    });
    return g;
  }, [rows]);

  const th = (key, label, cls) => (
    <th className={cls} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}
        title="Sort">{label}{sort.key===key ? (sort.dir>0?" ▲":" ▼") : ""}</th>
  );

  return (
    <React.Fragment>
      <h1 className="pagetitle">Schedule of values</h1>
      <p className="pagesub">Every remaining item of work, what it is worth, and who still has to sign for it.</p>

      <div className="toolbar noprint">
        <input className="field" style={{maxWidth:260}} placeholder="Search work, item no., division…"
               value={q} onChange={e=>setQ(e.target.value)} aria-label="Search line items"/>
        <select className="field" style={{maxWidth:190}} value={filter} onChange={e=>setFilter(e.target.value)} aria-label="Filter">
          <option value="all">All line items</option>
          <option value="mine">Waiting on me</option>
          <option value="open">Not yet released</option>
          <option value="late">Behind schedule</option>
          <option value="unassigned">No contractor on it</option>
          <option value="offered">Offered, not accepted</option>
          <option value="exception">Paid without approval</option>
        </select>
        {!ROLES[role].scoped && (
          <select className="field" style={{maxWidth:200}} value={party} onChange={e=>setParty(e.target.value)} aria-label="Filter by contractor">
            <option value="all">Every contractor</option>
            <option value="none">Unassigned</option>
            {contractors.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        <span className="muted" style={{fontSize:12}}>{rows.length} of {items.length}</span>
        <div style={{flex:1}}/>
        {role === "owner" && (
          <React.Fragment>
            <button className="btn ghost sm" onClick={()=>setAdding(a=>!a)}>
              {adding ? "Cancel" : "Add a line item"}</button>
            <button className="btn ghost sm" onClick={()=>{ setPicked([]); setBulk(b=>!b); }}>
              {bulk ? "Done selecting" : "Select several"}</button>
          </React.Fragment>
        )}
        <button className="btn ghost sm" onClick={()=>download("calico-schedule-of-values-" + iso(TODAY) + ".csv", toCSV(forExport(db), CSV_COLUMNS))}>Export CSV</button>
        <button className="btn ghost sm" onClick={()=>window.print()}>Print</button>
      </div>

      {adding && role === "owner" && (
        <NewLineItem {...{db, project:p, me, refresh, say}} onDone={()=>setAdding(false)}/>
      )}

      {undated > 0 && role === "owner" && !adding && (
        <div className="banner brass" style={{marginTop:14}}>
          <h4>{undated} line item{undated===1?"":"s"} with no dates</h4>
          <p>They won't show on the timeline and can't accrue a delay penalty. Open one and use
             <strong> Edit details</strong>, or select several and shift them together.</p>
        </div>
      )}

      {bulk && role === "owner" && (
        <BulkAssign {...{db, me, picked, setPicked, rows, say, refresh}}
                    onDone={()=>{ setBulk(false); setPicked([]); }}/>
      )}

      <div className="tablewrap">
        <table className="sov">
          <thead><tr>
            {bulk && <th className="plain" style={{width:32}}>
              <input type="checkbox" aria-label="Select all shown"
                checked={rows.length>0 && picked.length===rows.length}
                onChange={()=>setPicked(picked.length===rows.length ? [] : rows.map(r=>r.id))}/></th>}
            {th("no","Item")}
            {th("name","Description of work")}
            {th("scheduledValue","Sched. value","r")}
            {th("percent","% done")}
            {th("earned","Value in place","r")}
            {th("plannedFinish","Due")}
            {th("late","Late")}
            <th className="plain">Held by</th>
            <th className="plain">Status</th>
            <th className="plain">Signatures</th>
          </tr></thead>
          <tbody>
            {grouped.map(g => (
              <React.Fragment key={g.division}>
                <tr className="divrow"><td colSpan={9}>{g.division}</td></tr>
                {g.items.map(it => {
                  const s = signoffs(it), late = daysLate(it), exc = it.bankDisbursed>0 && !s.owner;
                  return (
                    <tr key={it.id} className={selId===it.id||picked.includes(it.id)?"sel":""}
                        onClick={()=> bulk
                          ? setPicked(x => x.includes(it.id) ? x.filter(y=>y!==it.id) : x.concat(it.id))
                          : setSelId(it.id)} style={{cursor:"pointer"}}>
                      {bulk && <td onClick={e=>e.stopPropagation()}>
                        <input type="checkbox" checked={picked.includes(it.id)} aria-label={"Select " + it.name}
                          onChange={()=>setPicked(x => x.includes(it.id) ? x.filter(y=>y!==it.id) : x.concat(it.id))}/></td>}
                      <td className="itemno">{it.no}</td>
                      <td>
                        <div className="itemname">{it.name}</div>
                        <div className="itemmeta">{it.photos.length>0 && <span>{it.photos.length} photos · </span>}
                          {openPunch(it).length > 0 && <span style={{color: blockingPunch(it).length ? "var(--oxide)" : "var(--brass)", fontWeight:600}}>
                            {openPunch(it).length} punch open{blockingPunch(it).length ? " · blocking" : punchHoldback(it) ? " · " + usd(punchHoldback(it)) + " held" : ""}{exc ? " · " : ""}</span>}
                          {exc && <span style={{color:"var(--oxide)",fontWeight:600}}>{usd(it.bankDisbursed)} paid, no owner sign-off</span>}</div>
                      </td>
                      <td className="r num">{usd(it.scheduledValue)}</td>
                      <td><div style={{display:"flex",alignItems:"center",gap:6}}><Bar value={it.percent} late={late>0 && it.percent<100}/><span className="num" style={{fontSize:11.5}}>{it.percent}%</span></div></td>
                      <td className="r num">{usd(it.scheduledValue*it.percent/100)}</td>
                      <td className="num" style={{fontSize:12}}>{fmtDate(it.plannedFinish)}</td>
                      <td className="num" style={{fontSize:12, color: late&&it.stage!=="closed"?"var(--oxide)":"var(--slate)"}}>{late ? late+"d" : "—"}</td>
                      <td style={{fontSize:12}}>
                        {it.assignment
                          ? <React.Fragment>
                              <div style={{fontWeight:500}}>{partyName(db, it.assignment.partyId)}</div>
                              {assignState(it) !== "accepted" &&
                                <span className={"tag " + ASSIGN_META[assignState(it)].tag}>{ASSIGN_META[assignState(it)].label}</span>}
                            </React.Fragment>
                          : <span className="tag n">Unassigned</span>}
                      </td>
                      <td><Tag stage={it.stage}/></td>
                      <td>
                        <span className="mono" style={{fontSize:11, letterSpacing:".08em"}}>
                          {SIGNOFF_ORDER.filter(r=>p.requiredSignoffs.includes(r)).map(r => (
                            <span key={r} title={ROLES[r].label + (s[r] ? " — signed " + fmtStamp(s[r].at) : " — not signed")}
                                  style={{color: s[r] ? (s[r].rejected?"var(--oxide)":"var(--verdigris)") : "var(--rule)"}}>
                              {s[r] ? (s[r].rejected ? "✕" : "■") : "□"}
                            </span>
                          ))}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            {rows.length === 0 && <tr><td colSpan={bulk?11:10} style={{padding:28,textAlign:"center"}} className="muted">Nothing matches that filter.</td></tr>}
          </tbody>
          <tfoot><tr style={{borderTop:"2px solid var(--ink)"}}>
            {bulk && <td/>}<td/><td style={{fontWeight:700}}>Totals — filtered</td>
            <td className="r num" style={{fontWeight:700}}>{usd(rows.reduce((s,i)=>s+i.scheduledValue,0))}</td>
            <td/>
            <td className="r num" style={{fontWeight:700}}>{usd(rows.reduce((s,i)=>s+i.scheduledValue*i.percent/100,0))}</td>
            <td colSpan={5}/>
          </tr></tfoot>
        </table>
      </div>
    </React.Fragment>
  );
}

/* Hand a batch of line items to one contractor in a single move. */
function BulkAssign({ db, picked, setPicked, rows, say, refresh, onDone }) {
  const [partyId, setPartyId] = useState("");
  const [shiftDays, setShiftDays] = useState(0);
  const [busy, setBusy] = useState(false);
  const eligible = db.parties.filter(x => x.role === "contractor" && x.status === "active");
  const chosen = eligible.find(x => x.id === partyId);
  const sel = db.lineItems.filter(i => picked.includes(i.id));
  const value = sel.reduce((s,i)=>s+i.scheduledValue,0);
  const taken = sel.filter(i => i.assignment && i.assignment.status === "accepted");
  const gaps = chosen ? complianceGaps(chosen) : [];
  const blocked = chosen ? !canBeAssigned(chosen) : true;
  const notOnboarded = chosen && !isOnboarded(chosen);

  return (
    <div className="card pad" style={{marginTop:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead right={<span className="muted" style={{fontSize:11}}>{picked.length} selected · {usd(value)}</span>}>
        Do something with several at once
      </SectionHead>

      {picked.length > 0 && (
        <div style={{border:"1px solid var(--rule)",borderRadius:3,padding:"11px 13px",background:"#fff",marginBottom:14}}>
          <div className="eyebrow" style={{marginBottom:7}}>Move their dates</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {[-14,-7,7,14,30].map(d => (
              <button key={d} className="btn ghost sm" disabled={busy} onClick={async()=>{
                setBusy(true);
                await api.shiftDates({ ids: picked, days: d, actor:"Kenneth Moye" });
                await refresh(); setBusy(false);
                say(picked.length + " line items moved " + (d>0?"+":"") + d + " days");
              }}>{d > 0 ? "+" : ""}{d} days</button>
            ))}
            <span className="muted" style={{fontSize:11.5,flex:1,minWidth:200}}>
              Shifts planned start and finish together. Items already accepted by a contractor will flag as a change
              order.
            </span>
          </div>
        </div>
      )}

      {picked.length === 0
        ? <p className="muted" style={{fontSize:12.5,margin:0}}>Tick the rows below, or use the header checkbox to take everything currently filtered.</p>
        : (
        <React.Fragment>
          <div className="eyebrow" style={{marginBottom:7}}>Or offer them to a contractor</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
            <label><div className="eyebrow">Contractor or sub</div>
              <select className="field" value={partyId} onChange={e=>setPartyId(e.target.value)}>
                <option value="">Choose…</option>
                {eligible.map(x => <option key={x.id} value={x.id}>{x.name}{x.trade!=="General"?" · "+x.trade:""}</option>)}
              </select></label>
            <label><div className="eyebrow">Shift every finish date by</div>
              <input className="field" type="number" step="1" value={shiftDays}
                     onChange={e=>setShiftDays(Number(e.target.value))} placeholder="0"/>
              <div className="muted" style={{fontSize:11,marginTop:3}}>
                Days. Use this when a new contractor needs a realistic runway on dates the last one blew.</div></label>
          </div>

          {taken.length > 0 && (
            <div className="banner brass" style={{marginTop:12}}>
              <h4>{taken.length} of these are already accepted by someone</h4>
              <p>{taken.map(x=>x.no).join(", ")}. Offering them again replaces the current assignment without splitting
                 the line. If work has already been done, close it out with <strong>Reassign this scope</strong> on the
                 line item instead.</p>
            </div>
          )}
          {chosen && (blocked || notOnboarded) && (
            <div className="banner" style={{marginTop:12}}>
              <h4>{chosen.name} can't take work yet</h4>
              <p>{blocked ? "Missing: " + gaps.join(", ") + ". " : ""}
                 {notOnboarded ? "They also haven't accepted the terms — send the invitation from Contractors & subs." : ""}</p>
            </div>
          )}

          <div className="toolbar">
            <button className="btn go" disabled={!partyId || blocked || busy} onClick={async()=>{
              setBusy(true);
              await api.bulkAssign({ ids: picked, partyId, shiftDays, actor: me.contact });
              await refresh(); setBusy(false); onDone();
              say(picked.length + " line items offered to " + chosen.name);
            }}>Offer {picked.length} line items{value?" · "+usd(value):""}</button>
            <button className="btn ghost" onClick={()=>setPicked([])}>Clear selection</button>
            <button className="btn ghost" onClick={onDone}>Cancel</button>
          </div>
          <p className="muted" style={{fontSize:11.5,marginBottom:0}}>
            Each becomes an offer they accept or decline individually, so they can take the electrical and pass on the
            millwork.
          </p>
        </React.Fragment>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Exceptions */
function Exceptions({ db, exceptions, project, setSelId, totals }) {
  return (
    <React.Fragment>
      <h1 className="pagetitle">Exceptions</h1>
      <p className="pagesub">Money that moved outside the agreed procedure. This is the evidence page — export it before any conversation with the lender.</p>

      {exceptions.length === 0
        ? <Empty title="No exceptions on record"
                 body={db.project.baselineAsOf
                   ? "Nothing has been paid without owner sign-off since the baseline of "
                     + fmtDate(db.project.baselineAsOf) + ". Everything disbursed before that date is recorded as "
                     + "an opening balance — it is in the ledger, but it was never approved through this system "
                     + "and cannot be now."
                   : "Every disbursement on this project has a matching owner approval."}/>
        : (
        <React.Fragment>
          <div className="strip" style={{marginTop:16}}>
            <Stat k="Unapproved disbursements" v={usd(totals.unapproved)} sub={exceptions.length + " line items"} tone="flag"/>
            <Stat k="Share of all disbursements" v={pct(totals.unapproved/totals.disbursed*100)} sub={"of " + usd(totals.disbursed) + " released"}/>
            <Stat k="Retainage that should be held" v={usd(exceptions.reduce((s,i)=>s+i.bankDisbursed*project.retainagePct/100,0))} sub={project.retainagePct + "% of amounts paid"}/>
          </div>
          <div className="tablewrap" style={{marginTop:16}}>
            <table className="sov">
              <thead><tr>
                <th className="plain">Item</th><th className="plain">Description of work</th>
                <th className="plain">Paid to</th>
                <th className="plain">Amount paid</th><th className="plain">Work in place</th>
                <th className="plain">Over-funded by</th><th className="plain">Missing signature</th>
              </tr></thead>
              <tbody>
                {exceptions.map(it => {
                  const earned = it.scheduledValue*it.percent/100;
                  const over = it.bankDisbursed - earned;
                  const s = signoffs(it);
                  return (
                    <tr key={it.id} onClick={()=>setSelId(it.id)} style={{cursor:"pointer"}}>
                      <td className="itemno">{it.no}</td>
                      <td><div className="itemname">{it.name}</div><div className="itemmeta">{it.division}</div></td>
                      <td style={{fontSize:12}}>{it.assignment ? partyName(db, it.assignment.partyId) : "—"}</td>
                      <td className="num">{usd(it.bankDisbursed)}</td>
                      <td className="num">{usd(earned)}</td>
                      <td className="num" style={{color: over>0?"var(--oxide)":"var(--slate)", fontWeight: over>0?600:400}}>{over>0?usd(over):"—"}</td>
                      <td>{SIGNOFF_ORDER.filter(r=>project.requiredSignoffs.includes(r) && !s[r]).map(r =>
                        <span key={r} className="tag b" style={{marginRight:4}}>{ROLES[r].label.split(" ")[0]}</span>)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="toolbar noprint">
            <button className="btn" onClick={()=>download("calico-exceptions-" + iso(TODAY) + ".csv", toCSV(exceptions.map(it=>({
              ...it, earned: Math.round(it.scheduledValue*it.percent/100), overFunded: Math.round(it.bankDisbursed - it.scheduledValue*it.percent/100)
            })), CSV_COLUMNS.concat([["earned","Value In Place"],["overFunded","Over-Funded By"]])))}>Export exception report</button>
            <button className="btn ghost" onClick={()=>window.print()}>Print for the lender</button>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

/* ------------------------------------------------------------ Project setup */
const SETUP_STEPS = [
  { key:"basics",  label:"The project" },
  { key:"money",   label:"Money & penalties" },
  { key:"tasks",   label:"The work" },
  { key:"team",    label:"Who's building it" },
  { key:"review",  label:"Start the job" },
];

function Setup({ db, role, me, say, refresh, setView, setSelId }) {
  const p = db.project;
  const [step, setStep] = useState(p.initialized ? "basics" : "basics");
  const [f, setF] = useState({
    name: p.name, address: p.address, startDate: p.startDate || "",
    contractCompletion: p.contractCompletion || "", contractSum: p.contractSum || 0,
    retainagePct: p.retainagePct, ldPerDay: p.ldPerDay, ldCapPct: p.ldCapPct,
    requiredSignoffs: p.requiredSignoffs,
    requireBond: !!p.requireBond,
  });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const wizard = !p.initialized;

  if (role !== "owner") return <Empty title="Owner only"
    body="Project setup, budgets and penalty rates are set by the owner. You'll see the terms that apply to you during onboarding."/>;

  const save = async (extra) => {
    setBusy(true);
    await api.initProject({ patch: { ...f, ...(extra||{}) }, actor: me.contact });
    await refresh(); setBusy(false);
  };
  const sumFromItems = db.lineItems.reduce((s,i)=>s+i.scheduledValue,0);
  const unassigned = db.lineItems.filter(i => !i.assignment).length;
  const contractors = db.parties.filter(x => x.role === "contractor" && x.status === "active");
  const ready = f.name && f.contractCompletion && db.lineItems.length > 0 && contractors.length > 0;

  return (
    <React.Fragment>
      <h1 className="pagetitle">{wizard ? "Set up the project" : "Project setup"}</h1>
      <p className="pagesub">{wizard
        ? "Five steps. Do them in order the first time — the numbers you set here are what every contractor is later held to."
        : "The terms of the job. Changing anything here is written to the audit trail, and changes to accepted line items go back to the contractor as a change order."}</p>

      <div className="steps noprint">
        {SETUP_STEPS.map((sx, i) => (
          <button key={sx.key} className={"stepbtn " + (step===sx.key?"on":"")} onClick={()=>setStep(sx.key)}>
            <span className="stepno">{i+1}</span>
            <span>{sx.label}</span>
          </button>
        ))}
      </div>

      {step === "basics" && (
        <div className="card pad" style={{maxWidth:720}}>
          <SectionHead>What and where</SectionHead>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
            <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Project name</div>
              <input className="field" value={f.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Market Street Renovation — Phase 2"/></label>
            <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Site address</div>
              <input className="field" value={f.address} onChange={e=>set("address",e.target.value)}/></label>
            <label><div className="eyebrow">Work starts</div>
              <input className="field" type="date" value={f.startDate} onChange={e=>set("startDate",e.target.value)}/></label>
            <label><div className="eyebrow">Substantial completion</div>
              <input className="field" type="date" value={f.contractCompletion} onChange={e=>set("contractCompletion",e.target.value)}/></label>
          </div>
          <div className="toolbar">
            <button className="btn" disabled={busy||!f.name} onClick={async()=>{ await save(); setStep("money"); say("Saved"); }}>
              {wizard ? "Save and continue" : "Save"}
            </button>
          </div>
        </div>
      )}

      {step === "money" && (
        <div className="card pad" style={{maxWidth:720}}>
          <SectionHead>Budget</SectionHead>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
            <label><div className="eyebrow">Budget for this project</div>
              <input className="field" type="number" min="0" step="1000" value={f.contractSum}
                     onChange={e=>set("contractSum",Number(e.target.value))}/>
              <div className="muted" style={{fontSize:11,marginTop:3}}>
                {sumFromItems ? "Line items currently total " + usd(sumFromItems) + "." : "Set it now or let the imported line items add up to it."}
                {sumFromItems > 0 && <button className="btn ghost sm" style={{marginLeft:6}}
                  onClick={()=>set("contractSum",sumFromItems)}>Use {usd(sumFromItems)}</button>}
              </div>
            </label>
            <label><div className="eyebrow">Retainage %</div>
              <input className="field" type="number" min="0" max="20" value={f.retainagePct}
                     onChange={e=>set("retainagePct",Number(e.target.value))}/>
              <div className="muted" style={{fontSize:11,marginTop:3,lineHeight:1.5}}>
                The share of every payment the owner holds back until the job is finished — usually
                5% or 10%, and it will be written in the contract. It comes off every draw, so
                leaving it at zero quietly asks the bank for more than you are owed.
                {!f.retainagePct && <b> Currently zero, which means nobody has set it.</b>}
              </div></label>
          </div>

          <div style={{marginTop:18}}>
            <SectionHead>Paperwork before work</SectionHead>
            <div className="note" style={{marginBottom:14}}>
              <div className="meta">Every contractor already needs a W-9, a licence number and
                unexpired insurance before scope can be offered to them.</div>
              <label className="grantrow" style={{borderTop:0,paddingTop:0}}>
                <input type="checkbox" checked={f.requireBond}
                       onChange={e=>set("requireBond", e.target.checked)}/>
                <span>
                  <span className="grantlabel">This project also requires a surety bond</span>
                  <span className="grantnote">
                    A bond is a surety's promise to pay if a contractor does not finish or does not
                    pay the people under them — which is the other half of "licensed and bonded".
                    Many small subs are not bonded, so this is off unless you say otherwise. Turn it
                    on and no scope can be offered to anybody without a current bond on file.
                  </span>
                </span>
              </label>
            </div>

            <SectionHead>Penalty for missing a date</SectionHead>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
              <label><div className="eyebrow">Per day, per line item</div>
                <input className="field" type="number" min="0" step="25" value={f.ldPerDay}
                       onChange={e=>set("ldPerDay",Number(e.target.value))}/>
                <div className="muted" style={{fontSize:11,marginTop:3,lineHeight:1.5}}>
                  What a contractor owes for each day past the finish date they accepted. It accrues
                  per calendar day at the site and comes off what they are paid. It only ever applies
                  to somebody who accepted that date.
                  {!f.ldPerDay && <b> Currently zero — no penalty will accrue.</b>}
                </div></label>
              <label><div className="eyebrow">Cap, % of that line's value</div>
                <input className="field" type="number" min="0" max="50" value={f.ldCapPct}
                       onChange={e=>set("ldCapPct",Number(e.target.value))}/></label>
            </div>
            <p className="muted" style={{fontSize:12,marginTop:9,maxWidth:560}}>
              A contractor sees this rate on every line item before they accept it, and initials a clause about it during
              onboarding. Penalties only ever run against dates someone accepted — that's what makes them collectible
              rather than just a number in a contract.
            </p>
          </div>

          <div style={{marginTop:18}}>
            <SectionHead>Signatures required before any payment</SectionHead>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {SIGNOFF_ORDER.map(r => (
                <label key={r} style={{display:"flex",gap:7,alignItems:"center",border:"1px solid var(--rule)",
                  borderRadius:3,padding:"8px 11px",background: f.requiredSignoffs.includes(r)?"var(--verdigris-soft)":"#fff"}}>
                  <input type="checkbox" disabled={r==="contractor"||r==="owner"}
                    checked={f.requiredSignoffs.includes(r)}
                    onChange={()=>set("requiredSignoffs", f.requiredSignoffs.includes(r)
                      ? f.requiredSignoffs.filter(x=>x!==r)
                      : SIGNOFF_ORDER.filter(x => x===r || f.requiredSignoffs.includes(x)))}/>
                  <span><span style={{fontWeight:600,fontSize:12.5,display:"block"}}>{ROLES[r].label}</span>
                    <span className="muted" style={{fontSize:11}}>{r==="contractor"||r==="owner"?"Always required":"Optional"}</span></span>
                </label>
              ))}
            </div>
          </div>

          <div className="toolbar">
            <button className="btn" disabled={busy} onClick={async()=>{ await save(); setStep("tasks"); say("Saved"); }}>
              {wizard ? "Save and continue" : "Save"}</button>
          </div>
        </div>
      )}

      {step === "tasks" && (
        <div style={{maxWidth:820}}>
          <div className="card pad">
            <SectionHead right={<span className="muted" style={{fontSize:11}}>{db.lineItems.length} on file</span>}>
              The itemised list of work
            </SectionHead>
            <p style={{fontSize:12.5,marginTop:0,maxWidth:600}}>
              Import the spreadsheet you already have. Column names don't have to match — you'll map them on the next
              screen. Each row becomes a line item with its own value, date, penalty rate and signature chain.
            </p>
            <div className="toolbar" style={{marginTop:4}}>
              <button className="btn" onClick={()=>setView("data")}>Import a spreadsheet</button>
              <button className="btn ghost" onClick={()=>setView("ledger")}>
                {db.lineItems.length ? "Review the " + db.lineItems.length + " line items" : "View the schedule"}</button>
            </div>
            {db.lineItems.length > 0 && (
              <div className="kv" style={{marginTop:14}}>
                <div><div className="k">Line items</div><div className="v">{db.lineItems.length}</div></div>
                <div><div className="k">Total value</div><div className="v">{usd(sumFromItems)}</div></div>
                <div><div className="k">Divisions</div><div className="v">{new Set(db.lineItems.map(i=>i.division)).size}</div></div>
                <div style={{background: unassigned ? "var(--brass-soft)" : undefined}}>
                  <div className="k">No contractor yet</div><div className="v">{unassigned}</div></div>
              </div>
            )}
          </div>
          <div className="toolbar">
            <button className="btn" onClick={()=>setStep("team")} disabled={db.lineItems.length===0}
              title={db.lineItems.length===0 ? "Import or add at least one line item first" : ""}>Continue</button>
          </div>
        </div>
      )}

      {step === "team" && (
        <div style={{maxWidth:820}}>
          <div className="card pad">
            <SectionHead>Invite the contractor</SectionHead>
            <p style={{fontSize:12.5,marginTop:0,maxWidth:620}}>
              Add them to the directory, then send the invitation. They land on an onboarding page that shows the scope,
              the dates, the penalty rate and the payment procedure, and they initial each clause before they can accept
              any work. That record is what makes the schedule enforceable later.
            </p>
            <div className="toolbar" style={{marginTop:4}}>
              <button className="btn" onClick={()=>setView("parties")}>Add and invite contractors</button>
            </div>
            {contractors.length > 0 && (
              <div style={{marginTop:14}}>
                {contractors.map(x => {
                  const ob = onboardingOf(x), inv = inviteFor(db, x.id);
                  return (
                    <div key={x.id} style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",
                      alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--rule-soft)"}}>
                      <div><div style={{fontWeight:600,fontSize:12.5}}>{x.name}</div>
                        <div className="itemmeta">{x.trade} · {db.lineItems.filter(i=>i.assignment&&i.assignment.partyId===x.id).length} line items</div></div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {complianceGaps(x).length === 0 ? <span className="tag g">Paperwork clear</span>
                          : <span className="tag b">{complianceGaps(x).length} document(s) missing</span>}
                        <span className={"tag " + (ob.status==="signed"?"g":ob.status==="acknowledged"?"p":"n")}>
                          {ob.status==="signed"?"Agreement signed":ob.status==="acknowledged"?"Terms initialled":"Not onboarded"}</span>
                        {inv && ob.status==="none" && <span className="tag p">Invite {inv.status}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="toolbar"><button className="btn" onClick={()=>setStep("review")}>Continue</button></div>
        </div>
      )}

      {step === "review" && (
        <div style={{maxWidth:820}}>
          <div className="card pad">
            <SectionHead>Before you start</SectionHead>
            <Checklist rows={[
              ["Project named and dated", !!(f.name && f.contractCompletion), "Set it under step 1."],
              ["Budget and penalty rate set", f.contractSum > 0 && f.ldPerDay > 0, "Step 2. A penalty rate of zero means no schedule leverage."],
              ["Work itemised", db.lineItems.length > 0, "Import your spreadsheet under step 3."],
              ["Line item values add to the budget", Math.abs(sumFromItems - f.contractSum) < 1,
                "Line items total " + usd(sumFromItems) + " against a " + usd(f.contractSum) + " budget."],
              ["At least one contractor in the directory", contractors.length > 0, "Step 4."],
              ["Everyone's paperwork on file", contractors.length > 0 && contractors.every(x=>complianceGaps(x).length===0),
                "W-9, licence and current insurance. Nobody can hold scope without it."],
              ["Contractors have accepted the terms", contractors.length > 0 && contractors.every(x=>isOnboarded(x)),
                "Send the invitation from Contractors & subs."],
              ["Every line item has someone on it", db.lineItems.length > 0 && unassigned === 0,
                unassigned + " line items still have no contractor."],
            ]}/>
          </div>
          <div className="toolbar">
            {!p.initialized
              ? <button className="btn go" disabled={busy||!ready} onClick={async()=>{
                  await save({ initialized:true }); setView("dashboard"); say("Project is live");
                }}>Start the job</button>
              : <span className="muted" style={{fontSize:12.5}}>
                  This project started {fmtStamp(p.setupCompletedAt)}. The checklist stays here as a running health check.
                </span>}
          </div>

          <div className="card pad" style={{marginTop:16, borderLeft:"3px solid var(--oxide)"}}>
            <SectionHead>Start a different project</SectionHead>
            <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:600}}>
              Clears every line item, contractor, photo and signature and takes you back through setup from scratch.
              The owner, lender, inspector and investor records stay. There is no undo in this prototype.
            </p>
            <button className="btn stop" disabled={busy} onClick={async()=>{
              setBusy(true);
              await api.resetProject({ actor: me.contact, project:{ name:"New project", address:"", ldPerDay:500, retainagePct:10, ldCapPct:8 } });
              await refresh(); setBusy(false); setStep("basics"); setView("setup"); say("Fresh project — start at step 1");
            }}>Clear and start over</button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

function Checklist({ rows }) {
  return (
    <div>
      {rows.map(([label, done, hint]) => (
        <div key={label} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 0",borderBottom:"1px solid var(--rule-soft)"}}>
          <span className="mono" style={{color: done ? "var(--verdigris)" : "var(--brass)", fontWeight:600, width:16}}>
            {done ? "✓" : "○"}</span>
          <span>
            <span style={{fontWeight:600,fontSize:12.5}}>{label}</span>
            {!done && hint && <span className="muted" style={{fontSize:11.5,display:"block",marginTop:2}}>{hint}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------- Contractor onboarding */
/* What a contractor sees the first time they sign in. They cannot accept any
   scope until every clause is initialled. */
function Onboarding({ db, me, meId, role, say, refresh, setView }) {
  const p = db.project;
  // `me` here is the party — their own company on this project.
  const party = (db.parties || []).find((x) => x.id === meId) || me;
  const ob = onboardingOf(me);
  const [initials, setInitials] = useState(ob.initials || "");
  const [done, setDone] = useState(ob.clauses || []);
  const [busy, setBusy] = useState(false);
  const mine = db.lineItems.filter(i => i.assignment && i.assignment.partyId === meId);
  const value = mine.reduce((s,i)=>s+i.scheduledValue,0);
  const finish = mine.map(i=>i.plannedFinish).filter(Boolean).sort().pop();
  const complete = done.length === TERMS.length && initials.trim().length >= 2;
  const gaps = complianceGaps(me);

  const toggle = (id) => setDone(d => d.includes(id) ? d.filter(x=>x!==id) : d.concat(id));

  if (isOnboarded(me)) return <OnboardingRecord {...{db, party:me, me, meId, role, say, refresh}}/>;

  return (
    <React.Fragment>
      <h1 className="pagetitle">Before you start on {p.name}</h1>
      <p className="pagesub">Read each one and put your initials next to it. This takes about three minutes and it is
        the record of what you agreed to, so it's worth the three minutes.</p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Scope offered to you" v={usd(value)} sub={mine.length + " line items"}/>
        <Stat k="Last date on your scope" v={finish ? fmtDate(finish) : "—"}/>
        <Stat k="Penalty per day late" v={usd(p.ldPerDay)} sub={"capped at " + p.ldCapPct + "% per item"} tone="flag"/>
        <Stat k="Retainage" v={p.retainagePct + "%"} sub="released at completion"/>
      </div>

      {gaps.length > 0 && (
        <div className="banner brass" style={{marginTop:16}}>
          <h4>Paperwork still needed</h4>
          <p>{gaps.join(", ")}. You can read and initial the terms now, but no work can be assigned to you until this is
             on file. Send it to {db.parties.find(x=>x.role==="owner").email}.</p>
        </div>
      )}

      <div className="card pad" style={{marginTop:16, maxWidth:840}}>
        <SectionHead right={<span className="muted" style={{fontSize:11}}>{done.length} of {TERMS.length} initialled</span>}>
          Terms · version {TERMS_VERSION}
        </SectionHead>

        {/* Asked for first, because every Initial button below is dead until it
            is filled in. It used to sit at the foot of the page, under the ten
            clauses it unlocks — so the first thing anybody tried to do failed,
            with the explanation out of sight. */}
        <div className="initialsbar">
          <label style={{maxWidth:170}}>
            <div className="eyebrow">Your initials</div>
            <input className="field" maxLength={4} value={initials} autoFocus
                   style={{textTransform:"uppercase",letterSpacing:".12em",fontWeight:600}}
                   onChange={e=>setInitials(e.target.value)} placeholder="RJ"/>
          </label>
          <div className="muted" style={{fontSize:12,flex:1,minWidth:220,lineHeight:1.5}}>
            {initials.trim().length < 2
              ? <React.Fragment><b>Start here.</b> Put your initials in, then initial each clause
                  below to say you have read it.</React.Fragment>
              : <React.Fragment>Signing as <strong>{me.contact}</strong> for <strong>{me.name}</strong>.
                  {done.length < TERMS.length
                    ? " " + (TERMS.length - done.length) + " clause" +
                      (TERMS.length - done.length === 1 ? "" : "s") + " left."
                    : " All ten initialled."}</React.Fragment>}
          </div>
        </div>
        {TERMS.map((t, i) => {
          const on = done.includes(t.id);
          const body = t.id === "penalties"
            ? t.body.replace("at the rate shown on that item", "at " + usd(p.ldPerDay) + " per day, capped at " + p.ldCapPct + "% of that line item's value")
            : t.id === "retainage" ? t.body.replace("Retainage is", p.retainagePct + "% retainage is") : t.body;
          return (
            <div key={t.id} className={"clause " + (on ? "on" : "")}>
              <div className="clausen mono">{String(i+1).padStart(2,"0")}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13.5,letterSpacing:"-.01em"}}>{t.title}</div>
                <p style={{fontSize:12.5,margin:"4px 0 0",lineHeight:1.5}}>{body}</p>
              </div>
              <button className={"initbox " + (on ? "on" : "")} onClick={()=>toggle(t.id)}
                      disabled={initials.trim().length < 2}
                      title={initials.trim().length < 2 ? "Put your initials in at the top first" : "Initial this clause"}>
                {on ? initials.trim().toUpperCase().slice(0,3) : "Initial"}
              </button>
            </div>
          );
        })}

        <div style={{marginTop:16,display:"flex",gap:14,alignItems:"flex-end",flexWrap:"wrap"}}>
          <div className="muted" style={{fontSize:12,flex:1,minWidth:220}}>
            Signing as <strong>{me.contact}</strong> for <strong>{me.name}</strong>.
            {done.length < TERMS.length && " " + (TERMS.length - done.length) + " clause(s) left."}
          </div>
        </div>

        <div className="toolbar">
          <button className="btn go" disabled={!complete || busy} onClick={async()=>{
            setBusy(true);
            await api.acknowledgeTerms({ partyId: meId, initials: initials.trim().toUpperCase(), clauses: done, actor: me.contact });
            await refresh(); setBusy(false); setView("onboarding");
            say("Terms accepted — your scope is open now");
          }}>Accept the terms</button>
          {!complete && <span className="muted" style={{fontSize:12}}>Initial every clause to continue.</span>}
        </div>
        <p className="muted" style={{fontSize:11.5,marginTop:10,marginBottom:0}}>
          This records that you read and understood the terms. The agreement itself is executed separately through
          Adobe Sign — the owner will send it to {me.email || "your email"}.
        </p>
      </div>

      {/* The other gate. Both have to be through before any scope is offered,
          and this one is the slower of the two — so it belongs in front of them
          from the start rather than after the clauses are done. */}
      {party && <Paperwork db={db} party={party} canReview={false} say={say} refresh={refresh}/>}
    </React.Fragment>
  );
}

/* The record once someone has been onboarded, plus the Adobe Sign hand-off. */
function OnboardingRecord({ db, party, me, meId, role, say, refresh }) {
  const ob = onboardingOf(party);
  const p = db.project;
  const isOwner = role === "owner";
  const [adobe, setAdobe] = useState(ob.adobe || { agreementId:"", status:"draft", sentAt:null, signedAt:null });
  const [busy, setBusy] = useState(false);
  const mine = db.lineItems.filter(i => i.assignment && i.assignment.partyId === party.id);

  const doc = () => {
    const rows = mine.map(i => `<tr><td>${i.no}</td><td>${i.name}</td><td style="text-align:right">${usd(i.scheduledValue)}</td><td>${fmtDate(i.plannedFinish)}</td><td style="text-align:right">${usd(i.ldPerDay != null ? i.ldPerDay : p.ldPerDay)}/day</td></tr>`).join("");
    const clauses = TERMS.map((t,i) => `<li><strong>${t.title}.</strong> ${t.body}<span style="float:right;font-family:monospace">${ob.clauses.includes(t.id) ? ob.initials : "____"}</span></li>`).join("");
    return `<!doctype html><html><head><meta charset="utf-8"><title>Contractor Agreement — ${party.name}</title>
<style>body{font:13px/1.55 Georgia,serif;max-width:760px;margin:36px auto;padding:0 20px;color:#161D1B}
h1{font-size:19px;margin:0 0 4px}h2{font-size:13px;text-transform:uppercase;letter-spacing:.12em;margin:26px 0 8px;border-bottom:1px solid #999;padding-bottom:4px}
table{width:100%;border-collapse:collapse;font-size:12px}td,th{border-bottom:1px solid #ccc;padding:6px 4px;text-align:left}
ol{padding-left:20px}li{margin-bottom:10px}.sig{margin-top:40px;display:flex;gap:40px}.sig div{flex:1;border-top:1px solid #333;padding-top:5px;font-size:11px}</style></head><body>
<h1>Contractor Agreement — Scope, Schedule and Penalties</h1>
<div>${p.name} · ${p.address}</div>
<h2>Parties</h2><div>Owner: ${db.parties.find(x=>x.role==="owner").name}<br>Contractor: ${party.name} (${party.trade}) · ${party.contact}<br>Licence ${party.licenseNo || "—"} · Insurance through ${fmtDate(party.coiExpires)}</div>
<h2>Scope and schedule</h2><table><tr><th>Item</th><th>Description of work</th><th style="text-align:right">Value</th><th>Finish by</th><th style="text-align:right">Penalty</th></tr>${rows}
<tr><td></td><td><strong>Total</strong></td><td style="text-align:right"><strong>${usd(mine.reduce((s,i)=>s+i.scheduledValue,0))}</strong></td><td colspan="2"></td></tr></table>
<h2>Terms (v${TERMS_VERSION}) — initialled</h2><ol>${clauses}</ol>
<h2>Payment</h2><div>${p.retainagePct}% retainage withheld from each payment. No line item is released for payment until every required signature is on file: ${p.requiredSignoffs.map(r=>ROLES[r].label).join(", ")}.</div>
<div class="sig"><div>Contractor — ${party.contact}, ${party.name}<br>Date</div><div>Owner — ${db.parties.find(x=>x.role==="owner").contact}<br>Date</div></div>
<p style="margin-top:26px;font-size:10.5px;color:#666">Generated ${fmtStamp(new Date().toISOString())}. Acknowledgment record ${ob.hash ? "#"+ob.hash : "—"}. This document reflects what was shown and initialled in Draw Control; it is not a substitute for review by counsel.</p>
</body></html>`;
  };

  return (
    <React.Fragment>
      <h1 className="pagetitle">{isOwner ? party.name + " — onboarding record" : "Your agreement"}</h1>
      <p className="pagesub">{isOwner
        ? "What they were shown, what they initialled, and where the executed agreement stands."
        : "What you accepted and when. Keep a copy."}</p>

      <div className="card pad" style={{marginTop:16, maxWidth:820}}>
        <SectionHead>Terms acknowledgment</SectionHead>
        <div className="kv">
          <div><div className="k">Status</div><div className="v" style={{fontSize:13,color:"var(--verdigris)"}}>
            {ob.status === "signed" ? "Signed" : "Initialled"}</div></div>
          <div><div className="k">Terms version</div><div className="v" style={{fontSize:13}}>{ob.termsVersion}</div></div>
          <div><div className="k">Initials</div><div className="v">{ob.initials}</div></div>
          <div><div className="k">Clauses</div><div className="v">{ob.clauses.length} / {TERMS.length}</div></div>
          <div style={{gridColumn:"span 2"}}><div className="k">Acknowledged</div>
            <div className="v" style={{fontSize:13}}>{fmtStamp(ob.acknowledgedAt)}</div></div>
          <div style={{gridColumn:"span 2"}}><div className="k">Record hash</div>
            <div className="v" style={{fontSize:13}}>#{ob.hash || fnv1a(party.id + ob.termsVersion)}</div></div>
        </div>
        <div className="stamprow" style={{marginTop:14}}>
          <Stamp role="contractor" org={party.name}
                 approval={{ id: party.id, who: party.contact, at: ob.acknowledgedAt,
                             hash: ob.hash || fnv1a(party.id + ob.termsVersion) }}/>
        </div>
        <div className="toolbar">
          <button className="btn ghost" onClick={()=>download("agreement-" + party.name.replace(/\W+/g,"-").toLowerCase() + ".html", doc(), "text/html")}>
            Download the agreement
          </button>
        </div>
        <p className="muted" style={{fontSize:11.5,marginBottom:0}}>
          Open it and print to PDF, then push that PDF through Adobe Sign. It carries the scope table, the dates, the
          penalty rate per line, and which clauses were initialled.
        </p>
      </div>

      <div className="card pad" style={{marginTop:16, maxWidth:820}}>
        <SectionHead right={<span className={"tag " + (adobe.status==="signed"?"g":adobe.status==="sent"?"p":"n")}>{adobe.status}</span>}>
          Adobe Sign
        </SectionHead>
        {isOwner ? (
          <React.Fragment>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
              <label style={{gridColumn:"span 2"}}><div className="eyebrow">Agreement ID</div>
                <input className="field" value={adobe.agreementId} placeholder="CBJCHBCAABAA…"
                       onChange={e=>setAdobe(a=>({...a, agreementId:e.target.value}))}/></label>
              <label><div className="eyebrow">Status</div>
                <select className="field" value={adobe.status} onChange={e=>setAdobe(a=>({...a, status:e.target.value}))}>
                  <option value="draft">Draft</option><option value="sent">Out for signature</option>
                  <option value="signed">Signed</option><option value="cancelled">Cancelled</option>
                </select></label>
            </div>
            <div className="toolbar">
              <button className="btn" disabled={busy||!adobe.agreementId} onClick={async()=>{
                setBusy(true);
                await api.recordAdobe({ partyId: party.id, role, actor: me.contact, adobe: { ...adobe,
                  sentAt: adobe.status === "sent" ? new Date().toISOString() : adobe.sentAt,
                  signedAt: adobe.status === "signed" ? new Date().toISOString() : adobe.signedAt } });
                await refresh(); setBusy(false); say("Agreement status recorded");
              }}>Record status</button>
            </div>
            <p className="muted" style={{fontSize:11.5,marginBottom:0}}>
              Entering it by hand works fine while this is a prototype. Once there's a backend, an Adobe Sign webhook
              writes this field automatically — the endpoint is in the backend contract.
            </p>
          </React.Fragment>
        ) : (
          <p style={{fontSize:12.5,margin:0}}>
            {adobe.status === "signed"
              ? "Signed " + fmtStamp(adobe.signedAt) + ". Agreement " + adobe.agreementId + "."
              : adobe.status === "sent" ? "The agreement is in your inbox for signature."
              : "The owner will send the agreement for signature."}
          </p>
        )}
      </div>
    </React.Fragment>
  );
}

/* ---------------------------------------------------------------- Timeline */
/* Bars against a calendar, so slippage is something you see rather than
   something you compute. */
function Timeline({ db, role, meId, items, setSelId }) {
  const p = db.project;
  const [groupBy, setGroupBy] = useState(ROLES[role].scoped ? "division" : "party");
  const [scale, setScale] = useState("week");

  const dated = items.filter(i => i.plannedStart && i.plannedFinish);
  const bounds = useMemo(() => {
    if (!dated.length) return null;
    const starts = dated.map(i => +new Date(i.plannedStart));
    const ends = dated.map(i => Math.max(+new Date(i.plannedFinish), +TODAY));
    const min = new Date(Math.min(...starts, +new Date(p.startDate || TODAY)));
    const max = new Date(Math.max(...ends, +new Date(p.contractCompletion || TODAY)));
    min.setDate(min.getDate() - 7); max.setDate(max.getDate() + 21);
    return { min, max, days: Math.max(1, Math.round((max - min) / 86400000)) };
  }, [dated, p]);

  const groups = useMemo(() => {
    if (!bounds) return [];
    const key = (it) => groupBy === "party"
      ? (it.assignment ? partyName(db, it.assignment.partyId) : "No contractor assigned")
      : it.division;
    const map = new Map();
    dated.forEach(it => { const k = key(it); if (!map.has(k)) map.set(k, []); map.get(k).push(it); });
    return [...map.entries()]
      .map(([k, v]) => [k, v.sort((a,b) => a.plannedStart < b.plannedStart ? -1 : 1)])
      .sort((a,b) => a[0] < b[0] ? -1 : 1);
  }, [dated, groupBy, db, bounds]);

  // Open somewhere useful rather than pinned to the far left of the calendar.
  const scroller = useRef();
  const jumpToToday = useCallback(() => {
    const el = scroller.current;
    if (!el || !bounds) return;
    const px = scale === "week" ? 5.2 : scale === "month" ? 2.4 : 10;
    const x = ((new Date(iso(TODAY)) - bounds.min) / 86400000) * px;
    el.scrollLeft = Math.max(0, x - el.clientWidth * 0.55);
  }, [bounds, scale]);
  useEffect(() => { jumpToToday(); }, [jumpToToday, groupBy]);

  if (!bounds) return <Empty title="Nothing to plot yet"
    body="Line items need a planned start and finish date before they show on a timeline. Add dates on the schedule of values or in your import."/>;

  const pxPerDay = scale === "week" ? 5.2 : scale === "month" ? 2.4 : 10;
  const width = Math.max(680, bounds.days * pxPerDay);
  const left = (d) => ((new Date(d) - bounds.min) / 86400000) * pxPerDay;
  const todayX = left(iso(TODAY));

  // month ticks across the top
  const ticks = [];
  const cur = new Date(bounds.min.getFullYear(), bounds.min.getMonth(), 1);
  while (cur <= bounds.max) {
    ticks.push({ x: left(iso(cur)), label: cur.toLocaleDateString("en-US",{month:"short",year:"2-digit"}) });
    cur.setMonth(cur.getMonth() + 1);
  }

  return (
    <React.Fragment>
      <h1 className="pagetitle">Timeline</h1>
      <p className="pagesub">Planned dates against today. The solid part of each bar is work actually in place, so a
        short bar sitting left of the line is a line item falling behind.</p>

      <div className="toolbar noprint">
        <select className="field" style={{maxWidth:190}} value={groupBy} onChange={e=>setGroupBy(e.target.value)} aria-label="Group by">
          <option value="party">Group by contractor</option>
          <option value="division">Group by division</option>
        </select>
        <select className="field" style={{maxWidth:150}} value={scale} onChange={e=>setScale(e.target.value)} aria-label="Zoom">
          <option value="month">Whole job</option>
          <option value="week">Standard</option>
          <option value="day">Zoomed in</option>
        </select>
        <button className="btn ghost sm" onClick={jumpToToday}>Jump to today</button>
        <span style={{display:"flex",gap:12,flexWrap:"wrap",marginLeft:4}}>
          <span className="key"><i style={{background:"var(--verdigris)"}}/>In place</span>
          <span className="key"><i style={{background:"var(--paper-3)"}}/>Remaining</span>
          <span className="key"><i style={{background:"var(--oxide)"}}/>Past its finish date</span>
          <span className="key"><i style={{background:"var(--brass)",width:2}}/>Today</span>
        </span>
      </div>

      <div className="tablewrap" style={{padding:0}} ref={scroller}>
        <div style={{display:"flex",minWidth:"min-content"}}>
          <div className="gutter">
            <div className="ghead">Line item</div>
            {groups.map(([g, list]) => (
              <React.Fragment key={g}>
                <div className="grow">{g}</div>
                {list.map(it => (
                  <button key={it.id} className="gitem" onClick={()=>setSelId(it.id)} title={it.name}>
                    <span className="mono muted" style={{fontSize:10}}>{it.no}</span>
                    <span className="gname">{it.name}</span>
                  </button>
                ))}
              </React.Fragment>
            ))}
          </div>

          <div style={{flex:"0 0 auto"}}>
            <div style={{width, position:"relative"}}>
              <div className="ghead" style={{position:"relative"}}>
                {ticks.map((t,i) => <span key={i} className="tick" style={{left:t.x}}>{t.label}</span>)}
              </div>
              <div style={{position:"absolute",top:0,bottom:0,left:todayX,width:2,background:"var(--brass)",zIndex:3}}/>
              {p.contractCompletion && (
                <div style={{position:"absolute",top:0,bottom:0,left:left(p.contractCompletion),width:1,
                  background:"var(--oxide)",zIndex:2,opacity:.65}} title={"Contract completion " + fmtDate(p.contractCompletion)}/>
              )}
              {ticks.map((t,i) => <div key={"g"+i} className="gridline" style={{left:t.x}}/>)}

              {groups.map(([g, list]) => (
                <React.Fragment key={g}>
                  <div className="grow"/>
                  {list.map(it => {
                    const x = left(it.plannedStart);
                    const w = Math.max(6, left(it.plannedFinish) - x);
                    const late = daysLate(it);
                    const doneW = w * clamp(it.percent,0,100) / 100;
                    // The red tail is exactly the days late, not the distance to today —
                    // a finished item that landed 4 days late shows 4 days, not 4 months.
                    const overW = late > 0 && it.stage !== "closed" ? Math.max(3, late * pxPerDay) : 0;
                    return (
                      <div className="gitem" key={it.id} style={{position:"relative"}}>
                        <button className="bar2" style={{left:x, width:w}} onClick={()=>setSelId(it.id)}
                          title={it.name + " · " + fmtDate(it.plannedStart) + " → " + fmtDate(it.plannedFinish) +
                                 " · " + it.percent + "% · " + usd(it.scheduledValue) + (late?" · "+late+"d late":"")}>
                          <i className="fill" style={{width: doneW}}/>
                          <span className="blabel">{it.percent}%</span>
                        </button>
                        {overW > 0 && <span className="over" style={{left:left(it.plannedFinish), width:overW}}
                          title={late + " days past the accepted finish date"}/>}
                        {openPunch(it).length > 0 && (
                          <span className="pdot" style={{left: x + w + (overW||0) + 5,
                            background: blockingPunch(it).length ? "var(--oxide)" : "var(--brass)"}}
                            title={openPunch(it).length + " open punch item(s)"}/>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="muted" style={{fontSize:12,marginTop:12}}>
        The red vertical line is contract completion ({fmtDate(p.contractCompletion)}). Anything with a bar extending
        past it is, by definition, pushing the whole job.
      </p>
    </React.Fragment>
  );
}

/* -------------------------------------------------------------- Daily log */
function DailyLog({ db, role, me, meId, items, say, refresh, setSelId }) {
  const [posting, setPosting] = useState(false);
  const [party, setParty] = useState("all");
  const canPost = role === "contractor";
  const logs = (db.dailyLogs || [])
    .filter(l => ROLES[role].scoped ? l.partyId === meId : (party === "all" || l.partyId === party))
    .slice().sort((a,b) => a.date < b.date ? 1 : -1);
  const today = iso(TODAY);
  const postedToday = logs.some(l => l.date === today && l.partyId === meId);

  return (
    <React.Fragment>
      <h1 className="pagetitle">Daily log</h1>
      <p className="pagesub">What happened on site, day by day. This is the record that fills the gap between a line
        item starting and a line item being submitted.</p>

      {canPost && !postedToday && !posting && (
        <div className="banner brass" style={{marginTop:16}}>
          <h4>Nothing posted for today</h4>
          <p>A couple of lines and a photo is enough. It takes a minute and it's what stops a schedule argument three
             months from now.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}} onClick={()=>setPosting(true)}>Post today's update</button></p>
        </div>
      )}

      <div className="toolbar noprint">
        {canPost && <button className="btn" onClick={()=>setPosting(x=>!x)}>{posting ? "Cancel" : "Post an update"}</button>}
        {!ROLES[role].scoped && (
          <select className="field" style={{maxWidth:210}} value={party} onChange={e=>setParty(e.target.value)} aria-label="Filter by contractor">
            <option value="all">Every contractor</option>
            {db.parties.filter(x => x.role === "contractor").map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        <span className="muted" style={{fontSize:12}}>{logs.length} entries</span>
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>download("calico-daily-log-" + iso(TODAY) + ".csv",
          toCSV(logs.map(l => ({ ...l, contractor: partyName(db, l.partyId),
            lineItems: (l.lineItemIds||[]).map(id => { const it = db.lineItems.find(x=>x.id===id); return it ? it.no : ""; }).filter(Boolean).join("; "),
            photoCount: (l.photos||[]).length })),
          [["date","Date"],["contractor","Contractor"],["who","Posted By"],["crew","Crew On Site"],["hours","Hours"],
           ["weather","Weather"],["summary","Work Performed"],["blockers","Blockers"],["lineItems","Line Items"],["photoCount","Photos"]]))}>
          Export daily log</button>
      </div>

      {posting && <DailyForm {...{db, me, meId, items, refresh, say}} onDone={()=>setPosting(false)}/>}

      {logs.length === 0
        ? <Empty title="No updates yet"
                 body={canPost ? "Post one at the end of the day: who was on site, what got done, and anything holding you up."
                               : "Nothing has been posted for this project yet."}/>
        : logs.map(l => {
          const its = (l.lineItemIds || []).map(id => db.lineItems.find(x => x.id === id)).filter(Boolean);
          return (
            <div className="card pad" key={l.id} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"baseline"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14,letterSpacing:"-.015em"}}>
                    {new Date(l.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
                  </div>
                  <div className="itemmeta">{partyName(db, l.partyId)} · {l.who}
                    {l.crew ? " · " + l.crew + " on site" : ""}{l.hours ? " · " + l.hours + "h" : ""}
                    {l.weather ? " · " + l.weather : ""}</div>
                </div>
                {l.blockers && <span className="tag b">Blocked</span>}
              </div>
              <p style={{fontSize:13,margin:"9px 0 0",lineHeight:1.55}}>{l.summary}</p>
              {l.blockers && (
                <div className="note" style={{borderLeftColor:"var(--oxide)",marginTop:9}}>
                  <div className="meta">Holding us up</div>
                  <div style={{fontSize:12.5}}>{l.blockers}</div>
                </div>
              )}
              {its.length > 0 && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:9}}>
                  {its.map(it => (
                    <button key={it.id} className="btn ghost sm" onClick={()=>setSelId(it.id)}>
                      {it.no} · {it.name.length > 34 ? it.name.slice(0,34) + "…" : it.name}
                    </button>
                  ))}
                </div>
              )}
              {(l.photos || []).length > 0 && (
                <div className="shots" style={{marginTop:10}}>
                  {l.photos.map(ph => (
                    <figure className="shot" key={ph.id} style={{margin:0}}>
                      <img src={ph.url} alt={"Site photo " + l.date}/>
                      <figcaption className="ts">{new Date(ph.capturedAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </div>
          );
        })}
    </React.Fragment>
  );
}

function DailyForm({ db, me, meId, items, refresh, say, onDone }) {
  const [f, setF] = useState({ date: iso(TODAY), crew:"", hours:"", weather:"", summary:"", blockers:"", lineItemIds:[] });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const active = items.filter(i => ["in_progress","submitted","returned"].includes(i.stage));
  const valid = f.summary.trim().length > 5;

  return (
    <div className="card pad" style={{marginBottom:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>Today on site</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <label><div className="eyebrow">Date</div>
          <input className="field" type="date" value={f.date} onChange={e=>set("date",e.target.value)}/></label>
        <label><div className="eyebrow">Crew on site</div>
          <input className="field" type="number" min="0" value={f.crew} onChange={e=>set("crew",e.target.value)} placeholder="4"/></label>
        <label><div className="eyebrow">Hours worked</div>
          <input className="field" type="number" min="0" step="0.5" value={f.hours} onChange={e=>set("hours",e.target.value)} placeholder="8"/></label>
        <label><div className="eyebrow">Weather</div>
          <input className="field" value={f.weather} onChange={e=>set("weather",e.target.value)} placeholder="Hot, scattered storms"/></label>
      </div>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">What got done</div>
        <textarea className="field" rows={3} value={f.summary} onChange={e=>set("summary",e.target.value)}
          placeholder="Pulled branch circuits in the east vendor stalls. Set boxes at stalls 1 and 2."/></label>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Anything holding you up</div>
        <textarea className="field" rows={2} value={f.blockers} onChange={e=>set("blockers",e.target.value)}
          placeholder="Waiting on the lighting package before we can trim out. Leave blank if nothing."/></label>

      {active.length > 0 && (
        <div style={{marginTop:14}}>
          <div className="eyebrow" style={{marginBottom:6}}>Which line items this touched</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {active.map(it => {
              const on = f.lineItemIds.includes(it.id);
              return (
                <button key={it.id} className={"chip " + (on?"on":"")}
                  onClick={()=>set("lineItemIds", on ? f.lineItemIds.filter(x=>x!==it.id) : f.lineItemIds.concat(it.id))}>
                  {it.no} · {it.name.length > 26 ? it.name.slice(0,26)+"…" : it.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{marginTop:14}}>
        <button className="btn ghost sm" onClick={()=>ref.current.click()}>
          {files.length ? files.length + " photo(s) attached" : "Add photos"}</button>
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden
               onChange={e=>{ setFiles([...e.target.files].slice(0,8)); e.target.value=""; }}/>
      </div>

      <div className="toolbar">
        <button className="btn go" disabled={!valid||busy} onClick={async()=>{
          setBusy(true);
          const entry = await api.addDailyLog({ log:{ ...f, crew: Number(f.crew)||0, hours: Number(f.hours)||0, photos:[] },
            actor: me.contact, partyId: meId });
          for (const file of files) {
            const url = await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(file); });
            await api.addDailyPhoto({ logId: entry.id, photo:{ url, filename:file.name, capturedAt:new Date().toISOString(), by:me.contact } });
          }
          await refresh(); setBusy(false); onDone(); say("Update posted for " + fmtDate(f.date));
        }}>Post the update</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- Funding (owner) */
function Funding({ db, role, me, meId, say, refresh, setView, setSelId }) {
  const [editing, setEditing] = useState(null);   // need object or "new"
  const [pledging, setPledging] = useState(null); // pledge object or "new"
  const [tab, setTab] = useState("needs");
  const t = capitalTotals(db);
  const needs = needsOf(db).slice().sort((a,b) => (a.order||0) - (b.order||0));

  if (role !== "owner") return <Empty title="Owner only"
    body="Funding needs and the investor pipeline are managed by the owner."/>;

  return (
    <React.Fragment>
      <h1 className="pagetitle">Funding</h1>
      <p className="pagesub">What the project still needs, who is in, and where each commitment actually stands.
        Only money that has cleared counts as raised.</p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Still needed" v={usd(t.remaining)} sub={`across ${t.open} open need${t.open===1?"":"s"}`} tone={t.remaining?"flag":"good"}/>
        <Stat k="Received" v={usd(t.received)} sub={pct(t.pct) + " of " + usd(t.need)} tone="good"/>
        <Stat k="Signed, not yet funded" v={usd(t.committed - t.received)}/>
        <Stat k="Interest expressed" v={usd(t.interested - t.committed)} sub="not committed"/>
        <Stat k="In the pipeline" v={pledgesOf(db).filter(p=>p.status!=="declined").length} sub="conversations"/>
      </div>

      <div className="steps noprint" style={{marginTop:16}}>
        {[["needs","Needs"],["pipeline","Investor pipeline"],["milestones","Milestones"]].map(([k,l],i) => (
          <button key={k} className={"stepbtn " + (tab===k?"on":"")} onClick={()=>setTab(k)}>
            <span className="stepno">{i+1}</span><span>{l}</span>
          </button>
        ))}
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>setView("investorview")}>See the investor view</button>
      </div>

      {tab === "needs" && (
        <React.Fragment>
          <div className="toolbar noprint">
            <button className="btn" onClick={()=>setEditing(editing === "new" ? null : "new")}>
              {editing === "new" ? "Cancel" : "Add a funding need"}</button>
            <button className="btn ghost" onClick={()=>download("calico-funding-needs-" + iso(TODAY) + ".csv",
              toCSV(needs.map(n => { const pr = needProgress(db, n); return { name:n.name,
                category:NEED_CATEGORIES[n.category], amount:n.amount, received:pr.received,
                committed:pr.committed - pr.received, interested:pr.interested - pr.committed,
                remaining:pr.remaining, deadline:n.deadline||"", why:n.why, consequence:n.consequence,
                items:(n.lineItemIds||[]).map(id=>{const i=db.lineItems.find(x=>x.id===id);return i?i.no:"";}).filter(Boolean).join("; "),
                status:n.status, visibility:n.visibility }; }),
              [["name","Need"],["category","Category"],["amount","Amount"],["received","Received"],
               ["committed","Signed Not Funded"],["interested","Interest Only"],["remaining","Still Needed"],
               ["deadline","Needed By"],["why","Why It Matters"],["consequence","If It Doesn't Happen"],
               ["items","Linked Line Items"],["status","Status"],["visibility","Shown To Investors"]]))}>
              Export needs</button>
          </div>

          {editing && <NeedForm {...{db, me, need: editing === "new" ? null : editing, refresh, say}}
                                onDone={()=>setEditing(null)}/>}

          {needs.length === 0 && !editing
            ? <Empty title="No funding needs yet"
                     body="Add what the project needs beyond the construction contract — carrying costs, equipment, inventory, payroll. This is what investors see."/>
            : needs.map(n => {
              const pr = needProgress(db, n);
              const its = db.lineItems.filter(i => (n.lineItemIds||[]).includes(i.id));
              return (
                <div className="card pad" key={n.id} style={{marginBottom:10,
                  borderLeft:"3px solid var(--" + (n.urgent ? "oxide" : pr.received >= n.amount ? "verdigris" : "rule") + ")"}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
                    <div style={{minWidth:200,flex:1}}>
                      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                        <span style={{fontWeight:700,fontSize:14.5,letterSpacing:"-.015em"}}>{n.name}</span>
                        {n.urgent && <span className="tag b">Time-sensitive</span>}
                        {n.visibility !== "investor" && <span className="tag n">Internal only</span>}
                        {pr.received >= n.amount && <span className="tag g">Funded</span>}
                      </div>
                      <div className="itemmeta">{NEED_CATEGORIES[n.category]}
                        {n.deadline ? " · needed by " + fmtDate(n.deadline) : ""}
                        {pr.count ? " · " + pr.count + " investor" + (pr.count===1?"":"s") : ""}</div>
                      <p style={{fontSize:12.5,margin:"7px 0 0",maxWidth:600,lineHeight:1.5}}>{n.why}</p>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div className="num" style={{fontSize:18,fontWeight:600}}>{usd(pr.received)}</div>
                      <div className="itemmeta">of {usd(n.amount)}</div>
                    </div>
                  </div>

                  <div className="fbar" style={{marginTop:11}}>
                    <i className="f-recv" style={{width: clamp(pr.pctReceived,0,100) + "%"}}/>
                    <i className="f-comm" style={{width: clamp(pr.pctCommitted - pr.pctReceived,0,100) + "%"}}/>
                  </div>
                  <div style={{display:"flex",gap:14,flexWrap:"wrap",marginTop:6}}>
                    <span className="key"><i style={{background:"var(--verdigris)"}}/>{usd(pr.received)} received</span>
                    <span className="key"><i style={{background:"var(--brass)"}}/>{usd(pr.committed - pr.received)} signed</span>
                    <span className="key"><i style={{background:"var(--paper-3)"}}/>{usd(pr.remaining)} still needed</span>
                  </div>

                  {its.length > 0 && (
                    <div style={{marginTop:11,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                      <span className="eyebrow">Related work</span>
                      {its.map(i => (
                        <button key={i.id} className="btn ghost sm" onClick={()=>setSelId(i.id)}>
                          {i.no} · {i.percent}%</button>
                      ))}
                    </div>
                  )}

                  <div className="toolbar noprint" style={{marginTop:10,marginBottom:0}}>
                    <button className="btn ghost sm" onClick={()=>setEditing(n)}>Edit</button>
                    <button className="btn ghost sm" onClick={()=>{ setTab("pipeline"); setPledging({ needId:n.id }); }}>
                      Log an investor</button>
                    {n.status === "open"
                      ? <button className="btn ghost sm" onClick={async()=>{
                          await api.closeNeed({ id:n.id, status:"closed", actor:me.contact });
                          await refresh(); say(n.name + " closed"); }}>Close this need</button>
                      : <button className="btn ghost sm" onClick={async()=>{
                          await api.closeNeed({ id:n.id, status:"open", actor:me.contact });
                          await refresh(); say(n.name + " reopened"); }}>Reopen</button>}
                  </div>
                </div>
              );
            })}
        </React.Fragment>
      )}

      {tab === "pipeline" && (
        <Pipeline {...{db, me, pledging, setPledging, refresh, say}}/>
      )}

      {tab === "milestones" && (
        <Milestones {...{db, me, role, refresh, say, setSelId, setView}}/>
      )}
    </React.Fragment>
  );
}

function NeedForm({ db, me, need, refresh, say, onDone }) {
  const [f, setF] = useState(need ? { ...need } : { name:"", category:"carrying", amount:"",
    why:"", consequence:"", deadline:"", urgent:false, lineItemIds:[], visibility:"investor" });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const valid = f.name.trim().length > 2 && Number(f.amount) > 0 && f.why.trim().length > 5;

  return (
    <div className="card pad" style={{marginBottom:12, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>{need ? "Edit " + need.name : "What does the project need?"}</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Name it the way an investor would say it</div>
          <input className="field" value={f.name} onChange={e=>set("name",e.target.value)}
                 placeholder="Commercial kitchen & operating equipment"/></label>
        <label><div className="eyebrow">Category</div>
          <select className="field" value={f.category} onChange={e=>set("category",e.target.value)}>
            {Object.entries(NEED_CATEGORIES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
        <label><div className="eyebrow">Amount needed</div>
          <input className="field" type="number" min="0" step="500" value={f.amount}
                 onChange={e=>set("amount",e.target.value)}/></label>
        <label><div className="eyebrow">Needed by</div>
          <input className="field" type="date" value={f.deadline||""} onChange={e=>set("deadline",e.target.value)}/></label>
      </div>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">What it unlocks</div>
        <textarea className="field" rows={2} value={f.why} onChange={e=>set("why",e.target.value)}
          placeholder="Phase 1 kitchen equipment behind the hood that is already installed. Without it the café and the two vendor stalls cannot open."/></label>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">What happens if it doesn't come together</div>
        <textarea className="field" rows={2} value={f.consequence||""} onChange={e=>set("consequence",e.target.value)}
          placeholder="Be straight about this. It's the part people actually read."/></label>

      <div style={{marginTop:14}}>
        <div className="eyebrow" style={{marginBottom:6}}>Related construction line items (optional)</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:130,overflow:"auto"}}>
          {db.lineItems.map(it => {
            const on = (f.lineItemIds||[]).includes(it.id);
            return (
              <button key={it.id} className={"chip " + (on?"on":"")}
                onClick={()=>set("lineItemIds", on ? f.lineItemIds.filter(x=>x!==it.id) : (f.lineItemIds||[]).concat(it.id))}>
                {it.no} · {it.name.length > 24 ? it.name.slice(0,24)+"…" : it.name}
              </button>
            );
          })}
        </div>
        <p className="muted" style={{fontSize:11.5,marginTop:7,marginBottom:0,maxWidth:600}}>
          This shows investors the work a need sits next to. It does not mean their contribution pays that contractor —
          the bank's construction loan does that.
        </p>
      </div>

      <div style={{display:"flex",gap:18,flexWrap:"wrap",marginTop:14}}>
        <label style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="checkbox" checked={!!f.urgent} onChange={e=>set("urgent",e.target.checked)}/>
          <span style={{fontSize:12.5,fontWeight:500}}>Time-sensitive</span></label>
        <label style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="checkbox" checked={f.visibility==="investor"}
                 onChange={e=>set("visibility", e.target.checked ? "investor" : "internal")}/>
          <span style={{fontSize:12.5,fontWeight:500}}>Show this to investors</span></label>
      </div>

      <div className="toolbar">
        <button className="btn go" disabled={!valid||busy} onClick={async()=>{
          setBusy(true);
          await api.saveNeed({ need:{ ...f, amount:Number(f.amount) }, actor:me.contact });
          await refresh(); setBusy(false); onDone(); say(need ? "Need updated" : f.name + " added");
        }}>{need ? "Save changes" : "Add the need"}</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* The pipeline. Kenneth's actual flow: interest comes in, he calls, the
   agreement goes out through Adobe Sign, then money arrives. */
function Pipeline({ db, me, pledging, setPledging, refresh, say }) {
  const rows = pledgesOf(db).slice().sort((a,b) => a.at < b.at ? 1 : -1);
  const byStatus = (k) => rows.filter(r => r.status === k);

  return (
    <React.Fragment>
      <div className="toolbar noprint">
        <button className="btn" onClick={()=>setPledging(pledging ? null : {})}>
          {pledging ? "Cancel" : "Log an investor conversation"}</button>
        <button className="btn ghost" onClick={()=>download("calico-investor-pipeline-" + iso(TODAY) + ".csv",
          toCSV(rows.map(r => ({ ...r, pathLabel:PATHS[r.path].label,
            statusLabel:PLEDGE_STATUS[r.status].label,
            need: r.needId ? (needsOf(db).find(n=>n.id===r.needId)||{}).name || "" : "General" })),
          [["label","Investor"],["pathLabel","Path"],["amount","Amount"],["statusLabel","Status"],
           ["need","Directed Toward"],["at","Last Update"],["note","Note"]]))}>Export pipeline</button>
      </div>

      {pledging && <PledgeForm {...{db, me, pledge:pledging, refresh, say}} onDone={()=>setPledging(null)}/>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:1,
        background:"var(--rule)",border:"1px solid var(--rule)",borderRadius:3,overflow:"hidden",marginBottom:16}}>
        {Object.entries(PLEDGE_STATUS).filter(([k])=>k!=="declined").map(([k,v]) => {
          const g = byStatus(k);
          return (
            <div key={k} style={{background:"#FBFAF7",padding:"10px 12px"}}>
              <div className="k eyebrow">{v.label}</div>
              <div className="num" style={{fontSize:17,fontWeight:600,marginTop:3,
                color: k==="received" ? "var(--verdigris)" : "inherit"}}>
                {usd(g.reduce((s,x)=>s+x.amount,0))}</div>
              <div className="itemmeta">{g.length} investor{g.length===1?"":"s"}</div>
            </div>
          );
        })}
      </div>

      {rows.length === 0
        ? <Empty title="Nobody in the pipeline yet"
                 body="Log each conversation as it happens — interest, agreement sent, signed, funds received. The needs above only count money that has cleared."/>
        : (
        <div className="tablewrap">
          <table className="sov">
            <thead><tr>
              <th className="plain">Investor</th><th className="plain">Path</th>
              <th className="plain">Amount</th><th className="plain">Directed toward</th>
              <th className="plain">Last update</th><th className="plain">Status</th>
              <th className="plain">Move it along</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const need = r.needId ? needsOf(db).find(n => n.id === r.needId) : null;
                const order = ["interested","sent","signed","received"];
                const next = order[order.indexOf(r.status) + 1];
                return (
                  <tr key={r.id}>
                    <td><button style={{background:"transparent",border:0,padding:0,textAlign:"left"}}
                        onClick={()=>setPledging(r)}>
                        <div className="itemname">{r.label}</div>
                        {r.note && <div className="itemmeta">{r.note.slice(0,52)}</div>}</button></td>
                    <td style={{fontSize:12}}>{PATHS[r.path].label}
                      <div className="itemmeta">{PATHS[r.path].equity ? "Equity" : "Debt"}</div></td>
                    <td className="r num">{usd(r.amount)}</td>
                    <td style={{fontSize:12}}>{need ? need.name : <span className="muted">General</span>}</td>
                    <td className="num" style={{fontSize:12}}>{fmtDate(iso(new Date(r.at)))}</td>
                    <td><span className={"tag " + PLEDGE_STATUS[r.status].tag}>{PLEDGE_STATUS[r.status].label}</span></td>
                    <td className="noprint">
                      {next && (
                        <button className="btn ghost sm" onClick={async()=>{
                          await api.savePledge({ pledge:{ ...r, status:next }, actor:me.contact, role:"owner" });
                          await refresh(); say(r.label + " → " + PLEDGE_STATUS[next].label.toLowerCase());
                        }}>{PLEDGE_STATUS[next].label}</button>
                      )}
                      {r.status !== "declined" && r.status !== "received" && (
                        <button className="btn ghost sm" style={{marginLeft:5}} onClick={async()=>{
                          await api.savePledge({ pledge:{ ...r, status:"declined" }, actor:me.contact, role:"owner" });
                          await refresh(); say(r.label + " passed");
                        }}>Passed</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </React.Fragment>
  );
}

function PledgeForm({ db, me, pledge, refresh, say, onDone }) {
  const [f, setF] = useState({ label:"", path:"fundI", amount:"", needId:"", status:"interested", note:"",
    ...(pledge && pledge.id ? pledge : {}), ...(pledge && pledge.needId && !pledge.id ? { needId: pledge.needId } : {}) });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const path = PATHS[f.path];
  const valid = String(f.label).trim().length > 1 && Number(f.amount) > 0;
  const capWarn = f.path === "fundI" && Number(f.amount) > 2500;
  const minWarn = f.path === "fundII" && Number(f.amount) < 2500;

  return (
    <div className="card pad" style={{marginBottom:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>{pledge && pledge.id ? "Update " + pledge.label : "Log an investor conversation"}</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <label><div className="eyebrow">Who</div>
          <input className="field" value={f.label} onChange={e=>set("label",e.target.value)}
                 placeholder="Investor 004"/>
          <div className="muted" style={{fontSize:11,marginTop:3}}>A reference, not a full record. Contact details belong in your CRM, not here.</div></label>
        <label><div className="eyebrow">Which path</div>
          <select className="field" value={f.path} onChange={e=>set("path",e.target.value)}>
            {Object.entries(PATHS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
        <label><div className="eyebrow">Amount</div>
          <input className="field" type="number" min="0" step="500" value={f.amount}
                 onChange={e=>set("amount",e.target.value)}/></label>
        <label><div className="eyebrow">Where it goes</div>
          <select className="field" value={f.needId||""} onChange={e=>set("needId",e.target.value)}>
            <option value="">General — not directed</option>
            {needsOf(db).map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select></label>
        <label><div className="eyebrow">Where it stands</div>
          <select className="field" value={f.status} onChange={e=>set("status",e.target.value)}>
            {Object.entries(PLEDGE_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
      </div>

      <div className="note" style={{marginTop:12,borderLeftColor:"var(--steel, var(--rule))"}}>
        <div className="meta">{path.label}</div>
        <div style={{fontSize:12.5}}>{path.summary} {path.detail}</div>
        <div className="muted" style={{fontSize:11.5,marginTop:4}}>Documents: {path.docs.join(", ")}.</div>
      </div>
      {capWarn && <p style={{fontSize:12,color:"var(--oxide)",margin:"8px 0 0"}}>
        Fund I contributions are capped at $2,500 per investor in the agreement.</p>}
      {minWarn && <p style={{fontSize:12,color:"var(--oxide)",margin:"8px 0 0"}}>
        Fund II has a $2,500 minimum for a half share.</p>}

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Note</div>
        <textarea className="field" rows={2} value={f.note||""} onChange={e=>set("note",e.target.value)}
          placeholder="Where the conversation actually is."/></label>

      <div className="toolbar">
        <button className="btn go" disabled={!valid||busy} onClick={async()=>{
          setBusy(true);
          await api.savePledge({ pledge:{ ...f, amount:Number(f.amount), needId:f.needId||null },
            actor:me.contact, role:"owner" });
          await refresh(); setBusy(false); onDone(); say("Pipeline updated");
        }}>{pledge && pledge.id ? "Save" : "Add to the pipeline"}</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Milestones */
/* Where the two sides of the project meet: something has to be built AND
   funded before it opens. */
function Milestones({ db, me, role, refresh, say, setSelId, setView }) {
  const ms = (db.milestones || []).slice().sort((a,b) => (a.order||0) - (b.order||0));
  return (
    <React.Fragment>
      <p className="muted" style={{fontSize:12.5,maxWidth:660,marginTop:0}}>
        Each of these has two constraints. The bank's loan pays for the construction side; investor capital covers the
        funding side. A milestone opens when both are satisfied, which is the only honest way to show an investor what
        their money is doing.
      </p>
      {ms.length === 0
        ? <Empty title="No milestones yet" body="Milestones tie construction line items to funding needs."/>
        : ms.map(m => {
          const pr = milestoneProgress(db, m);
          const late = m.targetDate && daysBetween(m.targetDate, iso(TODAY)) > 0;
          return (
            <div className="card pad" key={m.id} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"baseline"}}>
                <div>
                  <span style={{fontWeight:700,fontSize:14.5,letterSpacing:"-.015em"}}>{m.name}</span>
                  <div className="itemmeta">{m.targetDate ? "Target " + fmtDate(m.targetDate) : "No target date"}
                    {late ? " · " + daysBetween(m.targetDate, iso(TODAY)) + " days past" : ""}</div>
                </div>
                {late && <span className="tag b">Past target</span>}
              </div>
              <p style={{fontSize:12.5,margin:"8px 0 0",maxWidth:640,lineHeight:1.5}}>{m.description}</p>

              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:14,marginTop:13}}>
                <div>
                  <div className="eyebrow" style={{marginBottom:5}}>Built</div>
                  {pr.build == null
                    ? <div className="muted" style={{fontSize:12}}>No construction dependency.</div>
                    : (<React.Fragment>
                        <div className="fbar"><i className="f-recv" style={{width: clamp(pr.build,0,100)+"%"}}/></div>
                        <div className="itemmeta" style={{marginTop:5}}>{pct(pr.build)} of {pr.items.length} line items
                          {pr.blockers ? " · " + pr.blockers + " not yet approved" : " · all approved"}</div>
                        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:7}}>
                          {pr.items.map(i => <button key={i.id} className="btn ghost sm" onClick={()=>setSelId(i.id)}>{i.no}</button>)}
                        </div>
                      </React.Fragment>)}
                </div>
                <div>
                  <div className="eyebrow" style={{marginBottom:5}}>Funded</div>
                  {pr.fund == null
                    ? <div className="muted" style={{fontSize:12}}>No funding dependency.</div>
                    : (<React.Fragment>
                        <div className="fbar"><i className="f-comm" style={{width: clamp(pr.fund,0,100)+"%"}}/></div>
                        <div className="itemmeta" style={{marginTop:5}}>{pct(pr.fund)} raised
                          {pr.fundingGap ? " · " + usd(pr.fundingGap) + " still needed" : " · fully funded"}</div>
                        <div style={{fontSize:12,marginTop:7}}>{pr.needs.map(n=>n.name).join(" · ")}</div>
                      </React.Fragment>)}
                </div>
              </div>
            </div>
          );
        })}
    </React.Fragment>
  );
}

/* --------------------------------------------------------- Investor view */
/* Deliberately narrow. Investors see what the project needs, how far along it
   is, and where their own commitment stands. They do not see contractor
   penalties, punch-list disputes, backcharges, the bank exception report or
   lien waivers — none of that helps them decide, and half-read it does harm. */
/**
 * The briefing and the agreements.
 *
 * The briefing itself is the standalone page Kenneth authors — it opens in its
 * own tab rather than inside this frame, because that page is the artefact
 * investors were sent and it should look the same wherever it is read.
 *
 * The documents are fetched through links that expire, so a URL forwarded out
 * of an inbox stops working instead of becoming a permanent public copy of a
 * loan agreement.
 */
/* ============================================================================
   THE BRIEFING
   The page investors read, rendered from the document Kenneth publishes.

   It was a standalone HTML file with its own copy of every figure — the same
   numbers written twice, once in a config object and once in the markup. The
   design is unchanged (its stylesheet came across intact, scoped under
   .briefing); what changed is that nothing here is typed in. Every amount,
   every share, every section heading that quotes a total is computed from the
   published document, so a number Kenneth edits is the number an investor
   reads.
   ========================================================================== */

const bMoney = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
/** $120,000 -> $120K, for the places the design shows a short figure. */
const bShort = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000000) return "$" + (v / 1000000).toFixed(v % 1000000 ? 1 : 0) + "M";
  if (Math.abs(v) >= 1000) return "$" + Math.round(v / 1000) + "K";
  return "$" + v;
};
const sum = (rows, k = "amount") => (rows || []).reduce((s, r) => s + (Number(r[k]) || 0), 0);

/**
 * The response deadline, in three phases.
 *
 * A date, then an extended date that takes over on its own, then an open-ended
 * message once both have passed. The last phase matters: an investor arriving
 * late should not be met with an expired date and closed language.
 */
function deadlineState(cfg, today) {
  if (!cfg || !cfg.date) return null;
  const dayStart = (str) => { const p = String(str).split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); };
  const now = today ? dayStart(today) : (() => { const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate()); })();
  const daysTo = (str) => Math.round((dayStart(str) - now) / 86400000);

  const primary = daysTo(cfg.date);
  const extended = primary < 0 && cfg.extendedDate;
  const days = extended ? daysTo(cfg.extendedDate) : primary;

  if (days < 0) {
    return { open: true, headline: cfg.openHeadline || "", sub: cfg.openSub || "" };
  }
  return {
    open: false, soon: days <= 3,
    headline: extended ? cfg.extendedLabel : cfg.label,
    badge: extended ? cfg.extendedBadge : "",
    days: days > 1 ? `· ${days} days left` : days === 1 ? "· tomorrow" : "· today",
    sub: days === 0
      ? (extended ? cfg.extendedFinal : cfg.subFinal)
      : (extended ? cfg.extendedSub : cfg.sub),
  };
}

function Briefing({ page, documents, project, today, onInterest, isPreview }) {
  const [scenarioId, setScenarioId] = useState(page.defaultScenario || (page.scenarios || [])[0]?.id);
  const [dismissed, setDismissed] = useState(false);
  const need = page.need || [];
  const gated = page.gated || [];
  const needTotal = sum(need);
  const gatedTotal = sum(gated);
  const picture = needTotal + gatedTotal;
  const dl = deadlineState(page.deadline, today);

  // Matched on filename, not title: the title is Kenneth's to edit, and an
  // agreement should not fall off the page because he reworded its heading.
  const byName = {}, byFile = {};
  for (const d of documents || []) { byName[d.title] = d; byFile[d.filename] = d; }
  const options = page.options || [];
  const docHref = (d) => `/api/investor-documents/${d.id}/download?redirect=1`;

  const scenarios = page.scenarios || [];
  const scen = scenarios.find((s) => s.id === scenarioId) || scenarios[0];
  const covered = scen ? (Number(scen.investor) || 0) + (Number(scen.reductions) || 0) + (Number(scen.proposed) || 0) : 0;
  const gap = needTotal - covered;
  // The bar runs a little past the target so the marker is not welded to the
  // right-hand edge — the proportions on screen are the same ones as the page
  // it replaces.
  const scale = needTotal * 1.06 || 1;
  const src = page.sourceDetail || {};
  const needMax = Math.max(1, ...need.map((r) => Number(r.amount) || 0));

  return (
    <div className={"briefing" + (dl && !dismissed ? " has-dl" : "")}>
      <div className="page"><div className="wrap">

        <header className="titleblock">
          {page.meta?.revision && <div className="tb-eyebrow">{page.meta.revision}</div>}
          <h1 className="tb-title">{(project.name || "").replace(/ — .*/, "")}</h1>
          <p className="tb-sub">Capital status &amp; path to operations</p>
          <div className="tb-meta">
            {page.meta?.preparedFor && <span>Prepared for &nbsp;<b>{page.meta.preparedFor}</b></span>}
            {page.meta?.asOf && <span>As of &nbsp;<b>{page.meta.asOf}</b></span>}
            {page.meta?.source && <span>Source &nbsp;<b>{page.meta.source}</b></span>}
          </div>
        </header>

        {dl && !dismissed && (
          <div className={"dl" + (dl.soon ? " is-soon" : "")}>
            <span className="dl-sheen" aria-hidden="true"/>
            <div className="dl-in">
              <div className="dl-txt">
                <div className="dl-k">
                  {!dl.open && <span>Responses requested by </span>}
                  <span>{dl.headline}</span>
                  {!dl.open && <span className={"dl-days" + (dl.soon ? " is-soon" : "")}>
                    {" " + dl.days}</span>}
                  {dl.badge && <span className="dl-badge">{dl.badge}</span>}
                </div>
                <div className="dl-v">{dl.sub}</div>
              </div>
              {/* The reason the bar is there at all. It followed the reader down
                  the page in the original and has to keep doing so. */}
              <div className="dl-act">
                <a className="btn btn-solid" href="#interest">Express interest</a>
                <button className="dl-x" type="button" aria-label="Dismiss this notice"
                        onClick={() => setDismissed(true)}>×</button>
              </div>
            </div>
          </div>
        )}

        {/* ---- the three options, as a chooser ---------------------------- */}
        <section className="sec" id="options-top">
          <div className="sec-head">
            <span className="sec-eyebrow">Three ways to participate</span>
            <h2 className="sec-title">Choose your path</h2>
            <p className="sec-lede">Each option has its own agreement. Review the terms below,
              then review the full picture before deciding.</p>
          </div>
          <nav className="optstrip">
            {Object.entries(PATHS).map(([k, v], i) => (
              <a key={k} href={"#opt-" + k}>
                <span className="ostrip-n">Option {i + 1}</span>
                <span className="ostrip-k">{v.label}</span>
                <span className="ostrip-v">{v.summary}</span>
                <span className="ostrip-go"><span>Review terms →</span></span>
              </a>
            ))}
          </nav>
        </section>

        {/* ---- what the remaining need unlocks ---------------------------- */}
        <section className="sec">
          <div className="sec-head">
            <span className="sec-eyebrow">The position</span>
            <h2 className="sec-title">What the last {bMoney(needTotal)} unlocks</h2>
            <p className="sec-lede">Committed capital is already allocated to this project. It
              releases on operations, not on construction. The remaining need is small against
              what it opens.</p>
          </div>
          <div className="thresh">
            <div className="th-track">
              <div className="th-seg th-seg-need"
                   style={{flex: `0 0 ${picture ? (needTotal / picture) * 100 : 0}%`}}/>
              <div className="th-gate"/>
              <div className="th-seg th-seg-gated"/>
            </div>
            <div className="th-legend">
              <div className="th-item th-item-need">
                <span className="th-sw"/>
                <span className="th-body">
                  <span className="th-amt">{bShort(needTotal)}</span>
                  <span className="th-lab">Still needed</span>
                  <span className="th-note">Operating capital to open</span>
                  <span className="th-share">{picture ? Math.round((needTotal / picture) * 100) : 0}% of the picture</span>
                </span>
              </div>
              <div className="th-item th-item-gated">
                <span className="th-sw"/>
                <span className="th-body">
                  <span className="th-amt">{bShort(gatedTotal)}</span>
                  <span className="th-lab">Already committed</span>
                  <span className="th-note">Allocated to this project, released on operations</span>
                  <span className="th-share">{picture ? Math.round((gatedTotal / picture) * 100) : 0}% of the picture</span>
                </span>
              </div>
            </div>
            <div className="th-gatelabel">
              Released on <b>certificate of occupancy and start of operations</b> — not before
            </div>
            <div className="th-foot">
              {gated.map((g, i) => (
                <div className="th-foot-item" key={i}>
                  <div className="th-foot-k">{bMoney(g.amount)} · {g.label}</div>
                  {g.note && <div className="th-foot-v">{g.note}</div>}
                </div>
              ))}
              {page.gatedFootnote?.label && (
                <div className="th-foot-item">
                  <div className="th-foot-k">
                    {bShort(page.gatedFootnote.amount)} · {page.gatedFootnote.label}
                    {page.gatedFootnote.preliminary && <span className="prelim">Preliminary</span>}
                  </div>
                  {page.gatedFootnote.note && <div className="th-foot-v">{page.gatedFootnote.note}</div>}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ---- where it goes --------------------------------------------- */}
        <section className="sec">
          <div className="sec-head">
            <span className="sec-eyebrow">The need</span>
            <h2 className="sec-title">Where the {bMoney(needTotal)} goes</h2>
            <p className="sec-lede">{
              need.some((r) => r.urgent)
                ? "One line is past due and time-sensitive. The rest are opening capital and can move on a schedule."
                : "Opening capital, and what each line covers."
            }</p>
          </div>
          <div className="rows">
            {need.map((r, i) => (
              <div className={"row" + (r.urgent ? " is-urgent" : "")} key={i}>
                <span className="row-label">{r.label}</span>
                <span className="row-amt">{bMoney(r.amount)}</span>
                <div className="row-track">
                  <div className="row-fill" style={{width: ((Number(r.amount) || 0) / needMax) * 100 + "%"}}/>
                </div>
                {r.note && <div className="row-note">{r.note}</div>}
              </div>
            ))}
          </div>
          <div className="row-total">
            <span className="row-label">Total need</span>
            <span className="row-amt">{bMoney(needTotal)}</span>
          </div>
        </section>

        {/* ---- how it gets covered --------------------------------------- */}
        {scen && (
          <section className="sec">
            <div className="sec-head">
              <span className="sec-eyebrow">The sources</span>
              <h2 className="sec-title">How it gets covered</h2>
              <p className="sec-lede">Investor contributions are one of three levers. Cost
                reductions and pre-opening revenue reduce how much has to be raised at all.</p>
            </div>
            <div className="scen-tabs" role="tablist" aria-label="Participation scenario">
              {scenarios.map((s) => (
                <button key={s.id} className="scen-tab" role="tab"
                        aria-selected={s.id === scen.id} onClick={() => setScenarioId(s.id)}>
                  {s.name}
                </button>
              ))}
            </div>
            <div className="stack">
              <div className="stack-wrap">
                <div className="stack-bar">
                  <div className="seg seg-inv" style={{width: ((Number(scen.investor) || 0) / scale) * 100 + "%"}}/>
                  <div className="seg seg-red" style={{width: ((Number(scen.reductions) || 0) / scale) * 100 + "%"}}/>
                  <div className="seg seg-pro" style={{width: ((Number(scen.proposed) || 0) / scale) * 100 + "%"}}/>
                  <div className="stack-target" style={{left: (needTotal / scale) * 100 + "%"}}>
                    <span>{bShort(needTotal)} target</span>
                  </div>
                </div>
              </div>
              <div className="legend">
                {[["investor", "seg-inv"], ["reductions", "seg-red"], ["proposed", "seg-pro"]].map(([k, cls]) => (
                  <div className="leg" key={k}>
                    <span className={"leg-sw " + cls}/>
                    <span>
                      <span className="leg-k">{src[k]?.name || k}</span>
                      <span className="leg-amt"> {bMoney(scen[k])}</span>
                      {src[k]?.note && <span className="leg-v">{src[k].note}</span>}
                    </span>
                  </div>
                ))}
              </div>
              <div className="scen-result">
                <span>
                  <span className="res-lab">Covered</span>
                  <span className="res-k">{bMoney(covered)}</span>
                </span>
                <span>
                  <span className="res-lab">{gap > 0 ? "Remaining gap" : "Above target"}</span>
                  <span className={"res-k" + (gap > 0 ? " res-short" : "")}>{bMoney(Math.abs(gap))}</span>
                </span>
                <span>
                  <span className="res-lab">Of {bMoney(needTotal)} target</span>
                  <span className="res-k">{needTotal ? Math.round((covered / needTotal) * 100) : 0}%</span>
                </span>
              </div>
              {scen.caption && <p className="scen-caption">{scen.caption}</p>}
            </div>
          </section>
        )}

        {/* ---- the schedule ---------------------------------------------- */}
        {(page.timeline || []).length > 0 && (
          <section className="sec">
            <div className="sec-head">
              <span className="sec-eyebrow">The schedule</span>
              <h2 className="sec-title">Path to first revenue</h2>
              <p className="sec-lede">The bar and event space are the closest to complete and
                carry the first dollars. Licensing is the long pole.</p>
            </div>
            <div className="tl">
              {page.timeline.map((t, i) => (
                <div className={"tl-item" + (t.money ? " is-money" : "")} key={i}>
                  <div className="tl-when">{t.when}</div>
                  <div>
                    <div className="tl-what">{t.what}</div>
                    {t.note && <div className="tl-note">{t.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---- terms and documents --------------------------------------- */}
        <section className="sec">
          <div className="sec-head">
            <span className="sec-eyebrow">The paperwork</span>
            <h2 className="sec-title">Terms and documents</h2>
            <p className="sec-lede">Select an option to review its agreement. Executed copies are
              sent for signature once you express interest.</p>
          </div>
          {options.map((o) => (
            <article className="opt" id={"opt-" + o.id} key={o.id}>
              <div className="opt-head">
                <span className="opt-name">{o.name}</span>
                <span className={"opt-tag" + (o.equity ? " is-equity" : "")}>{o.tag}</span>
              </div>
              {/* The term-by-term list, not a paragraph about it. This is the
                  section a reader is deciding on. */}
              <ul className="opt-terms">
                {(o.terms || []).map((t, i) => (
                  <li key={i}><b>{t.k}</b><span>{t.v}</span></li>
                ))}
              </ul>
              {o.flag && <div className="opt-flag">{o.flag}</div>}
              <div className="opt-actions">
                {(o.docs || []).map((d, i) => {
                  const doc = byFile[d.file];
                  return doc
                    ? <a className="btn btn-ghost" key={i} target="_blank" rel="noopener"
                         href={docHref(doc)}>{d.label}</a>
                    : <span className="btn btn-ghost" key={i} aria-disabled="true">{d.label} — not uploaded</span>;
                })}
                <a className="btn btn-solid" href="#interest">Express interest</a>
              </div>
            </article>
          ))}
        </section>

        {/* ---- further reading ------------------------------------------- */}
        {(page.resources || []).length > 0 && (
          <section className="sec">
            <div className="sec-head">
              <span className="sec-eyebrow">Supporting material</span>
              <h2 className="sec-title">Before you decide</h2>
              <p className="sec-lede">The full project materials, for review alongside the
                agreements above.</p>
            </div>
            <div className="res">
              {page.resources.map((r, i) => {
                const doc = r.document && byName[r.document];
                const href = doc ? docHref(doc) : r.url;
                if (!href) return null;
                return (
                  <a key={i} href={href} target="_blank" rel="noopener">
                    <span className="res-k">{r.label}</span>
                    <span className="res-v">{r.note}</span>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* ---- express interest ------------------------------------------ */}
        <section className="sec" id="interest">
          <div className="sec-head">
            <span className="sec-eyebrow">Next step</span>
            <h2 className="sec-title">Express interest</h2>
            <p className="sec-lede">Submitting this does not commit you. Kenneth will send the
              agreement for signature.</p>
          </div>
          <InterestForm onInterest={onInterest} isPreview={isPreview}/>
        </section>

        {(page.footnotes || []).length > 0 && (
          <div className="foot">
            {page.footnotes.map((f, i) => <p key={i}>{f}</p>)}
          </div>
        )}

      </div></div>
    </div>
  );
}

/** The one thing on this page that writes anything. */
function InterestForm({ onInterest, isPreview }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", option: "", amount: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const OPTIONS = [
    ["Option 1 — Short-term loan", "loan"],
    ["Option 2 — Fund I additional contribution", "fundI"],
    ["Option 3 — Fund II, new investor", "fundII"],
    ["Not sure yet — please contact me", ""],
  ];

  if (done) {
    return <div className="form-note" style={{fontSize:15,lineHeight:1.6}}>
      <strong>Recorded.</strong> {done}</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // Quietly accepted and quietly dropped: a bot gets no signal to tune against.
      if (f.company) { setDone("Recorded."); return; }
      const r = await onInterest({
        name: f.name, email: f.email, phone: f.phone, option: f.option,
        path: (OPTIONS.find(([label]) => label === f.option) || [])[1] || "",
        amount: f.amount ? Number(f.amount) : null,
      });
      setDone(r.note || "");
    } catch (e2) { setErr(e2.message); setBusy(false); }
  };

  return (
    <form className="form" onSubmit={submit}>
      {/* A field a person never sees and a bot fills in. It came across from the
          original form, where the host provided the trap; here we check it. */}
      <p className="hp"><label>Do not fill this in: <input name="company" tabIndex={-1}
        autoComplete="off" value={f.company} onChange={(e) => set("company", e.target.value)}/></label></p>
      <div className="form-grid">
        <div className="f"><label htmlFor="f-name">Full name</label>
          <input id="f-name" required autoComplete="name" value={f.name}
                 onChange={(e) => set("name", e.target.value)}/></div>
        <div className="f"><label htmlFor="f-email">Email</label>
          <input id="f-email" type="email" required autoComplete="email" value={f.email}
                 onChange={(e) => set("email", e.target.value)}/></div>
        <div className="f"><label htmlFor="f-phone">Phone</label>
          <input id="f-phone" type="tel" autoComplete="tel" value={f.phone}
                 onChange={(e) => set("phone", e.target.value)}/></div>
        <div className="f"><label htmlFor="f-option">Option</label>
          <select id="f-option" required value={f.option}
                  onChange={(e) => set("option", e.target.value)}>
            <option value="">Select an option</option>
            {OPTIONS.map(([label]) => <option key={label}>{label}</option>)}
          </select></div>
        <div className="f"><label htmlFor="f-amount">Amount considering ($)</label>
          <input id="f-amount" type="number" inputMode="decimal" min="0" step="500" value={f.amount}
                 onChange={(e) => set("amount", e.target.value)}/></div>
      </div>
      <div className="opt-actions">
        <button className="btn btn-solid" type="submit" disabled={busy || isPreview}>
          {busy ? "Sending…" : isPreview ? "Express interest (preview)" : "Send to Kenneth"}
        </button>
      </div>
      {err && <p className="form-note form-error" style={{color:"#9E3B2A"}}>{err}</p>}
      <p className="form-note">This form records your interest only. No agreement is formed and no
        funds are transferred until you have reviewed the relevant document, executed it by digital
        signature, and received payment instructions directly from the owner. Please do not enter
        bank details, account numbers, or identification numbers here.</p>
    </form>
  );
}

function InvestorBriefing({ db, isPreview, say }) {
  const docs = db.documents || [];
  const published = db.pagePublished;
  if (!published && !isPreview) return null;

  const KIND = { loan:"Loan", fundI:"Fund I", fundII:"Fund II",
                 projections:"Projections", other:"" };

  return (
    <div className="sec" style={{marginTop:22}}>
      <SectionHead right={published
        ? <a className="btn ghost sm" target="_blank" rel="noopener"
             href={`/api/projects/${db.project.id}/investor-page.html`}>Open the briefing</a>
        : <span className="tag n">not published</span>}>
        The briefing and the agreements
      </SectionHead>
      {!published && isPreview && (
        <p className="muted" style={{fontSize:12.5,marginTop:0}}>
          The briefing is still a draft, so investors see nothing here yet.
        </p>
      )}
      {docs.length === 0
        ? <p className="muted" style={{fontSize:12.5,marginTop:0}}>No documents yet.</p>
        : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:10}}>
            {docs.map((d) => (
              <a key={d.id} className="card pad doccard" target="_blank" rel="noopener"
                 href={`/api/investor-documents/${d.id}/download?redirect=1`}>
                <div style={{display:"flex",gap:8,alignItems:"baseline",justifyContent:"space-between"}}>
                  <span style={{fontWeight:600,fontSize:13}}>{d.title}</span>
                  {KIND[d.kind] && <span className="tag n">{KIND[d.kind]}</span>}
                </div>
                {d.description && (
                  <p style={{fontSize:12,margin:"6px 0 0",lineHeight:1.5}}>{d.description}</p>
                )}
                <div className="mono muted" style={{fontSize:10.5,marginTop:8}}>
                  {Math.max(1, Math.round(d.size / 1024)).toLocaleString()} KB
                  {isPreview && !d.visible ? " · hidden from investors" : ""}
                </div>
              </a>
            ))}
          </div>
        )}
    </div>
  );
}

/**
 * Asking an investor for a phone number, once.
 *
 * The roster came from a spreadsheet with names and addresses and nothing else,
 * and a number is the one thing nobody but its owner can supply. So it is asked
 * for where they already are, rather than by chasing forty people individually.
 *
 * It asks and it does not insist: a briefing somebody opened to read about
 * money should not be behind a form. It can be dismissed for the session, and
 * it stops appearing for good once there is a number.
 */
function AskForPhone({ party, say, refresh }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return window.sessionStorage.getItem("pp.phone.asked") === party.id; } catch { return false; }
  });
  if (!party || party.phone || hidden) return null;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.saveMyContact(party.id, { phone });
      await refresh();
      say("Thank you — that is on file now.");
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const notNow = () => {
    try { window.sessionStorage.setItem("pp.phone.asked", party.id); } catch {}
    setHidden(true);
  };

  return (
    <div className="card pad" style={{marginBottom:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>A phone number for you</SectionHead>
      <p style={{fontSize:12.5,marginTop:0,lineHeight:1.6,maxWidth:600}}>
        We have your email but not a number. It is used only to reach you about
        this project — a signature needed, a date that moved — and never shared
        with other investors.
      </p>
      <div className="editline" style={{marginTop:10}}>
        <input className="field" type="tel" autoComplete="tel" style={{maxWidth:220}}
               placeholder="(601) 555-0123" value={phone}
               onChange={(e)=>setPhone(e.target.value)}/>
        <button className="btn go" disabled={busy || phone.trim().length < 7} onClick={save}>
          {busy ? "…" : "Save it"}</button>
        <button className="linkbtn" style={{fontSize:12}} onClick={notNow}>Not now</button>
      </div>
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
    </div>
  );
}

function InvestorView({ db, role, me, meId, say, refresh, setView }) {
  const isPreview = role === "owner";
  const page = db.page;
  const mine = pledgesOf(db).filter(x => x.partyId === meId);
  // Their own company on this project — the only party an investor is sent.
  const myParty = (db.parties || []).find(x => x.id === meId);
  const unreadReplies = (db.threads || []).reduce((n, t) => n + unreadIn(t, meId), 0);

  // The briefing IS the investor view now.
  //
  // It used to be a second, standalone page with its own copy of every figure,
  // reached by a button — so an investor saw one set of numbers in the app and
  // another in the document, and Kenneth had to publish twice for them to
  // agree. One document, one renderer, one set of numbers.
  // The channel to the owner does not depend on the briefing. Somebody who can
  // sign in can ask a question, published or not — being told there is nothing
  // to see and having no way to ask about it is the worst of both.
  const channel = (
    <div style={{marginTop:22}} id="investor-messages">
      <Messages {...{db, role, me, meId, say, refresh}} setSelId={()=>{}} embedded/>
    </div>
  );

  if (!page) {
    return (
      <React.Fragment>
        <Empty title={isPreview ? "The briefing isn't published yet" : "Nothing to show yet"}
               body={isPreview
                 ? "Investors see a holding message until you publish it. Investor page → Publish."
                 : developerName(db) + " hasn't published the investor briefing for this project yet."}/>
        {channel}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {isPreview && (
        <div className="banner brass" style={{marginBottom:14}}>
          <h4>This is what investors see</h4>
          <p>Exactly this — the same component, from the document you publish. Nothing about
             contractors, penalties or disputes appears here.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}}
                    onClick={()=>setView("investorpage")}>Edit the page</button></p>
        </div>
      )}

      {!isPreview && (
        <p className="muted noprint" style={{fontSize:12,margin:"0 0 12px"}}>
          New to this?{" "}
          <button className="btn ghost sm" onClick={()=>setView("docs")}>Read how it works</button>
        </p>
      )}

      {!isPreview && myParty && (
        <AskForPhone party={myParty} say={say} refresh={refresh}/>
      )}

      {/* The answer to their question is at the bottom of a long document. Say
          so at the top, where they are. */}
      {unreadReplies > 0 && (
        <div className="banner brass" style={{marginBottom:14}}>
          <h4>{developerName(db)} has answered you{unreadReplies === 1 ? "" : " " + unreadReplies + " times"}</h4>
          <p>It's at the foot of this page, under the briefing.{" "}
            <button className="btn ghost sm" style={{marginLeft:6}}
                    onClick={()=>{ const el = document.getElementById("investor-messages");
                                   if (el) el.scrollIntoView({behavior:"smooth", block:"start"}); }}>
              Read it</button></p>
        </div>
      )}

      {mine.length > 0 && (
        <div className="card pad" style={{marginBottom:14}}>
          <SectionHead>Where your commitment stands</SectionHead>
          <div className="tablewrap" style={{marginTop:8}}>
            <table className="sov">
              <tbody>
                {mine.map(x => (
                  <tr key={x.id}>
                    <td>{PATHS[x.path] ? PATHS[x.path].label : x.path}</td>
                    <td className="num r">{usd(x.amount)}</td>
                    <td className="r">
                      <span className={"tag " + (PLEDGE_STATUS[x.status] || {}).tag}>
                        {(PLEDGE_STATUS[x.status] || {}).label || x.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Briefing page={page} documents={db.documents || []} project={db.project}
                today={db.project.today} isPreview={isPreview}
                onInterest={async (payload) => {
                  const r = await api.expressInterest(payload);
                  await refresh();
                  return r;
                }}/>

      {/* The one channel across the boundary. It sits under the briefing
          because that is what the questions are about, and because an investor
          who has read it is the one worth answering. */}
      {channel}
    </React.Fragment>
  );
}

function Inbox({ db, role, me, meId, say, refresh, setView }) {
  const [tab, setTab] = useState("pending");
  const items = inboxOf(db).filter(x =>
    tab === "pending" ? x.status === "pending"
    : tab === "quarantine" ? x.status === "quarantined"
    : x.status === "filed" || x.status === "dismissed");
  const pending = inboxPending(db).length;
  const quarantined = inboxOf(db).filter(x => x.status === "quarantined").length;

  if (!["owner","lender"].includes(role)) return <Empty title="Not your queue"
    body="Emailed documents are reviewed by the owner before they're filed to a draw."/>;

  return (
    <React.Fragment>
      <h1 className="pagetitle">Document inbox</h1>
      <p className="pagesub">Attachments emailed in by the bank and contractors. Each one arrives with a suggested draw —
        confirm it and it lands in that draw's payment file.</p>

      <div className="card pad" style={{marginTop:16}}>
        <SectionHead>The address to give the bank</SectionHead>
        {(db.settings && db.settings.email && db.settings.email.inboundDomain) ? (
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <code className="mono" style={{background:"var(--paper-2)",border:"1px solid var(--rule)",
              borderRadius:2,padding:"7px 11px",fontSize:13}}>draws@{db.settings.email.inboundDomain}</code>
            <button className="btn ghost sm" onClick={()=>{
              navigator.clipboard && navigator.clipboard.writeText("draws@" + db.settings.email.inboundDomain);
              say("Address copied");
            }}>Copy</button>
          </div>
        ) : (
          // Handing someone an address that nothing is listening on is worse
          // than handing them nothing: the bank sends the check copy into the
          // void and everyone assumes it arrived.
          <div className="banner" style={{marginTop:6}}>
            <h4>No intake address yet</h4>
            <p>Email intake isn't set up on this project, so there is no address to give the bank.
               Until it is, attach the check copy to the draw yourself — Draw requests → the draw →
               Attach a document. Set the domain under Settings → Email &amp; inbox.</p>
          </div>
        )}
        <p className="muted" style={{fontSize:12,marginTop:10,marginBottom:0,maxWidth:660}}>
          Anything sent here gets parsed and queued. Each draw also has its own private address — use that one when you
          submit a draw and replies file themselves with no guesswork. You'll find it on the draw.
        </p>
      </div>

      <div className="steps noprint" style={{marginTop:16}}>
        {[["pending","Waiting on you",pending],["quarantine","Quarantined",quarantined],["done","Handled",null]].map(([k,l,n],i) => (
          <button key={k} className={"stepbtn " + (tab===k?"on":"")} onClick={()=>setTab(k)}>
            <span className="stepno">{i+1}</span><span>{l}{n ? " · " + n : ""}</span>
          </button>
        ))}
      </div>

      {tab === "quarantine" && quarantined > 0 && (
        <div className="banner" style={{marginBottom:12}}>
          <h4>Held back — these failed sender authentication</h4>
          <p>Mail claiming to be from a bank but failing SPF, DKIM or DMARC is the standard shape of a payment-diversion
             attempt. Nothing here can be filed until you move it out of quarantine deliberately.</p>
        </div>
      )}

      {items.length === 0
        ? <Empty title={tab === "pending" ? "Nothing waiting" : tab === "quarantine" ? "Nothing quarantined" : "Nothing handled yet"}
                 body={tab === "pending" ? "Emailed attachments show up here with a suggested draw. Give the bank the address above and they can send the check the moment it's cut."
                       : "Held or completed items appear here."}/>
        : items.map(x => <InboxItem key={x.id} {...{item:x, db, role, me, refresh, say, setView}}/>)}
    </React.Fragment>
  );
}

function InboxItem({ item, db, role, me, refresh, say, setView }) {
  // Anything that needs a decision opens by default; a confident match can stay
  // collapsed. Quarantined mail always opens — it is the item most worth reading.
  const [open, setOpen] = useState(item.status === "quarantined" ||
    (item.status === "pending" && item.confidence !== "high"));
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    drawId: item.matchedDrawId || "",
    type: item.guessType || "other",
    amount: item.guessAmount ? String(item.guessAmount) : "",
    reference: item.guessReference || "",
    paidOn: item.guessPaidOn || iso(TODAY),
  });
  const set = (k,v) => setF(y => ({...y, [k]:v}));
  const conf = MATCH_CONFIDENCE[item.confidence];
  const snd = SENDER_STATUS[item.sender];
  const draw = f.drawId ? db.draws.find(d => d.id === f.drawId) : null;
  const t = draw ? drawTotals(db, draw) : null;
  const meta = DOC_TYPES[f.type];
  const amt = Number(f.amount) || 0;
  const variance = t && meta.payment && amt ? amt - t.net : null;
  const done = item.status === "filed" || item.status === "dismissed";
  const blocked = item.status === "quarantined";

  return (
    <div className="card" style={{marginBottom:10, borderLeft:"3px solid var(--" +
      (blocked ? "oxide" : item.status === "filed" ? "verdigris" : conf.tag === "g" ? "verdigris" : conf.tag === "p" ? "brass" : "rule") + ")"}}>
      <button className="punchh" onClick={()=>setOpen(o=>!o)} aria-expanded={open} style={{padding:"12px 14px"}}>
        <span style={{minWidth:0}}>
          <span style={{fontWeight:600,fontSize:13.5}}>{item.filename}</span>
          <span className="itemmeta" style={{display:"block"}}>
            {item.fromName} &lt;{item.from}&gt; · {fmtStamp(item.receivedAt)} · {Math.round(item.size/1024)} KB
          </span>
          <span className="itemmeta" style={{display:"block"}}>{item.subject}</span>
        </span>
        <span style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
          {item.status === "filed" && <span className="tag g">Filed to {item.filedTo}</span>}
          {item.status === "dismissed" && <span className="tag n">Dismissed</span>}
          {!done && <span className={"tag " + snd.tag}>{snd.label}</span>}
          {!done && <span className={"tag " + conf.tag}>{conf.label}</span>}
          {!done && item.matchedDrawId && <span className="tag n">{item.matchedDrawId}</span>}
        </span>
      </button>

      {open && (
        <div className="punchb" style={{padding:"12px 14px 14px"}}>
          <div className="mono muted" style={{fontSize:10.5,marginBottom:9}}>
            To {item.to} · SPF {item.spf} · DKIM {item.dkim} · DMARC {item.dmarc}
          </div>
          {item.body && <div className="note" style={{marginBottom:11}}>
            <div className="meta">Message body</div>
            <div style={{fontSize:12.5}}>{item.body}</div></div>}

          <div className="note" style={{borderLeftColor: conf.tag==="g" ? "var(--verdigris)" : conf.tag==="p" ? "var(--brass)" : "var(--rule)"}}>
            <div className="meta">Why it guessed this</div>
            <div style={{fontSize:12.5}}>{(item.matchedOn||[]).join(" · ")}</div>
            <div className="muted" style={{fontSize:11.5,marginTop:3}}>{conf.blurb}</div>
          </div>

          {blocked && (
            <div className="banner" style={{marginTop:12}}>
              <h4>Quarantined</h4>
              <p>This message failed sender authentication and the domain isn't on the project. If you're certain it's
                 legitimate, confirm it by phone with a number you already have — not one in the email — before you do
                 anything with it.</p>
            </div>
          )}

          {!done && !blocked && (
            <React.Fragment>
              <div style={{marginTop:14}}>
                <SectionHead>File it</SectionHead>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:12}}>
                  <label><div className="eyebrow">Which draw</div>
                    <select className="field" value={f.drawId} onChange={e=>set("drawId",e.target.value)}>
                      <option value="">Choose a draw…</option>
                      {db.draws.slice().reverse().map(d => (
                        <option key={d.id} value={d.id}>{drawLabel(d)} · {usd(drawTotals(db,d).net)} · {DRAW_STATUS[d.status].label}</option>
                      ))}
                    </select></label>
                  <label><div className="eyebrow">What is it</div>
                    <select className="field" value={f.type} onChange={e=>set("type",e.target.value)}>
                      {Object.entries(DOC_TYPES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select></label>
                  {meta.payment && (
                    <React.Fragment>
                      <label><div className="eyebrow">Amount paid</div>
                        <input className="field" type="number" min="0" step="0.01" value={f.amount}
                               onChange={e=>set("amount",e.target.value)}/>
                        <div className="muted" style={{fontSize:11,marginTop:3}}>
                          {item.guessAmount ? "Read from the document — check it against the paper." : "Not readable; enter it."}
                        </div></label>
                      <label><div className="eyebrow">Date paid</div>
                        <input className="field" type="date" value={f.paidOn||""} onChange={e=>set("paidOn",e.target.value)}/></label>
                    </React.Fragment>
                  )}
                  <label><div className="eyebrow">Reference</div>
                    <input className="field" value={f.reference} onChange={e=>set("reference",e.target.value)}
                           placeholder={meta.payment ? "Check #40119" : "Optional"}/></label>
                </div>

                {draw && meta.payment && amt > 0 && (
                  <div className={"banner " + (Math.abs(variance) <= 1 ? "brass" : "")} style={{marginTop:12,
                    borderColor: Math.abs(variance) <= 1 ? "var(--verdigris)" : "var(--oxide)",
                    background: Math.abs(variance) <= 1 ? "var(--verdigris-soft)" : "var(--oxide-soft)",
                    borderLeftColor: Math.abs(variance) <= 1 ? "var(--verdigris)" : "var(--oxide)",
                    color: Math.abs(variance) <= 1 ? "#16403A" : "#5E2117"}}>
                    <h4>{Math.abs(variance) <= 1 ? "Matches the request" : usd(Math.abs(variance)) + (variance < 0 ? " short of" : " over") + " the request"}</h4>
                    <p>{drawLabel(draw)} asked for {usd(t.net)}. This says {usd(amt)}.</p>
                  </div>
                )}
                {item.guessAmount > 0 && meta.payment && (
                  <p className="muted" style={{fontSize:11.5,marginTop:9,marginBottom:0,maxWidth:620}}>
                    Amounts read off a scanned check are not reliable enough to post unchecked — the variance figure is
                    only worth having if this number is right, so it's yours to confirm.
                  </p>
                )}
              </div>

              <div className="toolbar noprint">
                <button className="btn go" disabled={!f.drawId || busy} onClick={async()=>{
                  setBusy(true);
                  await api.fileInboxItem({ id:item.id, drawId:f.drawId, actor:me.contact, role, doc:{
                    type:f.type, url:"", filename:item.filename, size:item.size,
                    amount: meta.payment ? amt : 0, reference:f.reference,
                    paidOn: meta.payment ? f.paidOn : null, note:"Received by email from " + item.from } });
                  await refresh(); setBusy(false);
                  say(item.filename + " filed to " + f.drawId);
                }}>File to {f.drawId || "…"}</button>
                <button className="btn ghost" disabled={busy} onClick={async()=>{
                  setBusy(true);
                  await api.dismissInboxItem({ id:item.id, reason:"Not relevant to a draw", actor:me.contact, role });
                  await refresh(); setBusy(false); say("Dismissed");
                }}>Not a draw document</button>
              </div>
            </React.Fragment>
          )}

          {blocked && (
            <div className="toolbar noprint">
              <button className="btn stop" disabled={busy} onClick={async()=>{
                setBusy(true);
                await api.dismissInboxItem({ id:item.id, reason:"Failed authentication — treated as phishing",
                  actor:me.contact, role });
                await refresh(); setBusy(false); say("Dismissed and logged");
              }}>Dismiss it</button>
            </div>
          )}

          {item.status === "filed" && (
            <div className="toolbar noprint">
              <button className="btn ghost" onClick={()=>setView("draws")}>Open {item.filedTo}</button>
            </div>
          )}
          {item.status === "dismissed" && item.reason &&
            <p className="muted" style={{fontSize:12,marginTop:10,marginBottom:0}}>{item.reason}</p>}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- Suppliers & materials */
function Supply({ db, role, me, meId, say, refresh, setSelId }) {
  const [tab, setTab] = useState("orders");
  const [editSup, setEditSup] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const canEdit = role === "owner";
  const items = procureOf(db);
  const atRisk = procureAtRisk(db);
  const openItems = items.filter(x => PROCURE_OPEN.includes(x.status));
  const committed = items.filter(x => x.status !== "cancelled")
    .reduce((n,x) => n + (x.quoted || x.budgeted || 0), 0);
  const budgeted = items.filter(x => x.status !== "cancelled").reduce((n,x) => n + (x.budgeted || 0), 0);

  return (
    <React.Fragment>
      <h1 className="pagetitle">Suppliers &amp; materials</h1>
      <p className="pagesub">What has to be bought, who from, and the date it has to be ordered to land on site in time.
        A finished wall waiting four weeks on a fixture is still a delay.</p>

      {atRisk.length > 0 && (
        <div className="banner" style={{marginTop:16}}>
          <h4>{atRisk.length} order{atRisk.length===1?"":"s"} at risk</h4>
          <p>{atRisk.filter(x=>procureRisk(db,x).level==="late").length} past the date they had to be placed,{" "}
             {atRisk.filter(x=>procureRisk(db,x).level==="soon").length} inside ten days. Lead time is the one delay you
             can't recover by adding crew.</p>
        </div>
      )}

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Open orders" v={openItems.length} sub={items.length + " tracked"}/>
        <Stat k="At risk" v={atRisk.length} tone={atRisk.length?"flag":"good"}/>
        <Stat k="Quoted / committed" v={usd(committed)} sub={"against " + usd(budgeted) + " budgeted"}
              tone={committed > budgeted ? "flag" : "good"}/>
        <Stat k="Over budget" v={usd(Math.max(0, committed - budgeted))} tone={committed>budgeted?"flag":""}/>
        <Stat k="Suppliers" v={suppliersOf(db).filter(x=>x.status==="active").length}/>
      </div>

      <div className="steps noprint" style={{marginTop:16}}>
        {[["orders","What to buy"],["suppliers","Suppliers"]].map(([k,l],i) => (
          <button key={k} className={"stepbtn " + (tab===k?"on":"")} onClick={()=>setTab(k)}>
            <span className="stepno">{i+1}</span><span>{l}</span>
          </button>
        ))}
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>download("calico-materials-" + iso(TODAY) + ".csv",
          toCSV(items.map(x => { const r = procureRisk(db,x); return { ...x,
            supplier: supplierName(db, x.supplierId),
            lineItem: x.lineItemId ? (db.lineItems.find(i=>i.id===x.lineItemId)||{}).no || "" : "",
            orderBy: orderByDate(db, x) || "", risk: r ? (r.level === "late" ? r.days + " days past" : "in " + r.days + " days") : "",
            statusLabel: PROCURE_STATUS[x.status].label }; }),
          [["lineItem","Line Item"],["description","Material"],["supplier","Supplier"],["qty","Quantity"],
           ["unit","Unit"],["unitCost","Unit Cost"],["budgeted","Budgeted"],["quoted","Quoted"],
           ["leadTimeWeeks","Lead Time Weeks"],["orderBy","Order By"],["neededOnSite","Needed On Site"],
           ["statusLabel","Status"],["risk","Risk"],["note","Note"]]))}>Export</button>
      </div>

      {tab === "orders" && (
        <React.Fragment>
          {canEdit && (
            <div className="toolbar noprint">
              <button className="btn" onClick={()=>setEditItem(editItem === "new" ? null : "new")}>
                {editItem === "new" ? "Cancel" : "Add something to buy"}</button>
            </div>
          )}
          {editItem && <ProcureForm {...{db, me, item: editItem === "new" ? null : editItem, refresh, say}}
                                    onDone={()=>setEditItem(null)}/>}

          {items.length === 0
            ? <Empty title="Nothing tracked yet"
                     body="Add the long-lead material first — furniture, kitchen equipment, anything special-order. Those are the ones that quietly move the opening date."/>
            : (
            <div className="tablewrap">
              <table className="sov">
                <thead><tr>
                  <th className="plain">Material</th><th className="plain">Supplier</th>
                  <th className="plain">For</th><th className="plain">Budget / quote</th>
                  <th className="plain">Lead</th><th className="plain">Order by</th>
                  <th className="plain">On site by</th><th className="plain">Status</th>
                </tr></thead>
                <tbody>
                  {items.slice().sort((a,b) => {
                    const ra = procureRisk(db,a), rb = procureRisk(db,b);
                    if (!!ra !== !!rb) return ra ? -1 : 1;
                    return (orderByDate(db,a)||"9") < (orderByDate(db,b)||"9") ? -1 : 1;
                  }).map(x => {
                    const r = procureRisk(db, x), by = orderByDate(db, x);
                    const li = x.lineItemId ? db.lineItems.find(i => i.id === x.lineItemId) : null;
                    const over = (x.quoted || 0) > (x.budgeted || 0) && x.budgeted;
                    return (
                      <tr key={x.id} onClick={()=>canEdit && setEditItem(x)} style={{cursor:canEdit?"pointer":"default"}}>
                        <td>
                          <div className="itemname">{x.description}</div>
                          <div className="itemmeta">{x.qty} {x.unit}{x.note ? " · " + x.note.slice(0,44) : ""}</div>
                        </td>
                        <td style={{fontSize:12}}>{supplierName(db, x.supplierId)}</td>
                        <td style={{fontSize:12}}>
                          {li ? <button className="btn ghost sm" onClick={e=>{e.stopPropagation(); setSelId(li.id);}}>{li.no}</button>
                              : <span className="muted">—</span>}</td>
                        <td className="r num" style={{fontSize:12}}>
                          {usd(x.budgeted)}
                          {x.quoted ? <div style={{color: over ? "var(--oxide)" : "var(--verdigris)"}}>{usd(x.quoted)}</div> : null}</td>
                        <td className="num" style={{fontSize:12}}>{x.leadTimeWeeks ? x.leadTimeWeeks + "w" : "—"}</td>
                        <td className="num" style={{fontSize:12,
                          color: r ? (r.level==="late" ? "var(--oxide)" : "var(--brass)") : "var(--slate)",
                          fontWeight: r ? 600 : 400}}>
                          {by ? fmtDate(by) : "—"}
                          {r && <div style={{fontSize:11}}>{r.level === "late" ? r.days + "d past" : "in " + r.days + "d"}</div>}</td>
                        <td className="num" style={{fontSize:12}}>{x.neededOnSite ? fmtDate(x.neededOnSite) : "—"}</td>
                        <td><span className={"tag " + PROCURE_STATUS[x.status].tag}>{PROCURE_STATUS[x.status].label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </React.Fragment>
      )}

      {tab === "suppliers" && (
        <React.Fragment>
          {canEdit && (
            <div className="toolbar noprint">
              <button className="btn" onClick={()=>setEditSup(editSup === "new" ? null : "new")}>
                {editSup === "new" ? "Cancel" : "Add a supplier"}</button>
            </div>
          )}
          {editSup && <SupplierForm {...{me, supplier: editSup === "new" ? null : editSup, refresh, say}}
                                    onDone={()=>setEditSup(null)}/>}
          {suppliersOf(db).map(sup => {
            const mine = items.filter(x => x.supplierId === sup.id);
            const open = mine.filter(x => PROCURE_OPEN.includes(x.status)).length;
            return (
              <div className="card pad" key={sup.id} style={{marginBottom:9}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
                  <div style={{minWidth:200,flex:1}}>
                    <div style={{fontWeight:700,fontSize:14,letterSpacing:"-.015em"}}>{sup.name}</div>
                    <div className="itemmeta">{sup.category}
                      {sup.contact ? " · " + sup.contact : ""}{sup.email ? " · " + sup.email : ""}
                      {sup.phone ? " · " + sup.phone : ""}</div>
                    {sup.note && <p className="muted" style={{fontSize:11.5,margin:"6px 0 0",maxWidth:560}}>{sup.note}</p>}
                  </div>
                  <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                    <span className="tag n">{sup.leadTimeWeeks}w lead</span>
                    {open > 0 && <span className="tag p">{open} open</span>}
                    {canEdit && <button className="btn ghost sm noprint" onClick={()=>setEditSup(sup)}>Edit</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

function SupplierForm({ supplier, me, refresh, say, onDone }) {
  const [f, setF] = useState(supplier ? { ...supplier } : { name:"", contact:"", email:"", phone:"",
    category:"", leadTimeWeeks:4, note:"" });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  return (
    <div className="card pad" style={{marginBottom:12, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>{supplier ? "Edit " + supplier.name : "New supplier"}</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <label><div className="eyebrow">Company</div>
          <input className="field" value={f.name} onChange={e=>set("name",e.target.value)}/></label>
        <label><div className="eyebrow">What they supply</div>
          <input className="field" value={f.category} onChange={e=>set("category",e.target.value)}
                 placeholder="Commercial kitchen"/></label>
        <label><div className="eyebrow">Contact</div>
          <input className="field" value={f.contact} onChange={e=>set("contact",e.target.value)}/></label>
        <label><div className="eyebrow">Email</div>
          <input className="field" type="email" value={f.email} onChange={e=>set("email",e.target.value)}/></label>
        <label><div className="eyebrow">Phone</div>
          <input className="field" value={f.phone} onChange={e=>set("phone",e.target.value)}/></label>
        <label><div className="eyebrow">Typical lead time (weeks)</div>
          <input className="field" type="number" min="0" value={f.leadTimeWeeks}
                 onChange={e=>set("leadTimeWeeks",Number(e.target.value))}/>
          <div className="muted" style={{fontSize:11,marginTop:3}}>Used to work backward from the on-site date.</div></label>
      </div>
      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Note</div>
        <textarea className="field" rows={2} value={f.note} onChange={e=>set("note",e.target.value)}
          placeholder="Terms, who referred them, anything you'll forget in a month."/></label>
      <div className="toolbar">
        <button className="btn go" disabled={!f.name.trim()||busy} onClick={async()=>{
          setBusy(true); await api.saveSupplier({ supplier:f, actor:me.contact });
          await refresh(); setBusy(false); onDone(); say(supplier ? "Supplier updated" : f.name + " added");
        }}>{supplier ? "Save" : "Add supplier"}</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

function ProcureForm({ db, item, me, refresh, say, onDone }) {
  const [f, setF] = useState(item ? { ...item } : { description:"", supplierId:"", lineItemId:"",
    qty:1, unit:"EA", unitCost:"", budgeted:"", quoted:"", leadTimeWeeks:"", neededOnSite:"",
    status:"needed", note:"" });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const preview = { ...f, leadTimeWeeks: f.leadTimeWeeks === "" ? null : Number(f.leadTimeWeeks) };
  const by = orderByDate(db, preview);
  const slack = by ? daysBetween(iso(TODAY), by) : null;
  const li = f.lineItemId ? db.lineItems.find(i => i.id === f.lineItemId) : null;

  return (
    <div className="card pad" style={{marginBottom:12, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>{item ? "Edit" : "Something that has to be bought"}</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:12}}>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">What it is</div>
          <input className="field" value={f.description} onChange={e=>set("description",e.target.value)}
                 placeholder="Quartersawn white oak, 4/4"/></label>
        <label><div className="eyebrow">Supplier</div>
          <select className="field" value={f.supplierId} onChange={e=>{
            set("supplierId", e.target.value);
            const sup = suppliersOf(db).find(s => s.id === e.target.value);
            if (sup && f.leadTimeWeeks === "") set("leadTimeWeeks", sup.leadTimeWeeks);
          }}>
            <option value="">Not chosen yet</option>
            {suppliersOf(db).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></label>
        <label><div className="eyebrow">For which line item</div>
          <select className="field" value={f.lineItemId||""} onChange={e=>set("lineItemId",e.target.value)}>
            <option value="">Not tied to one</option>
            {db.lineItems.map(i => <option key={i.id} value={i.id}>{i.no} · {i.name}</option>)}
          </select></label>
        <label><div className="eyebrow">Quantity</div>
          <input className="field" type="number" min="0" value={f.qty} onChange={e=>set("qty",Number(e.target.value))}/></label>
        <label><div className="eyebrow">Unit</div>
          <input className="field" value={f.unit} onChange={e=>set("unit",e.target.value)} placeholder="EA / LF / BF / LOT"/></label>
        <label><div className="eyebrow">Budgeted</div>
          <input className="field" type="number" min="0" value={f.budgeted} onChange={e=>set("budgeted",e.target.value)}/></label>
        <label><div className="eyebrow">Quoted</div>
          <input className="field" type="number" min="0" value={f.quoted} onChange={e=>set("quoted",e.target.value)}/>
          {f.quoted && f.budgeted && Number(f.quoted) > Number(f.budgeted) &&
            <div style={{fontSize:11,color:"var(--oxide)",marginTop:3}}>
              {usd(Number(f.quoted) - Number(f.budgeted))} over budget</div>}</label>
        <label><div className="eyebrow">Lead time (weeks)</div>
          <input className="field" type="number" min="0" value={f.leadTimeWeeks}
                 onChange={e=>set("leadTimeWeeks",e.target.value)}/></label>
        <label><div className="eyebrow">Needed on site</div>
          <input className="field" type="date" value={f.neededOnSite||""} onChange={e=>set("neededOnSite",e.target.value)}/>
          {li && li.plannedStart && <div className="muted" style={{fontSize:11,marginTop:3}}>
            {li.no} starts {fmtDate(li.plannedStart)}.</div>}</label>
        <label><div className="eyebrow">Status</div>
          <select className="field" value={f.status} onChange={e=>set("status",e.target.value)}>
            {Object.entries(PROCURE_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
      </div>

      {by && (
        <div className={"banner " + (slack < 0 ? "" : "brass")} style={{marginTop:14,
          borderColor: slack < 0 ? "var(--oxide)" : slack <= 10 ? "var(--brass)" : "var(--verdigris)",
          background: slack < 0 ? "var(--oxide-soft)" : slack <= 10 ? "var(--brass-soft)" : "var(--verdigris-soft)",
          borderLeftColor: slack < 0 ? "var(--oxide)" : slack <= 10 ? "var(--brass)" : "var(--verdigris)",
          color: slack < 0 ? "#5E2117" : slack <= 10 ? "#5C4415" : "#16403A"}}>
          <h4>Order by {fmtDate(by)}</h4>
          <p>{slack < 0 ? Math.abs(slack) + " days past that date already."
             : slack <= 10 ? "Only " + slack + " days left to place it."
             : slack + " days of slack."} Working back {preview.leadTimeWeeks || 0} weeks from {fmtDate(f.neededOnSite)}.</p>
        </div>
      )}

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Note</div>
        <textarea className="field" rows={2} value={f.note||""} onChange={e=>set("note",e.target.value)}/></label>

      <div className="toolbar">
        <button className="btn go" disabled={!f.description.trim()||busy} onClick={async()=>{
          setBusy(true);
          await api.saveProcurement({ item:{ ...f, qty:Number(f.qty)||0,
            unitCost:Number(f.unitCost)||0, budgeted:Number(f.budgeted)||0, quoted:Number(f.quoted)||0,
            leadTimeWeeks: f.leadTimeWeeks === "" ? null : Number(f.leadTimeWeeks),
            neededOnSite: f.neededOnSite || null, lineItemId: f.lineItemId || null,
            supplierId: f.supplierId || null }, actor:me.contact });
          await refresh(); setBusy(false); onDone(); say(item ? "Updated" : "Added to the buy list");
        }}>{item ? "Save" : "Add it"}</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Messages */
/*
 * A list beside the thread. Correspondence is scanned before it is read, and
 * the old layout stacked full-width cards and then replaced the whole lot with
 * whichever one you opened — so finding the message you half-remembered meant
 * going back and forth. Everything about who may write to whom is unchanged.
 *
 * One component, two audiences. `embedded` renders it as a section of the
 * investor view rather than as a page of its own: an investor has no
 * navigation, so their one channel to the owner lives under the briefing they
 * just read. What differs is the framing, and the fact that an investor is
 * never offered a recipient to choose.
 */
function Messages({ db, role, me, meId, say, refresh, setSelId, deepThread, setDeepThread, embedded }) {
  const [openId, setOpenId] = useState(deepThread || null);
  const asInvestor = role === "investor" || db.previewingAs === "investor";
  // An owner previewing the investor view is looking, not corresponding.
  const readOnly = db.previewingAs === "investor";
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const threads = threadsFor(db, meId, role).slice()
    .sort((a,b) => { const la = lastMessage(a), lb = lastMessage(b);
      return (la ? la.at : a.createdAt) < (lb ? lb.at : b.createdAt) ? 1 : -1; });
  const thread = threads.find(t => t.id === openId);

  // Marked read on opening, and again when something lands on a thread that is
  // already open — otherwise a message that arrives while you are looking
  // straight at it stays bold and keeps counting against the badge.
  useEffect(() => {
    if (thread && unreadIn(thread, meId) > 0) api.markThreadRead({ threadId: thread.id, partyId: meId }).then(refresh);
  }, [openId, thread && (thread.messages || []).length]);

  // Arrived from a link in an email for a thread that is not theirs, or that no
  // longer exists. Say so, rather than showing an empty list and letting them
  // wonder whether the message was withdrawn.
  const deepMissing = deepThread && !threads.some(t => t.id === deepThread);
  useEffect(() => { if (setDeepThread && deepThread) setDeepThread(null); }, []);

  const send = async () => {
    setBusy(true);
    await api.postMessage({ threadId:thread.id, body:draft, actor:me.contact, partyId:meId, role });
    setDraft(""); await refresh(); setBusy(false); say("Sent");
  };

  return (
    <React.Fragment>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",
                   gap:12,flexWrap:"wrap",marginBottom:12}}>
        <div style={{minWidth:0}}>
          {embedded
            ? <React.Fragment>
                <div style={{fontWeight:700,fontSize:15,letterSpacing:"-.02em"}}>Questions for {developerName(db)}</div>
                <p className="muted" style={{fontSize:12,margin:"2px 0 0"}}>
                  Anything you ask here goes to {developerName(db)} and to nobody else. You'll get an email
                  when they answer.</p>
              </React.Fragment>
            : <React.Fragment>
                <h1 className="pagetitle">Messages</h1>
                <p className="pagesub">Sent and received as email, kept here as the record. When it's about a specific
                  piece of work, say it on that line item instead — it stays attached to the thing it's about.</p>
              </React.Fragment>}
        </div>
        {!readOnly && (
          <Action className="btn" onClick={()=>setComposing(true)}
            what={asInvestor
              ? "Writes to " + developerName(db) + ", and to nobody else."
              : "Starts a thread. Everyone you pick gets it as an email linking back here."}>
            {asInvestor ? "Ask a question" : "New message"}</Action>
        )}
      </div>

      {deepMissing && (
        <div className="banner" style={{marginBottom:12}}>
          <h4>That conversation isn't here</h4>
          <p>The link was for a thread you're not on, on this project. If you think you should be, ask
             whoever sent it to add you.</p>
        </div>
      )}

      {threads.length === 0 ? (
        <Empty title={asInvestor ? "You haven't asked anything yet" : "No messages yet"}
               body={asInvestor
                 ? "If something in the briefing needs explaining, ask here. It reaches " + developerName(db) + " by email, their answer comes back to this page, and the exchange is kept as part of the record."
                 : "Anything you send from here goes out by email with a link back to the thread, and everything said stays on the record."}/>
      ) : (
        <div className={"mail" + (embedded ? " embed" : "") + (thread ? " reading" : "")}>
          <div className="maillist">
            {threads.map(t => {
              const last = lastMessage(t), unread = unreadIn(t, meId);
              const others = (t.participants||[]).filter(x => x !== meId);
              return (
                <button key={t.id} onClick={()=>setOpenId(t.id)}
                  className={"mailrow" + (unread ? " unread" : "") + (t.id === openId ? " on" : "")}>
                  <span className="who">
                    <b>{others.length ? others.map(x => partyName(db, x)).join(", ") : "Just you"}</b>
                    <span className="when">{last ? fmtBrief(last.at, db.project.today) : ""}</span>
                  </span>
                  <span className="subj">
                    {unread > 0 && <i className="dot"/>}
                    {t.subject}
                    {t.kind !== "direct" && THREAD_KINDS[t.kind]
                      ? <span className="muted" style={{fontWeight:400}}> · {THREAD_KINDS[t.kind].label}</span> : null}
                  </span>
                  {last && <span className="snip">{last.who}: {last.body}</span>}
                </button>
              );
            })}
          </div>

          <div className="mailview">
            {!thread ? (
              <div className="mailnone">
                <div className="eyebrow">Nothing open</div>
                <p style={{fontSize:12.5,margin:"6px 0 0",maxWidth:280}}>
                  Pick a conversation on the left to read it.</p>
              </div>
            ) : (
              <React.Fragment>
                <div className="mailh">
                  <div style={{minWidth:0}}>
                    <div className="subj">{thread.subject}</div>
                    <div className="itemmeta">
                      {(thread.participants||[]).map(x => partyName(db, x)).join(" · ")}</div>
                  </div>
                  <div style={{display:"flex",gap:6,flex:"0 0 auto"}}>
                    <button className="btn ghost sm mailback" onClick={()=>setOpenId(null)}>Back</button>
                    {thread.kind === "lineItem" && thread.refId && (
                      <button className="btn ghost sm" onClick={()=>setSelId(thread.refId)}>
                        Open {(db.lineItems.find(i=>i.id===thread.refId)||{}).no}</button>
                    )}
                  </div>
                </div>

                <div className="mailb">
                  {(thread.messages||[]).map(m => {
                    const mine = m.partyId === meId;
                    return (
                      <div key={m.id} className="note" style={{marginBottom:14,
                        borderLeftColor: mine ? "var(--verdigris)" : "var(--brass)"}}>
                        <div className="meta">{m.who}{m.who === partyName(db, m.partyId) ? "" : " · " + partyName(db, m.partyId)}
                          {" · "}{fmtStamp(m.at)}{m.viaEmail ? " · by email" : ""}</div>
                        <div style={{fontSize:13,whiteSpace:"pre-wrap",marginTop:3}}>{m.body}</div>
                      </div>
                    );
                  })}
                </div>

                {!readOnly && (
                  <div className="mailf">
                    <textarea className="field" rows={2} value={draft} onChange={e=>setDraft(e.target.value)}
                      onKeyDown={e=>{ if ((e.metaKey||e.ctrlKey) && e.key === "Enter" && draft.trim() && !busy) send(); }}
                      placeholder={asInvestor ? "Write your reply. Only " + developerName(db) + " sees it."
                                              : "Reply — they'll get it as an email with a link back here."}/>
                    <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
                      <button className="btn go sm" disabled={!draft.trim()||busy} onClick={send}>Send</button>
                      <span className="muted" style={{fontSize:11}}>
                        Goes out from the project address. Their answer belongs here, where it stays on the record.</span>
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      )}

      {composing && <Compose {...{db, me, meId, refresh, say, asInvestor}}
        onDone={(id)=>{ setComposing(false); if (id) setOpenId(id); }}/>}
    </React.Fragment>
  );
}

/* Module level on purpose: a component declared inside another component's body
   is a new type on every render, so React unmounts and rebuilds every chip each
   time one is clicked. */
function ToChip({ party, on, onPick }) {
  return (
    <button className={"chip sm " + (on ? "on" : "")} onClick={()=>onPick(party)}>
      {party.name}</button>
  );
}

/*
 * Bottom right, over whatever you were reading — writing a message is something
 * you do while looking at the thing that prompted it, and a full-page form made
 * you leave the thread to answer it.
 */
function Compose({ db, me, meId, refresh, say, onDone, asInvestor }) {
  const [f, setF] = useState({ subject:"", body:"", participants:[], kind:"direct", refId:"" });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const isInvestor = (x) => x.role === "investor";
  const byName = (a,b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" });
  const active = db.parties.filter(x => x.status === "active" && x.id !== meId);
  const people = active.filter(x => !isInvestor(x)).sort(byName);
  const investors = active.filter(isInvestor).sort(byName);

  // Selecting an investor clears everyone else, and vice versa. A thread is a
  // participant list, so two investors on one — or an investor alongside a
  // contractor — would tell each of them who the others are. The server refuses
  // the same combination; this is so nobody has to be refused to find out.
  const pick = (x) => {
    const on = f.participants.includes(x.id);
    if (on) return set("participants", f.participants.filter(y => y !== x.id));
    const selected = active.filter(c => f.participants.includes(c.id));
    const clash = isInvestor(x) ? selected.length > 0 : selected.some(isInvestor);
    set("participants", clash ? [x.id] : f.participants.concat(x.id));
  };
  const toInvestor = investors.some(x => f.participants.includes(x.id));
  const valid = f.subject.trim().length > 2 && f.body.trim().length > 2 &&
    (asInvestor || f.participants.length > 0);

  const shown = filter.trim()
    ? investors.filter(x => (x.name + " " + (x.contact||"")).toLowerCase().includes(filter.trim().toLowerCase()))
    : investors;

  return (
    <div className="composer">
      <div className="composerh">
        <span className="t">{asInvestor ? "Ask " + developerName(db) + " a question" : "New message"}</span>
        <button className="composerx" title="Close" onClick={()=>onDone(null)}>✕</button>
      </div>

      <div className="composerb">
        {asInvestor ? (
          <p className="muted" style={{fontSize:12,margin:"0 0 10px"}}>
            This goes to {developerName(db)}. No other investor sees it, or sees that you sent it.
          </p>
        ) : (
          <React.Fragment>
            <div className="eyebrow">To</div>
            <div className="torow">{people.map(x =>
              <ToChip key={x.id} party={x} on={f.participants.includes(x.id)} onPick={pick}/>)}</div>

            {investors.length > 0 && (
              <div className="togroup">
                <span className="eyebrow">Investors</span>
                {investors.length > 8 && (
                  <input className="field" value={filter} placeholder="Find an investor…"
                         style={{marginBottom:5}} onChange={e=>setFilter(e.target.value)}/>
                )}
                <div className="torow">{shown.map(x =>
                  <ToChip key={x.id} party={x} on={f.participants.includes(x.id)} onPick={pick}/>)}</div>
                {shown.length === 0 && <p className="muted" style={{fontSize:11.5,margin:"4px 0 0"}}>
                  Nobody by that name.</p>}
                <p className="muted" style={{fontSize:11,margin:"6px 0 0"}}>
                  One at a time, and never alongside anyone else — a thread names everyone on it, and no
                  investor should learn who the others are. To tell them all something at once, write an
                  <strong> investor update</strong> instead; it reaches each of them separately.
                </p>
              </div>
            )}

            {toInvestor && <p className="muted" style={{fontSize:11,margin:"7px 0 0"}}>
              This thread is between you and them alone. Nobody else can be added to it.</p>}
          </React.Fragment>
        )}

        <label style={{display:"block",marginTop:11}}><div className="eyebrow">Subject</div>
          <input className="field" value={f.subject} onChange={e=>set("subject",e.target.value)}/></label>

        {!asInvestor && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:9,marginTop:9}}>
            <label><div className="eyebrow">About</div>
              <select className="field" value={f.kind} onChange={e=>{ set("kind",e.target.value); set("refId",""); }}>
                <option value="direct">Nothing in particular</option>
                <option value="lineItem">A line item</option>
                <option value="draw">A draw</option>
              </select></label>
            {f.kind === "lineItem" && (
              <label><div className="eyebrow">Which line item</div>
                <select className="field" value={f.refId} onChange={e=>set("refId",e.target.value)}>
                  <option value="">Choose…</option>
                  {db.lineItems.map(i => <option key={i.id} value={i.id}>{i.no} · {i.name}</option>)}
                </select></label>
            )}
            {f.kind === "draw" && (
              <label><div className="eyebrow">Which draw</div>
                <select className="field" value={f.refId} onChange={e=>set("refId",e.target.value)}>
                  <option value="">Choose…</option>
                  {db.draws.map(d => <option key={d.id} value={d.id}>{drawLabel(d)}</option>)}
                </select></label>
            )}
          </div>
        )}

        <label style={{display:"block",marginTop:9}}><div className="eyebrow">Message</div>
          <textarea className="field" rows={6} value={f.body} onChange={e=>set("body",e.target.value)}/></label>

        {!asInvestor && (
          <p className="muted" style={{fontSize:11,margin:"8px 0 0"}}>
            If this is about a specific piece of work, a note on that line item or punch item keeps the record
            attached to the thing it's about — which is where you'll look for it later.
          </p>
        )}
      </div>

      <div className="composerf">
        <button className="btn go sm" disabled={!valid||busy} onClick={async()=>{
          setBusy(true);
          const t = await api.createThread({ actor:me.contact, partyId:meId, thread:{
            subject:f.subject, body:f.body, kind:f.kind, refId:f.refId || null,
            participants:[meId].concat(f.participants) } });
          await refresh(); setBusy(false); onDone(t && t.id); say("Sent");
        }}>Send</button>
        <button className="btn ghost sm" onClick={()=>onDone(null)}>Discard</button>
        <span className="muted" style={{fontSize:11}}>Sent as email, kept here.</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Docs */
/*
 * The manual, rendered from web/src/docs.js.
 *
 * Search is a plain substring match over the same data the page renders, which
 * is enough at this size and has the advantage of never disagreeing with what
 * is on the page. Every block type here exists because the content needed it;
 * there is no block type nothing uses.
 */
const docsText = (b) =>
  [b.p, b.note, b.heading2, ...(b.list || []), ...(b.steps || []),
   ...(b.table ? b.table.head.concat(...b.table.rows) : [])].filter(Boolean).join(" ");

const docsIndex = DOCS.flatMap((topic) =>
  topic.sections.map((sec) => ({
    topicId: topic.id, topicTitle: topic.title, secId: sec.id, heading: sec.heading,
    text: (sec.heading + " " + sec.blocks.map(docsText).join(" ")),
  })));

function DocsBlock({ b }) {
  if (b.note) return <p className="docsnote">{b.note}</p>;
  if (b.list) return <ul>{b.list.map((x, i) => <li key={i}>{x}</li>)}</ul>;
  if (b.steps) return <ol>{b.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>;
  if (b.table) return (
    <div className="tablewrap" style={{marginBottom:13}}>
      <table className="docstable">
        <thead><tr>{b.table.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{b.table.rows.map((r, i) =>
          <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
  if (b.heading2) return (
    <React.Fragment>
      <h4>{b.heading2}</h4>
      {b.p ? <p>{b.p}</p> : null}
      {b.list ? <ul>{b.list.map((x, i) => <li key={i}>{x}</li>)}</ul> : null}
    </React.Fragment>
  );
  return <p>{b.p}</p>;
}

function Docs() {
  const [topicId, setTopicId] = useState(DOCS[0].id);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const topic = DOCS.find((t) => t.id === topicId) || DOCS[0];

  const results = q.length < 2 ? [] : docsIndex
    .filter((e) => e.text.toLowerCase().includes(q))
    .slice(0, 20);

  // A line of context around the match, so a result is judgeable without
  // opening it.
  const excerpt = (text) => {
    const i = text.toLowerCase().indexOf(q);
    const from = Math.max(0, i - 55);
    return (from > 0 ? "…" : "") + text.slice(from, i + q.length + 95).trim() + "…";
  };

  const go = (tId, sId) => {
    setTopicId(tId); setQuery("");
    window.setTimeout(() => {
      const el = document.getElementById("doc-" + sId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const guides = DOCS.filter((t) => t.audience);
  const general = DOCS.filter((t) => !t.audience);

  return (
    <React.Fragment>
      <h1 className="pagetitle">Documentation</h1>
      <p className="pagesub">Every screen and every rule, written out. Search, or pick a topic.</p>

      <div className="docs" style={{marginTop:18}}>
        <div className="docsnav noprint">
          <input className="field docssearch" value={query} placeholder="Search the docs"
                 onChange={(e) => setQuery(e.target.value)}/>
          <div className="eyebrow docsgroup">The platform</div>
          {general.map((t) => (
            <button key={t.id} className={"docstopic" + (t.id === topicId && !q ? " on" : "")}
                    onClick={() => { setQuery(""); setTopicId(t.id); window.scrollTo(0, 0); }}>
              {t.title}</button>
          ))}
          <div className="eyebrow docsgroup">Guides by role</div>
          {guides.map((t) => (
            <button key={t.id} className={"docstopic" + (t.id === topicId && !q ? " on" : "")}
                    onClick={() => { setQuery(""); setTopicId(t.id); window.scrollTo(0, 0); }}>
              {t.title}</button>
          ))}
        </div>

        <div className="docsbody">
          {q.length >= 2 ? (
            <React.Fragment>
              <p className="docshit">
                {results.length === 0 ? "Nothing matches that." :
                 results.length === 1 ? "One match." : results.length + " matches."}
              </p>
              {results.map((r) => (
                <button key={r.topicId + r.secId} className="docsresult"
                        onClick={() => go(r.topicId, r.secId)}>
                  <span className="docscrumb">{r.topicTitle}</span>
                  <span className="t">{r.heading}</span>
                  <span className="c">{excerpt(r.text)}</span>
                </button>
              ))}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <h2>{topic.title}</h2>
              <p className="muted" style={{fontSize:13,marginTop:0}}>{topic.summary}</p>

              {topic.sections.length > 1 && (
                <div className="docsjump noprint">
                  {topic.sections.map((sec) => (
                    <a key={sec.id} href={"#doc-" + sec.id}
                       onClick={(e) => { e.preventDefault(); go(topic.id, sec.id); }}>
                      {sec.heading}</a>
                  ))}
                </div>
              )}

              {topic.sections.map((sec) => (
                <section key={sec.id} id={"doc-" + sec.id}>
                  <h3>{sec.heading}</h3>
                  {sec.blocks.map((b, i) => <DocsBlock key={i} b={b}/>)}
                </section>
              ))}
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

/* ---------------------------------------------- Investor progress updates */
function Updates({ db, role, me, say, refresh }) {
  const [editing, setEditing] = useState(null);
  const canEdit = role === "owner";
  const list = updatesOf(db);
  const photoPool = (db.dailyLogs || []).flatMap(l => (l.photos || []).map(p => ({ ...p, date: l.date })));

  return (
    <React.Fragment>
      <h1 className="pagetitle">Investor updates</h1>
      <p className="pagesub">A short post with a few pictures, every month or so. Investors who hear nothing assume the
        worst, and the next time you write is a capital call.</p>

      {canEdit && (
        <div className="toolbar noprint">
          <button className="btn" onClick={()=>setEditing(editing === "new" ? null : "new")}>
            {editing === "new" ? "Cancel" : "Write an update"}</button>
        </div>
      )}
      {editing && <UpdateForm {...{db, me, photoPool, update: editing === "new" ? null : editing, refresh, say}}
                              onDone={()=>setEditing(null)}/>}

      {list.length === 0
        ? <Empty title="Nothing posted yet" body="Pick a few photos from the daily log, write three sentences, publish."/>
        : list.map(u => (
          <div className="card pad" key={u.id} style={{marginBottom:10,
            borderLeft:"3px solid var(--" + (u.published ? "verdigris" : "rule") + ")"}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"baseline"}}>
              <div>
                <span style={{fontWeight:700,fontSize:14.5,letterSpacing:"-.015em"}}>{u.title}</span>
                <div className="itemmeta">{u.author} · {fmtStamp(u.at)}
                  {(u.photos||[]).length ? " · " + u.photos.length + " photos" : ""}</div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span className={"tag " + (u.published ? "g" : "n")}>{u.published ? "Published" : "Draft"}</span>
                {canEdit && <button className="btn ghost sm noprint" onClick={()=>setEditing(u)}>Edit</button>}
              </div>
            </div>
            <p style={{fontSize:13,margin:"9px 0 0",lineHeight:1.55,maxWidth:660,whiteSpace:"pre-wrap"}}>{u.body}</p>
            {(u.photos||[]).length > 0 && (
              <div className="shots" style={{marginTop:10}}>
                {u.photos.map(ph => (
                  <figure className="shot" key={ph.id} style={{margin:0}}>
                    <img src={ph.url} alt={u.title}/></figure>
                ))}
              </div>
            )}
          </div>
        ))}
    </React.Fragment>
  );
}

function UpdateForm({ db, update, me, photoPool, refresh, say, onDone }) {
  const [f, setF] = useState(update ? { ...update } : { title:"", body:"", photos:[], published:false });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const chosen = (f.photos || []).map(p => p.id);

  return (
    <div className="card pad" style={{marginBottom:12, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>{update ? "Edit update" : "Write an update"}</SectionHead>
      <label style={{display:"block"}}><div className="eyebrow">Headline</div>
        <input className="field" value={f.title} onChange={e=>set("title",e.target.value)}
               placeholder="Storefront glazing in, restrooms complete"/></label>
      <label style={{display:"block",marginTop:12}}><div className="eyebrow">What happened</div>
        <textarea className="field" rows={5} value={f.body} onChange={e=>set("body",e.target.value)}
          placeholder="Three or four sentences. What moved, what's next, anything they'd hear about anyway."/></label>

      <div style={{marginTop:14}}>
        <div className="eyebrow" style={{marginBottom:6}}>
          Pictures {photoPool.length ? "· from the daily log" : ""}
        </div>
        {photoPool.length === 0
          ? <div className="muted" style={{fontSize:12}}>
              No site photos posted yet. Once contractors are posting daily updates, you pick from those here rather
              than hunting through your phone.</div>
          : (
          <div className="shots">
            {photoPool.map(ph => {
              const on = chosen.includes(ph.id);
              return (
                <button key={ph.id} className="shot" style={{padding:0,border: on ? "2px solid var(--verdigris)" : "1px solid var(--rule)"}}
                  onClick={()=>set("photos", on ? f.photos.filter(x=>x.id!==ph.id) : (f.photos||[]).concat(ph))}>
                  <img src={ph.url} alt={"Site photo " + ph.date}/>
                  <span className="ts">{on ? "SELECTED" : fmtDate(ph.date)}</span>
                </button>
              );
            })}
          </div>
        )}
        <p className="muted" style={{fontSize:11.5,marginTop:8,marginBottom:0,maxWidth:600}}>
          Pick deliberately. Investors shouldn't be scrolling through every photo on the job — a close-up of a
          deficiency reads very differently without the punch item next to it.
        </p>
      </div>

      <div className="toolbar">
        <button className="btn go" disabled={!f.title.trim()||!f.body.trim()||busy} onClick={async()=>{
          setBusy(true);
          await api.saveUpdate({ update:{ ...f, published:true }, actor:me.contact });
          await refresh(); setBusy(false); onDone(); say("Published — investors can see it now");
        }}>Publish</button>
        <button className="btn ghost" disabled={!f.title.trim()||busy} onClick={async()=>{
          setBusy(true);
          await api.saveUpdate({ update:{ ...f, published:false }, actor:me.contact });
          await refresh(); setBusy(false); onDone(); say("Saved as a draft");
        }}>Save draft</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------- Punch list, whole job */
/* The screen Kenneth actually opens standing in the building. */
function PunchWalk({ db, role, me, meId, items, setSelId }) {
  const [filter, setFilter] = useState("open");
  const [party, setParty] = useState("all");
  const p = db.project;

  const all = useMemo(() => items.flatMap(it => punchOf(it).map(px => ({ px, it }))), [items]);
  const rows = all.filter(({px, it}) => {
    if (party !== "all" && px.assignedTo !== party) return false;
    if (filter === "open") return PUNCH_OPEN.includes(px.status);
    if (filter === "blocking") return px.severity === "blocking" && PUNCH_OPEN.includes(px.status);
    if (filter === "money") return px.severity === "holdback" && PUNCH_OPEN.includes(px.status);
    if (filter === "overdue") return punchOverdue(px);
    if (filter === "waiting") return px.status === "fixed";
    return true;
  }).sort((a,b) => {
    const rank = x => (punchOverdue(x.px) ? 0 : 1) + ["blocking","holdback","minor"].indexOf(x.px.severity);
    return rank(a) - rank(b);
  });

  const t = {
    open: all.filter(x => PUNCH_OPEN.includes(x.px.status)).length,
    blocking: all.filter(x => x.px.severity === "blocking" && PUNCH_OPEN.includes(x.px.status)).length,
    overdue: all.filter(x => punchOverdue(x.px)).length,
    waiting: all.filter(x => x.px.status === "fixed").length,
    held: all.reduce((n,x) => n + (PUNCH_OPEN.includes(x.px.status) && x.px.severity === "holdback" ? (x.px.holdbackAmount||0) : 0), 0),
    blockedValue: items.filter(it => blockingPunch(it).length > 0).reduce((n,it) => n + it.scheduledValue*it.percent/100, 0),
  };

  return (
    <React.Fragment>
      <h1 className="pagetitle">Punch list</h1>
      <p className="pagesub">Everything flagged on a walk, across every line item. Sorted so the things holding up money and the things past due come first.</p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Open items" v={t.open} sub={`${all.length} logged all-time`}/>
        <Stat k="Blocking approval" v={t.blocking} sub={t.blockedValue ? usd(t.blockedValue) + " held up" : "—"} tone={t.blocking?"flag":""}/>
        <Stat k="Cash withheld" v={usd(t.held)} sub="Returns when verified" tone={t.held?"":"good"}/>
        <Stat k="Past due" v={t.overdue} tone={t.overdue?"flag":""}/>
        <Stat k="Fix submitted" v={t.waiting} sub="Waiting on your walk"/>
      </div>

      <div className="toolbar noprint">
        <select className="field" style={{maxWidth:190}} value={filter} onChange={e=>setFilter(e.target.value)} aria-label="Filter punch items">
          <option value="open">Still open</option>
          <option value="blocking">Blocking approval</option>
          <option value="money">Holding money back</option>
          <option value="overdue">Past due</option>
          <option value="waiting">Fix submitted, needs a walk</option>
          <option value="all">Everything, including closed</option>
        </select>
        {!ROLES[role].scoped && (
          <select className="field" style={{maxWidth:210}} value={party} onChange={e=>setParty(e.target.value)} aria-label="Filter by contractor">
            <option value="all">Every contractor</option>
            {db.parties.filter(x => x.role === "contractor").map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        <span className="muted" style={{fontSize:12}}>{rows.length} shown</span>
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>download("calico-punch-list-" + iso(TODAY) + ".csv",
          toCSV(rows.map(({px,it}) => ({
            no: px.no, lineItem: it.no, work: it.name, title: px.title, location: px.location,
            severity: PUNCH_SEVERITY[px.severity].label, status: PUNCH_STATUS[px.status].label,
            held: px.holdbackAmount || 0, due: px.dueDate || "",
            overdue: punchOverdue(px) ? daysBetween(px.dueDate, iso(TODAY)) : 0,
            owedBy: px.assignedTo ? partyName(db, px.assignedTo) : "",
            raisedBy: px.thread[0] ? px.thread[0].who : "", raisedAt: px.raisedAt,
            latest: px.thread.length ? px.thread[px.thread.length-1].body : "",
          })),
          [["no","Punch No"],["lineItem","Line Item"],["work","Description of Work"],["title","Deficiency"],
           ["location","Location"],["severity","Effect On Payment"],["status","Status"],["held","Amount Withheld"],
           ["due","Fix By"],["overdue","Days Overdue"],["owedBy","Owed By"],["raisedBy","Raised By"],
           ["raisedAt","Raised At"],["latest","Latest Note"]]))}>Export punch list</button>
        <button className="btn ghost sm" onClick={()=>window.print()}>Print for the walk</button>
      </div>

      {rows.length === 0
        ? <Empty title="Nothing on this list"
                 body={filter === "open" ? "No open punch items. Anything flagged on a walk shows up here until it's verified fixed or waived."
                                         : "Nothing matches that filter."}/>
        : (
        <div className="tablewrap">
          <table className="sov">
            <thead><tr>
              <th className="plain">Punch</th><th className="plain">Deficiency</th>
              <th className="plain">Line item</th><th className="plain">Owed by</th>
              <th className="plain">Fix by</th><th className="plain">Withheld</th>
              <th className="plain">Effect</th><th className="plain">Status</th>
            </tr></thead>
            <tbody>
              {rows.map(({px, it}) => (
                <tr key={px.id} onClick={()=>setSelId(it.id)} style={{cursor:"pointer"}}>
                  <td className="itemno">{px.no}</td>
                  <td>
                    <div className="itemname">{px.title}</div>
                    <div className="itemmeta">{px.location || "—"}
                      {px.thread.length > 1 ? " · " + (px.thread.length - 1) + (px.thread.length === 2 ? " reply" : " replies") : ""}
                      {px.photos.length ? " · " + px.photos.length + " photos" : ""}</div>
                  </td>
                  <td style={{fontSize:12}}><div style={{fontWeight:500}}>{it.no}</div>
                    <div className="itemmeta">{it.name}</div></td>
                  <td style={{fontSize:12}}>{px.assignedTo ? partyName(db, px.assignedTo) : "—"}</td>
                  <td className="num" style={{fontSize:12, color: punchOverdue(px) ? "var(--oxide)" : "var(--slate)"}}>
                    {px.dueDate ? fmtDate(px.dueDate) : "—"}
                    {punchOverdue(px) && <div style={{fontWeight:600}}>{daysBetween(px.dueDate, iso(TODAY))}d over</div>}
                  </td>
                  <td className="r num" style={{color: px.holdbackAmount && PUNCH_OPEN.includes(px.status) ? "var(--brass)" : "var(--slate)"}}>
                    {px.holdbackAmount && PUNCH_OPEN.includes(px.status) ? usd(px.holdbackAmount) : "—"}</td>
                  <td><span className={"tag " + PUNCH_SEVERITY[px.severity].tag}>{PUNCH_SEVERITY[px.severity].label}</span></td>
                  <td><span className={"tag " + PUNCH_STATUS[px.status].tag}>{PUNCH_STATUS[px.status].label}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </React.Fragment>
  );
}

/* --------------------------------------------------- Contractors & subs */
function Parties({ db, role, me, meId, say, refresh, setView, setSelId }) {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const canManage = role === "owner";
  const contractors = db.parties.filter(x => x.role === "contractor");
  // Investors have their own screen. Forty-two of them under a heading on the
  // contractors page, with a nav badge counting only the one contractor, is a
  // list nobody would think to look at.
  const others = db.parties.filter(x => x.role !== "contractor" && x.role !== "investor");

  return (
    <React.Fragment>
      <h1 className="pagetitle">Contractors &amp; subs</h1>
      <p className="pagesub">Everyone who can sign for work, what they hold, and what they owe. Two gates before anyone
        can take scope: current paperwork on file, and the terms initialled.</p>

      {canManage && (
        <div className="toolbar noprint">
          <button className="btn" onClick={()=>setAdding(a=>!a)}>{adding ? "Cancel" : "Add a contractor or sub"}</button>
          <button className="btn ghost" onClick={()=>download("calico-parties-" + iso(TODAY) + ".csv",
            toCSV(db.parties.map(x => { const l = partyLedger(db, x.id); return { ...x,
              roleLabel: ROLES[x.role].label, items: l.items, assigned: l.assigned, inPlace: Math.round(l.inPlace),
              disbursed: l.disbursed, overFunded: Math.round(l.overFunded), penalties: Math.round(l.penalties),
              backcharge: l.backcharge, gaps: complianceGaps(x).join("; ") || "none" }; }),
            [["name","Company"],["contact","Contact"],["roleLabel","Role"],["trade","Trade"],["email","Email"],
             ["status","Status"],["licenseNo","License"],["coiExpires","Insurance Expires"],["w9","W-9"],
             ["items","Line Items"],["assigned","Assigned Value"],["inPlace","Value In Place"],["disbursed","Paid To Date"],
             ["overFunded","Over-Funded"],["penalties","Penalties"],["backcharge","Backcharges"],["gaps","Compliance Gaps"]]))}>
            Export directory
          </button>
        </div>
      )}

      {adding && <PartyForm onCancel={()=>setAdding(false)} onSave={async (party) => {
        await api.addParty({ party, actor: me.contact }); setAdding(false); await refresh();
        say(party.name + " added to the directory");
      }}/>}

      <div className="sec" style={{marginTop:adding?20:8}}>
        <SectionHead right={<span className="muted" style={{fontSize:11}}>{contractors.filter(x=>x.status==="active").length} active</span>}>
          Building the work
        </SectionHead>
        {contractors.length === 0
          ? <Empty title="No contractors on file" body="Add the general contractor and any subs working directly for the owner."/>
          : contractors.map(x => (
            <PartyCard key={x.id} party={x} db={db} canManage={canManage} me={me} meId={meId}
                       editing={editId===x.id} setEditId={setEditId} refresh={refresh} say={say} setSelId={setSelId}/>
          ))}
      </div>

      <div className="sec">
        <SectionHead>Signing, funding, watching</SectionHead>
        {others.map(x => (
          <PartyCard key={x.id} party={x} db={db} canManage={canManage} me={me} meId={meId}
                     editing={editId===x.id} setEditId={setEditId} refresh={refresh} say={say} setSelId={setSelId}/>
        ))}
      </div>

      <div className="card pad" style={{marginBottom:16}}>
        <SectionHead>Onboarding a new contractor</SectionHead>
        <ol style={{fontSize:12.5,margin:"0",paddingLeft:18,lineHeight:1.7,maxWidth:680}}>
          <li>Add them above with their trade, licence, insurance expiry and W-9.</li>
          <li><strong>Send invite</strong> — they get a link to the onboarding page.</li>
          <li>They read {TERMS.length} plain-language clauses and initial each one. The clause about delay penalties
              shows your actual rate, so nobody can later claim they didn't know.</li>
          <li>Download the generated agreement, print to PDF, and push it through Adobe Sign for execution.
              Record the agreement ID against them so the two records line up.</li>
          <li>Offer them scope — individually on a line item, or in a batch from the schedule of values.</li>
        </ol>
        <p className="muted" style={{fontSize:11.5,marginTop:10,marginBottom:0}}>
          The in-app initials are evidence they read and understood the terms. The Adobe Sign envelope is the executed
          contract. Have counsel review the clause wording against your real agreement before using this on a live job.
        </p>
      </div>

      <div className="card pad">
        <SectionHead>Replacing a contractor mid-scope</SectionHead>
        <p className="muted" style={{fontSize:12.5, marginTop:0, maxWidth:680}}>
          Open any line item and use <strong>Reassign this scope</strong>. It splits the line: the portion actually built
          closes out under the outgoing party's name at the value you agree, and the remaining balance becomes a fresh
          offer to whoever picks it up. Signatures and photos already given stay with the party that gave them — they
          never transfer. If that party was paid more than the work in place, the difference is recorded as a backcharge
          against them rather than quietly absorbed into the new sub's line.
        </p>
      </div>
    </React.Fragment>
  );
}

function PartyCard({ party, db, canManage, me, meId, editing, setEditId, refresh, say, setSelId }) {
  const [recordId, setRecordId] = useState(null);
  const l = partyLedger(db, party.id);
  const gaps = complianceGaps(party);
  const isContractor = party.role === "contractor";
  const items = db.lineItems.filter(i => i.assignment && i.assignment.partyId === party.id);
  const [open, setOpen] = useState(false);

  if (editing) return (
    <PartyForm party={party} onCancel={()=>setEditId(null)} onSave={async (patch) => {
      await api.updateParty({ id: party.id, patch, actor: me.contact }); setEditId(null); await refresh();
      say(patch.name + " updated");
    }}/>
  );

  return (
    <div className="card pad" style={{marginBottom:10, opacity: party.status === "active" ? 1 : .6}}>
      <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
        <div style={{minWidth:200}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontWeight:700,fontSize:14.5,letterSpacing:"-.015em"}}>{party.name}</span>
            {party.status !== "active" && <span className="tag n">Removed</span>}
            {party.id === meId && <span className="tag p">You</span>}
          </div>
          <div className="itemmeta">
            {ROLES[party.role].label}{party.trade && party.trade !== "General" ? " · " + party.trade : ""}
            {party.contact ? " · " + party.contact : ""}{party.email ? " · " + party.email : ""}
          </div>
          {party.note && <div className="muted" style={{fontSize:11.5,marginTop:4,maxWidth:520}}>{party.note}</div>}
        </div>
        <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
          {isContractor && (() => { const ob = onboardingOf(party), inv = inviteFor(db, party.id); return (
            <React.Fragment>
              <span className={"tag " + (ob.status==="signed"?"g":ob.status==="acknowledged"?"p":"b")}>
                {ob.status==="signed"?"Agreement signed":ob.status==="acknowledged"?"Terms initialled":"Not onboarded"}</span>
              {ob.status==="none" && inv && <span className="tag p">Invite {inv.status}</span>}
            </React.Fragment>
          ); })()}
          {isContractor && (gaps.length === 0
            ? <span className="tag g">Paperwork clear</span>
            : gaps.map(g => <span key={g} className="tag b">{g}</span>))}
          {canManage && party.status === "active" && isContractor && (
            <button className="btn ghost sm noprint" onClick={async()=>{
              await api.invite({ partyId: party.id, email: party.email, actor: me.contact });
              await refresh();
              say("Invitation sent to " + (party.email || party.name));
            }}>{inviteFor(db, party.id) ? "Resend invite" : "Send invite"}</button>
          )}
          {canManage && party.status === "active" && (
            <React.Fragment>
              <button className="btn ghost sm noprint" onClick={()=>setEditId(party.id)}>Edit</button>
              {party.role !== "owner" && (
                <button className="btn ghost sm noprint" onClick={async()=>{
                  await api.setPartyStatus({ id: party.id, status:"inactive", actor: me.contact,
                    reason: l.open ? l.open + " line items still open" : "" });
                  await refresh();
                  say(party.name + " removed — signatures already given stay on the record");
                }}>Remove</button>
              )}
            </React.Fragment>
          )}
          {canManage && party.status !== "active" && (
            <button className="btn ghost sm noprint" onClick={async()=>{
              await api.setPartyStatus({ id: party.id, status:"active", actor: me.contact }); await refresh();
              say(party.name + " reinstated");
            }}>Reinstate</button>
          )}
        </div>
      </div>

      {isContractor && isOnboarded(party) && (
        <div style={{marginTop:10,padding:"8px 11px",border:"1px solid var(--rule)",borderRadius:3,background:"#fff"}}>
          <div className="mono" style={{fontSize:11,color:"var(--slate)"}}>
            Terms v{onboardingOf(party).termsVersion} · initials {onboardingOf(party).initials} ·
            {" "}{onboardingOf(party).clauses.length}/{TERMS.length} clauses · {fmtStamp(onboardingOf(party).acknowledgedAt)}
            {onboardingOf(party).adobe ? " · Adobe " + onboardingOf(party).adobe.status : ""}
          </div>
          {canManage && <button className="btn ghost sm noprint" style={{marginTop:7}}
            onClick={()=>setRecordId(recordId===party.id?null:party.id)}>
            {recordId===party.id ? "Hide record" : "Open onboarding record"}</button>}
        </div>
      )}
      {recordId === party.id && (
        <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--rule)"}}>
          <OnboardingRecord {...{db, party, me, meId, role:"owner", say, refresh}}/>
        </div>
      )}

      {isContractor && l.items > 0 && (
        <React.Fragment>
          <div className="kv" style={{marginTop:12}}>
            <div><div className="k">Line items</div><div className="v">{l.items}</div></div>
            <div><div className="k">Assigned value</div><div className="v">{usd(l.assigned)}</div></div>
            <div><div className="k">Work in place</div><div className="v">{usd(l.inPlace)}</div></div>
            <div><div className="k">Paid to date</div><div className="v">{usd(l.disbursed)}</div></div>
            <div><div className="k">Over-funded</div><div className="v" style={{color:l.overFunded?"var(--oxide)":"inherit"}}>{l.overFunded?usd(l.overFunded):"—"}</div></div>
            <div><div className="k">Penalties accrued</div><div className="v" style={{color:l.penalties?"var(--oxide)":"inherit"}}>{usd(l.penalties)}</div></div>
            <div><div className="k">Punch open</div><div className="v" style={{color:l.punchOpen?"var(--brass)":"inherit"}}>
              {l.punchOpen || "—"}{l.punchHoldback?" · "+usd(l.punchHoldback):""}</div></div>
            <div><div className="k">Still open</div><div className="v" style={{color:l.late?"var(--oxide)":"inherit"}}>{l.open}{l.late?" · "+l.late+" late":""}</div></div>
          </div>
          <button className="btn ghost sm noprint" style={{marginTop:10}} onClick={()=>setOpen(o=>!o)}>
            {open ? "Hide scope" : "Show " + l.items + " line items"}
          </button>
          {open && (
            <div style={{marginTop:8,border:"1px solid var(--rule)",borderRadius:3}}>
              {items.map(it => (
                <button key={it.id} onClick={()=>setSelId(it.id)} style={{display:"flex",width:"100%",justifyContent:"space-between",
                  gap:10,alignItems:"center",background:"transparent",border:0,borderBottom:"1px solid var(--rule-soft)",
                  padding:"7px 10px",textAlign:"left"}}>
                  <span><span className="mono muted" style={{fontSize:11}}>{it.no}</span> <span style={{fontWeight:500}}>{it.name}</span></span>
                  <span style={{display:"flex",gap:6,alignItems:"center",whiteSpace:"nowrap"}}>
                    <span className="num" style={{fontSize:12}}>{usd(it.scheduledValue)}</span>
                    <span className={"tag " + ASSIGN_META[assignState(it)].tag}>{ASSIGN_META[assignState(it)].label}</span>
                    <Tag stage={it.stage}/>
                  </span>
                </button>
              ))}
            </div>
          )}
        </React.Fragment>
      )}
      {isContractor && l.items === 0 && (
        <div className="muted" style={{fontSize:12,marginTop:8}}>
          No scope assigned yet. Open a line item on the schedule of values and offer it to them.
        </div>
      )}

      {/* The paperwork, where the owner is already looking at the company that
          sent it — rather than on a screen of its own that nobody visits. */}
      {isContractor && canManage && (
        <Paperwork db={db} party={party} canReview say={say} refresh={refresh}/>
      )}
    </div>
  );
}

function PartyForm({ party, onSave, onCancel }) {
  const [f, setF] = useState(party ? { ...party } : {
    name:"", contact:"", role:"contractor", trade:"General", email:"", phone:"",
    licenseNo:"", coiExpires:"", w9:false, note:"",
  });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const gaps = complianceGaps({ ...f, status:"active" });
  const valid = f.name.trim().length > 1;

  return (
    <div className="card pad" style={{marginBottom:12, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>{party ? "Edit " + party.name : "New contractor or sub"}</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
        <label><div className="eyebrow">Company</div>
          <input className="field" value={f.name} onChange={e=>set("name",e.target.value)} placeholder="Pine Belt Electric"/></label>
        <label><div className="eyebrow">Contact person</div>
          <input className="field" value={f.contact} onChange={e=>set("contact",e.target.value)} placeholder="Who signs"/></label>
        <label><div className="eyebrow">Role on this job</div>
          <select className="field" value={f.role} onChange={e=>set("role",e.target.value)}>
            {Object.entries(ROLES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
        <label><div className="eyebrow">Trade</div>
          <select className="field" value={f.trade} onChange={e=>set("trade",e.target.value)}>
            {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label><div className="eyebrow">Email</div>
          <input className="field" type="email" value={f.email} onChange={e=>set("email",e.target.value)} placeholder="Where the sign-in link goes"/></label>
        <label><div className="eyebrow">Phone</div>
          <input className="field" value={f.phone||""} onChange={e=>set("phone",e.target.value)}/></label>
      </div>

      {f.role === "contractor" && (
        <React.Fragment>
          <div style={{marginTop:16}}>
            <SectionHead>Before they can be assigned work</SectionHead>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:12}}>
              <label><div className="eyebrow">State license no.</div>
                <input className="field" value={f.licenseNo} onChange={e=>set("licenseNo",e.target.value)}/></label>
              <label><div className="eyebrow">Insurance expires</div>
                <input className="field" type="date" value={f.coiExpires||""} onChange={e=>set("coiExpires",e.target.value)}/></label>
              <label style={{display:"flex",alignItems:"center",gap:8,paddingTop:16}}>
                <input type="checkbox" checked={!!f.w9} onChange={e=>set("w9",e.target.checked)}/>
                <span style={{fontSize:12.5,fontWeight:500}}>W-9 on file</span></label>
            </div>
            <div style={{marginTop:9}}>
              {gaps.length === 0
                ? <span className="tag g">Clear to be assigned work</span>
                : <span className="muted" style={{fontSize:12}}>
                    Missing: {gaps.join(", ")}. You can still add them — they just can't hold scope until this is complete.
                  </span>}
            </div>
          </div>
        </React.Fragment>
      )}

      <label style={{display:"block",marginTop:14}}><div className="eyebrow">Note</div>
        <textarea className="field" rows={2} value={f.note||""} onChange={e=>set("note",e.target.value)}
                  placeholder="How they came recommended, what they're being brought on for."/></label>

      <div className="toolbar">
        <button className="btn go" disabled={!valid} onClick={()=>onSave(f)}>{party ? "Save changes" : "Add to directory"}</button>
        <button className="btn ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- Line item drawer */
function Detail({ item, db, project, role, me, meId, onClose, doAction, refresh, say, freshStamp }) {
  const [note, setNote] = useState("");
  const [percent, setPercent] = useState(item.percent);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(null);   // "assign" | "reassign" | null
  useEffect(() => { setPercent(item.percent); }, [item.id, item.percent]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const s = signoffs(item);
  const acts = availableActions(item, role, meId);
  const assigned = item.assignment ? partyById(db, item.assignment.partyId) : null;
  const astate = assignState(item);
  const isMine = assigned && assigned.id === meId;
  const drift = termsChanged(item);
  const closedOut = item.stage === "closed";
  const before = item.photos.filter(p => p.kind === "before");
  const after  = item.photos.filter(p => p.kind === "after");
  const late = daysLate(item);
  const earned = item.scheduledValue * item.percent / 100;
  const retain = earned * project.retainagePct / 100;
  const penalty = ldAccrued(item, project);
  const canStart = before.length > 0;
  const canSubmit = after.length > 0;
  const openList = openPunch(item);
  const blockers = blockingPunch(item);
  const holdback = punchHoldback(item);
  const canRaise = ["owner","inspector","lender"].includes(role);

  const upload = async (kind, files) => {
    setBusy(true);
    for (const f of Array.from(files).slice(0,8)) {
      const dataUrl = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      await api.addPhoto(item.id, { kind, url: dataUrl, filename: f.name, capturedAt: new Date().toISOString(), by: me.who, role });
    }
    await refresh(); setBusy(false);
    say(files.length + " " + kind + " photo" + (files.length>1?"s":"") + " added to " + item.no);
  };
  const run = async (action) => {
    setBusy(true);
    const auto = action === "approve" && holdback > 0
      ? (note ? note + " — " : "") + "Approved with " + usd(holdback) + " held against " +
        openList.filter(x=>x.severity==="holdback").length + " open punch item(s)"
      : note;
    try { await doAction(item, action, { note: auto, percent: action==="submit" ? 100 : percent }); setNote(""); }
    finally { setBusy(false); }
  };
  const saveNote = async () => {
    if (!note.trim()) return;
    await api.addNote(item.id, { body: note, who: me.who, role, at: new Date().toISOString() });
    setNote(""); await refresh(); say("Note added to " + item.no);
  };

  return (
    <React.Fragment>
      <div className="scrim noprint" onClick={onClose}/>
      <aside className="drawer" role="dialog" aria-label={item.name}>
        <div className="drawerh">
          <div>
            <div className="eyebrow">{item.no} · {item.division}</div>
            <h2 style={{margin:"3px 0 0", fontSize:18, letterSpacing:"-.02em"}}>{item.name}</h2>
            <div style={{marginTop:6, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
              <Tag stage={item.stage}/>
              <span className={"tag " + ASSIGN_META[astate].tag}>{ASSIGN_META[astate].label}</span>
              {late > 0 && !closedOut && <span className="tag b">{late} days late</span>}
              {item.bankDisbursed>0 && !s.owner && <span className="tag b">Paid without approval</span>}
              {item.backcharge > 0 && <span className="tag b">{usd(item.backcharge)} backcharge</span>}
            </div>
            <div className="itemmeta" style={{marginTop:5}}>
              {assigned ? <React.Fragment>Held by <strong>{assigned.name}</strong>{assigned.trade && assigned.trade!=="General" ? " · " + assigned.trade : ""}</React.Fragment>
                        : "No contractor assigned"}
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
            {role === "owner" && !closedOut && (
              <button className="btn ghost sm noprint" onClick={()=>setPanel(panel==="edit"?null:"edit")}>
                {panel==="edit" ? "Cancel" : "Edit details"}</button>
            )}
            <button className="btn ghost sm noprint" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="drawerb">
          {panel === "edit" && (
            <LineItemEditor {...{item, db, project, me, refresh, say}} onDone={()=>setPanel(null)}/>
          )}

          <p style={{marginTop:0, fontSize:13, lineHeight:1.55}}>{item.scope}</p>
          {role === "owner" && panel !== "edit" && (!item.plannedFinish || !item.plannedStart) && (
            <div className="banner brass" style={{marginBottom:14}}>
              <h4>No {(!item.plannedStart && !item.plannedFinish) ? "dates" : !item.plannedStart ? "start date" : "finish date"} set</h4>
              <p>Without both dates this item can't appear on the timeline, and no delay penalty can run against it.{" "}
                <button className="btn ghost sm" style={{marginLeft:6}} onClick={()=>setPanel("edit")}>Set the dates</button></p>
            </div>
          )}

          <div className="kv" style={{marginBottom:20}}>
            <div><div className="k">Scheduled value</div><div className="v">{usd(item.scheduledValue)}</div></div>
            <div><div className="k">Value in place</div><div className="v">{usd(earned)}</div></div>
            <div><div className="k">Retainage ({project.retainagePct}%)</div><div className="v">{usd(retain)}</div></div>
            <div><div className="k">Disbursed</div><div className="v" style={{color: item.bankDisbursed>earned?"var(--oxide)":"inherit"}}>{usd(item.bankDisbursed)}</div></div>
            <div><div className="k">Percent complete</div><div className="v">{item.percent}%</div></div>
            <div><div className="k">Planned finish</div><div className="v" style={{fontSize:13}}>{fmtDate(item.plannedFinish)}</div></div>
            <div><div className="k">Days late</div><div className="v" style={{color: late?"var(--oxide)":"inherit"}}>{late || "—"}</div></div>
            <div><div className="k">Delay penalty</div><div className="v" style={{color: penalty?"var(--oxide)":"inherit"}}>{usd(penalty)}</div></div>
          </div>

          <div className="sec">
            <SectionHead right={role === "owner" && !closedOut ? (
              <button className="btn ghost sm noprint" onClick={()=>setPanel(panel==="assign"?null:(assigned?"reassign":"assign"))}>
                {assigned ? "Reassign this scope" : "Assign a contractor"}
              </button>
            ) : null}>Who is building this</SectionHead>

            {!assigned && !panel && (
              <div className="emptyshot" style={{textAlign:"left"}}>
                Nobody has taken this scope. Work can't start until a contractor accepts it, which is what fixes the value,
                the finish date and the penalty rate.
              </div>
            )}

            {assigned && (
              <div style={{border:"1px solid var(--rule)",borderRadius:3,padding:"10px 12px",background:"#fff"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",alignItems:"baseline"}}>
                  <div>
                    <div style={{fontWeight:600}}>{assigned.name}</div>
                    <div className="itemmeta">{assigned.contact}{assigned.email ? " · " + assigned.email : ""}</div>
                  </div>
                  <span className={"tag " + ASSIGN_META[astate].tag}>{ASSIGN_META[astate].label}</span>
                </div>
                {item.assignment.terms && (
                  <div className="mono" style={{fontSize:11.5,marginTop:8,color:"var(--slate)"}}>
                    {astate === "accepted" ? "Accepted" : "Offered"} {usd(item.assignment.terms.scheduledValue)}
                    {" · finish "}{fmtDate(item.assignment.terms.plannedFinish)}
                    {" · "}{usd(item.assignment.terms.ldPerDay || project.ldPerDay)}/day late
                    {item.assignment.respondedAt ? " · " + fmtStamp(item.assignment.respondedAt) : ""}
                    {item.assignment.acceptanceHash ? " · #" + item.assignment.acceptanceHash : ""}
                  </div>
                )}
                {drift && (
                  <div className="banner brass" style={{marginTop:10,marginBottom:0}}>
                    <h4>Terms changed after acceptance</h4>
                    <p>{drift.map(d => d.what + ": " + d.was + " → " + d.now).join(" · ")}. Treat this as a change order —
                       it needs to go back to {assigned.name} before you hold them to it.</p>
                  </div>
                )}
                {astate === "offered" && isMine && (
                  <div style={{marginTop:12,borderTop:"1px solid var(--rule-soft)",paddingTop:10}}>
                    <div className="eyebrow" style={{marginBottom:6}}>Your decision</div>
                    <p style={{fontSize:12.5,margin:"0 0 9px",maxWidth:520}}>
                      Accepting locks in {usd(item.assignment.terms.scheduledValue)}, a finish date of{" "}
                      {fmtDate(item.assignment.terms.plannedFinish)}, and {usd(item.assignment.terms.ldPerDay || project.ldPerDay)} per
                      day for every day past it. That's the record if this is ever disputed.
                    </p>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <button className="btn go" disabled={busy} onClick={async()=>{
                        setBusy(true);
                        await api.respondToAssignment({ id:item.id, accept:true, actor:me.contact, partyId:meId });
                        await refresh(); setBusy(false); say("Scope accepted — " + item.no);
                      }}>Accept these terms</button>
                      <button className="btn stop" disabled={busy||!note.trim()} title={!note.trim()?"Say why in the note field below":""}
                        onClick={async()=>{
                          setBusy(true);
                          await api.respondToAssignment({ id:item.id, accept:false, actor:me.contact, partyId:meId, note });
                          setNote(""); await refresh(); setBusy(false); say("Scope declined — " + item.no);
                        }}>Decline</button>
                    </div>
                  </div>
                )}
                {astate === "offered" && !isMine && role === "owner" &&
                  <p className="muted" style={{fontSize:11.5,marginBottom:0,marginTop:8}}>Waiting on {assigned.name} to accept or decline.</p>}
                {astate === "declined" && item.assignment.declineNote &&
                  <p style={{fontSize:12.5,marginBottom:0,marginTop:8,color:"var(--oxide)"}}>Declined: {item.assignment.declineNote}</p>}
              </div>
            )}

            {panel === "assign" && <AssignPanel {...{db, item, project, me, refresh, say, setPanel}}/>}
            {panel === "reassign" && <ReassignPanel {...{db, item, project, me, refresh, say, setPanel, onClose}}/>}

            {item.history && item.history.length > 0 && (
              <div style={{marginTop:12}}>
                <div className="eyebrow" style={{marginBottom:5}}>Handover history</div>
                {item.history.map((h,i) => (
                  <div className="note" key={i} style={{borderLeftColor:"var(--brass)"}}>
                    <div className="meta">{fmtStamp(h.at)} · {h.event === "closed_out" ? "Closed out" : "Reassigned"}</div>
                    <div style={{fontSize:12.5}}>
                      {h.event === "closed_out"
                        ? partyName(db, h.partyId) + " — " + usd(h.valueInPlace) + " of work in place, " + usd(h.disbursed) + " paid"
                        : partyName(db, h.from) + " → " + (h.to ? partyName(db, h.to) : "unassigned") + " — " + usd(h.valueTransferred) + " balance"}
                      {h.reason ? " · " + h.reason : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sec">
            <SectionHead>Signatures required before payment</SectionHead>
            <div className="stamprow">
              {SIGNOFF_ORDER.filter(r => project.requiredSignoffs.includes(r)).map(r =>
                <Stamp key={r} role={r} approval={s[r]} fresh={freshStamp===r}
                       org={s[r] && s[r].partyId ? partyName(db, s[r].partyId) : null}
                       pending={r === "contractor" && assigned ? assigned.name : null}/>)}
            </div>
            {!project.requiredSignoffs.includes("inspector") &&
              <p className="muted" style={{fontSize:11.5, marginBottom:0}}>Third-party inspection is switched off for this project. Turn it on under Procedure.</p>}
          </div>

          <div className="sec">
            <SectionHead right={canRaise && !closedOut ? (
              <button className="btn ghost sm noprint" onClick={()=>setPanel(panel==="punch"?null:"punch")}>
                {panel==="punch" ? "Cancel" : "Log something to revisit"}
              </button>
            ) : null}>
              Punch list{punchOf(item).length > 0 ? " · " + openList.length + " open of " + punchOf(item).length : ""}
            </SectionHead>

            {panel === "punch" && <PunchForm {...{item, project, me, meId, role, refresh, say}} onDone={()=>setPanel(null)}/>}

            {punchOf(item).length === 0 && panel !== "punch" && (
              <div className="emptyshot" style={{textAlign:"left"}}>
                Nothing flagged on this line. If you walk it and something needs revisiting, log it here rather than
                returning the whole item — that way the rest keeps moving.
              </div>
            )}

            {blockers.length > 0 && (
              <div className="banner" style={{marginBottom:10}}>
                <h4>{blockers.length} item{blockers.length===1?"":"s"} blocking approval</h4>
                <p>This line can't be approved until {blockers.length===1?"it's":"they're"} verified fixed or waived.</p>
              </div>
            )}
            {holdback > 0 && (
              <div className="banner brass" style={{marginBottom:10}}>
                <h4>{usd(holdback)} withheld against open punch items</h4>
                <p>Approval and release can go ahead on the rest. This amount comes off the draw on top of the
                   {" "}{project.retainagePct}% retainage and returns when the work is verified.</p>
              </div>
            )}

            {punchOf(item)
              .slice()
              .sort((a,b) => (PUNCH_OPEN.includes(b.status) - PUNCH_OPEN.includes(a.status))
                          || (["blocking","holdback","minor"].indexOf(a.severity) - ["blocking","holdback","minor"].indexOf(b.severity)))
              .map(px => <PunchCard key={px.id} {...{px, item, db, role, me, meId, refresh, say, project}}/>)}
          </div>

          <div className="sec">
            <SectionHead right={<span className="muted" style={{fontSize:11}}>{item.photos.length} on file</span>}>Field record</SectionHead>
            <div className="pair">
              <PhotoPane kind="before" label="Before starting" photos={before} role={role} busy={busy}
                         onUpload={f=>upload("before",f)} onDelete={async id=>{await api.removePhoto(item.id,id); refresh();}}/>
              <PhotoPane kind="after" label="Work complete" photos={after} role={role} busy={busy}
                         onUpload={f=>upload("after",f)} onDelete={async id=>{await api.removePhoto(item.id,id); refresh();}}/>
            </div>
          </div>

          {(acts.length > 0 || role !== "investor") && (
            <div className="sec">
              <SectionHead>{acts.length ? "Your move" : "Add to the record"}</SectionHead>
              {role === "contractor" && item.stage === "in_progress" && (
                <div style={{marginBottom:10}}>
                  <label className="eyebrow" htmlFor="pc">Percent complete</label>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginTop:4}}>
                    <input id="pc" type="range" min="0" max="100" value={percent} style={{flex:1}}
                           onChange={e=>setPercent(Number(e.target.value))}/>
                    <span className="num" style={{width:44,textAlign:"right",fontWeight:600}}>{percent}%</span>
                    <button className="btn ghost sm" onClick={async()=>{await api.updateLineItem(item.id,{percent}); refresh(); say("Progress updated");}}>Save</button>
                  </div>
                </div>
              )}
              <textarea className="field" rows={2} placeholder={
                role==="owner" ? "What did you see on the walk? Anything on the punch list?" :
                role==="contractor" ? "What was completed, and anything the owner should look at." :
                role==="inspector" ? "Field observations." : "Note for the record."
              } value={note} onChange={e=>setNote(e.target.value)}/>
              <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:9}}>
                {acts.filter(a=>a!=="reject").map(a => {
                  const spec = ACTIONS[a];
                  const gated = ["verify","approve","release"].includes(a) && blockers.length > 0;
                  const blocked = (a==="start" && !canStart) || (a==="submit" && !canSubmit) || gated;
                  const label = a === "approve" && holdback > 0 ? "Approve, hold " + usd(holdback) : spec.label;
                  // Why it cannot be pressed beats what it would do, when both apply.
                  const why = gated ? blockers.length + " punch item(s) block this line"
                    : blocked ? (a==="start" ? "Add at least one before photo first"
                                             : "Add at least one after photo first")
                    : null;
                  return (
                    <Action key={a} action={a} what={why}
                            className={"btn " + (a==="release"||a==="approve"||a==="verify"?"go":"")}
                            disabled={busy||blocked} onClick={()=>run(a)}>
                      {label}
                    </Action>
                  );
                })}
                {acts.includes("reject") &&
                  <button className="btn stop" disabled={busy || (!note.trim() && openList.length === 0)}
                          onClick={()=>run("reject")}
                          title={(!note.trim() && openList.length === 0) ? "Log a punch item or write what's wrong first" : ""}>
                    {openList.length > 0 ? "Return the whole line (" + openList.length + " open)" : "Return with punch list"}
                  </button>}
                <button className="btn ghost" disabled={busy||!note.trim()} onClick={saveNote}>Add note only</button>
              </div>
              {role === "contractor" && isMine && astate !== "accepted" && acts.length === 0 &&
                <p className="muted" style={{fontSize:11.5,marginBottom:0}}>Accept the scope above before starting work.</p>}
              {acts.includes("start") && !canStart && <p className="muted" style={{fontSize:11.5,marginBottom:0}}>Add at least one before photo to start this item.</p>}
              {acts.includes("submit") && !canSubmit && <p className="muted" style={{fontSize:11.5,marginBottom:0}}>Add at least one completion photo to submit for approval.</p>}
              {acts.includes("release") && !s.owner && <p style={{fontSize:11.5,color:"var(--oxide)",marginBottom:0}}>Owner sign-off is missing. Releasing now will be logged as an exception.</p>}
              {blockers.length > 0 && ["owner","inspector","lender"].includes(role) &&
                <p style={{fontSize:11.5,color:"var(--oxide)",marginBottom:0}}>
                  Clear or waive the {blockers.length} blocking punch item{blockers.length===1?"":"s"} above to move this line forward.</p>}
            </div>
          )}

          <div className="sec">
            <SectionHead>Notes</SectionHead>
            {item.notes.length===0 && item.approvals.filter(a=>a.note).length===0
              ? <div className="muted" style={{fontSize:12.5}}>No notes on this line item yet.</div>
              : (
              <React.Fragment>
                {item.approvals.filter(a=>a.note).map(a => (
                  <div className="note" key={a.id} style={{borderLeftColor: a.rejected?"var(--oxide)":"var(--verdigris)"}}>
                    <div className="meta">{a.who} · {ROLES[a.role].label} · {fmtStamp(a.at)}</div>
                    <div style={{fontSize:13}}>{a.note}</div>
                  </div>
                ))}
                {item.notes.map(n => (
                  <div className="note" key={n.id}>
                    <div className="meta">{n.who} · {ROLES[n.role].label} · {fmtStamp(n.at)}</div>
                    <div style={{fontSize:13}}>{n.body}</div>
                  </div>
                ))}
              </React.Fragment>
            )}
          </div>
        </div>
      </aside>
    </React.Fragment>
  );
}

/* ------------------------------------------------------------- Punch list */
/* One deficiency: what's wrong, where, who owes the fix, what it costs to
   leave it open, and the back-and-forth about it. */
function PunchCard({ px, item, db, role, me, meId, refresh, say, project }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(PUNCH_OPEN.includes(px.status));
  const ref = useRef();
  const isOwner = role === "owner", isInspector = role === "inspector";
  const holdsScope = role === "contractor" && item.assignment && item.assignment.partyId === meId;
  const overdue = punchOverdue(px);
  const sev = PUNCH_SEVERITY[px.severity];
  const st = PUNCH_STATUS[px.status];

  const move = async (status, needNote) => {
    if (needNote && !reply.trim()) return;
    setBusy(true);
    await api.punchTransition({ id:item.id, punchId:px.id, status, note:reply,
      actor:me.contact, role, partyId:meId });
    setReply(""); await refresh(); setBusy(false);
    say(px.no + " — " + PUNCH_STATUS[status].label.toLowerCase());
  };
  const comment = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    await api.addPunchComment({ id:item.id, punchId:px.id, body:reply, actor:me.contact, role, partyId:meId });
    setReply(""); await refresh(); setBusy(false);
  };
  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files).slice(0,6)) {
      const url = await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(f); });
      await api.addPunchPhoto({ id:item.id, punchId:px.id, photo:{
        kind: holdsScope ? "fix" : "issue", url, filename:f.name,
        capturedAt:new Date().toISOString(), by:me.contact, role } });
    }
    await refresh(); setBusy(false);
  };

  return (
    <div className="punch" style={{borderLeftColor: overdue ? "var(--oxide)" : "var(--" + (px.severity==="blocking"?"oxide":px.severity==="holdback"?"brass":"rule") + ")"}}>
      <button className="punchh" onClick={()=>setOpen(o=>!o)} aria-expanded={open}>
        <span style={{minWidth:0}}>
          <span className="mono muted" style={{fontSize:10.5}}>{px.no}</span>
          <span className="puncht">{px.title}</span>
          {px.location && <span className="itemmeta" style={{display:"block"}}>{px.location}</span>}
        </span>
        <span style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
          {px.holdbackAmount > 0 && PUNCH_OPEN.includes(px.status) &&
            <span className="tag p">{usd(px.holdbackAmount)} held</span>}
          {overdue && <span className="tag b">{daysBetween(px.dueDate, iso(TODAY))}d overdue</span>}
          <span className={"tag " + sev.tag}>{sev.label}</span>
          <span className={"tag " + st.tag}>{st.label}</span>
        </span>
      </button>

      {open && (
        <div className="punchb">
          <div className="mono muted" style={{fontSize:10.5,marginBottom:8}}>
            Raised by {px.thread[0] ? px.thread[0].who : "—"} · {fmtStamp(px.raisedAt)}
            {px.dueDate ? " · due " + fmtDate(px.dueDate) : ""}
            {px.assignedTo ? " · owed by " + partyName(db, px.assignedTo) : ""}
          </div>

          {px.thread.map(c => (
            <div className="note" key={c.id} style={{borderLeftColor: c.role === "contractor" ? "var(--verdigris)" : "var(--rule)"}}>
              <div className="meta">{c.who} · {ROLES[c.role] ? ROLES[c.role].label : c.role} · {fmtStamp(c.at)}</div>
              <div style={{fontSize:13}}>{c.body}</div>
            </div>
          ))}

          {px.photos.length > 0 && (
            <div className="shots" style={{marginTop:8}}>
              {px.photos.map(ph => (
                <figure className="shot" key={ph.id} style={{margin:0}} title={ph.kind + " · " + ph.by}>
                  <img src={ph.url} alt={ph.kind + " photo"}/>
                  <figcaption className="ts">{ph.kind === "fix" ? "FIX" : "ISSUE"} {new Date(ph.capturedAt).toLocaleDateString("en-US",{month:"numeric",day:"numeric"})}</figcaption>
                </figure>
              ))}
            </div>
          )}

          {role !== "investor" && (
            <React.Fragment>
              <textarea className="field" rows={2} style={{marginTop:9}} value={reply} onChange={e=>setReply(e.target.value)}
                placeholder={holdsScope ? "What you did about it." : "Add to the thread."}/>
              <div style={{display:"flex",flexWrap:"wrap",gap:7,marginTop:8}}>
                {holdsScope && PUNCH_OPEN.includes(px.status) && (
                  <React.Fragment>
                    <button className="btn go sm" disabled={busy || px.photos.length === 0}
                      title={px.photos.length === 0 ? "Add a photo of the fix first" : ""}
                      onClick={()=>move("fixed")}>Submit the fix</button>
                    <button className="btn ghost sm" disabled={busy || !reply.trim()}
                      title={!reply.trim() ? "Say why you disagree" : ""}
                      onClick={()=>move("disputed", true)}>Dispute this</button>
                  </React.Fragment>
                )}
                {(isOwner || isInspector) && PUNCH_OPEN.includes(px.status) && (
                  <React.Fragment>
                    <button className="btn go sm" disabled={busy} onClick={()=>move("verified")}>Verified fixed</button>
                    {px.status !== "open" && <button className="btn stop sm" disabled={busy || !reply.trim()}
                      title={!reply.trim() ? "Say what's still wrong" : ""}
                      onClick={()=>move("open", true)}>Still not right</button>}
                    {isOwner && <button className="btn ghost sm" disabled={busy || !reply.trim()}
                      title={!reply.trim() ? "Record why you're letting it go" : ""}
                      onClick={()=>move("waived", true)}>Waive it</button>}
                  </React.Fragment>
                )}
                {!PUNCH_OPEN.includes(px.status) && (isOwner || isInspector) &&
                  <button className="btn ghost sm" disabled={busy} onClick={()=>move("open")}>Reopen</button>}
                <button className="btn ghost sm" disabled={busy} onClick={()=>ref.current.click()}>
                  {holdsScope ? "Add fix photo" : "Add photo"}
                </button>
                <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden
                  onChange={e=>{ if(e.target.files.length) upload(e.target.files); e.target.value=""; }}/>
                <button className="btn ghost sm" disabled={busy || !reply.trim()} onClick={comment}>Comment only</button>
              </div>
            </React.Fragment>
          )}
        </div>
      )}
    </div>
  );
}

/* Log a deficiency during the walk, without bouncing the whole line item. */
function PunchForm({ item, project, me, meId, role, refresh, say, onDone }) {
  const [f, setF] = useState({ title:"", location:"", severity:"holdback", holdbackAmount:"", dueDate:"", body:"" });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const valid = f.title.trim().length > 2 && f.body.trim().length > 2;

  return (
    <div className="card pad" style={{marginTop:10, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>What needs revisiting</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">The problem, in one line</div>
          <input className="field" value={f.title} onChange={e=>set("title",e.target.value)}
                 placeholder="Two lites scratched at east bay"/></label>
        <label><div className="eyebrow">Where on site</div>
          <input className="field" value={f.location} onChange={e=>set("location",e.target.value)}
                 placeholder="East storefront, bays 3-4"/></label>
        <label><div className="eyebrow">Fix by</div>
          <input className="field" type="date" value={f.dueDate} onChange={e=>set("dueDate",e.target.value)}/></label>
      </div>

      <div style={{marginTop:14}}>
        <div className="eyebrow" style={{marginBottom:6}}>What this does to payment</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {Object.entries(PUNCH_SEVERITY).map(([k,v]) => (
            <label key={k} style={{flex:"1 1 170px",border:"1px solid " + (f.severity===k?"var(--ink)":"var(--rule)"),
              borderRadius:3,padding:"9px 11px",background: f.severity===k?"var(--paper-2)":"#fff",cursor:"pointer"}}>
              <div style={{display:"flex",gap:7,alignItems:"center"}}>
                <input type="radio" name="sev" checked={f.severity===k} onChange={()=>set("severity",k)}/>
                <span style={{fontWeight:600,fontSize:12.5}}>{v.label}</span>
              </div>
              <div className="muted" style={{fontSize:11,marginTop:3,lineHeight:1.4}}>{v.blurb}</div>
            </label>
          ))}
        </div>
      </div>

      {f.severity === "holdback" && (
        <label style={{display:"block",marginTop:12,maxWidth:240}}><div className="eyebrow">Amount to withhold</div>
          <input className="field" type="number" min="0" step="250" value={f.holdbackAmount}
                 onChange={e=>set("holdbackAmount",e.target.value)} placeholder="4500"/>
          <div className="muted" style={{fontSize:11,marginTop:3}}>
            Comes off the next draw on top of the {project.retainagePct}% retainage, and goes back when it's verified.
          </div>
        </label>
      )}

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">What you saw</div>
        <textarea className="field" rows={3} value={f.body} onChange={e=>set("body",e.target.value)}
          placeholder="Scratches run the full height on both lites. Needs replacement glass, not polishing."/></label>

      <div className="toolbar">
        <button className="btn" disabled={!valid || busy} onClick={async()=>{
          setBusy(true);
          await api.addPunch({ id:item.id, actor:me.contact, role, partyId:meId, punch:{
            title:f.title, location:f.location, severity:f.severity,
            holdbackAmount: f.severity === "holdback" ? Number(f.holdbackAmount) || 0 : 0,
            dueDate: f.dueDate || null, body:f.body } });
          await refresh(); setBusy(false); onDone();
          say("Logged " + (f.severity === "blocking" ? "a blocking item" : "a punch item") + " on " + item.no);
        }}>Log it</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* Owner's editor for a line item. The schedule of values is the owner's
   document: what the items are, what they're worth, and when they're due.
   A contractor never sets these — they accept or decline them. */
function LineItemEditor({ item, db, project, me, refresh, say, onDone }) {
  const [f, setF] = useState({
    no: item.no, name: item.name, division: item.division, scope: item.scope || "",
    scheduledValue: item.scheduledValue,
    plannedStart: item.plannedStart || "", plannedFinish: item.plannedFinish || "",
    ldPerDay: item.ldPerDay != null ? item.ldPerDay : "",
    materials: item.materials || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const accepted = item.assignment && item.assignment.status === "accepted";
  const holder = item.assignment ? partyById(db, item.assignment.partyId) : null;
  const t = accepted ? item.assignment.terms : null;

  // What this edit would change relative to terms the contractor accepted.
  const changes = [];
  if (t) {
    if (Number(f.scheduledValue) !== t.scheduledValue)
      changes.push(["Value", usd(t.scheduledValue), usd(Number(f.scheduledValue))]);
    if ((f.plannedFinish || null) !== t.plannedFinish)
      changes.push(["Finish date", fmtDate(t.plannedFinish), fmtDate(f.plannedFinish)]);
    if ((f.ldPerDay === "" ? null : Number(f.ldPerDay)) !== t.ldPerDay)
      changes.push(["Penalty per day", usd(t.ldPerDay || project.ldPerDay), usd(Number(f.ldPerDay) || project.ldPerDay)]);
  }
  const badDates = f.plannedStart && f.plannedFinish && f.plannedFinish < f.plannedStart;
  const days = f.plannedStart && f.plannedFinish && !badDates
    ? daysBetween(f.plannedStart, f.plannedFinish) : null;
  const valid = f.name.trim().length > 1 && Number(f.scheduledValue) >= 0 && !badDates;

  return (
    <div className="card pad" style={{marginBottom:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>Edit this line item</SectionHead>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <label><div className="eyebrow">Item no.</div>
          <input className="field" value={f.no} onChange={e=>set("no",e.target.value)}/></label>
        <label><div className="eyebrow">Division</div>
          <input className="field" value={f.division} onChange={e=>set("division",e.target.value)}
                 list="divisions"/>
          <datalist id="divisions">
            {[...new Set(db.lineItems.map(i=>i.division))].map(d => <option key={d} value={d}/>)}
          </datalist></label>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Description of work</div>
          <input className="field" value={f.name} onChange={e=>set("name",e.target.value)}/></label>
      </div>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Scope detail</div>
        <textarea className="field" rows={2} value={f.scope} onChange={e=>set("scope",e.target.value)}/></label>

      <div style={{marginTop:16}}>
        <SectionHead>Value, dates and penalty</SectionHead>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
          <label><div className="eyebrow">Scheduled value</div>
            <input className="field" type="number" min="0" step="500" value={f.scheduledValue}
                   onChange={e=>set("scheduledValue",e.target.value)}/></label>
          <label><div className="eyebrow">Planned start</div>
            <input className="field" type="date" value={f.plannedStart}
                   onChange={e=>set("plannedStart",e.target.value)}/></label>
          <label><div className="eyebrow">Planned finish</div>
            <input className="field" type="date" value={f.plannedFinish}
                   style={{borderColor: badDates ? "var(--oxide)" : undefined}}
                   onChange={e=>set("plannedFinish",e.target.value)}/></label>
          <label><div className="eyebrow">Penalty per day</div>
            <input className="field" type="number" min="0" step="25" value={f.ldPerDay}
                   onChange={e=>set("ldPerDay",e.target.value)} placeholder={String(project.ldPerDay)}/>
            <div className="muted" style={{fontSize:11,marginTop:3}}>Blank uses the project rate, {usd(project.ldPerDay)}.</div></label>
        </div>
        {badDates && <p style={{fontSize:12,color:"var(--oxide)",marginTop:8,marginBottom:0}}>
          The finish date is before the start date.</p>}
        {days != null && !badDates && <p className="muted" style={{fontSize:11.5,marginTop:8,marginBottom:0}}>
          {days} calendar days on the timeline. Dates are what put this bar in its place — an item with no finish date
          doesn't appear there and can't accrue a penalty.</p>}
      </div>

      <label style={{display:"block",marginTop:14}}><div className="eyebrow">Materials / allowances</div>
        <input className="field" value={f.materials} onChange={e=>set("materials",e.target.value)}/></label>

      {accepted && changes.length > 0 && (
        <div className="banner brass" style={{marginTop:14}}>
          <h4>This is a change order</h4>
          <p>{holder ? holder.name : "The contractor"} accepted {changes.map(c => c[0].toLowerCase() + " of " + c[1]).join(", ")}.
             You're changing it to {changes.map(c => c[2]).join(", ")}. Saving flags the line as changed after acceptance
             and it goes back to them — you can't hold someone to terms they didn't agree to.</p>
        </div>
      )}
      {accepted && changes.length === 0 && (
        <p className="muted" style={{fontSize:11.5,marginTop:12,marginBottom:0}}>
          {holder ? holder.name : "A contractor"} has accepted this scope. Editing the description or scope detail is
          fine; changing the value, finish date or penalty rate makes it a change order.
        </p>
      )}
      {!item.assignment && (
        <p className="muted" style={{fontSize:11.5,marginTop:12,marginBottom:0}}>
          Nobody holds this yet, so edit freely. What you set here is what gets offered to a contractor.
        </p>
      )}

      <div className="toolbar">
        <button className="btn go" disabled={!valid||busy} onClick={async()=>{
          setBusy(true);
          await api.updateLineItem(item.id, {
            no: f.no.trim(), name: f.name.trim(), division: f.division.trim() || "Uncategorised",
            scope: f.scope, scheduledValue: Number(f.scheduledValue),
            plannedStart: f.plannedStart || null, plannedFinish: f.plannedFinish || null,
            ldPerDay: f.ldPerDay === "" ? null : Number(f.ldPerDay),
            materials: f.materials, _actor: me.contact,
          });
          await refresh(); setBusy(false); onDone();
          say(changes.length ? "Saved — flagged as a change order" : "Line item updated");
        }}>Save changes</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* Add a line item by hand, for the ones that turn up after the import. */
function NewLineItem({ db, project, me, refresh, say, onDone }) {
  const [f, setF] = useState({ no:"", name:"", division:"", scope:"", scheduledValue:"",
    plannedStart:"", plannedFinish:"", ldPerDay:"" });
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const dupe = f.no && db.lineItems.some(i => i.no.toLowerCase() === f.no.trim().toLowerCase());
  const badDates = f.plannedStart && f.plannedFinish && f.plannedFinish < f.plannedStart;
  const valid = f.no.trim() && f.name.trim().length > 1 && Number(f.scheduledValue) > 0 && !dupe && !badDates;

  return (
    <div className="card pad" style={{marginTop:14, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>Add a line item</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <label><div className="eyebrow">Item no.</div>
          <input className="field" value={f.no} onChange={e=>set("no",e.target.value)} placeholder="09-300"
                 style={{borderColor: dupe ? "var(--oxide)" : undefined}}/>
          {dupe && <div style={{fontSize:11,color:"var(--oxide)",marginTop:3}}>Already used.</div>}</label>
        <label><div className="eyebrow">Division</div>
          <input className="field" value={f.division} onChange={e=>set("division",e.target.value)}
                 placeholder="09 Finishes" list="divisions2"/>
          <datalist id="divisions2">
            {[...new Set(db.lineItems.map(i=>i.division))].map(d => <option key={d} value={d}/>)}
          </datalist></label>
        <label style={{gridColumn:"1 / -1"}}><div className="eyebrow">Description of work</div>
          <input className="field" value={f.name} onChange={e=>set("name",e.target.value)}
                 placeholder="Ceiling tile & grid, event space"/></label>
        <label><div className="eyebrow">Scheduled value</div>
          <input className="field" type="number" min="0" step="500" value={f.scheduledValue}
                 onChange={e=>set("scheduledValue",e.target.value)}/></label>
        <label><div className="eyebrow">Planned start</div>
          <input className="field" type="date" value={f.plannedStart} onChange={e=>set("plannedStart",e.target.value)}/></label>
        <label><div className="eyebrow">Planned finish</div>
          <input className="field" type="date" value={f.plannedFinish} onChange={e=>set("plannedFinish",e.target.value)}
                 style={{borderColor: badDates ? "var(--oxide)" : undefined}}/></label>
        <label><div className="eyebrow">Penalty per day</div>
          <input className="field" type="number" min="0" step="25" value={f.ldPerDay}
                 onChange={e=>set("ldPerDay",e.target.value)} placeholder={String(project.ldPerDay)}/></label>
      </div>
      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Scope detail</div>
        <textarea className="field" rows={2} value={f.scope} onChange={e=>set("scope",e.target.value)}/></label>
      <div className="toolbar">
        <button className="btn go" disabled={!valid||busy} onClick={async()=>{
          setBusy(true);
          await api.createLineItem({ actor: me.contact, item:{
            no:f.no.trim(), name:f.name.trim(), division:f.division.trim(), scope:f.scope,
            scheduledValue:Number(f.scheduledValue), plannedStart:f.plannedStart||null,
            plannedFinish:f.plannedFinish||null, ldPerDay: f.ldPerDay===""?null:Number(f.ldPerDay) } });
          await refresh(); setBusy(false); onDone(); say(f.no + " added — offer it to a contractor when you're ready");
        }}>Add it</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* Offer a scope to a contractor, with the terms they'll be held to. */
function AssignPanel({ db, item, project, me, refresh, say, setPanel }) {
  const eligible = db.parties.filter(x => x.role === "contractor" && x.status === "active");
  const [partyId, setPartyId] = useState("");
  const [terms, setTerms] = useState({
    scheduledValue: item.scheduledValue,
    plannedFinish: item.plannedFinish || "",
    ldPerDay: item.ldPerDay != null ? item.ldPerDay : project.ldPerDay,
  });
  const [busy, setBusy] = useState(false);
  const chosen = eligible.find(x => x.id === partyId);
  const gaps = chosen ? complianceGaps(chosen) : [];
  const blocked = chosen ? !canBeAssigned(chosen) : true;

  return (
    <div className="card pad" style={{marginTop:10, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>Offer this scope</SectionHead>
      <label style={{display:"block",marginBottom:12}}>
        <div className="eyebrow">Contractor or sub</div>
        <select className="field" value={partyId} onChange={e=>setPartyId(e.target.value)}>
          <option value="">Choose from the directory…</option>
          {eligible.map(x => <option key={x.id} value={x.id}>{x.name}{x.trade!=="General"?" · "+x.trade:""}</option>)}
        </select>
      </label>

      {chosen && gaps.length > 0 && (
        <div className={"banner " + (blocked ? "" : "brass")} style={{marginBottom:12}}>
          <h4>{blocked ? "Can't assign work yet" : "Paperwork expiring"}</h4>
          <p>{chosen.name} is missing: {gaps.join(", ")}. {blocked
            ? "Get it on file under Contractors & subs first — this is the gate that keeps an uninsured crew off the site."
            : "Still assignable, but chase the renewal before they start."}</p>
        </div>
      )}

      <div className="eyebrow" style={{marginBottom:6}}>Terms they're agreeing to</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
        <label><div className="eyebrow">Value</div>
          <input className="field" type="number" min="0" step="500" value={terms.scheduledValue}
                 onChange={e=>setTerms(t=>({...t, scheduledValue:Number(e.target.value)}))}/></label>
        <label><div className="eyebrow">Finish by</div>
          <input className="field" type="date" value={terms.plannedFinish}
                 onChange={e=>setTerms(t=>({...t, plannedFinish:e.target.value}))}/></label>
        <label><div className="eyebrow">Penalty per day late</div>
          <input className="field" type="number" min="0" step="25" value={terms.ldPerDay}
                 onChange={e=>setTerms(t=>({...t, ldPerDay:Number(e.target.value)}))}/></label>
      </div>
      <p className="muted" style={{fontSize:11.5,marginTop:9}}>
        Nothing binds until they accept. Their acceptance is a signature — it freezes these three numbers and shows up
        on the audit trail with a hash.
      </p>

      <div className="toolbar">
        <button className="btn go" disabled={!partyId || blocked || busy || !terms.plannedFinish} onClick={async()=>{
          setBusy(true);
          await api.assign({ id:item.id, partyId, terms, actor:me.contact });
          await refresh(); setBusy(false); setPanel(null);
          say("Scope offered to " + chosen.name);
        }}>Send offer</button>
        <button className="btn ghost" onClick={()=>setPanel(null)}>Cancel</button>
      </div>
    </div>
  );
}

/* Take a scope off one party mid-stream and hand the balance to another. */
function ReassignPanel({ db, item, project, me, refresh, say, setPanel, onClose }) {
  const outgoing = partyById(db, item.assignment.partyId);
  const eligible = db.parties.filter(x => x.role === "contractor" && x.status === "active" && x.id !== outgoing.id);
  const earned = Math.round(item.scheduledValue * item.percent / 100);
  const [valueInPlace, setValueInPlace] = useState(earned);
  const [newPartyId, setNewPartyId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const balance = Math.max(0, item.scheduledValue - clamp(valueInPlace, 0, item.scheduledValue));
  const backcharge = Math.max(0, item.bankDisbursed - valueInPlace);
  const chosen = eligible.find(x => x.id === newPartyId);
  const blocked = chosen ? !canBeAssigned(chosen) : false;

  return (
    <div className="card pad" style={{marginTop:10, borderLeft:"3px solid var(--oxide)"}}>
      <SectionHead>Move this scope off {outgoing.name}</SectionHead>
      <p style={{fontSize:12.5,marginTop:0,maxWidth:560}}>
        This splits the line item. What {outgoing.name} actually built closes out under their name at the value you set
        below, and the remaining balance becomes a new offer. Their signatures and photos stay on the closed portion —
        the incoming sub is never made to warrant work they didn't do.
      </p>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:12,marginTop:12}}>
        <label><div className="eyebrow">Value of work in place</div>
          <input className="field" type="number" min="0" max={item.scheduledValue} step="500" value={valueInPlace}
                 onChange={e=>setValueInPlace(Number(e.target.value))}/>
          <div className="muted" style={{fontSize:11,marginTop:3}}>Reported {item.percent}% = {usd(earned)}. Walk it before you agree.</div>
        </label>
        <label><div className="eyebrow">Hand the balance to</div>
          <select className="field" value={newPartyId} onChange={e=>setNewPartyId(e.target.value)}>
            <option value="">Leave unassigned for now</option>
            {eligible.map(x => <option key={x.id} value={x.id}>{x.name}{x.trade!=="General"?" · "+x.trade:""}</option>)}
          </select>
        </label>
      </div>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Reason for the record</div>
        <textarea className="field" rows={2} value={reason} onChange={e=>setReason(e.target.value)}
                  placeholder="Notice of breach issued Aug 14, no crew on site for 3 weeks."/></label>

      {blocked && (
        <div className="banner" style={{marginTop:12}}>
          <h4>{chosen.name} can't hold scope yet</h4>
          <p>Missing: {complianceGaps(chosen).join(", ")}. Add it under Contractors &amp; subs, or leave the balance unassigned for now.</p>
        </div>
      )}

      <div className="kv" style={{marginTop:14}}>
        <div><div className="k">Closes out to {outgoing.name.split(" ")[0]}</div><div className="v">{usd(valueInPlace)}</div></div>
        <div><div className="k">Already paid on this line</div><div className="v">{usd(item.bankDisbursed)}</div></div>
        <div style={{background: backcharge ? "var(--oxide-soft)" : undefined}}>
          <div className="k">Backcharge to {outgoing.name.split(" ")[0]}</div>
          <div className="v" style={{color: backcharge ? "var(--oxide)" : "inherit"}}>{backcharge ? usd(backcharge) : "—"}</div></div>
        <div style={{background:"var(--verdigris-soft)"}}><div className="k">Balance transferred</div>
          <div className="v" style={{color:"var(--verdigris)"}}>{usd(balance)}</div></div>
      </div>
      {backcharge > 0 && (
        <p style={{fontSize:12,color:"var(--oxide)",marginTop:9,marginBottom:0,maxWidth:560}}>
          {outgoing.name} was paid {usd(backcharge)} more than the work in place. That follows them onto the Exceptions
          report as a backcharge — it does not get absorbed into the incoming sub's line.
        </p>
      )}

      <div className="toolbar">
        <button className="btn stop" disabled={busy || blocked || !reason.trim()}
                title={!reason.trim() ? "Write the reason first — this is the record" : ""}
                onClick={async()=>{
          setBusy(true);
          await api.reassign({ id:item.id, newPartyId: newPartyId || null, valueInPlace, reason, actor:me.contact });
          await refresh(); setBusy(false); setPanel(null); onClose();
          say("Scope split — " + usd(balance) + " moved off " + outgoing.name);
        }}>Split and reassign</button>
        <button className="btn ghost" onClick={()=>setPanel(null)}>Cancel</button>
      </div>
    </div>
  );
}

function PhotoPane({ kind, label, photos, role, onUpload, onDelete, busy }) {
  const ref = useRef();
  const canUpload = role === "contractor" || role === "inspector" || role === "owner";
  return (
    <div className="pane">
      <div className="paneh">
        <span className="eyebrow">{label}</span>
        {canUpload && (
          <React.Fragment>
            <button className="btn ghost sm noprint" disabled={busy} onClick={()=>ref.current.click()}>Add photos</button>
            <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden
                   onChange={e=>{ if(e.target.files.length) onUpload(e.target.files); e.target.value=""; }}/>
          </React.Fragment>
        )}
      </div>
      {photos.length === 0
        ? <div className="emptyshot">{canUpload ? "No " + kind + " photos yet. Shoot from the same spot each time." : "No " + kind + " photos on file."}</div>
        : (
        <div className="shots">
          {photos.map(p => (
            <figure className="shot" key={p.id} style={{margin:0}} title={p.filename + " · " + p.by}>
              <img src={p.url} alt={kind + " photo by " + p.by}/>
              <figcaption className="ts">{new Date(p.capturedAt).toLocaleDateString("en-US",{month:"numeric",day:"numeric"})} {new Date(p.capturedAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</figcaption>
              {canUpload && <button className="del noprint" onClick={()=>onDelete(p.id)} aria-label="Remove photo">×</button>}
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- Draw requests */
function Draws({ db, role, me, meId, say, refresh, setSelId, setView }) {
  const [picked, setPicked] = useState([]);
  const [open, setOpen] = useState(null);
  const p = db.project;
  const eligible = db.lineItems.filter(it => it.stage === "approved" && blockingPunch(it).length === 0);
  const heldOut = db.lineItems.filter(it => it.stage === "approved" && blockingPunch(it).length > 0);
  const sel = db.lineItems.filter(it => picked.includes(it.id));

  const gross = sel.reduce((s,it) => s + it.scheduledValue * it.percent/100, 0);
  const retain = gross * p.retainagePct/100;
  const prior = sel.reduce((s,it) => s + it.bankDisbursed, 0);
  const penalties = sel.reduce((s,it) => s + ldAccrued(it, p), 0);
  const holdbacks = sel.reduce((s,it) => s + punchHoldback(it), 0);
  const net = gross - retain - prior - penalties - holdbacks;

  const create = async () => {
    await api.createDraw(PROJECT_ID, { lineItemIds: picked, actor: me.contact,
      snapshot: { gross, retainage: retain, prior, penalties, holdbacks, net } });
    setPicked([]); await refresh(); say("Draw request created and sent to the lender");
  };

  const unpapered = drawsNeedingDocs(db);

  return (
    <React.Fragment>
      <h1 className="pagetitle">Draw requests</h1>
      <p className="pagesub">Bundle approved work into a payment application, then keep the proof of payment attached to
        it. The lender uploads the check once and everyone has it.</p>

      {unpapered.length > 0 && (
        <div className="banner" style={{marginTop:16}}>
          <h4>{unpapered.length} funded draw{unpapered.length===1?"":"s"} missing paperwork</h4>
          <p>{unpapered.map(drawLabel).join(", ")} — money moved but the file is incomplete. Open the draw to see what's
             outstanding; the lender and the contractor can both upload directly.</p>
        </div>
      )}

      {role === "owner" && (
        <div className="card pad" style={{marginTop:16}}>
          <SectionHead right={<span className="muted" style={{fontSize:11}}>{eligible.length} eligible</span>}>Build a new draw</SectionHead>
          {heldOut.length > 0 && (
            <div className="banner" style={{marginBottom:12}}>
              <h4>{heldOut.length} approved line{heldOut.length===1?"":"s"} held out of this draw</h4>
              <p>{heldOut.map(x=>x.no).join(", ")} — blocking punch items are still open.</p>
            </div>
          )}
          {eligible.length === 0
            ? <div className="muted" style={{fontSize:12.5}}>Nothing is owner-approved and clear of blocking punch items yet.</div>
            : (
            <React.Fragment>
              <div className="tablewrap" style={{border:0}}>
                <table className="sov" style={{minWidth:700}}>
                  <thead><tr>
                    <th className="plain" style={{width:34}}></th><th className="plain">Item</th>
                    <th className="plain">Value in place</th><th className="plain">Retainage</th>
                    <th className="plain">Already paid</th><th className="plain">Penalty offset</th>
                    <th className="plain">Punch held</th>
                  </tr></thead>
                  <tbody>
                    {eligible.map(it => {
                      const e = it.scheduledValue*it.percent/100;
                      return (
                        <tr key={it.id}>
                          <td><input type="checkbox" checked={picked.includes(it.id)} aria-label={"Include " + it.name}
                                onChange={()=>setPicked(x => x.includes(it.id) ? x.filter(y=>y!==it.id) : x.concat(it.id))}/></td>
                          <td><button style={{background:"transparent",border:0,padding:0,textAlign:"left"}} onClick={()=>setSelId(it.id)}>
                              <div className="itemname">{it.name}</div>
                              <div className="itemmeta">{it.no} · {it.assignment ? partyName(db, it.assignment.partyId) : "unassigned"}</div></button></td>
                          <td className="num">{usd(e)}</td>
                          <td className="num">({usd(e*p.retainagePct/100)})</td>
                          <td className="num">({usd(it.bankDisbursed)})</td>
                          <td className="num" style={{color: ldAccrued(it,p)?"var(--oxide)":"inherit"}}>{ldAccrued(it,p)?"("+usd(ldAccrued(it,p))+")":"—"}</td>
                          <td className="num" style={{color: punchHoldback(it)?"var(--brass)":"inherit"}}>
                            {punchHoldback(it)?"("+usd(punchHoldback(it))+")":"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {picked.length > 0 && (
                <div style={{marginTop:14, borderTop:"2px solid var(--ink)", paddingTop:12}}>
                  <div className="kv">
                    <div><div className="k">Value in place</div><div className="v">{usd(gross)}</div></div>
                    <div><div className="k">Less retainage {p.retainagePct}%</div><div className="v">({usd(retain)})</div></div>
                    <div><div className="k">Less previously paid</div><div className="v">({usd(prior)})</div></div>
                    <div><div className="k">Less delay penalties</div><div className="v" style={{color:penalties?"var(--oxide)":"inherit"}}>({usd(penalties)})</div></div>
                    <div><div className="k">Less punch holdbacks</div><div className="v" style={{color:holdbacks?"var(--brass)":"inherit"}}>({usd(holdbacks)})</div></div>
                    <div><div className="k">Line items</div><div className="v">{picked.length}</div></div>
                    <div><div className="k">Open punch items</div><div className="v">{sel.reduce((n,it)=>n+openPunch(it).length,0)}</div></div>
                    <div style={{background:"var(--verdigris-soft)"}}><div className="k">Net due this draw</div>
                      <div className="v" style={{color:net<0?"var(--oxide)":"var(--verdigris)"}}>{usd(net)}</div></div>
                  </div>
                  <div className="toolbar noprint">
                    <Action action="createDraw" className="btn go" onClick={create}>
                      Create draw request · {picked.length} items</Action>
                    <button className="btn ghost" onClick={()=>download("calico-draw-worksheet-" + iso(TODAY) + ".csv", toCSV(forExport(db).filter(x=>picked.includes(x.id)), CSV_COLUMNS))}>Export worksheet</button>
                    <button className="btn ghost" onClick={()=>setPicked([])}>Clear</button>
                  </div>
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      )}

      <div style={{marginTop:16}}>
        <SectionHead right={<span className="muted" style={{fontSize:11}}>{db.draws.length} total</span>}>Draw history</SectionHead>
        {db.draws.length === 0
          ? <Blocked db={db} setView={setView}
                     fallbackTitle="No draws yet"
                     fallbackBody="Approved work is waiting. Bundle it into a payment application above."/>
          : db.draws.slice().reverse().map(d => (
            <DrawCard key={d.id} {...{d, db, role, me, meId, say, refresh, setSelId}}
                      open={open===d.id} onToggle={()=>setOpen(o => o===d.id ? null : d.id)}/>
          ))}
      </div>
    </React.Fragment>
  );
}

function DrawCard({ d, db, role, me, meId, say, refresh, setSelId, open, onToggle }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = drawTotals(db, d);
  const its = drawItems(db, d);
  const docs = drawDocs(d);
  const paid = paidOnDraw(d);
  const gaps = missingDocs(db, d);
  const variance = paid - t.net;
  const st = DRAW_STATUS[d.status] || DRAW_STATUS.submitted;
  const canFund = role === "lender" && d.status === "submitted";
  const hasPaymentDoc = docs.some(x => DOC_TYPES[x.type].payment);

  return (
    <div className="card" style={{marginBottom:10, borderLeft:"3px solid var(--" +
      (d.status==="funded" ? (gaps.length ? "brass" : "verdigris") : d.status==="rejected" ? "oxide" : "rule") + ")"}}>
      <button className="punchh" onClick={onToggle} aria-expanded={open} style={{padding:"12px 14px"}}>
        <span style={{minWidth:0}}>
          <span className="mono" style={{fontWeight:600,fontSize:13.5}}>{drawLabel(d)}</span>
          <span className="itemmeta" style={{display:"block"}}>
            {its.length} line items · requested {fmtStamp(d.createdAt)}
            {d.fundedAt ? " · funded " + fmtDate(iso(new Date(d.fundedAt))) : ""}
          </span>
        </span>
        <span style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
          <span className="num" style={{fontWeight:600}}>{usd(t.net)}</span>
          {paid > 0 && Math.abs(variance) > 1 &&
            <span className="tag b">{usd(Math.abs(variance))} {variance < 0 ? "short" : "over"}</span>}
          {docs.length > 0 && <span className="tag n">{docs.length} document{docs.length===1?"":"s"}</span>}
          {d.status === "funded" && gaps.length > 0 && <span className="tag p">{gaps.length} missing</span>}
          <span className={"tag " + st.tag}>{st.label}</span>
        </span>
      </button>

      {open && (
        <div className="punchb" style={{padding:"12px 14px 14px"}}>
          <div className="kv">
            <div><div className="k">Value in place</div><div className="v">{usd(t.gross)}</div></div>
            <div><div className="k">Less retainage</div><div className="v">({usd(t.retainage)})</div></div>
            <div><div className="k">Less penalties</div><div className="v">({usd(t.penalties)})</div></div>
            <div><div className="k">Less punch holdbacks</div><div className="v">({usd(t.holdbacks)})</div></div>
            <div style={{background:"var(--paper-2)"}}><div className="k">Net requested</div><div className="v">{usd(t.net)}</div></div>
            <div style={{background: paid ? (Math.abs(variance)>1 ? "var(--oxide-soft)" : "var(--verdigris-soft)") : undefined}}>
              <div className="k">Actually paid</div>
              <div className="v" style={{color: paid ? (Math.abs(variance)>1 ? "var(--oxide)" : "var(--verdigris)") : "var(--slate)"}}>
                {paid ? usd(paid) : "—"}</div></div>
            <div><div className="k">Difference</div>
              <div className="v" style={{color: paid && Math.abs(variance)>1 ? "var(--oxide)" : "inherit"}}>
                {paid ? (Math.abs(variance) <= 1 ? "matches" : usd(variance)) : "—"}</div></div>
            <div><div className="k">Line items</div><div className="v">{its.length}</div></div>
          </div>

          {paid > 0 && Math.abs(variance) > 1 && (
            <div className="banner" style={{marginTop:12}}>
              <h4>The payment doesn't match the request</h4>
              <p>{usd(t.net)} was requested and {usd(paid)} was paid — {usd(Math.abs(variance))}
                 {variance < 0 ? " short" : " over"}. Worth an email before it becomes an argument at closeout.</p>
            </div>
          )}

          <div className="sec" style={{marginTop:16,marginBottom:0}}>
            <SectionHead right={role !== "investor" ? (
              <button className="btn ghost sm noprint" onClick={()=>setAdding(a=>!a)}>
                {adding ? "Cancel" : "Upload a document"}</button>
            ) : null}>
              Payment file{docs.length ? " · " + docs.length : ""}
            </SectionHead>

            {gaps.length > 0 && (
              <div className={"banner " + (d.status === "funded" ? "" : "brass")} style={{marginBottom:10}}>
                <h4>Still outstanding</h4>
                <p>{gaps.join(" · ")}. {d.status === "funded"
                  ? "The money has already moved, so this is the gap in the record."
                  : "The lender uploads the check when it's cut; each contractor on the draw uploads their lien waiver."}</p>
              </div>
            )}

            {adding && <DrawDocForm {...{d, db, me, meId, role, refresh, say, defaultAmount: t.net}}
                                    onDone={()=>setAdding(false)}/>}

            {docs.length === 0 && !adding && (
              <div className="emptyshot" style={{textAlign:"left"}}>
                Nothing attached yet. Once the check is cut, the lender uploads a copy here instead of Kenneth chasing it
                by email.
              </div>
            )}

            {docs.map(doc => {
              const meta = DOC_TYPES[doc.type];
              const img = (doc.url || "").startsWith("data:image");
              return (
                <div key={doc.id} style={{display:"flex",gap:11,alignItems:"flex-start",padding:"10px 0",
                  borderBottom:"1px solid var(--rule-soft)"}}>
                  <a href={doc.url} download={doc.filename} className="docthumb" title={"Download " + doc.filename}>
                    {img ? <img src={doc.url} alt={meta.label}/> : <span className="mono">{(doc.filename||"").split(".").pop().toUpperCase().slice(0,4)}</span>}
                  </a>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:12.5}}>{meta.label}
                      {doc.amount ? <span className="num" style={{marginLeft:8}}>{usd(doc.amount)}</span> : null}</div>
                    <div className="itemmeta">
                      {doc.reference ? doc.reference + " · " : ""}
                      {doc.paidOn ? "paid " + fmtDate(doc.paidOn) + " · " : ""}
                      {doc.filename}
                    </div>
                    <div className="mono muted" style={{fontSize:10.5,marginTop:2}}>
                      {doc.by} · {ROLES[doc.role] ? ROLES[doc.role].label : doc.role} · {fmtStamp(doc.uploadedAt)} · #{doc.hash}
                    </div>
                    {doc.note && <div style={{fontSize:12,marginTop:4}}>{doc.note}</div>}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <a className="btn ghost sm noprint" href={doc.url} download={doc.filename}>Download</a>
                    {(role === "owner" || doc.partyId === meId) && (
                      <button className="btn ghost sm noprint" disabled={busy} onClick={async()=>{
                        setBusy(true);
                        await api.removeDrawDocument({ id:d.id, docId:doc.id, actor:me.contact, role });
                        await refresh(); setBusy(false); say("Document removed — the audit entry stays");
                      }}>Remove</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {role !== "investor" && (
            <div style={{marginTop:12,padding:"9px 11px",border:"1px dashed var(--rule)",borderRadius:3,
              background:"var(--paper-2)"}}>
              <div className="eyebrow" style={{marginBottom:5}}>This draw's own email address</div>
              {intakeAddress(db, d) ? (
              <div style={{display:"flex",gap:9,alignItems:"center",flexWrap:"wrap"}}>
                <code className="mono" style={{fontSize:11.5}}>{intakeAddress(db, d)}</code>
                <button className="btn ghost sm noprint" onClick={()=>{
                  navigator.clipboard && navigator.clipboard.writeText(intakeAddress(db, d));
                  say("Address copied — paste it into the email to the bank");
                }}>Copy</button>
              </div>
              ) : (
                <p className="muted" style={{fontSize:12,margin:0,maxWidth:560}}>
                  Email intake isn't set up on this project yet, so this draw has no address to give out.
                  Set the inbound domain under Settings → Email &amp; inbox. Until then, attach the check
                  copy here yourself.
                </p>
              )}
              <p className="muted" style={{fontSize:11.5,margin:"7px 0 0",maxWidth:560}}>
                Put this in the CC when you send {d.id} to the bank. Anything they reply with lands straight on this
                draw with no guessing — including a reply weeks later on the same thread.
              </p>
            </div>
          )}

          <div className="sec" style={{marginTop:16,marginBottom:0}}>
            <SectionHead>Line items on this draw</SectionHead>
            {its.map(it => (
              <button key={it.id} onClick={()=>setSelId(it.id)} style={{display:"flex",width:"100%",
                justifyContent:"space-between",gap:10,alignItems:"center",background:"transparent",border:0,
                borderBottom:"1px solid var(--rule-soft)",padding:"7px 0",textAlign:"left"}}>
                <span><span className="mono muted" style={{fontSize:11}}>{it.no}</span>{" "}
                  <span style={{fontWeight:500}}>{it.name}</span>
                  <span className="itemmeta" style={{display:"block"}}>
                    {it.assignment ? partyName(db, it.assignment.partyId) : "unassigned"}</span></span>
                <span className="num" style={{fontSize:12.5,whiteSpace:"nowrap"}}>{usd(it.scheduledValue*it.percent/100)}</span>
              </button>
            ))}
          </div>

          {canFund && (
            <div className="toolbar noprint">
              <button className="btn go" disabled={busy} onClick={async()=>{
                setBusy(true);
                await api.transitionDraw({ id:d.id, status:"funded", actor:me.contact, role });
                await refresh(); setBusy(false);
                say(d.id + " funded — " + (hasPaymentDoc ? "applied to the line items" : "upload the check to close the file"));
              }}>{hasPaymentDoc ? "Mark funded · " + usd(paid) : "Mark funded without proof of payment"}</button>
              <button className="btn ghost" disabled={busy} onClick={async()=>{
                setBusy(true);
                await api.transitionDraw({ id:d.id, status:"rejected", actor:me.contact, role });
                await refresh(); setBusy(false); say(d.id + " returned to the owner");
              }}>Return to the owner</button>
              {!hasPaymentDoc && <span className="muted" style={{fontSize:11.5,maxWidth:340}}>
                Uploading the check first fills in the amount and reconciles it against the request automatically.</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DrawDocForm({ d, db, me, meId, role, refresh, say, onDone, defaultAmount }) {
  const suggest = role === "lender" ? "check" : role === "contractor" ? "waiverCond" : role === "inspector" ? "inspection" : "other";
  const [f, setF] = useState({ type: suggest, amount: DOC_TYPES[suggest].payment ? String(Math.round(defaultAmount||0)) : "",
    reference:"", paidOn: iso(TODAY), note:"" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const meta = DOC_TYPES[f.type];

  return (
    <div className="card pad" style={{marginBottom:12, borderLeft:"3px solid var(--brass)"}}>
      <SectionHead>Attach to {d.id}</SectionHead>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <label><div className="eyebrow">What is it</div>
          <select className="field" value={f.type} onChange={e=>{ const t=e.target.value; set("type",t);
            if (DOC_TYPES[t].payment && !f.amount) set("amount", String(Math.round(defaultAmount||0))); }}>
            {Object.entries(DOC_TYPES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
        {meta.payment && (
          <React.Fragment>
            <label><div className="eyebrow">Amount paid</div>
              <input className="field" type="number" min="0" step="0.01" value={f.amount}
                     onChange={e=>set("amount",e.target.value)}/>
              <div className="muted" style={{fontSize:11,marginTop:3}}>Requested {usd(defaultAmount)}.</div></label>
            <label><div className="eyebrow">Date paid</div>
              <input className="field" type="date" value={f.paidOn} onChange={e=>set("paidOn",e.target.value)}/></label>
          </React.Fragment>
        )}
        <label><div className="eyebrow">{meta.payment ? "Check or wire reference" : "Reference"}</div>
          <input className="field" value={f.reference} onChange={e=>set("reference",e.target.value)}
                 placeholder={meta.payment ? "Check #40119" : "Optional"}/></label>
      </div>

      <label style={{display:"block",marginTop:12}}><div className="eyebrow">Note</div>
        <textarea className="field" rows={2} value={f.note} onChange={e=>set("note",e.target.value)}
          placeholder={meta.payment ? "Anything the owner should know — a short pay, a hold, a split." : "Optional."}/></label>

      <div style={{marginTop:12,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <button className="btn ghost sm" onClick={()=>ref.current.click()}>
          {file ? "Replace file" : "Choose file"}</button>
        <input ref={ref} type="file" accept="image/*,application/pdf" hidden
               onChange={e=>{ if(e.target.files[0]) setFile(e.target.files[0]); e.target.value=""; }}/>
        {file && <span className="muted" style={{fontSize:12}}>{file.name} · {Math.round(file.size/1024)} KB</span>}
      </div>

      <div className="toolbar">
        <button className="btn go" disabled={!file||busy} title={!file ? "Attach the scan or photo first" : ""}
          onClick={async()=>{
            setBusy(true);
            const url = await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(file); });
            await api.addDrawDocument({ id:d.id, actor:me.contact, role, partyId:meId, doc:{
              type:f.type, url, filename:file.name, size:file.size,
              amount: meta.payment ? Number(f.amount)||0 : 0,
              reference:f.reference, paidOn: meta.payment ? f.paidOn : null, note:f.note } });
            await refresh(); setBusy(false); onDone();
            say(DOC_TYPES[f.type].label + " attached to " + d.id);
          }}>Attach it</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
      <p className="muted" style={{fontSize:11.5,marginBottom:0}}>
        Everyone on this draw can see it immediately. The file is hashed on upload so it can be shown to be the same
        document later.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- Audit trail */
/* ============================================================================
   CHANGE ORDERS
   A contract that grows without change orders is a contract nobody agreed to.
   Calico's grew 138.7% that way. Contractors propose; only the owner decides;
   approval moves the values and writes the record in one transaction.
   ========================================================================== */
function ChangeOrders({ db, role, me, meId, items, say, refresh, setView, setSelId }) {
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(null);
  const cos = db.changeOrders || [];
  const canPropose = ["owner","contractor","inspector"].includes(role);
  const canDecide = role === "owner";

  const decide = async (co, approve) => {
    let note = "";
    if (!approve) {
      note = window.prompt("Why is " + co.no + " being rejected?") || "";
      if (!note.trim()) return;
    }
    setBusy(co.id);
    try {
      await api.decideChangeOrder({ id: co.id, approve, note });
      await refresh();
      say(co.no + (approve ? " approved" : " rejected"));
    } catch (e) { say(e.message); }
    setBusy(null);
  };

  const proposed = cos.filter(c => c.status === "proposed");
  const settled = cos.filter(c => c.status !== "proposed");
  const net = cos.filter(c => c.status === "approved").reduce((n,c) => n + c.amount, 0);

  const approvedCount = cos.filter(c => c.status === "approved").length;

  return (
    <React.Fragment>
      <h1 className="pagetitle">Change orders</h1>
      <p className="pagesub">
        A change order is how the contract changes — extra work, a new price, a moved date. Nothing
        moves until the owner approves it, and every movement is recorded here with who agreed to it.
        {" "}{usd(net)} approved so far across {approvedCount}{" "}
        {approvedCount === 1 ? "order" : "orders"}.
      </p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Awaiting a decision" v={proposed.length} tone={proposed.length ? "flag" : undefined}
              sub={proposed.length ? usd(proposed.reduce((n,c)=>n+c.amount,0)) + " at stake" : "nothing pending"}/>
        <Stat k="Approved" v={usd(net)} sub="added to the contract sum"/>
        <Stat k="Contract sum" v={usd(db.project.contractSum)} sub="as it stands today"/>
      </div>

      {/* "Raise a change order" is the trade's phrase, not a new owner's. Say
          what it does, and keep the term nearby so the two connect. */}
      {canPropose && !drafting && (
        <div style={{margin:"14px 0"}}>
          <Action action="raiseChangeOrder" className="btn go" onClick={()=>setDrafting(true)}>
            Propose a change to the contract
          </Action>
        </div>
      )}
      {drafting && <ChangeOrderForm {...{db, items, say, refresh, onDone:()=>setDrafting(false)}}/>}

      {cos.length === 0 && !drafting && (
        <Empty title="No change orders yet"
               body="Anything that moves a line item's value or its finish date belongs here. A revision recorded only on the pay application is a number nobody signed for."/>
      )}

      {[["Waiting on the owner", proposed], ["Decided", settled]].map(([label, list]) => !list.length ? null : (
        <div key={label} style={{marginTop:18}}>
          <SectionHead>{label}</SectionHead>
          {list.map(co => (
            <div key={co.id} className="card pad" style={{marginTop:10}}>
              <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
                <span className="mono" style={{fontWeight:700}}>{co.no}</span>
                <span style={{fontWeight:600}}>{co.title}</span>
                <span className={"tag " + (co.status==="approved"?"g":co.status==="rejected"?"b":"p")}>{co.status}</span>
                <div style={{flex:1}}/>
                <span className={"delta " + (co.amount >= 0 ? "up" : "down")} style={{fontSize:15}}>
                  {co.amount >= 0 ? "+" : "−"}{usd(Math.abs(co.amount))}
                </span>
                {co.daysImpact ? <span className="muted" style={{fontSize:12}}>{co.daysImpact} days</span> : null}
              </div>
              {co.reason && <p className="muted" style={{fontSize:12.5,margin:"6px 0 0"}}>{co.reason}</p>}
              <div className="tablewrap" style={{marginTop:10}}>
                <table className="sov" style={{minWidth:0}}>
                  <thead><tr>
                    <th className="plain">Line item</th>
                    <th className="plain">Description</th>
                    <th className="plain r">Change</th>
                    <th className="plain r">New finish</th>
                  </tr></thead>
                  <tbody>
                    {co.lines.map(l => (
                      <tr key={l.id}>
                        <td className="mono">
                          {l.lineItemId
                            ? <button className="linkbtn mono" title="Open this line on the schedule of values"
                                      onClick={()=>{ setView("ledger"); setSelId(l.lineItemId); }}>
                                {l.lineNo || l.lineItemId}</button>
                            : <span>{l.newNo || "—"}</span>}
                        </td>
                        <td>
                          {/* What the line actually is. It used to show whatever
                              wording the change order was written with — "NEW
                              LINE" — which tells the reader nothing about the
                              work being paid for. */}
                          <div>{l.lineName || l.newName ||
                            (l.lineItemId ? "" : "A line that does not exist yet")}</div>
                          {l.lineDivision && (
                            <div className="itemmeta">{l.lineDivision}</div>
                          )}
                          {!l.lineItemId && (
                            <span className="tag p" style={{marginTop:4,display:"inline-block"}}>
                              creates a new line</span>
                          )}
                          {l.note && <div className="itemmeta">{l.note}</div>}
                        </td>
                        <td className="r"><span className={"delta " + (l.delta >= 0 ? "up" : "down")}>
                          {l.delta >= 0 ? "+" : "−"}{usd(Math.abs(l.delta))}</span></td>
                        <td className="r muted">{l.newFinish ? fmtDate(l.newFinish) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* These are moments, not calendar dates. fmtDate expects YYYY-MM-DD
                  and was handed a full timestamp, so both read "Invalid Date". */}
              <div className="muted" style={{fontSize:11.5,marginTop:8}}>
                raised {fmtStamp(co.createdAt)}{co.raisedBy ? " by " + co.raisedBy : ""}
                {co.decidedAt ? " · decided " + fmtStamp(co.decidedAt) : ""}
                {co.decidedNote ? " · " + co.decidedNote.replace(/\.\.$/, ".") : ""}
              </div>
              {canDecide && co.status === "proposed" && (
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <Action action="approveChangeOrder" className="btn go"
                          disabled={busy===co.id} onClick={()=>decide(co, true)}>Approve</Action>
                  <Action action="rejectChangeOrder" className="btn ghost"
                          disabled={busy===co.id} onClick={()=>decide(co, false)}>Reject</Action>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </React.Fragment>
  );
}

function ChangeOrderForm({ db, items, say, refresh, onDone }) {
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(0);
  const [lines, setLines] = useState([{ lineItemId: "", delta: "", newFinish: "" }]);
  const [busy, setBusy] = useState(false);

  const setLine = (i, patch) => setLines(ls => ls.map((l,j) => j===i ? {...l, ...patch} : l));
  const total = lines.reduce((n,l) => n + (parseFloat(l.delta) || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.proposeChangeOrder({
        title, reason, daysImpact: Number(days) || 0,
        lines: lines.filter(l => l.lineItemId && l.delta !== "").map(l => ({
          lineItemId: l.lineItemId, delta: parseFloat(l.delta) || 0, newFinish: l.newFinish || null })),
      });
      await refresh(); say("Change order raised — it needs the owner's decision."); onDone();
    } catch (e) { say(e.message); setBusy(false); }
  };

  return (
    <div className="card pad" style={{marginTop:12}}>
      <SectionHead>Propose a change to the contract</SectionHead>
      <label style={{display:"block",marginTop:8}}><div className="eyebrow">What changed</div>
        <input className="field" value={title} onChange={e=>setTitle(e.target.value)}
               placeholder="Added panel capacity for the kitchen"/></label>
      <label style={{display:"block",marginTop:8}}><div className="eyebrow">Why</div>
        <textarea className="field" rows={2} value={reason} onChange={e=>setReason(e.target.value)}
                  placeholder="What made this necessary, and who asked for it."/></label>

      <div className="eyebrow" style={{marginTop:12}}>Lines affected</div>
      {lines.map((l,i) => (
        <div key={i} style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
          <select className="field" style={{flex:2,minWidth:180}} value={l.lineItemId}
                  onChange={e=>setLine(i,{lineItemId:e.target.value})}>
            <option value="">Select a line item…</option>
            {items.map(it => <option key={it.id} value={it.id}>{it.no} · {it.name}</option>)}
          </select>
          <input className="field" style={{flex:1,minWidth:110}} value={l.delta} inputMode="decimal"
                 onChange={e=>setLine(i,{delta:e.target.value})} placeholder="± amount"/>
          <input className="field" style={{flex:1,minWidth:130}} type="date" value={l.newFinish}
                 onChange={e=>setLine(i,{newFinish:e.target.value})}/>
        </div>
      ))}
      <button className="btn ghost sm" style={{marginTop:8}}
              onClick={()=>setLines(ls=>[...ls,{lineItemId:"",delta:"",newFinish:""}])}>+ another line</button>

      <label style={{display:"block",marginTop:12,maxWidth:200}}><div className="eyebrow">Days added to the schedule</div>
        <input className="field" value={days} inputMode="numeric" onChange={e=>setDays(e.target.value)}/></label>

      <p className="muted" style={{fontSize:12,marginTop:12}}>
        Net effect <strong className="num">{total >= 0 ? "+" : "−"}{usd(Math.abs(total))}</strong>.
        Approving this moves the line items and the contract sum together. It does <em>not</em> move terms a
        party has already accepted — that difference shows on the line as drift, to be re-accepted rather
        than imposed.
      </p>
      <div style={{display:"flex",gap:8,marginTop:10}}>
        <button className="btn go" disabled={busy || !title.trim() || !lines.some(l=>l.lineItemId && l.delta!=="")}
                onClick={save}>Raise it</button>
        <button className="btn ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/* ============================================================================
   BUDGET vs SCHEDULE OF VALUES
   A budget line with no SOV line is how $72,000 of conveying systems went
   untracked on Calico for months. The two documents even numbered their
   divisions differently, which is exactly what a person skims past.
   ========================================================================== */
/**
 * Says which SOV line a budget division corresponds to.
 *
 * Deliberately a human act. Calico's two documents number their divisions
 * differently, so a guess here would be a reconciliation quietly agreeing with
 * itself — which is worse than no reconciliation at all.
 */
function MapBudgetLine({ db, budgetLineId, say, onMapped }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.mapBudgetLine({ id: budgetLineId, lineItemIds: picked });
      say("Mapped — the reconciliation will follow this from now on.");
      setOpen(false); onMapped();
    } catch (e) { say(e.message); }
    setBusy(false);
  };

  if (!open) return (
    <button className="btn ghost sm" style={{marginLeft:8}} onClick={()=>setOpen(true)}>Map to a line…</button>
  );
  return (
    <div style={{marginTop:8,padding:"8px 10px",background:"var(--paper-2)",borderRadius:2}}>
      <div className="eyebrow" style={{marginBottom:6}}>Which line on the schedule of values is this?</div>
      <select multiple size={Math.min(6, db.lineItems.length)} className="field" style={{width:"100%"}}
              value={picked}
              onChange={e=>setPicked([...e.target.selectedOptions].map(o=>o.value))}>
        {db.lineItems.map(li => <option key={li.id} value={li.no}>{li.no} · {li.name}</option>)}
      </select>
      <p className="muted" style={{fontSize:11.5,margin:"6px 0"}}>
        Pick more than one if the budget division is split across several lines. Leave it alone if the
        scope genuinely isn't on the schedule of values — that gap is the finding.
      </p>
      <div style={{display:"flex",gap:8}}>
        <button className="btn go sm" disabled={busy || !picked.length} onClick={save}>Map it</button>
        <button className="btn ghost sm" onClick={()=>setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function Reconcile({ db, role, say }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showMatched, setShowMatched] = useState(false);
  const load = () => api.reconciliation().then(setData).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  if (err) return <Empty title="Could not load the reconciliation" body={err}/>;
  if (!data) return <div className="eyebrow" style={{padding:20}}>Loading…</div>;

  // Plain language, because "unmapped-budget" means nothing to anyone reading
  // this for the first time.
  const KIND = {
    "unmapped-budget": {
      tag: "b", label: "not on the SOV",
      why: "Budgeted, but nothing on the schedule of values corresponds to it. Either it is being " +
           "carried under another division — in which case say which, below — or nobody is billing for it.",
    },
    "missing-from-sov": {
      tag: "b", label: "billed at nothing",
      why: "Mapped to a line on the schedule of values, but that line carries $0. The scope was " +
           "budgeted and then zeroed or never priced.",
    },
    "missing-from-budget": {
      tag: "p", label: "not in the budget",
      why: "On the schedule of values with no budget line claiming it. Either it belongs to a budget " +
           "division that hasn't been mapped yet, or it is scope nobody budgeted for.",
    },
    "matched": { tag: "g", label: "reconciles", why: "" },
    // Not scope, so it will never have a line on the schedule of values — and
    // therefore not something anyone has to explain.
    "contingency": {
      tag: "n", label: "contingency",
      why: "Held in the budget against the unknown. It is part of what the job was budgeted to cost, " +
           "which is why it counts toward the total, but it is not scope and has no line on the " +
           "schedule of values.",
    },
  };

  const gaps = data.lines.filter(r => r.kind !== "matched" && r.kind !== "contingency");
  const matched = data.lines.filter(r => r.kind === "matched" || r.kind === "contingency");
  const scale = Math.max(1, ...data.lines.map(r => Math.max(r.budget, r.sov)));
  const money = (v) => v ? usd(v) : <span className="muted">—</span>;

  const Delta = ({ v }) => {
    if (!v) return <span className="delta flat">—</span>;
    return <span className={"delta " + (v > 0 ? "up" : "down")}>{v > 0 ? "+" : "−"}{usd(Math.abs(v))}</span>;
  };
  const Compare = ({ r }) => (
    <div className="cmp" title={`budget ${usd(r.budget)} · schedule of values ${usd(r.sov)}`}>
      {r.budget ? <i className="b" style={{width:(r.budget/scale*100)+"%"}}/> : <i className="none"/>}
      {r.sov ? <i className="s" style={{width:(r.sov/scale*100)+"%"}}/> : <i className="none"/>}
    </div>
  );

  const Row = ({ r, quiet }) => {
    const k = KIND[r.kind] || KIND.matched;
    return (
      <tr className={quiet ? "quiet" : r.kind !== "matched" ? "gap" : ""}>
        <td>
          <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}}>
            <span className="mono" style={{fontWeight:600}}>{r.divisionNo}</span>
            <span style={{fontWeight: quiet ? 400 : 600}}>{r.division}</span>
            <span className={"tag " + k.tag}>{k.label}</span>
          </div>
          {!quiet && k.why && <div className="why">{k.why}</div>}
          {r.note && <div className="why" style={{fontStyle:"italic"}}>{r.note}</div>}
          {r.kind === "unmapped-budget" && r.id && (
            <MapBudgetLine {...{db, budgetLineId:r.id, say, onMapped:load}}/>
          )}
        </td>
        <td style={{width:130}}><Compare r={r}/></td>
        <td className="r num">{money(r.budget)}</td>
        <td className="r num">{money(r.sov)}</td>
        <td className="r"><Delta v={r.variance}/></td>
      </tr>
    );
  };

  return (
    <React.Fragment>
      <h1 className="pagetitle">Budget vs schedule of values</h1>
      <p className="pagesub">
        The budget says what the job should cost; the schedule of values says what is being billed.
        Where they disagree, one of the two documents is wrong — and it is usually the one nobody reads.
      </p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Budget" v={usd(data.totals.budget)} sub="Exhibit D, mapped divisions"/>
        <Stat k="Schedule of values" v={usd(data.totals.sov)} sub="current scheduled value"/>
        <Stat k="Variance" v={usd(Math.abs(data.totals.variance))}
              tone={Math.abs(data.totals.variance) > 1 ? "flag" : "good"}
              sub={data.totals.variance > 0 ? "SOV above budget" : "budget above SOV"}/>
        <Stat k="Needs an answer" v={gaps.length} tone={gaps.length ? "flag" : "good"}
              sub={gaps.length ? "of " + data.lines.length + " divisions" : "everything reconciles"}/>
      </div>

      {gaps.length > 0 && (
        <div className="banner" style={{marginTop:14}}>
          <h4>{gaps.length} division{gaps.length===1?"":"s"} exist in one document and not the other</h4>
          <p>Each of these is either scope nobody is billing for, or billing for scope nobody budgeted.
             Both are worth an answer in writing before the next payment application.</p>
        </div>
      )}

      <div className="toolbar noprint" style={{marginTop:14}}>
        <div className="cmplegend">
          <span><em className="b"/>Budget</span>
          <span><em className="s"/>Schedule of values</span>
          <span className="muted">bars scaled to {usd(scale)}</span>
        </div>
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>setShowMatched(v=>!v)}>
          {showMatched ? "Hide" : "Show"} the {matched.length} that reconcile
        </button>
        <button className="btn ghost sm" onClick={()=>download("calico-budget-vs-sov-" + iso(TODAY) + ".csv",
          toCSV(data.lines, [["divisionNo","Division No"],["division","Division"],["kind","Status"],
            ["budget","Budget"],["sov","Scheduled Value"],["variance","Variance"]]))}>Export CSV</button>
        <button className="btn ghost sm noprint" onClick={()=>window.print()}>Print</button>
      </div>

      <div className="tablewrap" style={{marginTop:10}}>
        <table className="sov">
          <thead>
            <tr>
              <th className="plain">Division</th>
              <th className="plain" style={{width:130}}>Compared</th>
              <th className="plain r">Budget</th>
              <th className="plain r">Schedule of values</th>
              <th className="plain r">Variance</th>
            </tr>
          </thead>
          <tbody>
            {gaps.length > 0 && (
              <tr className="grouphead"><td colSpan={5}>
                <span className="eyebrow">Needs an answer · {gaps.length}</span>
              </td></tr>
            )}
            {gaps.map((r,i) => <Row key={"g"+i} r={r}/>)}

            {showMatched && matched.length > 0 && (
              <tr className="grouphead"><td colSpan={5}>
                <span className="eyebrow">Reconciles · {matched.length}</span>
              </td></tr>
            )}
            {showMatched && matched.map((r,i) => <Row key={"m"+i} r={r} quiet/>)}
          </tbody>
          <tfoot>
            <tr>
              <td>Total{showMatched ? "" : " (including the " + matched.length + " not shown)"}</td>
              <td/>
              <td className="r num">{usd(data.totals.budget)}</td>
              <td className="r num">{usd(data.totals.sov)}</td>
              <td className="r"><Delta v={data.totals.variance}/></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="muted" style={{fontSize:11.5,marginTop:10,maxWidth:"70ch"}}>
        Budget figures come from Exhibit D (11 Feb 2026). Scheduled values are current, and move when a
        change order is approved. The two documents number their divisions differently, which is why the
        mapping between them is recorded rather than assumed.
      </p>
    </React.Fragment>
  );
}

/* ============================================================================
   THE INVESTOR PAGE
   The page investors actually read, edited field by field. It is stored as one
   document because that is how it is authored — a sentence and the number it
   refers to change together — and versioned on every save, because a page sent
   to fifty people is a representation made to them.
   ========================================================================== */

/*
 * These live out here on purpose. Declared inside the editor they would be new
 * component types on every keystroke, so React would unmount and remount the
 * input and the caret would jump out of the field on every character typed.
 */
function TextField({ value, onChange, placeholder, area, rows, width }) {
  return area
    ? <textarea className="field" rows={rows || 2} value={value || ""} placeholder={placeholder}
                style={{width: width || "100%", marginTop: 6}}
                onChange={(e) => onChange(e.target.value)}/>
    : <input className="field" value={value || ""} placeholder={placeholder}
             style={{width: width || "100%"}}
             onChange={(e) => onChange(e.target.value)}/>;
}

/**
 * Money, typed the way people type money.
 *
 * The text is held locally so that clearing the box, or typing "1200." on the
 * way to "1200.50", doesn't get rewritten underneath the caret. The parsed
 * number goes out on every keystroke; the tidy version appears on blur.
 */
function MoneyField({ value, onChange, width }) {
  const [raw, setRaw] = useState(value == null ? "" : String(value));
  const [live, setLive] = useState(false);
  useEffect(() => { if (!live) setRaw(value == null ? "" : String(value)); }, [value, live]);
  return (
    <input className="field num" inputMode="decimal" value={raw}
           style={{maxWidth: width || 132, textAlign: "right"}}
           onFocus={() => setLive(true)}
           onChange={(e) => {
             const t = e.target.value.replace(/[^0-9.\-]/g, "");
             setRaw(t);
             onChange(t === "" ? null : Number(t) || 0);
           }}
           onBlur={() => { setLive(false); setRaw(value == null ? "" : String(value)); }}/>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,whiteSpace:"nowrap"}}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}/>
      {label}
    </label>
  );
}

/** Move a row up or down, remove it, or add one after it. */
function RowTools({ onUp, onDown, onRemove, onAdd, first, last }) {
  return (
    <div className="rowtools noprint">
      <button className="btn ghost sm" title="Move up" disabled={first} onClick={onUp}>↑</button>
      <button className="btn ghost sm" title="Move down" disabled={last} onClick={onDown}>↓</button>
      {onAdd && <button className="btn ghost sm" onClick={onAdd}>+ below</button>}
      <button className="btn ghost sm" onClick={onRemove}>Remove</button>
    </div>
  );
}

const IP_TABS = [
  ["headline",  "Headline"],
  ["need",      "What's needed"],
  ["committed", "Already committed"],
  ["coverage",  "How it gets covered"],
  ["timeline",  "Path to revenue"],
  ["documents", "Documents"],
  ["resources", "Supporting material"],
  ["footnotes", "Disclosures"],
  ["simulator", "Revenue simulator"],
];

/**
 * The people on the capital side, as a list you can work.
 *
 * They were being rendered under a heading on the contractors page, where the
 * navigation counted only contractors — so forty-two imported investors showed
 * a badge reading "1" and appeared below a fold nobody scrolled to.
 *
 * The column that matters most here is whether an address can be reached at
 * all: a roster loaded from a spreadsheet is deliberately held until the owner
 * is ready, and that state has to be visible rather than discovered by sending
 * something and waiting for silence.
 */
/**
 * What has been held for somebody, before you decide to let mail through.
 *
 * "Release" on its own asks you to act without showing you what you are acting
 * on. This shows it: why the address is held, since when, and every message the
 * system tried to send and refused.
 *
 * One thing it deliberately does not offer is releasing an individual message.
 * A refused message is recorded and dropped — the row keeps its subject and
 * cause but only a hash of the body — so there is no spool to let through one
 * at a time. Pretending otherwise would be a button that quietly did nothing.
 * An invitation is the exception worth acting on, and it can simply be sent
 * again.
 */
function HeldDetail({ people, onClose, onAllowed, say }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const addresses = people.map((p) => (p.email || "").toLowerCase()).filter(Boolean);
  const load = () => api.heldDetail(addresses).then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const byAddress = {};
  for (const d of data || []) byAddress[d.address] = d;
  const totalRefused = (data || []).reduce((n, d) => n + d.refused.length, 0);
  const stillHeld = (data || []).filter((d) => d.held);

  const allow = async () => {
    setBusy(true); setErr(null);
    const failed = [];
    for (const d of stillHeld) {
      try { await api.releaseAddress(d.address); } catch (e) { failed.push(d.address); }
    }
    setBusy(false);
    if (failed.length) { setErr(`Could not allow: ${failed.join(", ")}`); return; }
    say(`Mail can reach ${stillHeld.length === 1 ? people[0].name : stillHeld.length + " people"} now.`);
    await onAllowed();
    onClose();
  };

  const resend = async (p) => {
    try { const r = await api.resendInvite(p.id); say(r.held
      ? "Still held — allow mail first, then send it again."
      : `Invitation sent to ${p.name}.`); await load(); }
    catch (e) { say(e.message); }
  };

  return (
    <React.Fragment>
      <div className="scrim" onClick={onClose}/>
      <div className="drawer">
        <div className="drawerh">
          <div>
            <div className="eyebrow">Held mail</div>
            <h2 style={{fontSize:17,margin:"4px 0 0",letterSpacing:"-.02em"}}>
              {people.length === 1 ? people[0].name : `${people.length} people`}
            </h2>
          </div>
          <button className="btn ghost sm" onClick={onClose}>Close</button>
        </div>

        <div className="drawerb">
          {!data && !err && <p className="eyebrow">Loading…</p>}
          {err && <p style={{fontSize:12.5,color:"var(--oxide)"}}>{err}</p>}

          {data && (
            <React.Fragment>
              <p style={{fontSize:12.5,lineHeight:1.6,marginTop:0}}>
                {stillHeld.length === 0
                  ? "Nothing is being held. Mail already reaches them."
                  : <React.Fragment>
                      The system is <b>refusing to write</b> to {stillHeld.length === 1
                        ? "this address" : `these ${stillHeld.length} addresses`}.
                      {" "}{totalRefused === 0
                        ? "Nothing has been attempted since the hold went on."
                        : `${totalRefused} message${totalRefused === 1 ? " has" : "s have"} been ` +
                          `turned away — recorded below, not delivered.`}
                    </React.Fragment>}
              </p>

              {stillHeld.length > 0 && stillHeld[0].detail && (
                <p className="muted" style={{fontSize:12,marginTop:0}}>
                  <b>Why:</b> {stillHeld[0].detail}
                  {stillHeld[0].since ? ` · since ${fmtStamp(stillHeld[0].since)}` : ""}
                </p>
              )}

              {people.map((p) => {
                const d = byAddress[(p.email || "").toLowerCase()];
                if (!d) return null;
                return (
                  <div key={p.id} style={{marginTop:16}}>
                    {people.length > 1 && (
                      <div className="eyebrow">{p.name}
                        {!d.held && <span className="tag g" style={{marginLeft:8}}>already allowed</span>}</div>
                    )}
                    {d.refused.length === 0
                      ? <p className="muted" style={{fontSize:12,margin:"6px 0 0"}}>
                          Nothing has been attempted to {d.address}.</p>
                      : (
                        <div className="tablewrap" style={{marginTop:8}}>
                          <table className="sov">
                            <thead><tr><th>What it was</th><th>Subject</th><th>When</th><th></th></tr></thead>
                            <tbody>
                              {d.refused.map((m) => (
                                <tr key={m.id}>
                                  <td><span className="tag n">{(m.kind || "message").replace(/\./g, " ")}</span></td>
                                  <td style={{fontSize:12.5}}>{m.subject}</td>
                                  <td className="mono" style={{fontSize:11,whiteSpace:"nowrap"}}>{fmtStamp(m.at)}</td>
                                  <td className="r">
                                    {m.resendable && !d.held && (
                                      <button className="btn ghost sm" onClick={()=>resend(p)}>Send it now</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                  </div>
                );
              })}

              <div className="note" style={{marginTop:16}}>
                <div className="meta">Why you cannot pick individual messages</div>
                <div style={{fontSize:12.5,lineHeight:1.55}}>
                  A refused message is recorded and dropped, not queued — the record keeps its
                  subject and cause, but never the text. There is nothing waiting in a spool to let
                  through. Allowing mail affects what happens <i>from now on</i>. An invitation is
                  the one thing worth having again, and it can simply be sent afresh.
                </div>
              </div>
            </React.Fragment>
          )}
        </div>

        <div className="drawerf">
          <button className="btn go" disabled={busy || !stillHeld.length} onClick={allow}>
            {busy ? "…" : stillHeld.length > 1
              ? `Allow mail to ${stillHeld.length} people` : "Allow mail from now on"}
          </button>
          <span className="muted" style={{fontSize:11.5,flex:1,lineHeight:1.45}}>
            Nothing is sent by allowing. It only stops the refusals.
          </span>
        </div>
      </div>
    </React.Fragment>
  );
}

/**
 * The three pieces of paperwork a contractor needs on file, and what is
 * happening with each.
 *
 * The same component serves both sides. A contractor sees their own and can
 * upload; an owner sees anybody's and can accept or return one. Nobody sees
 * another contractor's — the server does not send it, and this could not show
 * it if it wanted to.
 */
const PAPERWORK_KINDS = [
  { kind: "w9", label: "W-9",
    why: "So you can be paid. The owner needs it on file before any scope is offered." },
  { kind: "license", label: "Licence",
    why: "Your trade licence. The owner records the number from it." },
  { kind: "insurance", label: "Insurance certificate",
    why: "Your certificate of insurance. The owner records the date it expires — work cannot be offered on expired cover." },
  // "Licensed and bonded" is two documents. Not every job asks for the second,
  // so this appears only where the owner has said the project requires it.
  { kind: "bond", label: "Surety bond", optional: true,
    why: "Your payment or performance bond. The owner records the bond number, the surety, the amount and when it runs out." },
];

function Paperwork({ db, party, canReview, say, refresh }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const docs = party.documents || [];
  const latest = (kind) => docs.find((d) => d.kind === kind);
  // A bond only counts as required where the project asks for one.
  const needed = (kind) => kind !== "bond" || !!db.project.requireBond;

  const upload = async (kind, file) => {
    setBusy(kind); setErr(null);
    try {
      await api.uploadPaperwork({ file, kind, partyId: party.id });
      await refresh();
      say("Uploaded. The owner will look at it — nothing is on file until they accept it.");
    } catch (e) { setErr(e.message); }
    setBusy(null);
  };

  // What the project believes today, which is what the gate reads.
  const dated = (v) => v
    ? (new Date(v) < new Date() ? "Expired " + fmtDate(v) : "Good until " + fmtDate(v))
    : "No expiry recorded";
  const fact = (kind) => kind === "w9" ? (party.w9 ? "On file" : "Not on file")
    : kind === "license" ? (party.licenseNo || "No number recorded")
    : kind === "bond" ? (party.bondNo
        ? `${party.bondNo}${party.bondSurety ? " · " + party.bondSurety : ""} · ${dated(party.bondExpires)}`
        : "No bond on file")
    : dated(party.coiExpires);
  const factOk = (kind) => kind === "w9" ? !!party.w9
    : kind === "license" ? !!party.licenseNo
    : kind === "bond" ? (!!party.bondExpires && new Date(party.bondExpires) >= new Date())
    : !!party.coiExpires && new Date(party.coiExpires) >= new Date();
  const required = PAPERWORK_KINDS.filter((k) => needed(k.kind));

  return (
    <div className="card pad" style={{marginTop:14}}>
      <SectionHead right={
        <span className={"tag " + (required.every((k) => factOk(k.kind)) ? "g" : "b")}>
          {required.filter((k) => factOk(k.kind)).length} of {required.length} on file
        </span>}>
        Paperwork
      </SectionHead>
      <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:600}}>
        {canReview
          ? "What this company has sent, and what the project believes as a result. Accepting a document is what puts the fact on file — uploading one does not."
          : "Send these once. Nothing is on file until the owner has looked at each one and accepted it, and no scope can be offered to you until all three are."}
      </p>

      {PAPERWORK_KINDS.filter((k) => !k.optional || needed(k.kind) || latest(k.kind))
        .map(({ kind, label, why }) => {
        const d = latest(kind);
        return (
          <div className="editrow" key={kind}>
            <div className="editline">
              <span style={{fontWeight:600,fontSize:13.5,flex:"0 0 auto"}}>{label}</span>
              <span className={"tag " + (factOk(kind) ? "g" : "b")}>{fact(kind)}</span>
              {d && <span className={"tag " + (d.status === "accepted" ? "g"
                : d.status === "rejected" ? "p" : "n")}>
                {d.status === "pending" ? "waiting on the owner" : d.status}</span>}
              <div style={{flex:1}}/>
              {d && (
                <a className="btn ghost sm" target="_blank" rel="noopener"
                   href={`/api/party-documents/${d.id}/download?redirect=1`}>Open</a>
              )}
              <label className="btn ghost sm" style={{cursor:"pointer"}}>
                {busy === kind ? "…" : d ? "Replace" : "Upload"}
                <input type="file" style={{display:"none"}} disabled={busy === kind}
                       accept="application/pdf,image/*"
                       onChange={(e) => { const f = e.target.files?.[0]; e.target.value = "";
                                          if (f) upload(kind, f); }}/>
              </label>
            </div>
            <div className="itemmeta">{why}</div>
            {d && (
              <div className="itemmeta">
                {d.filename} · {Math.max(1, Math.round(d.size / 1024)).toLocaleString()} KB ·
                sent {fmtStamp(d.uploadedAt)}{d.uploadedBy ? " by " + d.uploadedBy : ""}
                {d.reviewedAt ? ` · ${d.status} ${fmtStamp(d.reviewedAt)}` +
                  (d.reviewedBy ? " by " + d.reviewedBy : "") : ""}
                {d.reviewNote ? ` · "${d.reviewNote}"` : ""}
              </div>
            )}
            {canReview && d && d.status !== "accepted" && (
              <div className="rowtools">
                <button className="btn go sm" onClick={()=>setReviewing({ doc: d, kind, label })}>
                  Look at it</button>
              </div>
            )}
          </div>
        );
      })}
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}

      {reviewing && (
        <ReviewPaperwork {...reviewing} say={say} refresh={refresh}
                         onClose={()=>setReviewing(null)}/>
      )}
    </div>
  );
}

/**
 * Accepting somebody's paperwork.
 *
 * Accepting is where a person takes responsibility, so it asks for what can
 * only be read off the document itself — the licence number, the expiry date.
 * Those become the facts the gate reads, which is why the contractor cannot
 * set them.
 */
function ReviewPaperwork({ doc, kind, label, onClose, say, refresh }) {
  const [note, setNote] = useState("");
  const [reference, setReference] = useState(doc.reference || "");
  const [expiresOn, setExpiresOn] = useState(doc.expiresOn || "");
  const [surety, setSurety] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const decide = async (accept) => {
    setBusy(true); setErr(null);
    try {
      await api.reviewPaperwork(doc.id, { accept, note, reference,
        expiresOn: expiresOn || null,
        surety: surety || undefined,
        amount: amount ? Number(amount) : undefined });
      await refresh();
      say(accept ? `${label} accepted.` : `${label} returned to them.`);
      onClose();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <React.Fragment>
      <div className="scrim" onClick={onClose}/>
      <div className="drawer">
        <div className="drawerh">
          <div>
            <div className="eyebrow">Paperwork</div>
            <h2 style={{fontSize:17,margin:"4px 0 0",letterSpacing:"-.02em"}}>{label}</h2>
          </div>
          <button className="btn ghost sm" onClick={onClose}>Close</button>
        </div>
        <div className="drawerb">
          <p style={{fontSize:12.5,lineHeight:1.6,marginTop:0}}>
            Open it and read it before deciding. Accepting is what puts the fact on file, and the
            gate on offering scope reads that fact — not the document.
          </p>
          <a className="btn" target="_blank" rel="noopener"
             href={`/api/party-documents/${doc.id}/download?redirect=1`}>Open {doc.filename}</a>

          {kind === "license" && (
            <label style={{display:"block",marginTop:16}}>
              <div className="eyebrow">Licence number, as it appears on the document</div>
              <TextField value={reference} onChange={setReference} placeholder="e.g. MS-12345"/>
            </label>
          )}
          {kind === "bond" && (
            <React.Fragment>
              <label style={{display:"block",marginTop:16}}>
                <div className="eyebrow">Bond number</div>
                <TextField value={reference} onChange={setReference} placeholder="as printed on the bond"/>
              </label>
              <label style={{display:"block",marginTop:12}}>
                <div className="eyebrow">Surety — who is standing behind it</div>
                <TextField value={surety} onChange={setSurety} placeholder="e.g. Travelers Casualty"/>
                <div className="itemmeta">
                  The bond is worth what this company will pay. A bond from nobody you can name is
                  worth checking before you accept it.
                </div>
              </label>
              <div className="editline" style={{marginTop:12}}>
                <label>
                  <div className="eyebrow">Amount</div>
                  <MoneyField value={amount === "" ? null : Number(amount)}
                              onChange={(v)=>setAmount(v == null ? "" : String(v))}/>
                </label>
                <label>
                  <div className="eyebrow">Expires</div>
                  <input className="field" type="date" value={expiresOn}
                         onChange={(e)=>setExpiresOn(e.target.value)} style={{maxWidth:180}}/>
                </label>
              </div>
            </React.Fragment>
          )}
          {kind === "insurance" && (
            <label style={{display:"block",marginTop:16}}>
              <div className="eyebrow">Expiry date on the certificate</div>
              <input className="field" type="date" value={expiresOn}
                     onChange={(e)=>setExpiresOn(e.target.value)} style={{maxWidth:200}}/>
              <div className="itemmeta">Scope cannot be offered on cover that has run out.</div>
            </label>
          )}
          <label style={{display:"block",marginTop:16}}>
            <div className="eyebrow">Note {`(required if you are sending it back)`}</div>
            <TextField area value={note} onChange={setNote}
                       placeholder="Anything they need to know"/>
          </label>
          {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
        </div>
        <div className="drawerf">
          <button className="btn go" disabled={busy} onClick={()=>decide(true)}>
            {busy ? "…" : "Accept it"}</button>
          <button className="btn stop" disabled={busy || !note.trim()}
                  title={!note.trim() ? "Say why, so they know what to fix" : ""}
                  onClick={()=>decide(false)}>Send it back</button>
          <span className="muted" style={{fontSize:11.5,flex:1,lineHeight:1.45}}>
            Accepting records that you have read it, and when.
          </span>
        </div>
      </div>
    </React.Fragment>
  );
}

/**
 * A sortable column header.
 *
 * Declared out here on purpose. Inside the table it would be a new component
 * type on every render, so React would throw away and rebuild every header the
 * moment you clicked one — and the click after that would land on an element
 * that had already been replaced. The same reason the editable fields live at
 * module scope.
 */
function Th({ col, sort, onSort, children, right }) {
  const on = sort.col === col;
  return (
    <th className={"sortable" + (right ? " r" : "") + (on ? " on" : "")}
        onClick={() => onSort(col)} title="Sort by this column"
        aria-sort={on ? (sort.dir > 0 ? "ascending" : "descending") : "none"}>
      {children}<span className="sortmark">{on ? (sort.dir > 0 ? "▲" : "▼") : "↕"}</span>
    </th>
  );
}

/**
 * What you can do to several people at once.
 *
 * Every action says how many it will touch and what it will not touch — the
 * counts are the point. "Release 41" and "Release 3" are different decisions,
 * and an action that quietly skips half the selection is worse than one that
 * refuses.
 */
function BulkBar({ chosen, held, userFor, bulk, working, clear, onReview }) {
  const [preset, setPreset] = useState("");
  const withAccount = chosen.filter((p) => userFor(p.id));
  const partners = withAccount.filter((p) => userFor(p.id).role === "partner");
  const contactable = chosen.filter((p) => p.email && !held.includes(p));

  if (working) {
    return (
      <div className="banner brass" style={{marginTop:14}}>
        <h4>{working.label} — {working.done} of {working.total}</h4>
        <p>One at a time, so each is recorded separately. Leave this open until it finishes.</p>
      </div>
    );
  }

  return (
    <div className="card pad bulkbar" style={{marginTop:14}}>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <span className="tag b">{chosen.length} selected</span>
        <button className="linkbtn" style={{fontSize:12}} onClick={clear}>Clear</button>
        <div style={{flex:1}}/>
      </div>

      <div className="toolbar" style={{marginTop:10}}>
        <button className="btn" disabled={!held.length} onClick={() => onReview(held)}>
          Review held mail <span className="muted">{held.length}</span>
        </button>

        <button className="btn ghost" disabled={!contactable.length}
          onClick={() => window.confirm(
            `Hold mail to ${contactable.length} ${contactable.length === 1 ? "person" : "people"}?\n\n` +
            `Nothing will be sent to them until you allow it again. Attempts are recorded and refused.`)
            && bulk("Held", contactable,
                    (p) => api.holdAddress(p.email, "Held by the owner from the investor list."))}>
          Hold mail <span className="muted">{contactable.length}</span>
        </button>

        <div style={{flex:1}}/>

        <button className="btn ghost" disabled={!withAccount.length}
          onClick={() => window.confirm(
            `Make ${withAccount.length} of them partners?\n\n` +
            `A partner can see the project and the audit trail. Everything beyond that you tick on ` +
            `afterwards. Anything granted to them today is cleared.\n\nThis sends nothing.`)
            && bulk("Made partners", withAccount,
                    (p) => api.setMemberRole(userFor(p.id).id, "partner"))}>
          Make partners <span className="muted">{withAccount.length}</span>
        </button>
      </div>

      {partners.length > 0 && (
        <div className="editline" style={{marginTop:12}}>
          <span className="eyebrow">Give {partners.length} partner{partners.length === 1 ? "" : "s"} the same job</span>
          <select className="field" style={{maxWidth:230}} value={preset}
                  onChange={(e)=>setPreset(e.target.value)}>
            <option value="">Choose a group…</option>
            {GRANT_PRESETS.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <button className="btn" disabled={!preset}
            onClick={() => {
              const g = GRANT_PRESETS.find((x) => x.id === preset);
              if (!g) return;
              if (!window.confirm(
                `Give ${partners.length} partner${partners.length === 1 ? "" : "s"} "${g.name}"?\n\n` +
                `${g.what}\n\nThis replaces what each of them can do today.`)) return;
              bulk(`Set to "${g.name}"`, partners,
                   (p) => api.setGrants(userFor(p.id).id, g.caps));
            }}>Apply</button>
          {preset && (
            <span className="muted" style={{fontSize:12,flex:"1 1 240px"}}>
              {GRANT_PRESETS.find((x) => x.id === preset)?.what}
            </span>
          )}
        </div>
      )}

      {chosen.length !== withAccount.length && (
        <p className="muted" style={{fontSize:11.5,marginTop:10,marginBottom:0}}>
          {chosen.length - withAccount.length} of those selected have no account yet, so role and
          permission changes skip them. Invite them first.
        </p>
      )}
    </div>
  );
}

function Investors({ db, role, say, refresh, setView }) {
  const [held, setHeld] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [sort, setSort] = useState({ col: "name", dir: 1 });
  const [picked, setPicked] = useState(() => new Set());
  const [working, setWorking] = useState(null);   // { done, total, label }
  const [report, setReport] = useState(null);

  const load = () => api.undeliverable()
    .then((rows) => setHeld(rows))
    .catch(() => setHeld([]));
  useEffect(() => { load(); }, []);

  // Removed investors deactivate rather than disappear — their record and
  // anything they signed stays — but they are off the working list.
  const [showRemoved, setShowRemoved] = useState(false);
  const investors = db.parties.filter((p) => p.role === "investor"
    && (showRemoved || p.status !== "inactive"));
  const removedCount = db.parties.filter((p) => p.role === "investor" && p.status === "inactive").length;
  const byAddress = {};
  for (const h of held || []) byAddress[String(h.address).toLowerCase()] = h;

  // An investor from a spreadsheet is a name and an address; they only become
  // somebody who can be given access once they have accepted an invitation and
  // have an account. Showing which is which is the difference between "why
  // can't I promote them" and "ah, they haven't joined yet".
  const userFor = (partyId) => (db.users || []).find((u) => u.partyId === partyId);
  const pledgesFor = (partyId) => pledgesOf(db).filter((x) => x.partyId === partyId);

  const elevate = async (p, u) => {
    if (!window.confirm(
      `Make ${p.name} a partner?\n\nThey will be able to see the project and the audit trail. ` +
      `Everything beyond that — approving work, the capital side, settings — you tick on for them ` +
      `afterwards, one at a time.\n\nThis does not send them anything.`)) return;
    setBusy(p.id);
    try {
      await api.setMemberRole(u.id, "partner");
      await refresh();
      say(`${p.name} is a partner now. Choose what they can do in Settings → Access.`);
    } catch (e) { say(e.message); }
    setBusy(null);
  };
  const state = (p) => {
    const h = byAddress[String(p.email || "").toLowerCase()];
    if (!p.email) return { k: "none", label: "No address", tag: "b" };
    if (!h) return { k: "ok", label: "Can be contacted", tag: "g" };
    return h.reason === "manual"
      ? { k: "held", label: "Held", tag: "n", why: h.detail }
      : { k: "dead", label: "Not delivering", tag: "p", why: h.detail };
  };

  // One value per column to sort on, so the comparison is the same shape
  // whatever is being compared.
  const sortKey = (p, col) => {
    const u = userFor(p.id);
    switch (col) {
      case "type":      return p.trade === "Entity" ? "Company" : "Individual";
      case "email":     return (p.email || "~").toLowerCase();
      case "mail":      return state(p).label;
      case "access":    return u ? (ROLES[u.role] ? ROLES[u.role].label : u.role) : "~none";
      case "committed": return pledgesFor(p.id).reduce((s, x) => s + x.amount, 0);
      default:          return (p.name || "").toLowerCase();
    }
  };
  const by = (a, b) => {
    const x = sortKey(a, sort.col), y = sortKey(b, sort.col);
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
    return c * sort.dir || (a.name || "").localeCompare(b.name || "");
  };
  const toggleSort = (col) =>
    setSort((s2) => s2.col === col ? { col, dir: -s2.dir } : { col, dir: 1 });

  const rows = investors
    .filter((p) => only === "all"
      || (only === "entity" && p.trade === "Entity")
      || (only === "person" && p.trade !== "Entity")
      || (only === "held" && state(p).k === "held"))
    .filter((p) => !q.trim()
      || (p.name + " " + (p.email || "")).toLowerCase().includes(q.trim().toLowerCase()))
    .sort(by);

  // Selection follows the person, not the row, so filtering or re-sorting does
  // not quietly change what an action is about to apply to.
  const chosen = investors.filter((p) => picked.has(p.id));
  const visibleIds = rows.map((p) => p.id);
  const allVisiblePicked = visibleIds.length > 0 && visibleIds.every((id) => picked.has(id));
  const toggleOne = (id) => setPicked((s2) => {
    const n = new Set(s2); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllVisible = () => setPicked((s2) => {
    const n = new Set(s2);
    allVisiblePicked ? visibleIds.forEach((id) => n.delete(id)) : visibleIds.forEach((id) => n.add(id));
    return n;
  });

  /**
   * Apply something to everyone selected, one at a time.
   *
   * Deliberately not a single bulk endpoint: each release, each role change is
   * its own audited act, and one failure must not decide the fate of the other
   * forty. What happened to each is reported rather than averaged into
   * "success".
   */
  const bulk = async (label, list, fn) => {
    if (!list.length) return;
    setWorking({ done: 0, total: list.length, label });
    setReport(null);
    const failed = [];
    let done = 0;
    for (const p of list) {
      try { await fn(p); }
      catch (e) { failed.push({ name: p.name, why: e.message }); }
      setWorking({ done: ++done, total: list.length, label });
    }
    await refresh(); await load();
    setWorking(null);
    setReport({ label, ok: list.length - failed.length, failed });
    if (!failed.length) say(`${label}: ${list.length} done.`);
  };

  const counts = {
    all: investors.length,
    entity: investors.filter((p) => p.trade === "Entity").length,
    person: investors.filter((p) => p.trade !== "Entity").length,
    held: investors.filter((p) => state(p).k === "held").length,
  };

  // Show what is being held before asking anyone to lift it.
  const [showing, setShowing] = useState(null);

  return (
    <React.Fragment>
      <h1 className="pagetitle">Investors</h1>
      <p className="pagesub">
        Everyone on the capital side of this project — individuals and companies — and whether the
        system is allowed to write to them yet.
      </p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Investors" v={counts.all} sub={`${counts.person} people · ${counts.entity} companies`}/>
        <Stat k="Held from mail" v={counts.held}
              tone={counts.held ? "flag" : "good"}
              sub={counts.held ? "nothing can reach them" : "all reachable"}/>
        <Stat k="Committed" v={usd(pledgesOf(db).filter(x => x.status === "signed" || x.status === "received")
              .reduce((s, x) => s + x.amount, 0))} sub="signed or received"/>
        <Stat k="Received" v={usd(pledgesOf(db).filter(x => x.status === "received")
              .reduce((s, x) => s + x.amount, 0))} sub="funds in" tone="good"/>
      </div>

      {counts.held > 0 && (
        <div className="banner" style={{marginTop:14}}>
          <h4>{counts.held} of these are held on purpose</h4>
          <p>Nothing is sent to a held address — an attempt is recorded and refused. That is how an
             imported roster stays private until you are ready to introduce it. Release someone here
             when you want to be able to write to them; releasing sends nothing by itself.</p>
        </div>
      )}

      <div className="toolbar noprint" style={{marginTop:14}}>
        {[["all","Everyone"],["person","People"],["entity","Companies"],["held","Held"]].map(([k,l]) => (
          <button key={k} className={"btn " + (only===k ? "" : "ghost")} onClick={()=>setOnly(k)}>
            {l} <span className="muted">{counts[k]}</span>
          </button>
        ))}
        <div style={{flex:1}}/>
        {removedCount > 0 && (
          <button className={"btn " + (showRemoved ? "" : "ghost")}
                  onClick={()=>setShowRemoved(v=>!v)}>
            Removed <span className="muted">{removedCount}</span>
          </button>
        )}
        <input className="field" style={{maxWidth:220}} placeholder="Find a name or address"
               value={q} onChange={(e)=>setQ(e.target.value)}/>
      </div>

      {showing && (
        <HeldDetail people={showing} say={say} onClose={()=>setShowing(null)}
                    onAllowed={async ()=>{ await load(); await refresh(); }}/>
      )}

      {role === "owner" && chosen.length > 0 && (
        <BulkBar {...{chosen, held: (chosen.filter((p) => state(p).k === "held")),
                      userFor, bulk, working, clear: () => setPicked(new Set()),
                      onReview: (list) => setShowing(list)}}/>
      )}

      {report && (
        <div className={"banner" + (report.failed.length ? "" : " brass")} style={{marginTop:12}}>
          <h4>{report.label}: {report.ok} done{report.failed.length ? `, ${report.failed.length} not` : ""}</h4>
          {report.failed.length > 0 && (
            <ul style={{margin:"6px 0 0 16px",fontSize:12.5,lineHeight:1.6}}>
              {report.failed.slice(0, 8).map((f, i) => <li key={i}><b>{f.name}</b> — {f.why}</li>)}
              {report.failed.length > 8 && <li>…and {report.failed.length - 8} more</li>}
            </ul>
          )}
          <p><button className="btn ghost sm" onClick={()=>setReport(null)}>Dismiss</button></p>
        </div>
      )}

      {held === null && <p className="eyebrow" style={{padding:"12px 0"}}>Loading…</p>}

      {rows.length === 0
        ? <Empty title="Nobody here" body={q ? "No investor matches that." : "No investors on this project yet."}/>
        : (
          <div className="tablewrap" style={{marginTop:12}}>
            <table className="sov">
              <thead>
                <tr>
                  {role === "owner" && (
                    <th style={{width:28}}>
                      <input type="checkbox" checked={allVisiblePicked} onChange={toggleAllVisible}
                             aria-label="Select everyone shown"
                             title={allVisiblePicked ? "Clear these" : "Select everyone shown"}/>
                    </th>
                  )}
                  <Th col="name" sort={sort} onSort={toggleSort}>Investor</Th>
                  <Th col="type" sort={sort} onSort={toggleSort}>Type</Th>
                  <Th col="email" sort={sort} onSort={toggleSort}>Email</Th>
                  <Th col="mail" sort={sort} onSort={toggleSort}>Mail</Th>
                  <Th col="access" sort={sort} onSort={toggleSort}>Access</Th>
                  <Th col="committed" sort={sort} onSort={toggleSort} right>Committed</Th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const st = state(p);
                  const u = userFor(p.id);
                  const mine = pledgesFor(p.id);
                  const committed = mine.reduce((s, x) => s + x.amount, 0);
                  return (
                    <tr key={p.id} className={picked.has(p.id) ? "picked" : ""}>
                      {role === "owner" && (
                        <td>
                          <input type="checkbox" checked={picked.has(p.id)}
                                 onChange={()=>toggleOne(p.id)}
                                 aria-label={"Select " + p.name}/>
                        </td>
                      )}
                      <td>
                        <div className="itemname">{p.name}</div>
                        {p.contact && p.contact !== p.name && <div className="itemmeta">{p.contact}</div>}
                        {p.note && <div className="itemmeta">{p.note}</div>}
                      </td>
                      <td><span className="tag n">{p.trade === "Entity" ? "Company" : "Individual"}</span></td>
                      <td className="mono" style={{fontSize:11.5}}>{p.email || <span className="muted">—</span>}</td>
                      <td>
                        <span className={"tag " + st.tag} title={st.why || ""}>{st.label}</span>
                      </td>
                      <td>
                        {u
                          ? <span className={"tag " + (u.role === "partner" ? "g" : "n")}>
                              {ROLES[u.role] ? ROLES[u.role].label : u.role}</span>
                          : <span className="muted" style={{fontSize:11.5}}>No account yet</span>}
                      </td>
                      <td className="num r">{committed ? usd(committed) : <span className="muted">—</span>}</td>
                      <td className="r" style={{whiteSpace:"nowrap"}}>
                        {role === "owner" && st.k === "held" && (
                          <button className="btn ghost sm" onClick={()=>setShowing([p])}
                                  title="See what has been held for them, and decide">
                            Held mail…</button>
                        )}
                        {role === "owner" && u && u.role === "investor" && (
                          <button className="btn ghost sm" style={{marginLeft:6}}
                                  disabled={busy === p.id} onClick={()=>elevate(p, u)}>
                            Make a partner</button>
                        )}
                        {role === "owner" && u && u.role === "partner" && (
                          <button className="btn ghost sm" style={{marginLeft:6}}
                                  onClick={()=>setView("settings")}>
                            What they can do</button>
                        )}
                        {role === "owner" && (
                          <button className="btn ghost sm" style={{marginLeft:6}}
                                  title="Take them off this project"
                                  onClick={async ()=>{
                                    if (!window.confirm(
                                      `Remove ${p.name} from this project?\n\n` +
                                      `They stop appearing on the list and nothing more is sent to ` +
                                      `them. Their record and anything they signed stays — nothing ` +
                                      `on this system is deleted.`)) return;
                                    setBusy(p.id);
                                    try {
                                      await api.setPartyStatus({ id: p.id, status: "inactive" });
                                      await refresh(); await load();
                                      say(`${p.name} removed from the project.`);
                                    } catch (e) { say(e.message); }
                                    setBusy(null);
                                  }}>Remove</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {rows.some((p) => !userFor(p.id)) && (
        <p className="muted" style={{fontSize:11.5,marginTop:11,maxWidth:660}}>
          <b>No account yet</b> means they were loaded from a list and have never signed in. To give
          one of them more than investor access, invite them first — they choose a password, and then
          you can make them a partner. Nothing can be sent to a held address until you release it.
        </p>
      )}

      <p className="muted" style={{fontSize:11.5,marginTop:11,maxWidth:660}}>
        Investors never see each other. Every message this system sends goes to one person — there is
        no group send, and no address appears on anybody else's copy.
      </p>
    </React.Fragment>
  );
}

function InvestorPageEditor({ db, say, refresh }) {
  const [doc, setDoc] = useState(null);
  const [docs, setDocs] = useState([]);
  const [versions, setVersions] = useState([]);
  const [published, setPublished] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("headline");

  const load = () => api.investorPage().then((r) => {
    setDoc(r.content || {});
    setDocs(r.documents || []);
    setVersions(r.versions || []);
    setPublished(!!r.published);
    setDirty(false);
  }).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  if (err && !doc) return <Empty title="Could not open the investor page" body={err}/>;
  if (!doc) return <p className="eyebrow" style={{padding:20}}>Loading…</p>;

  // Edits stay local until saved, so a half-written sentence never reaches an
  // investor mid-thought.
  const edit = (fn) => {
    const next = JSON.parse(JSON.stringify(doc));
    fn(next);
    setDoc(next);
    setDirty(true);
  };
  const list = (key) => doc[key] || [];
  const rowOps = (key, i, make) => ({
    first: i === 0,
    last: i === list(key).length - 1,
    onUp: () => edit((d) => { const a = d[key]; const t = a[i - 1]; a[i - 1] = a[i]; a[i] = t; }),
    onDown: () => edit((d) => { const a = d[key]; const t = a[i + 1]; a[i + 1] = a[i]; a[i] = t; }),
    onRemove: () => edit((d) => { d[key].splice(i, 1); }),
    onAdd: make ? () => edit((d) => { d[key].splice(i + 1, 0, make()); }) : null,
  });

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.saveInvestorPage({ content: doc });
      await load();
      say("Saved. The version before this one is kept.");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const togglePublished = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.publishInvestorPage({ publish: !published });
      await load();
      say(r.published ? "Published. Investors can read it now."
                      : "Unpublished. Investors see a holding message.");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const needTotal  = list("need").reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const gatedTotal = list("gated").reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const pageUrl = `/api/projects/${db.project.id}/investor-page.html`;

  return (
    <React.Fragment>
      <h1 className="pagetitle">Investor page</h1>
      <p className="pagesub">
        Every number an investor reads comes from here. Editing is safe: nothing changes for them
        until you save, and each save keeps the version before it.
      </p>

      <div className="strip" style={{marginTop:16}}>
        <Stat k="Still needed" v={usd(needTotal)} sub={list("need").length + " lines"}/>
        <Stat k="Already committed" v={usd(gatedTotal)} sub={list("gated").length + " lines"}/>
        <Stat k="Documents" v={docs.filter((d) => d.visible).length}
              sub={docs.length === docs.filter((d) => d.visible).length
                ? "all visible"
                : (docs.length - docs.filter((d) => d.visible).length) + " hidden"}/>
        <Stat k="Status" v={published ? "Published" : "Draft"}
              tone={published ? "good" : "flag"}
              sub={published ? "investors can read it" : "investors cannot see it"}/>
      </div>

      <div className="toolbar noprint" style={{marginTop:14}}>
        <button className="btn go" disabled={busy || !dirty} onClick={save}>
          {busy ? "Working…" : dirty ? "Save changes" : "Saved"}
        </button>
        <button className="btn" disabled={busy} onClick={togglePublished}>
          {published ? "Unpublish" : "Publish"}
        </button>
        <a className="btn ghost" href={pageUrl} target="_blank" rel="noopener">Open the page</a>
        <a className="btn ghost" href={pageUrl + "?ideas=1"} target="_blank" rel="noopener">
          With the simulator
        </a>
        <div style={{flex:1}}/>
        {dirty && <span className="muted" style={{fontSize:12}}>unsaved changes</span>}
      </div>
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}

      {!published && (
        <div className="banner" style={{marginTop:12}}>
          <h4>Not published</h4>
          <p>
            Anyone opening the link sees a holding message. Publishing is refused while the status
            line still calls the page a draft — so nobody publishes a page that describes itself as
            unapproved.
          </p>
        </div>
      )}

      <div className="tabs noprint" style={{marginTop:16}}>
        {IP_TABS.map(([k, label]) => (
          <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>

      {tab === "headline" && (
        <div className="card pad" style={{marginTop:12,maxWidth:720}}>
          <SectionHead>The top of the page</SectionHead>
          {[["revision","Status line"],["preparedFor","Prepared for"],
            ["asOf","As of"],["source","Source of the figures"]].map(([k, label]) => (
            <label key={k} style={{display:"block",marginTop:10}}>
              <div className="eyebrow">{label}</div>
              <TextField value={(doc.meta || {})[k]}
                         onChange={(v) => edit((d) => { d.meta = d.meta || {}; d.meta[k] = v; })}/>
            </label>
          ))}

          <SectionHead>The response deadline</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            Three phases in order: the date you ask people to reply by, an extended window that takes
            over on its own, then an open-ended message once both have passed. Clear the first date to
            remove the bar entirely.
          </p>
          {[["date","First date — YYYY-MM-DD"],["label","How that date reads"],
            ["sub","The line beneath it"],["subFinal","What it says on the last day"],
            ["extendedDate","Extended date — YYYY-MM-DD"],["extendedLabel","How the extension reads"],
            ["extendedBadge","Badge on the extension"],["extendedSub","The line while extended"],
            ["extendedFinal","On the last extended day"],
            ["openHeadline","Once both dates have passed"],["openSub","And the line beneath that"]]
            .map(([k, label]) => (
            <label key={k} style={{display:"block",marginTop:8}}>
              <div className="eyebrow">{label}</div>
              <TextField value={(doc.deadline || {})[k]}
                onChange={(v) => edit((d) => { d.deadline = d.deadline || {}; d.deadline[k] = v || null; })}/>
            </label>
          ))}
        </div>
      )}

      {tab === "need" && (
        <div className="card pad" style={{marginTop:12,maxWidth:820}}>
          <SectionHead right={<span className="num" style={{fontWeight:700}}>{usd(needTotal)}</span>}>
            What the money is for
          </SectionHead>
          {list("need").map((r, i) => (
            <div key={i} className="editrow">
              <div className="editline">
                <TextField value={r.label} placeholder="What it is"
                           onChange={(v) => edit((d) => { d.need[i].label = v; })}/>
                <MoneyField value={r.amount} onChange={(v) => edit((d) => { d.need[i].amount = v; })}/>
                <Check label="urgent" checked={r.urgent}
                       onChange={(v) => edit((d) => { d.need[i].urgent = v; })}/>
              </div>
              <TextField area value={r.note} placeholder="Why it matters — optional"
                         onChange={(v) => edit((d) => { d.need[i].note = v; })}/>
              <RowTools {...rowOps("need", i, () => ({ label:"", amount:0 }))}/>
            </div>
          ))}
          <button className="btn ghost sm" style={{marginTop:12}}
            onClick={() => edit((d) => { d.need = d.need || []; d.need.push({ label:"", amount:0 }); })}>
            + Add a line
          </button>
        </div>
      )}

      {tab === "committed" && (
        <div className="card pad" style={{marginTop:12,maxWidth:820}}>
          <SectionHead right={<span className="num" style={{fontWeight:700}}>{usd(gatedTotal)}</span>}>
            Capital already committed
          </SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            Money allocated to the project that releases on reaching operations.
          </p>
          {list("gated").map((r, i) => (
            <div key={i} className="editrow">
              <div className="editline">
                <TextField value={r.label} placeholder="What it is"
                           onChange={(v) => edit((d) => { d.gated[i].label = v; })}/>
                <MoneyField value={r.amount} onChange={(v) => edit((d) => { d.gated[i].amount = v; })}/>
              </div>
              <TextField area value={r.note} placeholder="Conditions — optional"
                         onChange={(v) => edit((d) => { d.gated[i].note = v; })}/>
              <RowTools {...rowOps("gated", i, () => ({ label:"", amount:0 }))}/>
            </div>
          ))}
          <button className="btn ghost sm" style={{marginTop:12}}
            onClick={() => edit((d) => { d.gated = d.gated || []; d.gated.push({ label:"", amount:0 }); })}>
            + Add a line
          </button>

          <SectionHead>Shown as a footnote instead</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            For a figure too large to sit on the same scale as the rest — the CDFI refinance, for
            instance. It reads as a note rather than a bar.
          </p>
          <div className="editline" style={{marginTop:8}}>
            <TextField value={(doc.gatedFootnote || {}).label} placeholder="What it is"
              onChange={(v) => edit((d) => { d.gatedFootnote = d.gatedFootnote || {}; d.gatedFootnote.label = v; })}/>
            <MoneyField value={(doc.gatedFootnote || {}).amount}
              onChange={(v) => edit((d) => { d.gatedFootnote = d.gatedFootnote || {}; d.gatedFootnote.amount = v; })}/>
            <Check label="mark preliminary" checked={(doc.gatedFootnote || {}).preliminary}
              onChange={(v) => edit((d) => { d.gatedFootnote = d.gatedFootnote || {}; d.gatedFootnote.preliminary = v; })}/>
          </div>
          <TextField area value={(doc.gatedFootnote || {}).note} placeholder="The note itself"
            onChange={(v) => edit((d) => { d.gatedFootnote = d.gatedFootnote || {}; d.gatedFootnote.note = v; })}/>
        </div>
      )}

      {tab === "coverage" && (
        <div className="card pad" style={{marginTop:12,maxWidth:880}}>
          <SectionHead>Coverage scenarios</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            Three ways the gap closes. Each one is investor contributions plus cost reductions plus
            pre-opening revenue, measured against the {usd(needTotal)} above — so the shortfall
            recalculates as you type.
          </p>
          {list("scenarios").map((sc, i) => {
            const tot = (Number(sc.investor)||0) + (Number(sc.reductions)||0) + (Number(sc.proposed)||0);
            const gap = needTotal - tot;
            return (
              <div key={i} className="editrow">
                <div className="editline">
                  <TextField width={190} value={sc.name} placeholder="Name"
                             onChange={(v) => edit((d) => { d.scenarios[i].name = v; })}/>
                  <span className="eyebrow">investors</span>
                  <MoneyField width={116} value={sc.investor}
                              onChange={(v) => edit((d) => { d.scenarios[i].investor = v; })}/>
                  <span className="eyebrow">reductions</span>
                  <MoneyField width={116} value={sc.reductions}
                              onChange={(v) => edit((d) => { d.scenarios[i].reductions = v; })}/>
                  <span className="eyebrow">pre-open</span>
                  <MoneyField width={116} value={sc.proposed}
                              onChange={(v) => edit((d) => { d.scenarios[i].proposed = v; })}/>
                  <span className={"tag " + (gap > 0 ? "p" : "g")}>
                    {gap > 0 ? "short " + usd(gap) : gap < 0 ? "over " + usd(-gap) : "covers it"}
                  </span>
                </div>
                <TextField area value={sc.caption} placeholder="What has to be true for this one"
                           onChange={(v) => edit((d) => { d.scenarios[i].caption = v; })}/>
                <RowTools {...rowOps("scenarios", i)}/>
              </div>
            );
          })}

          <SectionHead>What each source means</SectionHead>
          {["investor","reductions","proposed"].map((k) => (
            <div key={k} style={{marginTop:10}}>
              <div className="eyebrow">{k}</div>
              <TextField value={((doc.sourceDetail || {})[k] || {}).name} placeholder="Label"
                onChange={(v) => edit((d) => {
                  d.sourceDetail = d.sourceDetail || {};
                  d.sourceDetail[k] = d.sourceDetail[k] || {}; d.sourceDetail[k].name = v; })}/>
              <TextField area value={((doc.sourceDetail || {})[k] || {}).note} placeholder="The explanation"
                onChange={(v) => edit((d) => {
                  d.sourceDetail = d.sourceDetail || {};
                  d.sourceDetail[k] = d.sourceDetail[k] || {}; d.sourceDetail[k].note = v; })}/>
            </div>
          ))}
        </div>
      )}

      {tab === "timeline" && (
        <div className="card pad" style={{marginTop:12,maxWidth:820}}>
          <SectionHead>The path to first revenue</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            Written the way you would say it out loud — "Mid-Aug", "September". Tick the money marker
            for a row that is an obligation rather than a step.
          </p>
          {list("timeline").map((r, i) => (
            <div key={i} className="editrow">
              <div className="editline">
                <TextField width={130} value={r.when} placeholder="When"
                           onChange={(v) => edit((d) => { d.timeline[i].when = v; })}/>
                <TextField value={r.what} placeholder="What happens"
                           onChange={(v) => edit((d) => { d.timeline[i].what = v; })}/>
                <Check label="money" checked={r.money}
                       onChange={(v) => edit((d) => { d.timeline[i].money = v; })}/>
              </div>
              <TextField area value={r.note} placeholder="Detail — optional"
                         onChange={(v) => edit((d) => { d.timeline[i].note = v; })}/>
              <RowTools {...rowOps("timeline", i, () => ({ when:"", what:"" }))}/>
            </div>
          ))}
          <button className="btn ghost sm" style={{marginTop:12}}
            onClick={() => edit((d) => { d.timeline = d.timeline || []; d.timeline.push({ when:"", what:"" }); })}>
            + Add a step
          </button>
        </div>
      )}

      {tab === "footnotes" && (
        <div className="card pad" style={{marginTop:12,maxWidth:820}}>
          <SectionHead>Disclosures</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            These are the paragraphs a lawyer reads first. Keep the one saying this page is not an
            offering document.
          </p>
          {list("footnotes").map((f, i) => (
            <div key={i} className="editrow">
              <TextField area rows={3} value={f} onChange={(v) => edit((d) => { d.footnotes[i] = v; })}/>
              <RowTools {...rowOps("footnotes", i, () => "")}/>
            </div>
          ))}
          <button className="btn ghost sm" style={{marginTop:12}}
            onClick={() => edit((d) => { d.footnotes = d.footnotes || []; d.footnotes.push(""); })}>
            + Add a disclosure
          </button>
        </div>
      )}

      {tab === "documents" && <InvestorDocs docs={docs} say={say} reload={load}/>}

      {tab === "resources" && (
        <div className="card pad" style={{marginTop:12,maxWidth:820}}>
          <SectionHead>Before you decide</SectionHead>
          <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:640}}>
            Further reading, shown under the agreements. A row can point at a document you have
            uploaded here, or at a link somewhere else — a file in your Drive, for instance.
            A link goes wherever it points: it is not served through this system and does not
            expire, so only put things there you are content for a reader to keep.
          </p>
          {list("resources").map((r, i) => (
            <div key={i} className="editrow">
              <div className="editline">
                <TextField value={r.label} placeholder="What it is"
                           onChange={(v) => edit((d) => { d.resources[i].label = v; })}/>
              </div>
              <TextField value={r.note} placeholder="One line about it"
                         onChange={(v) => edit((d) => { d.resources[i].note = v; })}/>
              <div className="editline" style={{marginTop:8}}>
                <select className="field" style={{maxWidth:230}}
                        value={r.document || ""}
                        onChange={(e) => edit((d) => {
                          d.resources[i].document = e.target.value || undefined;
                          if (e.target.value) d.resources[i].url = undefined;
                        })}>
                  <option value="">A link somewhere else</option>
                  {docs.map((doc) => <option key={doc.id} value={doc.title}>{doc.title}</option>)}
                </select>
                {!r.document && (
                  <TextField value={r.url} placeholder="https://…"
                             onChange={(v) => edit((d) => { d.resources[i].url = v; })}/>
                )}
              </div>
              <RowTools {...rowOps("resources", i, () => ({ label:"", note:"", url:"" }))}/>
            </div>
          ))}
          <button className="btn ghost sm" style={{marginTop:12}}
            onClick={() => edit((d) => { d.resources = d.resources || [];
                                         d.resources.push({ label:"", note:"", url:"" }); })}>
            + Add a row
          </button>
        </div>
      )}

      {tab === "simulator" && (
        <div className="card pad" style={{marginTop:12,maxWidth:880}}>
          <SectionHead right={<span className="tag n">never sent to investors</span>}>
            Revenue simulator
          </SectionHead>
          <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:640}}>
            The team's scratchpad for revenue options. The server leaves it out of the investor page
            rather than hiding it there — which is what the old passcode did, while still delivering
            every word of it to every reader.
          </p>
          {["preOpen","postOpen","outOfScope"].map((group) => {
            const g = (doc.ideas || {})[group];
            if (!g) return null;
            const items = Array.isArray(g) ? g : (g.items || []);
            const at = (d) => Array.isArray(d.ideas[group]) ? d.ideas[group] : d.ideas[group].items;
            return (
              <div key={group} style={{marginTop:18}}>
                <div className="eyebrow">{g.heading || group}</div>
                {items.map((it, i) => (
                  <div key={i} className="editrow">
                    <div className="editline">
                      <Check label="on" checked={it.on !== false}
                             onChange={(v) => edit((d) => { at(d)[i].on = v; })}/>
                      <TextField value={it.name} onChange={(v) => edit((d) => { at(d)[i].name = v; })}/>
                    </div>
                    {(it.inputs || []).length > 0 && (
                      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:8}}>
                        {it.inputs.map((inp, j) => (
                          <label key={j}>
                            <div className="eyebrow">{inp.label || inp.k}</div>
                            <MoneyField width={108} value={inp.v}
                              onChange={(v) => edit((d) => { at(d)[i].inputs[j].v = v == null ? 0 : v; })}/>
                          </label>
                        ))}
                      </div>
                    )}
                    {it.formula && (
                      <label style={{display:"block",marginTop:8}}>
                        <div className="eyebrow">Formula — uses the field names above</div>
                        <input className="field mono" style={{fontSize:12}} value={it.formula}
                               onChange={(e) => edit((d) => { at(d)[i].formula = e.target.value; })}/>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {versions.length > 0 && (
        <div className="card pad" style={{marginTop:16,maxWidth:720}}>
          <SectionHead>Saved versions</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            Kept so you can show exactly what the page said on a given day.
          </p>
          <div className="tablewrap" style={{marginTop:10}}>
            <table className="sov">
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id}>
                    <td className="mono" style={{whiteSpace:"nowrap"}}>{fmtStamp(v.at)}</td>
                    <td>{v.note || <span className="muted">no note</span>}</td>
                    <td className="r">
                      {v.published ? <span className="tag g">published</span>
                                   : <span className="tag n">draft</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

/** The agreements and reports an investor may download. */
function InvestorDocs({ docs, say, reload }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const KINDS = [["loan","Loan"],["fundI","Fund I"],["fundII","Fund II"],
                 ["projections","Projections"],["other","Other"]];

  const patch = async (id, body) => {
    try { await api.updateInvestorDocument({ id, ...body }); await reload(); }
    catch (e) { say(e.message); }
  };

  return (
    <div className="card pad" style={{marginTop:12,maxWidth:880}}>
      <SectionHead>Documents</SectionHead>
      <p className="muted" style={{fontSize:12.5,marginTop:0,maxWidth:640}}>
        Served through links that expire, not public addresses. Hiding one takes it off the page at
        once; removing one keeps the record that it was there.
      </p>
      {docs.length === 0 && (
        <Empty title="No documents yet" body="Add the agreements investors need to read."/>
      )}
      {docs.map((d) => (
        <div key={d.id} className="editrow">
          <div className="editline">
            <TextField value={d.title} onChange={(v) => patch(d.id, { title: v })}/>
            <select className="field" style={{maxWidth:150}} value={d.kind}
                    onChange={(e) => patch(d.id, { kind: e.target.value })}>
              {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <Check label="visible" checked={d.visible}
                   onChange={(v) => patch(d.id, { visible: v })}/>
          </div>
          <TextField value={d.description} placeholder="What an investor should know before opening it"
                     onChange={(v) => patch(d.id, { description: v })}/>
          <div className="editline" style={{marginTop:8}}>
            <span className="muted mono" style={{fontSize:11}}>
              {d.filename} · {Math.max(1, Math.round(d.size / 1024)).toLocaleString()} KB · {d.hash}
            </span>
            <div style={{flex:1}}/>
            <button className="btn ghost sm" onClick={async () => {
              try {
                const r = await api.downloadInvestorDocument(d.id);
                window.open(r.url, "_blank", "noopener");
              } catch (e) { say(e.message); }
            }}>Open</button>
            <button className="btn ghost sm" onClick={async () => {
              if (!window.confirm(`Remove "${d.title}" from the page? The record that it existed is kept.`)) return;
              try { await api.deleteInvestorDocument(d.id); await reload(); say("Removed."); }
              catch (e) { say(e.message); }
            }}>Remove</button>
          </div>
        </div>
      ))}
      <label className="btn ghost" style={{marginTop:14,display:"inline-block",cursor:busy?"default":"pointer"}}>
        {busy ? "Uploading…" : "Add a document"}
        <input type="file" accept="application/pdf" style={{display:"none"}} disabled={busy}
               onChange={async (e) => {
                 const f = e.target.files && e.target.files[0];
                 e.target.value = "";
                 if (!f) return;
                 setBusy(true); setErr(null);
                 try {
                   await api.uploadInvestorDocument({ file: f, kind: "other",
                     title: f.name.replace(/\.[A-Za-z0-9]+$/, "") });
                   await reload();
                   say("Uploaded. Give it a title and say which option it belongs to.");
                 } catch (e2) { setErr(e2.message); }
                 setBusy(false);
               }}/>
      </label>
      {err && <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>{err}</p>}
    </div>
  );
}

function Audit({ db }) {
  const [q, setQ] = useState("");
  // Verification happens on the SERVER, which holds the hashing key material
  // and recomputes SHA-256 over the stored rows. The browser cannot verify a
  // chain it was handed — and a client-side recomputation that disagrees with
  // the server produces the worst possible outcome: a false "records have been
  // altered" on a log that is fine.
  const [check, setCheck] = useState(null);
  useEffect(() => { api.verifyChain().then(setCheck).catch(() => setCheck({ error: true })); }, [db.audit.length]);

  const badFrom = check && check.brokenAt ? Number(check.brokenAt.id) : null;
  const chain = db.audit.map(e => ({ ...e, valid: badFrom === null || Number(e.id) < badFrom }));
  const broken = !!(check && check.brokenAt);
  const pending = !check;
  const rows = chain.filter(e => !q || (e.actor+e.action+e.target+e.detail).toLowerCase().includes(q.toLowerCase())).reverse();

  return (
    <React.Fragment>
      <h1 className="pagetitle">Audit trail</h1>
      <p className="pagesub">Append-only. Every row is hashed against the one before it, so a changed record breaks the chain visibly.</p>

      <div className={"banner " + (broken || pending ? "" : "brass")} style={{marginTop:16,
           borderColor: broken?"var(--oxide)":"var(--verdigris)",
           background: broken?"var(--oxide-soft)":pending?"var(--paper-2)":"var(--verdigris-soft)",
           borderLeftColor: broken?"var(--oxide)":"var(--verdigris)",
           color: broken?"#5E2117":"#16403A"}}>
        <h4>{pending ? "Verifying…"
             : broken ? "Chain broken — records have been altered"
             : "Chain verified · " + chain.length + " records"}</h4>
        <p>{pending
          ? "Asking the server to recompute every hash in the chain."
          : broken
          ? "Record " + check.brokenAt.id + " no longer matches its hash, and everything after it is therefore unverified. Export and investigate before relying on this log."
          : "Head hash " + (check && check.head ? check.head.hash.slice(0, 16) + "…" : "—") +
            ", recomputed by the server just now over " + chain.length + " records."}</p>
      </div>

      <div className="toolbar noprint">
        <input className="field" style={{maxWidth:280}} placeholder="Search actor, action, item…" value={q} onChange={e=>setQ(e.target.value)} aria-label="Search audit trail"/>
        <div style={{flex:1}}/>
        <button className="btn ghost sm" onClick={()=>download("calico-audit-" + iso(TODAY) + ".csv",
          toCSV(chain, [["at","Timestamp"],["actor","Actor"],["role","Role"],["action","Action"],["target","Line Item"],["detail","Detail"],["prevHash","Prev Hash"],["hash","Hash"]]))}>Export audit trail</button>
      </div>

      <div className="tablewrap audit">
        <div className="row h"><div>Timestamp</div><div>Role</div><div>Event</div><div>Hash</div></div>
        {rows.map(e => (
          <div className="row" key={e.id}>
            <div className="muted">{fmtStamp(e.at)}</div>
            <div style={{color: e.action==="disbursement.unapproved" ? "var(--oxide)" : "inherit"}}>{e.role}</div>
            <div>
              <strong style={{fontWeight:600}}>{e.action}</strong>{e.target && <span className="muted"> · {e.target}</span>}
              {e.detail && <div className="muted" style={{fontSize:11}}>{e.actor} — {e.detail}</div>}
            </div>
            <div className={e.valid ? "chainok" : "chainbad"} title={e.valid?"Hash verified":"Hash mismatch"}>{e.valid?"✓ ":"✕ "}{e.hash}</div>
          </div>
        ))}
      </div>
    </React.Fragment>
  );
}

/* -------------------------------------------------------- Import / export */
const TEMPLATES = {
  tasks: { file:"template-schedule-of-values.csv", label:"Schedule of values (tasks)",
    blurb:"The main one. Every item of work with its value, dates and penalty rate.",
    cols:[["Item No","06-100"],["Division","06 Carpentry"],["Description of Work","Bar millwork & back bar"],
      ["Scope Detail","Front bar, back bar casework, rail, taps rough-in coordination."],
      ["Scheduled Value","64500"],["% Complete","0"],["Status","not_started"],
      ["Planned Start","2026-09-01"],["Planned Finish","2026-09-30"],["Actual Start",""],["Actual Finish",""],
      ["Materials / Allowances","Quartersawn white oak; brass rail"],["Penalty Per Day","350"],
      ["Disbursed To Date","0"],["Assigned To",""]] },
  contractors: { file:"template-contractors.csv", label:"Contractors & subs",
    blurb:"Bulk-load the directory instead of typing each one.",
    cols:[["Company","Pine Belt Electric"],["Contact","Estimator"],["Role","contractor"],["Trade","Electrical"],
      ["Email","bids@example.com"],["Phone","601-555-0100"],["License","MS-EC-11111"],
      ["Insurance Expires","2027-02-28"],["W-9 On File","yes"],["Note","Local sub, referred by the architect."]] },
  materials: { file:"template-materials-and-costs.csv", label:"Materials & cost projections",
    blurb:"Buyout tracking against each line item — what you budgeted versus what it actually costs.",
    cols:[["Item No","06-100"],["Material","Quartersawn white oak, 4/4"],["Quantity","420"],["Unit","BF"],
      ["Unit Cost","8.75"],["Budgeted","3675"],["Quoted","3940"],["Supplier","Local mill"],
      ["Lead Time Weeks","4"],["Needed On Site","2026-09-08"],["Ordered","no"]] },
  milestones: { file:"template-milestones.csv", label:"Milestones & deadlines",
    blurb:"The dates that matter to the bank and the licensing authority, not just to the trades.",
    cols:[["Milestone","Health department inspection"],["Target Date","2026-10-09"],
      ["Depends On Items","11-100; 11-200; 15-200"],["Owner","Kenneth Moye"],
      ["Consequence If Missed","Opening slips 3+ weeks; licensing window closes."],["Notes",""]] },
};

function DataIO({ db, role, me, say, refresh }) {
  const [mode, setMode] = useState("upsert");
  const [raw, setRaw] = useState(null);      // { headers, rows, filename }
  const [map, setMap] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const canImport = role === "owner";

  const pick = async (file) => {
    setErr(null); setRaw(null);
    try {
      const rows = parseCSV(await file.text());
      if (rows.length < 2) throw new Error("That file has a header row but no data underneath it.");
      const headers = rows[0];
      setRaw({ headers, rows: rows.slice(1), filename: file.name });
      setMap(guessMapping(headers));
    } catch (e) { setErr(e.message); }
  };

  const preview = raw ? rowsToItems(raw.rows, map, db.parties) : null;
  const missing = REQUIRED_FIELDS.filter(f => map[f] == null);
  const unmapped = raw ? raw.headers.map((h,i)=>({h,i})).filter(({i}) => !Object.values(map).includes(i)) : [];

  const commit = async () => {
    setBusy(true);
    await api.importLineItems(PROJECT_ID, { items: preview, mode, actor: me.contact });
    setRaw(null); setMap({}); await refresh(); setBusy(false);
    say(preview.length + " line items imported");
  };
  const tmpl = (t) => toCSV([Object.fromEntries(t.cols.map(([l,v],i)=>[i,v]))],
    t.cols.map(([l],i)=>[String(i), l]));

  return (
    <React.Fragment>
      <h1 className="pagetitle">Import / export</h1>
      <p className="pagesub">Bring in the spreadsheet you already keep. Your column names don't have to match anything —
        you'll map them on screen and see exactly what lands before it lands.</p>

      <div className="card pad" style={{marginTop:16}}>
        <SectionHead>Import your schedule of values</SectionHead>
        {!canImport
          ? <div className="muted" style={{fontSize:12.5}}>Only the owner can change the schedule of values.</div>
          : (
          <React.Fragment>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <button className="btn" onClick={()=>ref.current.click()}>Choose a CSV file</button>
              <input ref={ref} type="file" accept=".csv,text/csv" hidden
                     onChange={e=>{ if(e.target.files[0]) pick(e.target.files[0]); e.target.value=""; }}/>
              <select className="field" style={{maxWidth:190}} value={mode} onChange={e=>setMode(e.target.value)} aria-label="Import mode">
                <option value="upsert">Add to what's there</option>
                <option value="replace">Replace everything</option>
              </select>
              {raw && <span className="muted" style={{fontSize:12}}>{raw.filename} · {raw.rows.length} rows</span>}
            </div>

            {err && <div className="banner" style={{marginTop:12}}><h4>That file didn't open</h4><p>{err}</p></div>}

            {raw && (
              <React.Fragment>
                <div style={{marginTop:18}}>
                  <SectionHead right={<span className="muted" style={{fontSize:11}}>
                    {Object.keys(map).length} of {raw.headers.length} columns matched</span>}>
                    Match your columns
                  </SectionHead>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:10}}>
                    {CSV_COLUMNS.filter(([k]) => k !== "assignStatus").map(([k,label]) => (
                      <label key={k}>
                        <div className="eyebrow">{label}{REQUIRED_FIELDS.includes(k) && <span style={{color:"var(--oxide)"}}> · required</span>}</div>
                        <select className="field" value={map[k] == null ? "" : map[k]}
                          style={{borderColor: REQUIRED_FIELDS.includes(k) && map[k]==null ? "var(--oxide)" : undefined}}
                          onChange={e=>setMap(m => { const n = {...m};
                            if (e.target.value === "") delete n[k]; else n[k] = Number(e.target.value); return n; })}>
                          <option value="">— not in my file —</option>
                          {raw.headers.map((h,i) => <option key={i} value={i}>{h || "(column " + (i+1) + ")"}</option>)}
                        </select>
                      </label>
                    ))}
                  </div>
                  {unmapped.length > 0 && (
                    <p className="muted" style={{fontSize:11.5,marginTop:10,marginBottom:0}}>
                      Ignoring {unmapped.length} column{unmapped.length===1?"":"s"}: {unmapped.map(u=>u.h||"(blank)").join(", ")}.
                    </p>
                  )}
                </div>

                {missing.length > 0 ? (
                  <div className="banner" style={{marginTop:14}}>
                    <h4>Still need {missing.length} column{missing.length===1?"":"s"}</h4>
                    <p>{missing.map(f=>FIELD_LABEL[f]).join(", ")}. Point {missing.length===1?"it":"them"} at a column
                       above, or add {missing.length===1?"it":"them"} to your spreadsheet and re-upload.</p>
                  </div>
                ) : (
                  <React.Fragment>
                    <div className="kv" style={{marginTop:14}}>
                      <div><div className="k">Rows read</div><div className="v">{preview.length}</div></div>
                      <div><div className="k">Total value</div><div className="v">{usd(preview.reduce((s,i)=>s+i.scheduledValue,0))}</div></div>
                      <div><div className="k">With dates</div><div className="v">{preview.filter(i=>i.plannedFinish).length}</div></div>
                      <div><div className="k">Divisions</div><div className="v">{new Set(preview.map(i=>i.division)).size}</div></div>
                    </div>
                    <div style={{marginTop:12,maxHeight:260,overflow:"auto",border:"1px solid var(--rule)",borderRadius:3}}>
                      <table className="sov" style={{minWidth:520}}>
                        <thead><tr><th className="plain">Item</th><th className="plain">Description</th>
                          <th className="plain">Value</th><th className="plain">Finish</th><th className="plain">Assigned</th></tr></thead>
                        <tbody>
                          {preview.slice(0,40).map(i => (
                            <tr key={i.id}>
                              <td className="itemno">{i.no}</td>
                              <td><div className="itemname">{i.name}</div><div className="itemmeta">{i.division}</div></td>
                              <td className="r num">{usd(i.scheduledValue)}</td>
                              <td className="num" style={{fontSize:12, color: i.plannedFinish ? "inherit" : "var(--brass)"}}>
                                {i.plannedFinish ? fmtDate(i.plannedFinish) : "none"}</td>
                              <td style={{fontSize:12}}>{i.assignment ? partyName(db, i.assignment.partyId) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {preview.filter(i=>!i.plannedFinish).length > 0 && (
                      <p style={{fontSize:12,color:"var(--brass)",marginTop:9}}>
                        {preview.filter(i=>!i.plannedFinish).length} rows have no finish date. They'll import, but they
                        won't appear on the timeline and no delay penalty can run against them.
                      </p>
                    )}
                    {mode === "replace" && (
                      <p style={{fontSize:12,color:"var(--oxide)",marginTop:9}}>
                        Replacing removes all {db.lineItems.length} current line items, including photos, punch items
                        and signatures already on file.
                      </p>
                    )}
                    <div className="toolbar">
                      <button className="btn go" disabled={busy} onClick={commit}>Import {preview.length} line items</button>
                      <button className="btn ghost" onClick={()=>{ setRaw(null); setMap({}); }}>Cancel</button>
                    </div>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(310px,1fr))",gap:16,marginTop:16}}>
        <div className="card pad">
          <SectionHead>Blank templates</SectionHead>
          <p className="muted" style={{fontSize:12,marginTop:0}}>
            Start from one of these if you don't already have a spreadsheet. Each has one filled example row.
          </p>
          {Object.entries(TEMPLATES).map(([k,t]) => (
            <div key={k} style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",
              padding:"9px 0",borderBottom:"1px solid var(--rule-soft)"}}>
              <span style={{minWidth:0}}>
                <span style={{fontWeight:600,fontSize:12.5,display:"block"}}>{t.label}</span>
                <span className="muted" style={{fontSize:11.5}}>{t.blurb}</span>
              </span>
              <button className="btn ghost sm" style={{whiteSpace:"nowrap"}}
                onClick={()=>download(t.file, tmpl(t))}>Download</button>
            </div>
          ))}
          <p className="muted" style={{fontSize:11.5,marginTop:10,marginBottom:0}}>
            Materials and milestones export as reference for now — line items are the ones the app runs on.
          </p>
        </div>

        <div className="card pad">
          <SectionHead>Export</SectionHead>
          <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-start"}}>
            <button className="btn ghost" onClick={()=>download("calico-schedule-of-values-" + iso(TODAY) + ".csv", toCSV(forExport(db), CSV_COLUMNS))}>Schedule of values</button>
            <button className="btn ghost" onClick={()=>download("calico-audit-" + iso(TODAY) + ".csv",
              toCSV(db.audit, [["at","Timestamp"],["actor","Actor"],["role","Role"],["action","Action"],["target","Line Item"],["detail","Detail"],["hash","Hash"]]))}>Audit trail</button>
            <button className="btn ghost" onClick={()=>download("calico-signatures-" + iso(TODAY) + ".csv",
              toCSV(db.lineItems.flatMap(it => (it.approvals||[]).map(a => ({ no: it.no, name: it.name, role: a.role,
                org: a.partyId ? partyName(db, a.partyId) : "", who: a.who, at: a.at, note: a.note, hash: a.hash,
                rejected: a.rejected?"yes":"no" }))),
              [["no","Item No"],["name","Description of Work"],["role","Role"],["org","Company"],["who","Signed By"],
               ["at","Signed At"],["note","Note"],["hash","Signature Hash"],["rejected","Returned"]]))}>Signature register</button>
            <button className="btn ghost" onClick={()=>download("calico-onboarding-" + iso(TODAY) + ".csv",
              toCSV(db.parties.filter(x=>x.role==="contractor").map(x => { const o = onboardingOf(x); return {
                name:x.name, contact:x.contact, trade:x.trade, status:o.status, version:o.termsVersion||"",
                initials:o.initials||"", clauses:(o.clauses||[]).length, at:o.acknowledgedAt||"",
                adobe:o.adobe?o.adobe.agreementId:"", adobeStatus:o.adobe?o.adobe.status:"" }; }),
              [["name","Company"],["contact","Contact"],["trade","Trade"],["status","Onboarding Status"],
               ["version","Terms Version"],["initials","Initials"],["clauses","Clauses Initialled"],
               ["at","Acknowledged At"],["adobe","Adobe Agreement"],["adobeStatus","Adobe Status"]]))}>Onboarding register</button>
            <button className="btn ghost" onClick={()=>download("calico-draw-payments-" + iso(TODAY) + ".csv",
              toCSV((db.draws||[]).map(d => { const t = drawTotals(db, d), paid = paidOnDraw(d);
                const pay = drawDocs(d).find(x => DOC_TYPES[x.type].payment) || {};
                return { id:d.id, status:d.status, created:d.createdAt, funded:d.fundedAt||"",
                  items:d.lineItemIds.length, gross:Math.round(t.gross), retainage:Math.round(t.retainage),
                  penalties:Math.round(t.penalties), holdbacks:Math.round(t.holdbacks), net:Math.round(t.net),
                  paid, variance: paid ? Math.round(paid - t.net) : "", ref: pay.reference || "",
                  paidOn: pay.paidOn || "", docs: drawDocs(d).length,
                  missing: missingDocs(db, d).join("; ") || "none" }; }),
              [["id","Draw"],["status","Status"],["created","Requested"],["funded","Funded"],["items","Line Items"],
               ["gross","Value In Place"],["retainage","Retainage"],["penalties","Penalties"],["holdbacks","Punch Held"],
               ["net","Net Requested"],["paid","Actually Paid"],["variance","Difference"],["ref","Payment Reference"],
               ["paidOn","Date Paid"],["docs","Documents"],["missing","Missing From File"]]))}>Draw payment register</button>
            <button className="btn ghost" onClick={()=>download("calico-funding-" + iso(TODAY) + ".csv",
              toCSV(needsOf(db).map(n => { const pr = needProgress(db, n); return { name:n.name,
                category:NEED_CATEGORIES[n.category], amount:n.amount, received:pr.received,
                signed:pr.committed - pr.received, interested:pr.interested - pr.committed,
                remaining:pr.remaining, deadline:n.deadline||"", investors:pr.count,
                status:n.status, visibility:n.visibility }; }),
              [["name","Need"],["category","Category"],["amount","Amount"],["received","Received"],
               ["signed","Signed Not Funded"],["interested","Interest Only"],["remaining","Still Needed"],
               ["deadline","Needed By"],["investors","Investors"],["status","Status"],["visibility","Shown To Investors"]]))}>Funding needs</button>
            <button className="btn ghost" onClick={()=>download("calico-project-export.json", JSON.stringify(db,null,2), "application/json")}>Full project (JSON)</button>
          </div>
          <p className="muted" style={{fontSize:11.5,marginTop:12,marginBottom:0}}>
            Photos are excluded from CSV. The JSON export includes everything and is what you'd hand a backend to seed a
            real database.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
}


export default App;
